import { makeApi } from './make-api';
import { z } from 'zod';

const ProfileResponseDto = z
  .object({
    id: z.string().describe('User ID'),
    email: z.string().describe('User email address'),
    firstName: z.string().describe('User first name'),
    lastName: z.string().describe('User last name'),
    language: z.string().describe('User language preference'),
    createdAt: z.string().describe('Account creation date'),
    updatedAt: z.string().describe('Last update date'),
    isDemoUser: z.boolean().describe('Whether user is in demo mode'),
  })
  .passthrough();
const usersUpdateProfile_Body = z
  .object({
    firstName: z.string().min(1).max(100).describe('User first name'),
    lastName: z.string().min(1).max(100).describe('User last name'),
    language: z.enum(['en', 'pl', 'es', 'de']).describe('User language preference'),
  })
  .partial();
const ChartViewPreferenceDto = z
  .object({
    defaultTimeframe: z.string().describe('Default timeframe for the chart'),
    returnType: z
      .string()
      .describe("Analytics performance chart return basis: 'mwr' | 'twr' | 'simple'"),
    icMode: z.string().describe("Analytics performance chart IC mode: 'external' | 'costBasis'"),
    logScale: z
      .boolean()
      .describe('The reader switched this chart to a logarithmic value axis (absent means linear)'),
  })
  .partial()
  .passthrough();
const ChartsPreferencesDto = z
  .object({
    portfolioTransactions: ChartViewPreferenceDto.describe(
      'Portfolio transactions chart preferences',
    ),
    portfolioAnalytics: ChartViewPreferenceDto.describe('Portfolio analytics chart preferences'),
    portfolioHoldings: ChartViewPreferenceDto.describe('Portfolio holdings chart preferences'),
    positionPriceHistory: ChartViewPreferenceDto.describe(
      'Position price-history chart preferences',
    ),
    instrumentPrice: ChartViewPreferenceDto.describe(
      'Instrument price chart preferences (every page that shows it)',
    ),
    investmentsValue: ChartViewPreferenceDto.describe(
      'Investments combined value chart preferences',
    ),
    charting: ChartViewPreferenceDto.describe('Charting page fundamentals chart preferences'),
    qcFairValue: ChartViewPreferenceDto.describe('Quality Check fair value chart preferences'),
    qcPriceChannel: ChartViewPreferenceDto.describe(
      'Quality Check price channel chart preferences',
    ),
    portfolioDividends: ChartViewPreferenceDto.describe('Portfolio dividends chart preferences'),
    researchDividends: ChartViewPreferenceDto.describe(
      'Research dividends history chart preferences',
    ),
    researchProjections: ChartViewPreferenceDto.describe(
      'Research analyst targets chart preferences',
    ),
    researchOwnership: ChartViewPreferenceDto.describe('Research ownership chart preferences'),
  })
  .partial()
  .passthrough();
const UiPreferencesDto = z
  .object({
    charts: ChartsPreferencesDto.describe('Per-chart view preferences'),
    theme: z.enum(['light', 'dark', 'auto']).describe('UI theme'),
    language: z.string().describe('UI language code'),
    customTimeframes: z.array(z.unknown()).describe('Custom chart timeframes'),
    customBenchmarks: z.array(z.unknown()).describe('Custom benchmarks'),
    showDefaultNewsTickers: z
      .boolean()
      .describe('Show the Mag 7 platform-default news tickers (absent means shown)'),
    disabledDefaultTickers: z
      .array(z.string())
      .describe(
        'Mag 7 platform defaults the user switched off, by symbol. Supersedes the all-or-nothing flag above, which stays readable: false there means every default is off.',
      ),
    tables: z
      .object({})
      .partial()
      .passthrough()
      .describe('Per-table column set, column order, and sort, keyed by table scope'),
    displayCurrency: z
      .string()
      .describe('Account-level display currency (ISO 4217) for aggregate money surfaces'),
    portfolioDisplayCurrencies: z
      .record(
        z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .nullable(),
      )
      .describe(
        'Per-portfolio display currency, keyed by portfolio or merged-portfolio id. Merges by id: send only the books that change, null clears one. Ids the user does not own are dropped.',
      ),
    dismissedImportFileIds: z
      .array(z.string())
      .describe(
        "Import files whose 'Import did not finish' health notice the user dismissed, by file id. A statement our parsers reject fails on every retry, so the row's own remedy can never clear it. The client caps the list; the cap is generous because a long-pending orphan never ages out of the check.",
      ),
    planSlotNoticeAckTier: z.string(),
    portfolioOrder: z
      .array(z.string())
      .describe(
        'The order the user arranged their portfolio cards in on /investments, by portfolio or merged-portfolio id. Ids not listed (a book created later) follow in creation order.',
      ),
    topPositionsCount: z
      .coerce.number()
      .int()
      .gt(0)
      .describe(
        "How many rows the /investments 'largest positions' panel lists before one 'Other' row. Absent means the panel keeps its default.",
      ),
    allocationRingCount: z
      .coerce.number()
      .int()
      .gt(0)
      .describe(
        "How many of the largest positions the position allocation ring names before folding the rest into 'Other'. Separate from topPositionsCount. Absent means the ring keeps its default.",
      ),
    hiddenPortfolioCards: z
      .array(z.string())
      .describe(
        'Portfolio cards the user hid from /investments (an old, inactive book), by portfolio or merged-portfolio id. Hiding only removes the card; the book still counts in every total.',
      ),
    dismissedHealthFindingKeys: z
      .array(z.string())
      .describe(
        "Advisory health findings the user has accepted ('I checked this - it is fine'), by finding key. Only the kinds the app cannot decide for itself are offered the verdict; an accepted finding stops counting toward the 0-100 score and can be taken back.",
      ),
  })
  .partial()
  .passthrough();
const PreferencesResponseDto = z
  .object({
    id: z.string().describe('Preferences ID'),
    userId: z.string().describe('User ID'),
    uiPreferences: UiPreferencesDto.describe('UI preferences including charts settings'),
    createdAt: z.string().datetime({ offset: true }).describe('Created timestamp'),
    updatedAt: z.string().datetime({ offset: true }).describe('Updated timestamp'),
  })
  .passthrough();
const usersUpdatePreferences_Body = z
  .object({ uiPreferences: UiPreferencesDto.describe('UI preferences including charts settings') })
  .partial();
const CustomTimeframeResponseDto = z
  .object({
    id: z.string().describe('Unique identifier for the custom timeframe'),
    name: z.string().describe('Display name for the custom timeframe'),
    fromDate: z.string().describe('Start date (ISO format: YYYY-MM-DD)'),
    toDate: z
      .string()
      .describe("End date (ISO format: YYYY-MM-DD). Null means dynamic 'today'.")
      .nullable(),
    createdAt: z.string().describe('When this custom timeframe was created'),
  })
  .passthrough();
const usersCreateCustomTimeframe_Body = z.object({
  name: z.string().min(1).max(50).describe('Optional name for the custom timeframe').optional(),
  fromDate: z.string().describe('Start date for the custom timeframe (ISO format: YYYY-MM-DD)'),
  toDate: z
    .string()
    .describe(
      "End date for the custom timeframe (ISO format: YYYY-MM-DD). If not provided, defaults to dynamic 'today'.",
    )
    .optional(),
});
const usersCreateCustomBenchmark_Body = z.object({
  symbol: z.string().min(1).describe('Benchmark symbol'),
  name: z.string().min(1).describe('Display name for the benchmark'),
});
const usersDeleteAccount_Body = z
  .object({
    reason: z
      .enum([
        'missing_broker_support',
        'missing_features',
        'data_accuracy',
        'too_expensive',
        'switching_tool',
        'not_useful',
        'stopped_investing',
        'privacy_concerns',
        'other',
      ])
      .describe('Predefined reason for leaving (anonymous exit survey)'),
    comment: z.string().max(2000).describe('Free-text feedback (anonymous exit survey)'),
  })
  .partial();
const OnboardingStatusResponseDto = z
  .object({
    completed: z.boolean().describe('Whether the user has completed onboarding'),
    currentStep: z
      .string()
      .describe(
        'Current onboarding step (welcome, import-data, imported, review, or null when complete)',
      )
      .nullable(),
  })
  .passthrough();
const usersUpdateOnboardingProgress_Body = z.object({
  step: z
    .enum(['welcome', 'import-data', 'imported', 'review'])
    .describe('Onboarding step identifier'),
});
const CfdOpenPositionHintDto = z
  .object({
    symbol: z.string().describe('Underlying symbol the hint is about (e.g. ORCL.US)'),
    approximateLots: z
      .coerce.number()
      .describe('Approximate open lot count (div-eq rows at the last ex-date)'),
    approximateVolume: z
      .coerce.number()
      .describe(
        'Approximate total open volume: Σ div-eq amount ÷ per-share rate at the last ex-date. Open price/date stay unknown — the statement never carries them for open CFDs.',
      )
      .optional(),
    direction: z
      .enum(['BUY', 'SELL'])
      .describe('Inferred direction: short CFDs PAY the dividend equivalent (negative rows).')
      .optional(),
    lastEvidenceAt: z
      .string()
      .datetime({ offset: true })
      .describe('The most recent dividend-equivalent date observed'),
    source: z.literal('DIVIDEND_EQUIVALENT'),
  })
  .passthrough();
const usersSaveOnboardingDraft_Body = z.object({
  draft: z.object({}).partial().passthrough().describe('The onboarding draft data to save'),
  baseSavedAt: z
    .string()
    .describe(
      'Optimistic-concurrency base: the savedAt of the draft this save builds on (null when no draft was loaded). When present, the save is accepted only while the stored draft still carries this savedAt; a stale base answers 409 ONBOARDING_DRAFT_CONFLICT. Omit the field for the legacy blind overwrite.',
    )
    .nullish(),
});
const usersResetOnboardingSession_Body = z.object({
  walletIds: z
    .array(z.string().uuid())
    .describe('Wallet IDs created during the onboarding session'),
  portfolioIds: z
    .array(z.string().uuid())
    .describe('Portfolio IDs created during the onboarding session'),
});
const importUnknownLabelsConfirm_Body = z.object({
  event: z
    .enum([
      'trade.buy',
      'trade.sell',
      'trade.short_open',
      'trade.short_cover',
      'cash.deposit',
      'cash.withdrawal',
      'cash.external_by_sign',
      'cash.internal_transfer',
      'cash.fx_conversion',
      'cash.sweep',
      'security.transfer_in',
      'security.transfer_out',
      'income.dividend_cash',
      'income.dividend_stock',
      'income.drip',
      'income.substitute_payment',
      'income.interest_cash',
      'income.bond_coupon',
      'income.securities_lending',
      'income.return_of_capital',
      'income.capital_gain_distribution',
      'tax.withholding',
      'tax.refund',
      'tax.correction',
      'tax.transaction',
      'tax.capital_gains',
      'tax.other',
      'fee.commission',
      'fee.custody_platform',
      'fee.adr',
      'fee.margin_interest',
      'fee.advisor',
      'fee.regulatory',
      'fee.fx',
      'fee.other',
      'fee.refund',
      'ca.split',
      'ca.reverse_split',
      'ca.spinoff',
      'ca.merger_cash',
      'ca.merger_stock',
      'ca.symbol_change',
      'ca.delisting',
      'ca.rights_subscription',
      'ca.bonus_shares',
      'ca.tender_redemption',
      'ca.cash_in_lieu',
      'ca.reorganization',
      'opt.open',
      'opt.close',
      'opt.expiry',
      'opt.assignment',
      'opt.exercise',
      'fut.settlement',
      'cfd.open',
      'cfd.close',
      'cfd.financing',
      'cfd.dividend_adjustment',
      'bond.purchase',
      'bond.redemption',
      'crypto.staking_reward',
      'crypto.send',
      'crypto.receive',
      'promo.free_share',
      'promo.referral_bonus',
      'promo.cashback',
      'adj.correction',
      'adj.cancellation_reversal',
      'noise.order_block',
      'noise.summary_row',
      'noise.accrual_marker',
      'noise.platform_event',
      'noise.card_spend',
      'noise.bill_pay',
      'noise.p2p',
      'trade.buy_or_sell',
    ])
    .describe('What the rows under this label are, as a canonical event'),
});
const ShareVisibility = z.enum(['public', 'subscribers', 'hidden']);
const ShareSectionsDto = z.object({
  performanceChart: ShareVisibility,
  monthlyReturns: ShareVisibility,
  dividends: ShareVisibility,
  positionAllocation: ShareVisibility,
  assetAllocation: ShareVisibility,
  sectorDistribution: ShareVisibility,
  geographicBreakdown: ShareVisibility,
  industryDistribution: ShareVisibility,
  transactionHistory: ShareVisibility,
  holdings: ShareVisibility,
  tradeActivity: ShareVisibility,
  riskMetrics: ShareVisibility,
  returnStatistics: ShareVisibility,
  marketCapBuckets: ShareVisibility,
  dividendBuckets: ShareVisibility,
  portfolioInfo: ShareVisibility,
});
const PrivacySettingsDto = z.object({
  showValues: z
    .boolean()
    .describe(
      'SEC-011, the master money switch. false = no absolute amounts anywhere on the page (percentages only) and the dividends section is not published.',
    ),
  showPositions: z
    .boolean()
    .describe('Whether holding and trade rows carry their QUANTITY. Hides no row.'),
  positionsToHide: z
    .array(z.string())
    .describe('Position ids withheld from every section, alerts included.')
    .optional(),
  displayName: z
    .string()
    .max(100)
    .describe('The name the page shows instead of the portfolio name.')
    .optional(),
  performanceChartReturnType: z
    .enum(['mwr', 'twr', 'simple'])
    .describe('Which return the performance chart plots.')
    .optional(),
  benchmarks: z
    .array(z.string())
    .describe("Benchmark symbols drawn against the performance chart (e.g. ['SPY']).")
    .optional(),
  description: z
    .string()
    .max(500)
    .describe('The owner description shown on the page (up to 500 characters).')
    .optional(),
  tags: z
    .array(z.string().min(1).max(24))
    .max(5)
    .describe("Up to 5 tags of up to 24 characters each (e.g. ['Growth', 'Tech']).")
    .optional(),
  sections: ShareSectionsDto.describe(
    "Visibility of EVERY section (all keys required): 'public' (anyone with the link), 'subscribers' (readers subscribed to this link whose plan includes portfolio subscriptions) or 'hidden' (nobody). performanceChart also governs the drawdown, the period-returns strip and the benchmark lines; dividends is forced 'hidden' while showValues is false.",
  ),
});
const portfolioCoreUpdateShareById_Body = z
  .object({
    privacySettings: PrivacySettingsDto,
    expiresIn: z.coerce.number().gte(7).lte(90).nullable(),
    isActive: z.boolean(),
  })
  .partial();
const portfolioCoreCreateShare_Body = z.object({
  privacySettings: PrivacySettingsDto,
  expiresIn: z.coerce.number().gte(7).lte(90).nullish(),
});
const startDate = z.union([z.string(), z.string()]).optional();
const PortfolioTransactionResponseDto = z
  .object({
    id: z.string().describe('Transaction ID'),
    portfolioId: z.string().describe('Portfolio ID'),
    portfolioName: z.string().describe('Portfolio name').optional(),
    walletId: z.string().describe('Wallet ID').nullish(),
    positionId: z.string().describe('Position ID'),
    type: z
      .enum([
        'BUY',
        'SELL',
        'FEE',
        'DIVIDEND',
        'BUY_TO_OPEN',
        'BUY_TO_CLOSE',
        'SELL_TO_OPEN',
        'SELL_TO_CLOSE',
        'EXERCISE',
        'ASSIGNMENT',
        'EXPIRE',
        'SPLIT',
        'DELIVERY_INBOUND',
        'DELIVERY_OUTBOUND',
        'INTEREST',
        'TAX_REFUND',
        'FEE_REFUND',
      ])
      .describe('Transaction type'),
    instrumentSymbol: z.string().describe('Instrument symbol'),
    instrumentName: z.string().describe('Instrument name'),
    quantity: z.coerce.number().describe('Quantity'),
    price: z.coerce.number().describe('Price per unit'),
    fees: z.coerce.number().describe('Transaction fees, denominated in `feesCurrency`'),
    feesCurrency: z
      .string()
      .describe(
        "The currency `fees` is DENOMINATED in, stated by the server (`feesCurrencyOf`): a kernel fee is in `priceCurrency` (it is folded into the cost beside `quantity x price`), while a commission the statement books apart (`display_fees`: DEGIRO, myfund, the fiat fee of a crypto trade, Binance money fee) is in `currency`, the settlement currency of `totalAmount`. Often NOT `priceCurrency`. Consume this - never assume `fees` shares the price's or the amount's currency.",
      ),
    taxes: z
      .coerce.number()
      .describe('Withholding taxes (dividends) — gross = totalAmount + taxes (#560)')
      .nullish(),
    totalAmount: z.coerce.number().describe('Total transaction amount'),
    currency: z.string().describe('Transaction currency (portfolio base currency)'),
    priceCurrency: z
      .string()
      .describe(
        'The currency `price` is DENOMINATED in — often NOT `currency`. `currency` denominates `totalAmount`; a cross-currency row stores the price in the instrument trading currency and bridges it with `exchangeRate`. Derived server-side per storage class (see wirePriceCurrency) because `exchangeRate === 1` is not a reliable discriminator for option/dividend rows. Consume this — never re-derive the denomination from `exchangeRate`.',
      ),
    instrumentCurrency: z
      .string()
      .describe('Instrument trading currency (stock currency from exchange)')
      .optional(),
    assetType: z
      .enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION', 'CFD', 'FUTURE', 'FUND'])
      .describe(
        "The asset CLASS of the traded instrument, off the linked position. The positions wire has always published this (a client splits its holdings into stock/option/crypto sections on it); the transactions wire never did, so a consumer holding only a transaction row could not tell an equity from a coin. Deriving it from the ticker is not an option: bare `AAPL` and bare `BTC` are indistinguishable, and coin tickers collide with real equities (COMP, MKR). ABSENT when the row has no linked position (the position was deleted, or the row predates one) - correctness-or-null: consumers must treat 'unknown' as its own case rather than defaulting to STOCK.",
      )
      .optional(),
    exchangeRate: z
      .coerce.number()
      .describe(
        'Stored instrument→booked exchange rate (multiplicative: totalAmount = qty × price × rate). NULL = honestly unknown (correctness-or-null, IP-P4): the import could not know the rate (settlement-priced rows, unresolved stock currency, cross-currency imported dividends). Never re-fabricate 1 from a null — render an explicit unknown instead.',
      )
      .nullable(),
    bookExchangeRate: z
      .coerce.number()
      .describe(
        "Trade-day rate from the instrument currency into the book's currency (the rate this lot's cost converts at); null when the row carries none. Per-portfolio lists only.",
      )
      .nullish(),
    displayCurrency: z
      .string()
      .describe(
        "The portfolio's CURRENT currency the converted* fields are denominated in (per-portfolio lists only)",
      )
      .optional(),
    convertedTotalAmount: z
      .coerce.number()
      .describe(
        'totalAmount converted to displayCurrency (spot rate); null when no usable rate exists (never a fabricated 1:1)',
      )
      .nullish(),
    convertedPrice: z
      .coerce.number()
      .describe(
        'price converted from its OWN denomination (priceCurrency) into displayCurrency; null when no usable rate exists (never a fabricated 1:1)',
      )
      .nullish(),
    convertedFees: z
      .coerce.number()
      .describe(
        'fees converted from their OWN denomination (`feesCurrency`) into displayCurrency, at the trade-day rate of that currency; null when no usable rate exists (never a fabricated 1:1)',
      )
      .nullish(),
    convertedRealizedGainLoss: z
      .coerce.number()
      .describe(
        'taxLotInfo.realizedGainLoss converted to displayCurrency (spot rate); null when absent or no usable rate exists',
      )
      .nullish(),
    convertedTaxes: z
      .coerce.number()
      .describe(
        'taxes converted to displayCurrency at the rate of convertedTotalAmount (they share `currency`), so a dividend received gross = convertedTotalAmount + convertedTaxes; null when absent or no usable rate exists',
      )
      .nullish(),
    notes: z.string().describe('Transaction notes').optional(),
    taxLotInfo: z.unknown().describe('Tax lot information for sell transactions').optional(),
    isGift: z.boolean().describe('Whether this is a gift/promotional stock transaction').optional(),
    synthesizedOpeningLot: z
      .boolean()
      .describe(
        'True when the backend synthesized this BUY as an unknown-basis opening lot for a window-truncated sells-without-buys import (the real purchase predates the export window); a system row, not a user trade.',
      )
      .optional(),
    executedAt: z.string().describe('Execution timestamp'),
    createdAt: z.string().describe('Creation timestamp'),
    deletedAt: z.string().describe('Deletion timestamp (for soft deleted transactions)').optional(),
    sourcePortfolioId: z
      .string()
      .describe('Source portfolio ID (for merged portfolios)')
      .optional(),
    sourcePortfolioName: z
      .string()
      .describe('Source portfolio name (for merged portfolios)')
      .optional(),
  })
  .passthrough();
const PaginatedPortfolioTransactionsResponseDto = z
  .object({
    items: z.array(PortfolioTransactionResponseDto),
    total: z.coerce.number().describe('Total number of items'),
    page: z.coerce.number().describe('Current page number'),
    limit: z.coerce.number().describe('Number of items per page'),
    totalPages: z.coerce.number().describe('Total number of pages'),
    hasNext: z.boolean().describe('Whether there is a next page'),
    hasPrevious: z.boolean().describe('Whether there is a previous page'),
    omittedNoRate: z
      .coerce.number()
      .int()
      .describe(
        'Rows an active min/max amount window left out only because no trade-day rate carries them into the display currency (never compared in their own currency). Absent when the converted path did not run',
      )
      .optional(),
    withholdingBookedSeparately: z
      .boolean()
      .describe(
        "True when this page holds a dividend with no tax whose withholding the book records as its own tax row (a SnapTrade TAX activity), so its amount is not 'printed net' (D58)",
      )
      .optional(),
  })
  .passthrough();
const portfolioCoreUpdateTransaction_Body = z
  .object({
    quantity: z.coerce.number().gte(0.0001).describe('Transaction quantity'),
    price: z.coerce.number().gte(0).describe('Transaction price'),
    fees: z.coerce.number().gte(0).describe('Transaction fees'),
    taxes: z
      .coerce.number()
      .gte(0)
      .describe(
        'Withholding taxes (for dividends); null = the statement does not state the withholding (D58)',
      )
      .nullable(),
    notes: z.string().describe('Transaction notes'),
    isGift: z
      .literal(true)
      .describe(
        'Declare a BUY at price 0 a gift or promotion (its whole value is gain). Refused on any other row; send price 0 with it to declare a priced BUY one.',
      ),
    executedAt: z.union([z.string(), z.string()]).describe('Execution date (ISO format)'),
    walletId: z.string().uuid().describe('Wallet ID for the transaction'),
    exchangeRate: z
      .coerce.number()
      .gte(0.000001)
      .describe('Exchange rate for cross-currency transactions'),
    bookExchangeRate: z
      .coerce.number()
      .gte(0.000001)
      .describe(
        'Trade-day instrument-to-book-currency rate for a row booked in the instrument currency (transfer-in, foreign-wallet sell). Writes the trade-day FX mirror only — never the stored total.',
      ),
    walletExchangeRate: z
      .coerce.number()
      .gte(0.000001)
      .describe(
        'Instrument-to-wallet exchange rate for the cash leg of a cross-currency edit (create-path parity with the buy/sell walletExchangeRate)',
      ),
    topUpAmount: z
      .coerce.number()
      .gt(0)
      .describe(
        "BUY edits only: deposit this much more (in the WALLET currency) to fund the edited buy. It is added to the buy's linked top-up deposit (created on the buy's date when there is none), which follows the buy through later edits and is deleted with it.",
      ),
  })
  .partial();
const portfolioCoreBuy_Body = z.object({
  symbol: z.string().min(1).describe('Asset symbol'),
  quantity: z.coerce.number().gte(0.0001).describe('Quantity to buy'),
  price: z.coerce.number().gte(0).describe('Price per unit'),
  executedAt: z.union([z.string(), z.string()]).describe('Execution date (ISO 8601)').optional(),
  purchaseDate: z
    .union([z.string(), z.string()])
    .describe('Purchase date (ISO 8601) - alias for executedAt')
    .optional(),
  fees: z.coerce.number().gte(0).describe('Transaction fees').optional().default(0),
  notes: z.string().describe('Transaction notes').optional(),
  walletId: z.string().uuid().describe('Wallet ID for the transaction').optional(),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'When the target is a MERGED portfolio and the funding wallet funds no member (and no member already holds the symbol), the member portfolio to record the buy on. Required to disambiguate; the response otherwise carries code MERGED_TRANSACTION_AMBIGUOUS plus the candidate members.',
    )
    .optional(),
  assetType: z
    .enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION', 'FUND'])
    .describe('Type of the asset. CFD is not accepted — CFDs are opened via the CFD endpoints.')
    .optional(),
  currency: z.string().describe('Asset currency').optional().default('USD'),
  exchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe(
      'Instrument→position exchange rate at time of purchase. Absent: resolved from stored FX data (identity pairs use 1). Never send a fabricated cross-currency 1.',
    )
    .optional(),
  walletExchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe(
      'Instrument→wallet exchange rate for the wallet deduction (e.g. the rate the broker actually applied). Absent: the rate is resolved from stored FX data.',
    )
    .optional(),
  isGift: z
    .boolean()
    .describe('Whether this is a gift/promotional stock with $0 cost basis')
    .optional()
    .default(false),
  optionSymbol: z.string().describe('OCC option symbol (e.g., AAPL250620C00150000)').optional(),
  strikePrice: z.coerce.number().gte(0).describe('Strike price of the option contract').optional(),
  expirationDate: z
    .union([z.string(), z.string()])
    .describe('Expiration date (ISO 8601)')
    .optional(),
  optionType: z.enum(['CALL', 'PUT']).describe('Option type').optional(),
  underlyingSymbol: z.string().describe('Underlying stock symbol (e.g., AAPL)').optional(),
  contractSize: z.coerce.number().gte(1).describe('Number of shares per contract').optional().default(100),
  transactionType: z
    .enum(['BUY', 'BUY_TO_OPEN', 'BUY_TO_CLOSE'])
    .describe('Transaction type for options (BUY_TO_OPEN or BUY_TO_CLOSE)')
    .optional(),
  isDelivery: z
    .boolean()
    .describe(
      'The shares ARRIVED rather than being bought (spin-off, bonus issue, broker transfer-in). Records DELIVERY_INBOUND and settles NO cash: no wallet debit and no balance requirement. Send the value per share on the day for a spin-off or a transfer-in, 0 for a bonus issue.',
    )
    .optional()
    .default(false),
  spinOffParentPositionId: z
    .string()
    .uuid()
    .describe(
      "With isDelivery, for a SPIN-OFF: the id of the open position the shares were spun off from. The received shares are recorded at price (their market value on the day) and the same amount comes off that holding's cost; nothing is realized and no cash moves.",
    )
    .optional(),
  topUpAmount: z
    .coerce.number()
    .gt(0)
    .describe(
      'Deposit this much (in the WALLET currency) into the wallet to fund the buy, dated on the buy. The deposit is linked to the buy: it moves with the buy when its date, amount or wallet is edited, and is deleted with it. Omit when the wallet already holds the cash - a buy the wallet cannot cover is still recorded (the cash goes below zero).',
    )
    .optional(),
});
const portfolioCoreSell_Body = z.object({
  symbol: z.string().min(1).describe('Asset symbol'),
  quantity: z.coerce.number().gte(0.0001).describe('Quantity to sell'),
  price: z.coerce.number().gte(0).describe('Price per unit'),
  executedAt: z.union([z.string(), z.string()]).describe('Execution date (ISO 8601)'),
  fees: z.coerce.number().gte(0).describe('Transaction fees').optional().default(0),
  notes: z.string().describe('Transaction notes').optional(),
  transactionType: z
    .enum(['SELL', 'SELL_TO_CLOSE', 'SELL_TO_OPEN'])
    .describe('Transaction type for options (SELL_TO_CLOSE or SELL_TO_OPEN)')
    .optional(),
  walletId: z.string().uuid().describe('Wallet ID for the transaction').optional(),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'When the target is a MERGED portfolio and the symbol is held in more than one member, the member portfolio to sell from. Required to disambiguate; the sell is capped at that member position.',
    )
    .optional(),
  currency: z
    .string()
    .describe(
      "The currency the price is in. Omitted: the instrument's own trading currency (a pence line is priced in pounds), never the portfolio currency.",
    )
    .optional(),
  exchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe(
      'Instrument→position exchange rate at time of sale. Absent: resolved from stored FX data (identity pairs use 1). Never send a fabricated cross-currency 1.',
    )
    .optional(),
  walletExchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe(
      'Instrument→wallet exchange rate for the proceeds credit (e.g. the rate the broker actually applied). Absent: the rate is resolved from stored FX data.',
    )
    .optional(),
  taxLotMethod: z
    .enum(['FIFO', 'LIFO', 'SPECIFIC'])
    .describe(
      'Lot matching method. FIFO (default) consumes the oldest lots, LIFO the newest; SPECIFIC sells from exactly the open lot named by `lotId` (see GET /portfolios/:id/lots). LIFO/SPECIFIC consumptions persist as replayable lot pins, so later recalculations keep them.',
    )
    .optional(),
  lotId: z
    .string()
    .min(1)
    .describe(
      'SPECIFIC only: the stable id of the open lot to sell from, as returned by GET /portfolios/:id/lots. Required when taxLotMethod is SPECIFIC.',
    )
    .optional(),
  assetType: z
    .enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION', 'FUND'])
    .describe('Asset type (STOCK, ETF, OPTION, etc.). CFD is not accepted — see the CFD endpoints.')
    .optional(),
  optionSymbol: z.string().describe('OCC option symbol (e.g., AAPL250620C00150000)').optional(),
  strikePrice: z.coerce.number().gte(0).describe('Option strike price').optional(),
  expirationDate: z
    .union([z.string(), z.string()])
    .describe('Option expiration date (ISO 8601)')
    .optional(),
  optionType: z.enum(['CALL', 'PUT']).describe('Option type').optional(),
  underlyingSymbol: z.string().describe('Underlying stock symbol (e.g., AAPL)').optional(),
  contractSize: z.coerce.number().gte(1).describe('Number of shares per contract').optional().default(100),
  isDelivery: z
    .boolean()
    .describe(
      'The shares LEFT in kind (a transfer out to another account) instead of being sold. Records DELIVERY_OUTBOUND and credits NO cash; price is the value per share on the day they left, and the realized result is booked as for a sale. Ignored for options.',
    )
    .optional()
    .default(false),
});
const portfolioCoreDividend_Body = z.object({
  positionId: z.string().uuid().describe('Position ID receiving the dividend'),
  symbol: z.string().min(1).describe('Asset symbol'),
  grossAmount: z.coerce.number().gte(0.01).describe('Gross dividend amount (before tax)'),
  quantity: z.coerce.number().gte(0.0001).describe('Number of shares receiving dividend'),
  taxes: z.coerce.number().gte(0).describe('Withholding tax amount').optional().default(0),
  executedAt: z
    .union([z.string(), z.string()])
    .describe('Dividend record date (ISO 8601)')
    .optional(),
  currency: z.string().describe('Dividend currency').optional().default('USD'),
  exchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe('Exchange rate at time of dividend')
    .optional()
    .default(1),
  notes: z.string().describe('Transaction notes').optional(),
  walletId: z.string().uuid().describe('Wallet ID for crediting the dividend'),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'When the target is a MERGED portfolio and the symbol is held in more than one member, the member portfolio the dividend applies to. Required to disambiguate.',
    )
    .optional(),
});
const portfolioCoreAssignment_Body = z.object({
  positionId: z.string().uuid().describe('Option position ID being assigned'),
  quantity: z
    .coerce.number()
    .gte(1)
    .describe('Number of contracts to assign (defaults to all)')
    .optional(),
  executedAt: z.union([z.string(), z.string()]).describe('Execution date (ISO 8601)').optional(),
  walletId: z.string().uuid().describe('Wallet ID for the transaction').optional(),
});
const portfolioCoreExercise_Body = z.object({
  positionId: z.string().uuid().describe('Option position ID being exercised'),
  quantity: z
    .coerce.number()
    .gte(1)
    .describe('Number of contracts to exercise (defaults to all)')
    .optional(),
  executedAt: z.union([z.string(), z.string()]).describe('Execution date (ISO 8601)').optional(),
  walletId: z.string().uuid().describe('Wallet ID for the transaction').optional(),
});
const portfolioCoreExpire_Body = z.object({
  positionId: z.string().uuid().describe('Open option position whose contracts have lapsed'),
  walletId: z
    .string()
    .uuid()
    .describe("Wallet the contract settled in (defaults to the position's)")
    .optional(),
});
const portfolioCoreCompareProjections_Body = z.object({
  portfolioIds: z.array(z.string().uuid()).describe('Array of portfolio IDs to compare'),
  yearsToProject: z
    .coerce.number()
    .int()
    .gte(1)
    .lte(40)
    .describe('Number of years to project (1 to 40; 5 when omitted)')
    .optional()
    .default(5),
});
const portfolioCoreCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Portfolio name'),
  currency: z.string().min(1).max(3).describe('Base currency for the portfolio'),
  description: z.string().max(500).describe('Portfolio description').optional(),
  strategy: z
    .enum(['GROWTH', 'BALANCED', 'CONSERVATIVE', 'AGGRESSIVE', 'INCOME', 'CRYPTO', 'CUSTOM'])
    .describe('Investment strategy')
    .optional(),
  isMarginEnabled: z.boolean().describe('Enable margin account mode').optional().default(false),
  showCashAsPosition: z
    .boolean()
    .describe(
      'Show the free cash of the linked wallet as a slice on the asset and position allocation rings and as a row in the concentration list. Off: holdings only.',
    )
    .optional()
    .default(true),
  taxExemptAccount: z
    .boolean()
    .describe(
      'An IKE / IKZE account: its interest carries no 19% Belka tax, so a treasury bond coupon or redemption is booked without it by default.',
    )
    .optional()
    .default(false),
  marginRate: z
    .coerce.number()
    .gte(0)
    .lte(1)
    .describe('Initial margin rate (e.g., 0.5 = 50% = 2:1 leverage)')
    .optional()
    .default(0.5),
  maintenanceMarginRate: z
    .coerce.number()
    .gte(0)
    .lte(1)
    .describe('Maintenance margin rate (e.g., 0.25 = 25%)')
    .optional()
    .default(0.25),
  walletId: z
    .string()
    .uuid()
    .describe(
      'Existing wallet (owned by the caller) to link as the portfolio cash wallet. Omitted: an empty cash wallet is created in the portfolio currency and linked automatically (every regular portfolio has a cash wallet).',
    )
    .optional(),
});
const portfolioCoreUpdate_Body = z
  .object({
    name: z.string().max(100).describe('Portfolio name'),
    description: z.string().max(500).describe('Portfolio description'),
    strategy: z
      .enum(['GROWTH', 'BALANCED', 'CONSERVATIVE', 'AGGRESSIVE', 'INCOME', 'CRYPTO', 'CUSTOM'])
      .describe('Investment strategy'),
    currency: z.string().max(3).describe('Portfolio currency (e.g., USD, PLN, EUR)'),
    walletId: z
      .string()
      .uuid()
      .describe(
        "Move the portfolio's MAIN cash wallet to this one of the caller's wallets. The wallet it leaves stops counting in the portfolio (its cash, deposits and returns go with it). To HOLD CASH IN SEVERAL CURRENCIES keep the main wallet and use addCashWalletIds instead.",
      ),
    addCashWalletIds: z
      .array(z.string().uuid())
      .max(10)
      .describe(
        'ADD wallets of the caller to the portfolio as extra cash wallets, each counted in its own currency next to the main wallet (a USD portfolio that also holds PLN and EUR cash). One wallet per currency; a wallet linked to another portfolio is refused, and so is a wallet of another broker account. Every regular portfolio can hold several currencies; a merged one holds none (409 CASH_SIDE_REFUSED). Moves no money.',
      ),
    removeCashWalletIds: z
      .array(z.string().uuid())
      .max(10)
      .describe(
        'DEPRECATED, do not use (#5144). DETACH extra cash wallets added with addCashWalletIds. The wallet and its money are NOT deleted: it just stops counting in this portfolio. Refused (409 CASH_SIDE_REFUSED, in-use) while a live trade of the portfolio settled in it; the main wallet cannot be detached (move it with walletId, which may name a wallet the portfolio already holds).',
      ),
    isMarginEnabled: z
      .boolean()
      .describe(
        'A margin account: cash may go below zero because the broker lends against the holdings, and the return is then measured on the capital the owner put at risk, not on the loan. Off: a wallet below zero is read as money paid in that the ledger is missing.',
      ),
    showCashAsPosition: z
      .boolean()
      .describe(
        'Show the free cash of the linked wallet as a slice on the asset and position allocation rings and as a row in the concentration list. Off: holdings only.',
      ),
    taxExemptAccount: z
      .boolean()
      .describe(
        'An IKE / IKZE account: its interest carries no 19% Belka tax, so a treasury bond coupon or redemption is booked without it by default.',
      ),
  })
  .partial();
const portfolioCoreCreateCfdPosition_Body = z.object({
  symbol: z.string().describe('CFD symbol as the broker names it (GOLD, US100, ORCL.US…)'),
  direction: z.enum(['BUY', 'SELL']).describe('BUY = long, SELL = short'),
  quantity: z
    .coerce.number()
    .gte(0.000001)
    .describe('Volume in lots (positive; direction carries the sign)'),
  openPrice: z
    .coerce.number()
    .gte(0.000001)
    .describe('Per-unit open price in the underlying quote currency'),
  openedAt: z.union([z.string(), z.string()]).describe('When the position was opened (ISO 8601)'),
  contractSize: z
    .coerce.number()
    .gte(0.000001)
    .describe('Units per lot; defaults to the curated map value, or 1 for stock-CFDs')
    .optional(),
  margin: z.coerce.number().gte(0).describe('Broker-blocked margin (account currency)').optional(),
  swapAccrued: z
    .coerce.number()
    .describe('Accrued financing to date, signed as the broker reports (negative = cost)')
    .optional(),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'When the target is a MERGED portfolio and no sole member already holds an open CFD for the symbol (and the merge has more than one member), the member portfolio to write the position to. Required to disambiguate; the response otherwise carries code MERGED_TRANSACTION_AMBIGUOUS plus the candidate members.',
    )
    .optional(),
});
const portfolioCoreUpdateCfdPosition_Body = z
  .object({
    direction: z.enum(['BUY', 'SELL']),
    quantity: z.coerce.number().gte(0.000001).describe('Volume in lots'),
    openPrice: z.coerce.number().gte(0.000001).describe('Per-unit open price'),
    openedAt: z.union([z.string(), z.string()]).describe('When the position was opened (ISO 8601)'),
    contractSize: z.coerce.number().gte(0.000001).describe('Units per lot'),
    margin: z.coerce.number().gte(0).describe('Broker-blocked margin (account currency)'),
    swapAccrued: z.coerce.number().describe('Accrued financing to date, signed (negative = cost)'),
    overridePrice: z
      .coerce.number()
      .gte(0.000001)
      .describe(
        'Your current price for the underlying (open-price unit). Used only when no live source prices the row; null clears it and returns the row to the automatic price',
      )
      .nullable(),
  })
  .partial();
const portfolioCoreCloseCfdPosition_Body = z.object({
  closedAt: z
    .union([z.string(), z.string()])
    .describe('When the position (part) was closed (ISO 8601)'),
  closePrice: z
    .coerce.number()
    .gte(0.000001)
    .describe('Per-unit close price, same unit as the open price'),
  volume: z
    .coerce.number()
    .gte(0.000001)
    .describe('Lots to close; omitted = the whole position')
    .optional(),
  fees: z
    .coerce.number()
    .gte(0)
    .describe('Commission charged at close, account currency (positive)')
    .optional(),
  swap: z
    .coerce.number()
    .describe('Financing settled at close, signed as the broker reports it (negative = cost)')
    .optional(),
  margin: z
    .coerce.number()
    .gte(0)
    .describe(
      'Broker-blocked margin for the closed lots (account currency) when the row carries none; it becomes the capital-at-risk fragment the return denominator reads',
    )
    .optional(),
  clientRequestId: z
    .string()
    .uuid()
    .describe(
      'Client-minted idempotency key. A re-sent request with the same key (the client gave up on a slow first attempt the server completed) replays the stored result instead of booking the P&L twice.',
    )
    .optional(),
});
const portfolioRiskLimitsUpdate_Body = z.object({
  limits: z
    .array(
      z.object({
        id: z
          .enum([
            'volatility',
            'position',
            'sector',
            'beta',
            'var',
            'drawdown',
            'effectiveBets',
            'benchmarkCorrelation',
            'sectorRisk',
            'stress',
            'currency',
          ])
          .describe('Catalog id of the limit (volatility, position, sector, beta, var, ...).'),
        value: z
          .coerce.number()
          .describe(
            "The threshold, in the catalog entry's display unit: percent limits are percent numbers (20 = 20%), beta / correlation / effective bets are plain numbers.",
          ),
      }),
    )
    .max(5)
    .describe(
      'The COMPLETE new set in display order (at most 5); it replaces whatever was stored. An EMPTY list puts the portfolio back on the default limits.',
    ),
});
const BondTermsDto = z
  .object({
    symbol: z.string().describe('The position symbol, trimmed and upper-cased'),
    hasBookings: z
      .boolean()
      .describe('A coupon, maturity or early redemption of the bond is booked in the book'),
    firstPeriodStart: z
      .string()
      .describe(
        "The first day of the period firstPeriodRatePct prices, fixed when the terms were saved: 'manual' the period of the save day, 'cpi' / 'nbp' the issue day's period (else the save day's); null for a 'fixed' rate and a 'purchase' bond (each lot's own period 1)",
      )
      .nullable(),
    scheduleAnchor: z
      .enum(['purchase', 'fixed'])
      .describe(
        "'fixed': coupon dates common to every holder (a listed corporate or municipal bond); 'purchase': periods start on each lot's purchase day (treasury-like)",
      ),
    nominal: z.coerce.number().describe('The nominal of one bond (100 treasury-like, often 1000 listed)'),
    currency: z.string().describe("ISO 4217: the position's purchase currency"),
    termMonths: z
      .coerce.number()
      .describe("'purchase' only: the term of a lot in months, a multiple of periodMonths")
      .nullable(),
    maturityDate: z.string().describe("'fixed' only: the bond's maturity day").nullable(),
    couponAnchorDate: z
      .string()
      .describe("'fixed' only: any coupon day you know; null steps back from the maturity")
      .nullable(),
    issueDate: z
      .string()
      .describe("'fixed' + capitalised only: the issue day, where compounding starts")
      .nullable(),
    payout: z
      .enum(['periodic', 'capitalised'])
      .describe("'periodic': paid every period; 'capitalised': compounded, paid at maturity"),
    periodMonths: z
      .union([z.literal(1), z.literal(3), z.literal(6), z.literal(12)])
      .describe('The coupon (or compounding) period, in months'),
    rateKind: z
      .enum(['fixed', 'cpi', 'nbp', 'manual'])
      .describe(
        "'fixed', CPI + margin, the NBP reference rate + margin, or 'manual' (any other index, such as WIBOR: you give each new period's rate; 'fixed' schedule only)",
      ),
    firstPeriodRatePct: z
      .coerce.number()
      .describe(
        "The rate of the current period, the one firstPeriodStart names (for 'fixed': of every period), percent (7.5 = 7.5 %)",
      ),
    marginPct: z
      .coerce.number()
      .describe(
        "The margin over CPI or the NBP rate; 0 for 'fixed' and 'manual', percent (7.5 = 7.5 %)",
      ),
    earlyRedemptionFee: z
      .coerce.number()
      .describe("'purchase' only: the fee a bond per early redemption; null: none")
      .nullable(),
    rates: z
      .array(
        z
          .object({
            periodStart: z.string().describe('The first day of the period'),
            ratePct: z.coerce.number().describe("The period's rate, percent (7.5 = 7.5 %)"),
          })
          .passthrough(),
      )
      .max(600)
      .describe("'manual' only: the rates of the periods after the first, by period start"),
    dayCount: z
      .enum(['ACT_ACT', 'ACT_365', '30_360'])
      .describe(
        "'fixed' only, required: how a coupon counts days: 'ACT_365' (the Polish term sheets: days / 365), 'ACT_ACT' (rate / periods a year), '30_360' (US/NASD); null for 'purchase'",
      )
      .nullable(),
  })
  .passthrough();
const TreasuryBondHoldingDto = z
  .object({
    symbol: z.string().describe('The treasury code, normalised (EDO0934), or the bond symbol'),
    source: z
      .enum(['issuer', 'terms'])
      .describe("'issuer': the issuer's formula values it; 'terms': its holder's saved terms do"),
    currency: z.string().describe('ISO 4217: every amount of the bond is in it (issuer: PLN)'),
    terms: BondTermsDto.describe(
      "The holder's terms that value the bond; null for the issuer's",
    ).nullable(),
    family: z
      .enum(['OTS', 'ROR', 'DOR', 'TOS', 'COI', 'ROS', 'EDO', 'ROD'])
      .describe('The treasury family; null for a terms bond')
      .nullable(),
    termMonths: z
      .coerce.number()
      .describe(
        "The term of a lot, in months; null for a terms bond whose lots all mature on the bond's maturity date (a listed one)",
      )
      .nullable(),
    periodMonths: z.coerce.number().describe('The interest period, in months'),
    paysCoupons: z
      .boolean()
      .describe('Pays its interest out each period (COI, ROR, DOR, periodic terms)'),
    saleMonth: z
      .string()
      .describe('YYYY-MM the series was on sale (every lot was bought in it); null for terms')
      .nullable(),
    series: z
      .object({
        firstPeriodRatePct: z.coerce.number().describe('The first-period rate, percent'),
        marginPct: z.coerce.number().describe('The margin over CPI / the NBP rate, percent'),
        saleFrom: z.string().describe('First day of the sale window').nullable(),
        saleTo: z.string().describe('Last day of the sale window').nullable(),
        exchangePrice: z
          .coerce.number()
          .describe('The price of one bond bought by exchange (zamiana), PLN')
          .nullable(),
        fixedInterest: z
          .coerce.number()
          .describe('OTS only: the interest one bond pays at maturity, PLN')
          .nullable(),
      })
      .passthrough()
      .describe("The issuer's page for the series; null until described, and for terms")
      .nullable(),
    taxExemptAccount: z
      .boolean()
      .describe('The book is an IKE / IKZE account: the default booking withholds no tax'),
    lastEarlyRedemptionDate: z
      .string()
      .describe(
        'The latest disposition day, today or before, whose payout (the 5th Polish business day after it) lands before the earliest maturity of the lots held; a later disposition is refused (TREASURY_LOT_MATURED). Null when no lot is held, and for a bond with no early redemption (BOND_NO_EARLY_REDEMPTION)',
      )
      .nullable(),
    bookedLotDates: z
      .array(z.string())
      .describe(
        'The lot purchase days any booked coupon, maturity or early redemption names (a fully redeemed lot included), ascending: a lot listed here cannot have its purchase date moved',
      ),
    lots: z
      .array(
        z
          .object({
            purchaseDate: z.string().describe('The purchase day'),
            quantity: z.coerce.number().describe('Bonds of the lot held today'),
            maturityDate: z
              .string()
              .describe(
                "The day the lot matures: its own for periods from each purchase, the bond's for a listed one",
              ),
            earlyRedemptionFee: z
              .coerce.number()
              .describe(
                "The early-redemption fee CAP a bond under the lot's fee regime; 0 with none, in the bond's currency",
              ),
          })
          .passthrough(),
      )
      .describe('The open lots today, FIFO (oldest first)'),
    due: z
      .array(
        z
          .object({
            kind: z
              .enum(['coupon', 'maturity'])
              .describe('A coupon paid, or the lot redeemed at maturity'),
            date: z.string().describe('The pay day (coupon) or the maturity day'),
            lotPurchaseDate: z
              .string()
              .describe('The purchase day of the lot the event belongs to'),
            quantity: z.coerce.number().describe('Bonds of the lot the event pays'),
            principal: z
              .coerce.number()
              .describe(
                "The nominal paid back: 0 for a coupon, the nominal a bond at maturity, in the bond's currency",
              ),
            interest: z.coerce.number().describe("The interest paid, in the bond's currency"),
            tax: z
              .coerce.number()
              .describe(
                "The 19% Belka tax on it, as if withheld (a tax-exempt booking drops it), in the bond's currency",
              ),
            net: z.coerce.number().describe("principal + interest - tax, in the bond's currency"),
          })
          .passthrough(),
      )
      .describe('Coupons and maturities due by today and not yet booked, oldest first'),
    pending: z
      .string()
      .describe(
        'The input the valuer still lacks (series, cpi:<month>, nbp:<month>, or rate:<day>: a period rate the holder gives); null when none',
      )
      .nullable(),
  })
  .passthrough();
const TreasuryRedemptionQuoteDto = z
  .object({
    symbol: z.string().describe('The treasury code, or the bond symbol'),
    currency: z.string().describe('ISO 4217: every amount of the quote is in it'),
    date: z.string().describe('The disposition day'),
    accrualDate: z
      .string()
      .describe('The 5th Polish business day after it: interest accrues to this day'),
    quantity: z.coerce.number().describe('Bonds redeemed'),
    principal: z.coerce.number().describe("The nominal a bond, in the bond's currency"),
    interest: z.coerce.number().describe("The interest accrued, in the bond's currency"),
    fee: z.coerce.number().describe("The early-redemption fee, in the bond's currency"),
    tax: z.coerce.number().describe("The 19% Belka tax, as if withheld, in the bond's currency"),
    net: z.coerce.number().describe("principal + interest - fee - tax, in the bond's currency"),
    lots: z
      .array(
        z
          .object({
            purchaseDate: z.string().describe('The lot the bonds come from'),
            quantity: z.coerce.number().describe('Bonds taken from it'),
            interest: z.coerce.number().describe("Their interest, in the bond's currency"),
            fee: z.coerce.number().describe("Their fee, in the bond's currency"),
            tax: z.coerce.number().describe("Their tax, rounded once on the lot, in the bond's currency"),
          })
          .passthrough(),
      )
      .describe('The lots the redemption takes, FIFO'),
  })
  .passthrough();
const treasuryBondsBook_Body = z.object({
  walletId: z
    .string()
    .uuid()
    .describe(
      "The book's cash wallet the money moves through, in the bond's currency (never converted)",
    ),
  withholdTax: z
    .boolean()
    .describe(
      'Withhold the 19% Belka tax. The UI defaults it to !taxExemptAccount; false for IKE / IKZE',
    ),
  events: z
    .array(
      z.union([
        z.object({
          kind: z.enum(['coupon', 'maturity']),
          date: z.string().describe('The event day, as the holding lists it'),
          lotPurchaseDate: z.string().describe('The lot, as the holding lists it'),
        }),
        z.object({
          kind: z.literal('early_redemption'),
          date: z.string().describe('The disposition day, today or earlier'),
          quantity: z.coerce.number().int().gte(1).describe('Bonds to redeem, taken FIFO'),
        }),
      ]),
    )
    .min(1)
    .describe('The events to book, all in one transaction; every amount is recomputed server-side'),
});
const PortfolioEventDto = z.union([
  z.object({
    kind: z.enum([
      'fee.commission',
      'fee.custody_platform',
      'fee.adr',
      'fee.regulatory',
      'fee.fx',
      'fee.advisor',
      'fee.other',
    ]),
    amount: z
      .coerce.number()
      .gt(0)
      .lte(1000000000000000)
      .describe('The fee paid, in the cash wallet currency (a positive number)'),
    date: z
      .string()
      .describe(
        'The day the money moved (YYYY-MM-DD), today or earlier; the cash row is dated to it',
      ),
    note: z.string().max(200).describe('A note shown beside the kind in the cash list').optional(),
  }),
  z.object({
    kind: z.literal('fee.refund'),
    amount: z
      .coerce.number()
      .gt(0)
      .lte(1000000000000000)
      .describe('The fee refunded, in the cash wallet currency (a positive number)'),
    date: z
      .string()
      .describe(
        'The day the money moved (YYYY-MM-DD), today or earlier; the cash row is dated to it',
      ),
    note: z.string().max(200).describe('A note shown beside the kind in the cash list').optional(),
  }),
  z.object({
    kind: z.literal('tax.withholding'),
    amount: z
      .coerce.number()
      .gt(0)
      .lte(1000000000000000)
      .describe('The tax withheld, in the cash wallet currency (a positive number)'),
    on: z
      .enum(['dividend', 'interest'])
      .describe('The income the tax was withheld from: it is netted into that income'),
    date: z
      .string()
      .describe(
        'The day the money moved (YYYY-MM-DD), today or earlier; the cash row is dated to it',
      ),
    note: z.string().max(200).describe('A note shown beside the kind in the cash list').optional(),
  }),
  z.object({
    kind: z.literal('tax.transaction'),
    amount: z
      .coerce.number()
      .gt(0)
      .lte(1000000000000000)
      .describe(
        'The transaction tax paid (stamp duty, a financial-transaction tax), in the cash wallet currency',
      ),
    date: z
      .string()
      .describe(
        'The day the money moved (YYYY-MM-DD), today or earlier; the cash row is dated to it',
      ),
    note: z.string().max(200).describe('A note shown beside the kind in the cash list').optional(),
  }),
  z.object({
    kind: z.enum(['income.interest_cash', 'income.bond_coupon', 'income.securities_lending']),
    amount: z
      .coerce.number()
      .gt(0)
      .lte(1000000000000000)
      .describe('The GROSS income, before any tax withheld, in the cash wallet currency'),
    taxWithheld: z
      .coerce.number()
      .gte(0)
      .lte(1000000000000000)
      .describe('The tax withheld from it, if any; booked as its own tax row, as a statement does')
      .optional(),
    date: z
      .string()
      .describe(
        'The day the money moved (YYYY-MM-DD), today or earlier; the cash row is dated to it',
      ),
    note: z.string().max(200).describe('A note shown beside the kind in the cash list').optional(),
  }),
  z.object({
    kind: z.enum(['ca.split', 'ca.reverse_split']),
    symbol: z.string().min(1).max(32).describe('The holding that split, as the portfolio holds it'),
    fromFactor: z
      .coerce.number()
      .gt(0)
      .lte(1000000000)
      .describe(
        'Shares before, per the ratio: 1 in a 4-for-1 split, 10 in a 1-for-10 reverse split',
      ),
    toFactor: z
      .coerce.number()
      .gt(0)
      .lte(1000000000)
      .describe('Shares after, per the ratio: 4 in a 4-for-1 split, 1 in a 1-for-10 reverse split'),
    date: z
      .string()
      .describe(
        'The ex-date (YYYY-MM-DD), today or earlier: the first day the shares traded split. Every trade of the holding made before it is rescaled',
      ),
  }),
]);
const portfolioEventsRecord_Body = z.object({
  event: PortfolioEventDto.describe('The event to book: its kind decides its fields'),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'On a MERGED portfolio with more than one member: the member whose cash wallet the event lands in, or (a split) the member holding the symbol',
    )
    .optional(),
});
const RecordPortfolioEventResultDto = z.union([
  z
    .object({
      portfolioId: z
        .string()
        .uuid()
        .describe('The book the event was booked into (a member, on a merge)'),
      walletId: z.string().uuid().describe('The cash wallet the rows were written to'),
      rows: z
        .array(
          z
            .object({
              id: z.string().uuid(),
              type: z.string().describe('The wallet row type (FEE, FEE_REFUND, INTEREST)'),
              amount: z.coerce.number().describe('Signed, in the wallet currency'),
            })
            .passthrough(),
        )
        .describe('The wallet rows written, in order'),
      currency: z.string().describe('The wallet currency'),
    })
    .passthrough(),
  z
    .object({
      portfolioId: z
        .string()
        .uuid()
        .describe('The book the split was booked on (a member, on a merge)'),
      symbol: z.string(),
      splitDate: z
        .string()
        .describe(
          'The ex-date applied: where the vendor registry dates the same split, when it does',
        ),
      fromFactor: z.coerce.number(),
      toFactor: z.coerce.number(),
      adjustedTransactionCount: z
        .coerce.number()
        .int()
        .describe('The trades rescaled: every quantity-bearing row traded before the ex-date'),
      quantityBefore: z.coerce.number().describe('The holding before the split'),
      quantityAfter: z.coerce.number().describe('The holding after it'),
    })
    .passthrough(),
]);
const bondTermsSet_Body = z.object({
  scheduleAnchor: z
    .enum(['purchase', 'fixed'])
    .describe(
      "'fixed': coupon dates common to every holder (a listed corporate or municipal bond); 'purchase': periods start on each lot's purchase day (treasury-like)",
    ),
  nominal: z.coerce.number().describe('The nominal of one bond (100 treasury-like, often 1000 listed)'),
  currency: z.string().describe("ISO 4217: the position's purchase currency"),
  termMonths: z
    .coerce.number()
    .describe("'purchase' only: the term of a lot in months, a multiple of periodMonths")
    .nullable(),
  maturityDate: z.string().describe("'fixed' only: the bond's maturity day").nullable(),
  couponAnchorDate: z
    .string()
    .describe("'fixed' only: any coupon day you know; null steps back from the maturity")
    .nullable(),
  issueDate: z
    .string()
    .describe("'fixed' + capitalised only: the issue day, where compounding starts")
    .nullable(),
  payout: z
    .enum(['periodic', 'capitalised'])
    .describe("'periodic': paid every period; 'capitalised': compounded, paid at maturity"),
  periodMonths: z
    .union([z.literal(1), z.literal(3), z.literal(6), z.literal(12)])
    .describe('The coupon (or compounding) period, in months'),
  rateKind: z
    .enum(['fixed', 'cpi', 'nbp', 'manual'])
    .describe(
      "'fixed', CPI + margin, the NBP reference rate + margin, or 'manual' (any other index, such as WIBOR: you give each new period's rate; 'fixed' schedule only)",
    ),
  firstPeriodRatePct: z
    .coerce.number()
    .describe(
      "The rate of the current period, the one firstPeriodStart names (for 'fixed': of every period), percent (7.5 = 7.5 %)",
    ),
  marginPct: z
    .coerce.number()
    .describe(
      "The margin over CPI or the NBP rate; 0 for 'fixed' and 'manual', percent (7.5 = 7.5 %)",
    ),
  earlyRedemptionFee: z
    .coerce.number()
    .describe("'purchase' only: the fee a bond per early redemption; null: none")
    .nullable(),
  rates: z
    .array(
      z.object({
        periodStart: z.string().describe('The first day of the period (YYYY-MM-DD)'),
        ratePct: z.coerce.number().describe("The period's rate, percent (7.5 = 7.5 %)"),
      }),
    )
    .max(600)
    .describe("'manual' only: the rates of the periods after the first, by period start"),
  dayCount: z
    .enum(['ACT_ACT', 'ACT_365', '30_360'])
    .describe(
      "'fixed' only, required: how a coupon counts days: 'ACT_365' (the Polish term sheets: days / 365), 'ACT_ACT' (rate / periods a year), '30_360' (US/NASD); null for 'purchase'",
    )
    .nullable(),
});
const bondTermsPreview_Body = z.object({
  scheduleAnchor: z
    .enum(['purchase', 'fixed'])
    .describe(
      "'fixed': coupon dates common to every holder (a listed corporate or municipal bond); 'purchase': periods start on each lot's purchase day (treasury-like)",
    ),
  nominal: z.coerce.number().describe('The nominal of one bond (100 treasury-like, often 1000 listed)'),
  currency: z.string().describe("ISO 4217: the position's purchase currency"),
  termMonths: z
    .coerce.number()
    .describe("'purchase' only: the term of a lot in months, a multiple of periodMonths")
    .nullable(),
  maturityDate: z.string().describe("'fixed' only: the bond's maturity day").nullable(),
  couponAnchorDate: z
    .string()
    .describe("'fixed' only: any coupon day you know; null steps back from the maturity")
    .nullable(),
  issueDate: z
    .string()
    .describe("'fixed' + capitalised only: the issue day, where compounding starts")
    .nullable(),
  payout: z
    .enum(['periodic', 'capitalised'])
    .describe("'periodic': paid every period; 'capitalised': compounded, paid at maturity"),
  periodMonths: z
    .union([z.literal(1), z.literal(3), z.literal(6), z.literal(12)])
    .describe('The coupon (or compounding) period, in months'),
  rateKind: z
    .enum(['fixed', 'cpi', 'nbp', 'manual'])
    .describe(
      "'fixed', CPI + margin, the NBP reference rate + margin, or 'manual' (any other index, such as WIBOR: you give each new period's rate; 'fixed' schedule only)",
    ),
  firstPeriodRatePct: z
    .coerce.number()
    .describe(
      "The rate of the current period, the one firstPeriodStart names (for 'fixed': of every period), percent (7.5 = 7.5 %)",
    ),
  marginPct: z
    .coerce.number()
    .describe(
      "The margin over CPI or the NBP rate; 0 for 'fixed' and 'manual', percent (7.5 = 7.5 %)",
    ),
  earlyRedemptionFee: z
    .coerce.number()
    .describe("'purchase' only: the fee a bond per early redemption; null: none")
    .nullable(),
  rates: z
    .array(
      z.object({
        periodStart: z.string().describe('The first day of the period (YYYY-MM-DD)'),
        ratePct: z.coerce.number().describe("The period's rate, percent (7.5 = 7.5 %)"),
      }),
    )
    .max(600)
    .describe("'manual' only: the rates of the periods after the first, by period start"),
  dayCount: z
    .enum(['ACT_ACT', 'ACT_365', '30_360'])
    .describe(
      "'fixed' only, required: how a coupon counts days: 'ACT_365' (the Polish term sheets: days / 365), 'ACT_ACT' (rate / periods a year), '30_360' (US/NASD); null for 'purchase'",
    )
    .nullable(),
  purchaseDate: z.string().describe('The day the one bond is bought (YYYY-MM-DD)'),
  firstPeriodStart: z
    .string()
    .describe(
      'SAVED terms only (the edit dialog): the period firstPeriodRatePct prices, as BondTermsDto states it, while the grid and the kind of rate are the saved ones. Left out: the period of today, as a new save stores it',
    )
    .nullish(),
});
const BondTermsPreviewDto = z
  .object({
    asOf: z.string().describe('Today: the day the holdings rows value bonds on'),
    unitValue: z
      .coerce.number()
      .describe('One bond today, accrued interest included; null while an input is pending')
      .nullable(),
    accruedInterest: z.coerce.number().describe('The interest one bond has accrued today').nullable(),
    maturityDate: z.string().describe("The lot's maturity (purchase anchor) or the bond's"),
    nextEvent: z
      .object({
        kind: z.enum(['coupon', 'maturity']),
        date: z.string(),
        gross: z
          .coerce.number()
          .describe('The interest one bond receives then, before tax; null while not known')
          .nullable(),
      })
      .passthrough()
      .describe('The next payment after today; null once the bond has matured')
      .nullable(),
    pending: z
      .string()
      .describe("The input the value or the next coupon waits for ('rate:<day>', 'cpi:<month>')")
      .nullable(),
  })
  .passthrough();
const fundUnitsBook_Body = z.object({
  walletId: z
    .string()
    .uuid()
    .describe("Wallet that pays for the units: the book's own cash wallet"),
  seriesId: z
    .coerce.number()
    .int()
    .gte(1)
    .describe(
      "A fund of the catalog (fund_units_search_series): the holding is bound to that series at once and valued by the unit price its TFI publishes every business day. Its currency must be the book's. Omit it for a fund outside the catalog.",
    )
    .optional(),
  name: z
    .string()
    .min(1)
    .max(120)
    .describe(
      "The fund's name, ALWAYS. With `seriesId`: that series' `holdingName` exactly as fund_units_search_series returned it (the fund and its unit category, 'PKO Emerytura 2050 (kat. A)'): the person approving sees this name, and another one is refused (422 FUND_SERIES_NAME_MISMATCH). Without `seriesId`: a fund OUTSIDE the catalog, as its holder calls it, valued by the person's own values and the unit prices of the purchases and sales recorded for it.",
    ),
  quantity: z
    .coerce.number()
    .gte(0.0001)
    .describe('Units bought: a fund is held in fractions (12.3456 units)'),
  price: z
    .coerce.number()
    .gt(0)
    .describe("Price of ONE unit in the fund's currency: the VNAV of the order day"),
  purchaseDate: z
    .union([z.string(), z.string()])
    .describe('Day of the purchase (YYYY-MM-DD); not in the future'),
  fees: z
    .coerce.number()
    .gte(0)
    .describe("Entry fee in the fund's currency (not part of the unit price)")
    .optional(),
  sourcePortfolioId: z
    .string()
    .uuid()
    .describe(
      'MERGED portfolios: the member portfolio the units are recorded in. A merge records nothing of its own; when one member holds the fund already, or the merge has one member, it is found without this.',
    )
    .optional(),
});
const BookedFundUnitsDto = z
  .object({
    positionId: z.string().describe('The holding the units are in'),
    portfolioId: z
      .string()
      .describe('The book the units were recorded in: the member, for a merged portfolio'),
    symbol: z.string().describe('The key the holding is stored under'),
    name: z.string().describe('The name the holding carries'),
    seriesId: z
      .coerce.number()
      .int()
      .describe(
        'The catalog series the holding is now valued by; null for a fund outside the catalog',
      )
      .nullable(),
  })
  .passthrough();
const NotificationDto = z
  .object({
    id: z.string().uuid(),
    type: z.enum([
      'news',
      'earnings',
      'insider_trade',
      'analyst_rating',
      'billing_payment_failed',
      'billing_payment_succeeded',
      'billing_trial_ending',
      'billing_plan_changed',
      'corporate_action',
      'broker_connection_broken',
      'alert',
      'bug_report_update',
      'subscribed_portfolio_trade',
    ]),
    symbol: z.string().nullish(),
    title: z.string(),
    body: z.string().nullish(),
    linkUrl: z.string().describe('Locale-less path, prepend /${locale} in client').nullish(),
    iconSymbol: z.string().nullish(),
    payload: z.object({}).partial().passthrough(),
    importance: z.enum(['urgent', 'high', 'normal', 'low']),
    readAt: z.string().datetime({ offset: true }).nullish(),
    seenAt: z.string().datetime({ offset: true }).nullish(),
    deliveredAt: z.string().datetime({ offset: true }).nullish(),
    scheduledFor: z.string().datetime({ offset: true }).nullish(),
    createdAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const NotificationPreferencesDto = z
  .object({
    newsEnabled: z.boolean(),
    newsMinActionability: z.enum(['urgent', 'high', 'watch', 'background', 'noise']),
    newsMaterialOnly: z.boolean(),
    earningsEnabled: z.boolean(),
    insiderEnabled: z.boolean(),
    insiderMinValueUsd: z.string().describe('Decimal string').nullish(),
    analystEnabled: z.boolean(),
    analystDirection: z.enum(['all', 'upgrades_only', 'downgrades_only']),
    channelsWeb: z.boolean(),
    channelsPush: z.boolean(),
    channelsEmail: z.boolean(),
    subscribedTradesEnabled: z
      .boolean()
      .describe('In-app trade alerts from the shared portfolios the reader subscribes to.'),
  })
  .passthrough();
const notificationPreferencesUpdate_Body = z
  .object({
    newsEnabled: z.boolean(),
    newsMinActionability: z.enum(['urgent', 'high', 'watch', 'background', 'noise']),
    newsMaterialOnly: z.boolean(),
    earningsEnabled: z.boolean(),
    insiderEnabled: z.boolean(),
    insiderMinValueUsd: z
      .string()
      .regex(/^[+-]?(\d*[.])?\d+$/)
      .nullable(),
    analystEnabled: z.boolean(),
    analystDirection: z.enum(['all', 'upgrades_only', 'downgrades_only']),
    channelsWeb: z.boolean(),
    channelsPush: z.boolean(),
    channelsEmail: z.boolean(),
    subscribedTradesEnabled: z.boolean(),
  })
  .partial();
const notificationSymbolsUpdate_Body = z.object({ symbol: z.string(), enabled: z.boolean() });
const notificationSymbolsReplaceAll_Body = z.object({
  mutedSymbols: z
    .array(z.string())
    .max(5000)
    .describe('Symbols to mute (opt-out). Omitted symbols are enabled by default.'),
});
const ForecastLegDto = z
  .object({
    value: z.coerce.number().describe('Consensus average').nullable(),
    high: z.coerce.number().describe('High of the analyst range').nullable(),
    low: z.coerce.number().describe('Low of the analyst range').nullable(),
    numAnalysts: z.coerce.number().describe('Number of contributing analysts').nullable(),
  })
  .passthrough();
const EarningsLegDto = z
  .object({
    value: z.coerce.number().describe('Consensus average').nullable(),
    high: z.coerce.number().describe('High of the analyst range').nullable(),
    low: z.coerce.number().describe('Low of the analyst range').nullable(),
    numAnalysts: z.coerce.number().describe('Number of contributing analysts').nullable(),
    basis: z
      .enum(['usGaap', 'reported', 'adjusted'])
      .describe(
        "Which definition this earnings figure is measured on. 'adjusted' = the street's analyst-comparable basis. 'usGaap' = the issuer's own filed statements, with a 10-K/10-Q proving they were prepared under US GAAP. 'reported' = the issuer's own filed statements, reporting framework not established (the majority: the SEC as-reported feed covers registrants only, so an IFRS filer and an un-resynced domestic one look alike here — a reader must NOT infer IFRS from it). null = no claim at all.",
      )
      .nullable(),
  })
  .passthrough();
const internalFundamentalsNotifyUpdate_Body = z.object({
  symbol: z.string(),
  period: z.string(),
  frequency: z.string(),
  source: z.string(),
  metrics: z
    .object({
      revenue: z.coerce.number(),
      netIncome: z.coerce.number(),
      eps: z.coerce.number(),
      adjustedEps: z.coerce.number().nullable(),
    })
    .partial()
    .optional(),
  currency: z
    .string()
    .describe(
      'The ISO-4217 code the figures are filed in — see `FundamentalsFlashUpdateEvent.currency`.',
    )
    .optional(),
});
const internalFundamentalsIngestIssuerBalance_Body = z.object({
  symbol: z.string(),
  kind: z.enum(['bank', 'miner']),
  asOf: z.string().nullish(),
  source: z.string(),
  borrowings: z.coerce.number().nullish(),
  bankCash: z.coerce.number().nullish(),
  deposits: z.coerce.number().nullish(),
  digitalAssets: z.coerce.number().nullish(),
});
const internalFundamentalsIngestAdsRatio_Body = z.object({
  symbol: z.string(),
  sharesPerAds: z.coerce.number().nullable(),
  source: z.string(),
  accession: z.string().nullish(),
});
const trackedTickersSetActiveTickers_Body = z.object({
  symbols: z
    .array(z.string().max(32))
    .max(200)
    .describe(
      'The symbols to keep active. Any tracked symbol not listed is switched off; an empty array switches all of them off.',
    ),
});
const companyProfilesAdminEnqueueBatch_Body = z.object({
  symbols: z.array(z.string()).min(1).max(1000).describe('Tickers to enqueue (max 1000)'),
});
const ProfileRunDetailDto = z
  .object({
    id: z.string(),
    symbol: z.string(),
    instrumentId: z.string().nullable(),
    status: z.enum(['queued', 'researching', 'ready', 'failed']),
    promptVersion: z.string(),
    researchModel: z.string().nullable(),
    costUsd: z.coerce.number().describe('Vendor-reported cost of the run (USD)').nullable(),
    error: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    profile: z.object({}).partial().passthrough().nullable(),
  })
  .passthrough();
const walletsCreate_Body = z.object({
  name: z.string().min(1).describe('Name of the wallet'),
  currency: z
    .enum([
      'USD',
      'EUR',
      'GBP',
      'JPY',
      'CHF',
      'CAD',
      'AUD',
      'NZD',
      'CNY',
      'INR',
      'SEK',
      'NOK',
      'DKK',
      'PLN',
      'CZK',
      'HUF',
      'RON',
      'RUB',
      'TRY',
      'ILS',
      'AED',
      'SAR',
      'BRL',
      'MXN',
      'ARS',
      'CLP',
      'COP',
      'PEN',
      'UYU',
      'ZAR',
      'KRW',
      'SGD',
      'HKD',
      'TWD',
      'THB',
      'IDR',
      'MYR',
      'PHP',
      'VND',
    ])
    .describe('Currency code'),
  isDefault: z
    .boolean()
    .describe('Set as default wallet (default false)')
    .optional()
    .default(false),
});
const WalletResponseDto = z
  .object({
    id: z.string().describe('Wallet ID'),
    userId: z.string().describe('User ID'),
    name: z.string().describe('Wallet name'),
    currency: z.string().describe('Currency code'),
    balance: z.coerce.number().describe('Current balance'),
    availableBalance: z
      .coerce.number()
      .describe(
        'Balance minus the margin blocked in open CFD positions funded by this wallet (equal to balance when there is no CFD exposure)',
      ),
    isDefault: z.boolean().describe('Is default wallet'),
    currencyBalances: z
      .array(
        z
          .object({
            currency: z.string().describe('Currency code of this cash bucket'),
            cash: z
              .coerce.number()
              .describe(
                'Broker-reported cash in this currency (signed; negatives are margin debits)',
              ),
          })
          .passthrough(),
      )
      .describe(
        'Broker-reported per-currency cash buckets (SnapTrade multi-currency wallets only; absent for manual wallets). `balance` is the fold of these into `currency` at real FX rates.',
      )
      .optional(),
    portfolioId: z
      .string()
      .describe(
        'The portfolio holding this wallet, as its home cash wallet or as one of its cash sides (a broker account holds one wallet per currency); null for a wallet no portfolio holds',
      )
      .nullable(),
    portfolioName: z
      .string()
      .describe('Name of the portfolio holding this wallet (see portfolioId); null when none')
      .nullable(),
    createdAt: z.string().datetime({ offset: true }).describe('Creation date'),
    updatedAt: z.string().datetime({ offset: true }).describe('Last update date'),
  })
  .passthrough();
const WalletTransactionResponseDto = z
  .object({
    id: z.string().describe('Transaction ID'),
    walletId: z.string().describe('Wallet ID'),
    walletName: z
      .string()
      .describe(
        "The owning wallet's name. Served on the CROSS-wallet list, where a row cannot be read without it. The client used to join this itself against its own wallets query, which printed a hardcoded 'Unknown Wallet' whenever the join missed — including for every row while that second query was still in flight. Absent on the single-wallet history, whose rows all belong to the wallet already named on the page.",
      )
      .optional(),
    type: z
      .enum([
        'DEPOSIT',
        'WITHDRAW',
        'TRANSFER_IN',
        'TRANSFER_OUT',
        'INVEST',
        'DIVEST',
        'REMOVAL',
        'INTEREST',
        'INTEREST_CHARGE',
        'FEE',
        'FEE_REFUND',
        'TAX_REFUND',
        'DIVIDEND',
        'OPTION_PREMIUM_PAID',
        'OPTION_PREMIUM_RECEIVED',
        'OPTION_EXERCISE',
        'OPTION_ASSIGNMENT',
        'PROFIT',
        'FX_CONVERSION',
      ])
      .describe('Transaction type'),
    displayLabel: z
      .string()
      .describe(
        "Human-readable transaction type label. Names the row as it is SHOWN, which is not always its booking: an own-account move (`isInternalTransfer`) booked as a deposit or a withdrawal reads 'Transfer In' / 'Transfer Out' by the sign of its amount.",
      ),
    amount: z.coerce.number().describe('Transaction amount'),
    currency: z.string().describe('Currency code'),
    exchangeRate: z.coerce.number().describe('Exchange rate (for currency conversion)').nullish(),
    reference: z.string().describe('Transaction reference').nullish(),
    analyticsOnly: z
      .boolean()
      .describe(
        'TRUE when this row never moved cash: realized profit/loss the ledger records for analytics only. Computed ONCE here by the kernel predicate `isAnalyticsOnlyTxn` (type PROFIT, or a PORTFOLIO_PROFIT_ reference on any type). REQUIRED, never optional: a client that re-derived the rule would drift from the SQL that already excludes these rows from the running balance, which is exactly the B12 defect. A user summed the amount column of their own ledger and got 1 009,45 more than their 9,25 balance, because nothing on the wire told the two kinds of row apart.',
      ),
    isInternalTransfer: z
      .boolean()
      .describe(
        "TRUE when this row is money moving between two of the user's OWN accounts (an XTB ISA sub-account, an IBKR internal transfer), not money from or to a bank. It may still be BOOKED as DEPOSIT / WITHDRAW / REMOVAL, since per book it is that book's capital (owner ruling E#1), so `type` keeps the booking and `displayLabel` names it a transfer. REQUIRED: absent would read as 'not a transfer', the defect it ends (an XTB wallet listed its ISA moves as deposits).",
      ),
    contributionSource: z
      .enum(['EMPLOYEE', 'EMPLOYER', 'STATE', 'UNSTATED'])
      .describe(
        "WHO paid in this pension-plan flow (PPK / PPE, owner ruling R59, plan decision O8): EMPLOYEE, EMPLOYER or STATE (the state's welcome and annual payments) when the file names the payer, UNSTATED when it is a pension-plan deposit or payout and the file does not say who paid. Null on every row that is no pension-plan contribution (a portfolio deposit, an IKE, a fee): the user's own money. REQUIRED, never optional: absent would read as 'the user's own money', the one thing this field exists to tell apart from what the employer and the state add.",
      )
      .nullable(),
    portfolioTransactionId: z
      .string()
      .describe(
        'The portfolio transaction this cash leg belongs to (a dividend, a trade), or null for a row booked on the wallet alone. REQUIRED, never optional: a client edits a linked dividend through its portfolio transaction, and absent would send it to the bare cash edit, which leaves the holding behind.',
      )
      .nullable(),
    convertedAmount: z
      .coerce.number()
      .describe(
        'APPROXIMATE `amount` in `displayCurrency` at the rate stored for the row trade day, read at request time and never stored. Set only when the row currency differs from the requested display currency and a rate exists.',
      )
      .optional(),
    displayCurrency: z
      .string()
      .describe('The currency of `convertedAmount`; set with it')
      .optional(),
    date: z.string().describe('Transaction date'),
    description: z.string().describe('Transaction description').nullish(),
    createdAt: z.string().describe('Creation timestamp'),
  })
  .passthrough();
const PaginationMetaDto = z
  .object({
    total: z.coerce.number().describe('Total number of items'),
    page: z.coerce.number().describe('Current page number'),
    limit: z.coerce.number().describe('Number of items per page'),
    totalPages: z.coerce.number().describe('Total number of pages'),
  })
  .passthrough();
const walletsUpdate_Body = z
  .object({
    name: z.string().min(1).describe('Name of the wallet'),
    currency: z
      .enum([
        'USD',
        'EUR',
        'GBP',
        'JPY',
        'CHF',
        'CAD',
        'AUD',
        'NZD',
        'CNY',
        'INR',
        'SEK',
        'NOK',
        'DKK',
        'PLN',
        'CZK',
        'HUF',
        'RON',
        'RUB',
        'TRY',
        'ILS',
        'AED',
        'SAR',
        'BRL',
        'MXN',
        'ARS',
        'CLP',
        'COP',
        'PEN',
        'UYU',
        'ZAR',
        'KRW',
        'SGD',
        'HKD',
        'TWD',
        'THB',
        'IDR',
        'MYR',
        'PHP',
        'VND',
      ])
      .describe('Currency code'),
    isDefault: z.boolean().describe('Set as default wallet (default false)').default(false),
  })
  .partial();
const walletsDeposit_Body = z.object({
  amount: z.coerce.number().gte(0.01).describe('Amount to deposit'),
  description: z.string().max(255).describe('Optional description for the transaction').optional(),
  date: z
    .union([z.string(), z.string()])
    .describe('Optional date for the deposit transaction (defaults to current date)')
    .optional(),
});
const walletsWithdraw_Body = z.object({
  amount: z.coerce.number().gte(0.01).describe('Amount to withdraw'),
  description: z.string().max(255).describe('Optional description for the transaction').optional(),
  date: z
    .union([z.string(), z.string()])
    .describe('Optional date for the withdrawal transaction (defaults to current date)')
    .optional(),
});
const walletsTransfer_Body = z.object({
  fromWalletId: z.string().uuid().describe('ID of the source wallet'),
  toWalletId: z.string().uuid().describe('ID of the destination wallet'),
  amount: z.coerce.number().gte(0.01).describe('Amount to transfer'),
  date: z.union([z.string(), z.string()]).describe('Date of the transfer (ISO 8601)').optional(),
  exchangeRate: z
    .coerce.number()
    .gte(0.0001)
    .describe('Exchange rate to use (optional, will be fetched if not provided)')
    .optional(),
  description: z.string().describe('Optional description for the transfer').optional(),
  fee: z.coerce.number().gte(0).describe('Transfer fee (deducted from source wallet)').optional(),
});
const walletsUpdateTransaction_Body = z
  .object({
    amount: z.coerce.number().describe('Transaction amount (signed: outflows negative)'),
    description: z.string().describe('Transaction description'),
    date: z.union([z.string(), z.string()]).describe('Transaction date'),
    type: z
      .enum([
        'DEPOSIT',
        'WITHDRAW',
        'TRANSFER_IN',
        'TRANSFER_OUT',
        'INVEST',
        'DIVEST',
        'REMOVAL',
        'INTEREST',
        'INTEREST_CHARGE',
        'FEE',
        'FEE_REFUND',
        'TAX_REFUND',
        'DIVIDEND',
        'OPTION_PREMIUM_PAID',
        'OPTION_PREMIUM_RECEIVED',
        'OPTION_EXERCISE',
        'OPTION_ASSIGNMENT',
        'PROFIT',
        'FX_CONVERSION',
      ])
      .describe('Transaction type'),
    contributionSource: z
      .enum(['EMPLOYEE', 'EMPLOYER', 'STATE', 'UNSTATED'])
      .describe(
        "Who paid in this pension-plan flow (PPK / PPE): EMPLOYEE (the user), EMPLOYER, STATE, or UNSTATED when it is not known. Settable only on a row that ALREADY carries a payer (a pension-plan deposit or payout the import booked); any other row answers 400 NOT_A_PENSION_CONTRIBUTION. Once every flow of a book has a named payer, its return on the participant's own contributions can be worked out.",
      ),
  })
  .partial();
const PositionValuationDto = z
  .object({
    portfolioId: z
      .string()
      .describe(
        'The book the value belongs to: on a merged view, the member that holds the bond or fund unit',
      ),
    symbol: z.string().describe('The symbol as the holdings list shows it'),
    asOfDate: z.string().describe('The day the value applies from (YYYY-MM-DD)'),
    unitPrice: z
      .coerce.number()
      .describe("Value of ONE unit of the position's quantity, in its purchase currency"),
  })
  .passthrough();
const positionValuationsSet_Body = z.object({
  symbol: z.string().min(1).max(32).describe('The symbol exactly as the holdings list shows it'),
  asOfDate: z
    .string()
    .describe('The day the value applies from (YYYY-MM-DD); it holds until the next dated mark'),
  unitPrice: z
    .coerce.number()
    .gt(0)
    .describe(
      "Value of ONE unit of the position's quantity, in its purchase currency (a 1,000-nominal bond quoted 101.5% is 1015). null removes the mark for that day.",
    )
    .nullable(),
});
const PositionValuationSeriesDto = z
  .object({
    symbol: z.string().describe('The bond or fund unit, as asked'),
    currency: z.string().describe('The currency the values are in: the purchase currency'),
    timestamps: z
      .array(z.coerce.number())
      .describe('One per held calendar day, UTC midnight, epoch seconds'),
    closePrices: z
      .array(z.coerce.number())
      .describe(
        'The value of ONE unit that day: the issuer formula, or the price in force (a fund unit: the user mark of that day, else the unit price its TFI published for it when the holding is bound to a published series, else the unit price of its own latest transaction)',
      ),
  })
  .passthrough();
const TreasurySeriesDto = z
  .object({
    code: z.string().describe('The code, upper-case (TOS0527)'),
    family: z
      .enum(['OTS', 'ROR', 'DOR', 'TOS', 'COI', 'ROS', 'EDO', 'ROD'])
      .describe('The family the code names'),
    termMonths: z.coerce.number().describe('The term in months (TOS: 36)'),
    saleMonth: z.string().describe('YYYY-MM: the one month the series was on sale'),
    maturityMonth: z.string().describe('YYYY-MM: the month it matures'),
    status: z
      .enum(['known', 'unknown', 'unreachable'])
      .describe(
        "known: the issuer's page describes the series (the fields below are set). unknown: the issuer answered that it never sold such a series (check the code). unreachable: the issuer could not be asked now; the code may still be right",
      ),
    firstPeriodRatePct: z
      .coerce.number()
      .describe('The first interest period rate, in PERCENT (6.55 = 6.55%); null unless known')
      .nullable(),
    marginPct: z
      .coerce.number()
      .describe(
        'The margin over inflation (COI, ROS, EDO, ROD) or over the NBP reference rate (ROR, DOR) for the later periods, in percent; 0 for the fixed-rate OTS and TOS; null unless known',
      )
      .nullable(),
    saleFrom: z.string().describe('First sale day (YYYY-MM-DD); null unless known').nullable(),
    saleTo: z.string().describe('Last sale day (YYYY-MM-DD); null unless known').nullable(),
  })
  .passthrough();
const FundUnitSeriesDto = z
  .object({
    seriesId: z.coerce.number().int().describe('The series id: what the booking route takes'),
    provider: z.string().describe('The provider key, stable (PKO_TFI)'),
    providerLabel: z.string().describe('The TFI as a person reads it (PKO TFI)'),
    fundName: z
      .string()
      .describe("The fund's name as its TFI publishes it, without the unit category"),
    unitCategory: z
      .string()
      .describe('The unit category (A, B, K), or null where the TFI publishes none')
      .nullable(),
    holdingName: z
      .string()
      .describe(
        'The name a holding of this series is kept under: the fund name with its unit category (PKO Emerytura 2050 (kat. A))',
      ),
    currency: z
      .string()
      .min(3)
      .max(3)
      .regex(/^[A-Z]{3}$/)
      .describe('ISO 4217: the currency the unit price is published in'),
    latestPrice: z
      .coerce.number()
      .describe('The newest published unit price (VNAV), in the series currency; null if none yet')
      .nullable(),
    latestPriceDay: z
      .string()
      .describe('The day the newest price is FOR (YYYY-MM-DD); null if none yet')
      .nullable(),
  })
  .passthrough();
const FundUnitSeriesListDto = z
  .object({ items: z.array(FundUnitSeriesDto).describe('The series that fit, best fit first') })
  .passthrough();
const FundUnitSeriesPriceDto = z
  .object({
    seriesId: z.coerce.number().int().describe('The series asked about'),
    currency: z
      .string()
      .min(3)
      .max(3)
      .regex(/^[A-Z]{3}$/)
      .describe('ISO 4217: the currency the unit price is published in'),
    requestedDay: z.string().describe('The day asked for (YYYY-MM-DD)'),
    day: z
      .string()
      .describe(
        'The day the price is FOR: the requested day, or the newest earlier one (a Saturday is priced by Friday); null when the series had published nothing by then',
      )
      .nullable(),
    price: z
      .coerce.number()
      .describe('The unit price (VNAV) in force that day, in the series currency; null if none')
      .nullable(),
  })
  .passthrough();
const ProjectionYearDto = z.object({
  year: z.coerce.number().int().gte(0).lte(5).describe('Year number (0-5, where 0 is current year)'),
  revenue: z.coerce.number().describe('Projected revenue'),
  netIncome: z.coerce.number().describe('Projected net income'),
  eps: z.coerce.number().describe('Projected earnings per share'),
  peHigh: z.coerce.number().gte(0).lte(1000).describe('High PE ratio for valuation'),
  peLow: z.coerce.number().gte(0).lte(1000).describe('Low PE ratio for valuation'),
  revenueGrowthRate: z
    .coerce.number()
    .gte(-100)
    .lte(500)
    .describe('Revenue growth rate (percentage)')
    .optional(),
  netIncomeGrowthRate: z
    .coerce.number()
    .gte(-100)
    .lte(500)
    .describe('Net income growth rate (percentage)')
    .optional(),
});
const projectionsCreateProjection_Body = z.object({
  stockSymbol: z.string().min(1).describe('Stock symbol'),
  companyName: z.string().min(1).describe('Company name'),
  scenarioType: z.enum(['BULL', 'BASE', 'BEAR']).describe('Scenario type'),
  sharesOutstanding: z.coerce.number().describe('Shares outstanding').optional(),
  years: z.array(ProjectionYearDto).min(1).max(6).describe('Projection years data').optional(),
  autoFetchInitialData: z
    .boolean()
    .describe('Auto-fetch initial data from Finnhub')
    .optional()
    .default(true),
});
const ProjectionYearResponseDto = z
  .object({
    year: z.coerce.number().int().gte(0).lte(5).describe('Year number (0-5, where 0 is current year)'),
    revenue: z.coerce.number().describe('Projected revenue'),
    netIncome: z.coerce.number().describe('Projected net income'),
    eps: z.coerce.number().describe('Projected earnings per share'),
    peHigh: z.coerce.number().gte(0).lte(1000).describe('High PE ratio for valuation'),
    peLow: z.coerce.number().gte(0).lte(1000).describe('Low PE ratio for valuation'),
    revenueGrowthRate: z
      .coerce.number()
      .gte(-100)
      .lte(500)
      .describe('Revenue growth rate (percentage)')
      .optional(),
    netIncomeGrowthRate: z
      .coerce.number()
      .gte(-100)
      .lte(500)
      .describe('Net income growth rate (percentage)')
      .optional(),
    id: z.string().describe('Unique identifier'),
    sharePriceHigh: z.coerce.number().describe('Calculated high share price'),
    sharePriceLow: z.coerce.number().describe('Calculated low share price'),
  })
  .passthrough();
const ProjectionResponseDto = z
  .object({
    id: z.string().describe('Unique identifier'),
    userId: z.string().describe('User ID'),
    stockSymbol: z.string().describe('Stock symbol'),
    companyName: z.string().describe('Company name'),
    scenarioType: z.enum(['BULL', 'BASE', 'BEAR']).describe('Scenario type'),
    sharesOutstanding: z.coerce.number().describe('Shares outstanding'),
    years: z.array(ProjectionYearResponseDto).describe('Projection years data'),
    revenueCAGR: z.coerce.number().describe('CAGR for revenue (5-year)').optional(),
    netIncomeCAGR: z.coerce.number().describe('CAGR for net income (5-year)').optional(),
    sharePriceHighCAGR: z.coerce.number().describe('CAGR for share price high (5-year)').optional(),
    sharePriceLowCAGR: z.coerce.number().describe('CAGR for share price low (5-year)').optional(),
    createdAt: z.string().datetime({ offset: true }).describe('Creation timestamp'),
    updatedAt: z.string().datetime({ offset: true }).describe('Last update timestamp'),
  })
  .passthrough();
const projectionsUpdateProjection_Body = z
  .object({
    stockSymbol: z.string().min(1).describe('Stock symbol'),
    companyName: z.string().min(1).describe('Company name'),
    scenarioType: z.enum(['BULL', 'BASE', 'BEAR']).describe('Scenario type'),
    sharesOutstanding: z.coerce.number().describe('Shares outstanding'),
    years: z.array(ProjectionYearDto).min(1).max(6).describe('Projection years data'),
    autoFetchInitialData: z
      .boolean()
      .describe('Auto-fetch initial data from Finnhub')
      .default(true),
  })
  .partial();
const ShareViewerAccess = z.enum(['anonymous', 'viewer', 'subscriber', 'owner']);
const FollowingResponseDto = z
  .object({
    subscriptions: z.array(
      z
        .object({
          shareId: z
            .string()
            .describe(
              'The PUBLIC share id: the /public/portfolio/<shareId> link, and what unsubscribe takes.',
            ),
          portfolioName: z.string().describe('The name the owner published the book under.'),
          subscribedAt: z.string(),
          linkActive: z.boolean().describe('The link still serves (neither revoked nor expired).'),
          access: ShareViewerAccess.describe(
            'Who is reading: anonymous (no token), viewer (signed in, no subscriber access), subscriber (a subscription on this link AND the plan), owner.',
          ),
          hasPlan: z.boolean().describe("The reader's plan includes portfolio subscriptions."),
          oneMonthPercent: z
            .coerce.number()
            .describe(
              '1M return. ABSENT without access to the performance section; null until computed.',
            )
            .nullish(),
          yearToDatePercent: z
            .coerce.number()
            .describe(
              'YTD return. ABSENT without access to the performance section; null until computed.',
            )
            .nullish(),
          lastActiveAt: z
            .string()
            .describe(
              'YYYY-MM-DD of the latest trade. ABSENT without access to the portfolio-info card.',
            )
            .nullish(),
        })
        .passthrough(),
    ),
    feed: z
      .array(
        z
          .object({
            shareId: z.string(),
            portfolioName: z.string(),
            date: z.string(),
            type: z.string(),
            side: z
              .enum(['buy', 'sell'])
              .describe(
                "The trade's side by the server's own rule — read it, never re-derive it from `type`.",
              ),
            symbol: z.string(),
            name: z
              .string()
              .describe(
                "What the trade is shown under: the issuer's name for a listed holding (the one the owner's own list shows), the name its statement gave a fund unit (whose symbol is a key made from its name), and the ticker where no name resolved.",
              ),
            assetType: z
              .string()
              .describe(
                'The class of the traded holding, so a reader leads a fund unit by its name and a listed one by its ticker. Absent when no position backs the trade.',
              )
              .optional(),
            quantity: z
              .coerce.number()
              .describe('Absent unless the owner shows how many shares.')
              .nullish(),
            price: z.coerce.number().describe('Absent unless the owner shows amounts.').nullish(),
            total: z.coerce.number().describe('Absent unless the owner shows amounts.').nullish(),
            currency: z
              .string()
              .describe(
                'The currency `price` and `total` are in, exactly as on the page. Absent with them.',
              )
              .optional(),
          })
          .passthrough(),
      )
      .describe('The latest trades across the links, newest first.'),
  })
  .passthrough();
const snaptradeGetConnectionPortal_Body = z
  .object({
    broker: z.string().describe("Specific broker slug to connect (e.g., 'ALPACA', 'ROBINHOOD')"),
    customRedirect: z
      .string()
      .url()
      .describe('Custom redirect URI after connection (http/https, real hostname)'),
    reconnectConnectionId: z
      .string()
      .describe(
        'Repair an EXISTING broken connection (SnapTrade reconnect) instead of creating a second authorization beside it: the id of the connection to repair',
      ),
  })
  .partial();
const snaptradeHandleCallback_Body = z.object({
  authorizationId: z.string().describe('Authorization ID from SnapTrade callback'),
  status: z.string().describe('Status of the connection attempt').optional(),
  autoSync: z
    .boolean()
    .describe('Whether to automatically sync after connection (accepted, not acted on)')
    .optional()
    .default(true),
});
const snaptradeUpsertOpeningLot_Body = z.object({
  symbol: z.string().min(1).max(100).describe('Instrument symbol the override applies to'),
  price: z.coerce.number().gt(0).describe('Purchase price per share, in `currency`'),
  currency: z.string().min(3).max(10).describe('Currency the price is denominated in'),
  executedAt: z
    .union([z.string(), z.string()])
    .describe('Purchase date of the opening lot: an ISO instant or a bare `YYYY-MM-DD` day'),
  quantity: z
    .coerce.number()
    .gt(0)
    .describe(
      'Quantity the user confirmed (display/audit only — the live broker gap remains the synthesized lot quantity).',
    )
    .optional(),
});
const SyncResultDto = z
  .object({
    success: z.boolean(),
    connectionId: z.string(),
    walletsCreated: z.coerce.number(),
    portfoliosCreated: z.coerce.number(),
    transactionsSynced: z.coerce.number(),
    holdingsSynced: z.coerce.number(),
    wallets: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          currency: z.string(),
          balance: z.coerce.number(),
          transactionCount: z.coerce.number(),
        })
        .passthrough(),
    ),
    portfolios: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          positionsCount: z.coerce.number(),
          soldPositionsCount: z.coerce.number(),
          totalValue: z.coerce.number(),
        })
        .passthrough(),
    ),
    errors: z.array(
      z
        .object({
          accountId: z.string(),
          accountName: z.string(),
          error: z.string(),
          details: z.string().optional(),
        })
        .passthrough(),
    ),
    syncHistoryId: z.string().optional(),
  })
  .passthrough();
const ImportUnknownLabelDto = z
  .object({
    id: z.string(),
    broker: z.string(),
    column: z.string().describe('The statement column the label was read from'),
    labelKey: z
      .string()
      .describe('The label as the census keys it, or a placeholder while one user holds it'),
    masked: z
      .boolean()
      .describe(
        'True while one user holds the label: labelKey is the placeholder, and nothing of the record (its cash included) may leave the team',
      ),
    language: z.string(),
    status: z.enum(['open', 'issue_filed', 'mapped', 'ignored']),
    userCount: z.coerce.number().int().describe('Distinct users with a hit'),
    rows: z.coerce.number().int().describe('Rows left out of the books, summed over the hits'),
    amounts: z
      .record(z.coerce.number())
      .describe('Cash per currency summed over the hits; never converted between currencies'),
    confirmations: z
      .array(z.object({ event: z.string(), users: z.coerce.number().int() }).passthrough())
      .describe('What users answered the rows are, most answers first'),
    samples: z
      .array(
        z
          .object({ date: z.string(), amount: z.coerce.number(), currency: z.string() })
          .partial()
          .passthrough(),
      )
      .describe("The most recently seen hit's samples: a date, an amount and a currency only"),
    createdAt: z.string().datetime({ offset: true }),
    lastSeenAt: z
      .string()
      .datetime({ offset: true })
      .describe('null when no hit is left')
      .nullable(),
  })
  .passthrough();
const UpdateImportUnknownLabelDto = z.object({
  status: z
    .enum(['open', 'issue_filed', 'mapped', 'ignored'])
    .describe('The step of the lifecycle the record moves to'),
});
const WatchlistItemResponseDto = z
  .object({
    id: z.string(),
    watchlistId: z.string(),
    symbol: z.string(),
    assetType: z.string().nullish(),
    addedPrice: z.coerce.number().nullish(),
    notes: z.string().nullish(),
    pinned: z
      .boolean()
      .describe("Pinned in the research sidebar ('Przypięte') — fox F0.")
      .default(false),
    addedAt: z.string().datetime({ offset: true }).describe('ISO instant the symbol was added.'),
    name: z.string().describe('Instrument display name').optional(),
    currentPrice: z.coerce.number().describe('Current market price').optional(),
    dayChangePercent: z.coerce.number().describe('Day change percent (e.g. -1.23 for -1.23%)').optional(),
    marketCap: z.coerce.number().describe('Market capitalization in base currency').optional(),
    sector: z.string().optional(),
    industry: z.string().optional(),
    currency: z.string().optional(),
    low52w: z.coerce.number().describe('52-week low').optional(),
    high52w: z.coerce.number().describe('52-week high').optional(),
    logoUrl: z.string().optional(),
    preMarketPrice: z
      .coerce.number()
      .describe(
        "Most recent pre-market last-trade price for the symbol. Frozen after pre-market closes (09:30 ET); overwritten when the next day's pre-market opens.",
      )
      .optional(),
    preMarketChangePercent: z
      .coerce.number()
      .describe(
        'Most recent pre-market change percent vs. the previous regular-session close. Signed.',
      )
      .optional(),
    afterHoursPrice: z
      .coerce.number()
      .describe(
        "Most recent after-hours last-trade price for the symbol. Frozen after after-hours closes (20:00 ET); overwritten when today's after-hours opens at 16:00 ET.",
      )
      .optional(),
    afterHoursChangePercent: z
      .coerce.number()
      .describe("Most recent after-hours change percent vs. today's regular-session close. Signed.")
      .optional(),
    metrics: z
      .record(z.coerce.number())
      .describe(
        'Flat map of additional fundamentals metrics keyed by frontend metric IDs (peRatio, psRatio, evToEbitda, grossMargin, roe, debtToEquity, ...)',
      )
      .optional(),
  })
  .passthrough();
const WatchlistResponseDto = z
  .object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    isDefault: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    itemCount: z.coerce.number().describe('Count of items in this watchlist'),
    items: z
      .array(WatchlistItemResponseDto)
      .describe('Items (only populated on detail GET)')
      .optional(),
  })
  .passthrough();
const watchlistsCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Watchlist name'),
  description: z.string().max(500).describe('Watchlist description').optional(),
  isDefault: z.boolean().describe('Mark as default watchlist').optional().default(false),
});
const watchlistsUpdate_Body = z
  .object({
    name: z.string().max(100).describe('Watchlist name'),
    description: z.string().max(500).describe('Watchlist description'),
    isDefault: z.boolean().describe('Mark as default watchlist'),
  })
  .partial();
const watchlistsAddItem_Body = z.object({
  symbol: z.string().min(1).max(50).describe('Instrument symbol (e.g. AAPL.US)'),
  assetType: z
    .string()
    .max(50)
    .describe(
      'Asset type hint (e.g. STOCK, ETF). IGNORED whenever the instrument registry knows the symbol — the server resolves the type there, so a caller cannot mislabel an instrument. Used only as a fallback for a symbol the registry has never seen.',
    )
    .optional(),
  addedPrice: z.coerce.number().gte(0).describe('Price snapshot at time of adding').optional(),
  notes: z.string().max(1000).describe('Free-form user notes').optional(),
});
const QualityPairSeriesDto = z
  .object({
    years: z.array(z.string()).describe('Fiscal-year labels, oldest first.'),
    primary: z.array(z.coerce.number().nullable()),
    secondary: z.array(z.coerce.number().nullable()),
    line: z.array(z.coerce.number().nullable()),
  })
  .passthrough();
const TabPercentilesDto = z
  .object({
    overview: z.coerce.number().describe('vs the whole universe.').nullable(),
    dividends: z.coerce.number().describe('vs dividend payers.').nullable(),
    growth: z.coerce.number().describe('vs the industry.').nullable(),
    solidity: z.coerce.number().describe('vs the industry.').nullable(),
    valuation: z.coerce.number().describe('vs the industry (multiple norms are sector-shaped).').nullish(),
    momentum: z
      .coerce.number()
      .describe('vs the universe (the axis is itself a universe percentile).')
      .nullish(),
  })
  .passthrough();
const FactorProfileDto = z
  .object({
    quality: z.coerce.number().nullable(),
    growth: z.coerce.number().nullable(),
    momentum: z.coerce.number().nullable(),
    dividend: z.coerce.number().nullable(),
    volatility: z.coerce.number().nullable(),
    value: z.coerce.number().nullable(),
  })
  .passthrough();
const FairValueBasisDto = z
  .object({
    fairValue: z.coerce.number(),
    deviationPct: z
      .coerce.number()
      .describe('(price/FV − 1)×100; positive = above FV. Currency-gated.')
      .nullable(),
    multipleAvg5y: z
      .coerce.number()
      .describe('The 5y year-end multiple anchor (earnings/sales bases only).')
      .nullable(),
  })
  .passthrough();
const WeightVectorDto = z
  .object({
    overview: z.coerce.number(),
    dividends: z.coerce.number(),
    growth: z.coerce.number(),
    solidity: z.coerce.number(),
    valuation: z.coerce.number().optional(),
    momentum: z.coerce.number().optional(),
  })
  .passthrough();
const ScreenerTabScoreDto = z
  .object({
    score: z
      .coerce.number()
      .describe(
        'Raw number of passed criteria. NULL when the tab had nothing evaluable — a company that pays no dividend has an ABSENT dividend score, never 0/10.',
      )
      .nullable(),
    max: z.coerce.number().describe("The tab's scale: 15 / 10 / 12 / 12 / 9 / 10."),
  })
  .passthrough();
const positionsUpdatePosition_Body = z
  .object({ notes: z.string(), targetPrice: z.coerce.number() })
  .partial();
const Position = z.object({}).partial().passthrough();
const positionsUpdatePositionCore_Body = z
  .object({
    symbol: z.string().max(20).describe('Stock symbol'),
    quantity: z.coerce.number().gte(0).describe('Position quantity'),
    avgCostBasis: z.coerce.number().gte(0).describe('Average cost basis per share'),
    assetType: z
      .enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION', 'FUND'])
      .describe('Asset type. CFD is not accepted here — CFDs are edited via the CFD endpoint.'),
    ratio: z
      .coerce.number()
      .gt(0)
      .describe(
        'New units per old unit, when the holding BECAME another instrument in a share-for-share merger (0.67 GNL per RTL share). Re-expresses every trade of the position in the new units (quantity x ratio, price / ratio) and the position the same way; cost, amounts and dates are kept. Sent with `symbol` (the successor) to answer the import review question on an exit at no value; alone when the position already carries the successor.',
      ),
    acknowledgeUnrelated: z
      .boolean()
      .describe(
        "Explicit acknowledgement that the new symbol identifies a DIFFERENT company than the current one. Required (via the 422 SYMBOL_IDENTITY_MISMATCH round-trip) when the identity guard detects a cross-company remap — e.g. mapping delisted 'Big 5 Sporting Goods' (BGFV) onto 'Dick's Sporting Goods' (DKS) rewrites history as another company.",
      ),
  })
  .partial();
const RenamedFundUnitDto = z
  .object({
    id: z.string().describe('The position id'),
    symbol: z.string().describe('The key the holding is stored under: a rename never changes it'),
    name: z.string().describe('The name the holding now carries'),
  })
  .passthrough();
const newsletterSubscribe_Body = z.object({
  email: z.string().email().describe('Email address for newsletter subscription'),
  language: z.string().describe('Language preference for newsletter').optional(),
  source: z.string().describe('Source of the subscription').optional(),
});
const contactCreateInquiry_Body = z.object({
  name: z.string().min(1).describe('Name of the person contacting'),
  email: z.string().email().describe('Email address for contact'),
  company: z.string().describe('Company name').optional(),
  message: z.string().min(10).describe('Message content'),
});
const PublicShareViewerDto = z
  .object({
    access: ShareViewerAccess.describe(
      'Who is reading: anonymous (no token), viewer (signed in, no subscriber access), subscriber (a subscription on this link AND the plan), owner.',
    ),
    isSubscribed: z
      .boolean()
      .describe('A subscription row on this link — true even while the plan has lapsed.'),
    hasPlan: z.boolean().describe("The reader's plan includes portfolio subscriptions."),
  })
  .passthrough();
const ShareSectionVisibilityDto = z
  .object({
    performanceChart: ShareVisibility,
    monthlyReturns: ShareVisibility,
    dividends: ShareVisibility,
    positionAllocation: ShareVisibility,
    assetAllocation: ShareVisibility,
    sectorDistribution: ShareVisibility,
    geographicBreakdown: ShareVisibility,
    industryDistribution: ShareVisibility,
    transactionHistory: ShareVisibility,
    holdings: ShareVisibility,
    tradeActivity: ShareVisibility,
    riskMetrics: ShareVisibility,
    returnStatistics: ShareVisibility,
    marketCapBuckets: ShareVisibility,
    dividendBuckets: ShareVisibility,
    portfolioInfo: ShareVisibility,
  })
  .passthrough();
const PublicPortfolioResponseDto = z
  .object({
    externalCapitalReturnPercent: z
      .coerce.number()
      .describe(
        "Return on EXTERNAL capital, percent. THE canonical return: one rule, one denominator, identical on every surface. READ IT WITH externalCapitalReturnBasisKind — 'inception' means inception-to-date, 'window' means relative to the requested timeframe's opening bar. Null when it is not defined for this book or this request — read externalCapitalSuppressedReason for which. A null is a gap, NEVER 0%.",
      )
      .nullable(),
    externalCapitalBasisSource: z
      .enum(['externalFlows', 'deployedCash', 'tradeLedger', 'none'])
      .describe(
        "Which rung of the data-derived ladder produced the basis. 'none' IS the absence value, which is why this field is not nullable.",
      ),
    externalCapitalSuppressedReason: z
      .enum([
        'positionFilter',
        'noBasisYet',
        'noValueAtDate',
        'zeroBasis',
        'negativeValue',
        'dustBasis',
        'equityWipedOut',
        'microExternalBasis',
        'insufficientExternalHistory',
        'ledgerCannotFundHoldings',
        'neverDeployed',
        'cfdCapitalUnavailable',
        'fxRateUnavailable',
        'costFxRateUnavailable',
        'valueFxRateUnavailable',
        'noWindowOpenBasis',
        'noWindowOpenEquity',
        'capitalAddedInWindow',
        'windowEquityWipedOut',
        'syntheticPoint',
        'unavailable',
        'nonFiniteInput',
        'mergedFundingEntangled',
        'mergedFundingUndeclared',
        'mergedMemberRefused',
      ])
      .describe(
        "Why the return is absent. Null IFF externalCapitalReturnPercent is a number. 'positionFilter' — undefined for a filtered selection. 'noBasisYet'/'zeroBasis' — the book had no external capital at risk yet; render a gap. 'noValueAtDate' — no valuation on that date. 'negativeValue' — the portfolio is worth less than nothing, so a percentage-of-capital is not a measurement. 'dustBasis' — the capital at risk is a rounding residue (at or below 0.05), so a ratio over it is noise. 'equityWipedOut' — the loss exceeds the whole book. 'microExternalBasis' — the basis clears the dust floor but is a negligible fraction (below 5%) of the capital the book deployed, so it was funded by internal churn / already-withdrawn deposits and a return on external capital is not meaningfully computable (evaluated per point, on the externalFlows and deployedCash rungs). 'insufficientExternalHistory' — the book has no external-flow history (deployedCash rung) and its wallet cash balance exceeds the deployed basis, so the value is dominated by non-redeployed sell-proceeds cash and the return is an artefact of the missing deposit history; surface the 'please fill in your history' prompt. (When such a book is still majority-invested an honest cost-basis return is served instead — see externalCapitalReturnBasisKind 'costBasis' — and this reason is null.) 'cfdCapitalUnavailable' — the book has no denominator at all AND carries closed-CFD realized-P&L rows whose capital-at-risk is unknown (source statement not re-imported/backfilled yet), so the closes' unknown margin is the cause of the missing basis; a book with any stateable capital serves instead and reports cfdCapitalMissingCount. The remedy is re-importing the broker statement. 'fxRateUnavailable' — a wallet row has no exchange rate in force on its day (none stored for it or the 14 days before), so it is missing from the cash and the external capital alike; refused from that day on, externalCapitalUnpricedCashRows counts the rows. 'costFxRateUnavailable' — a cost row (a buy, an opened option, a delivery in) has no exchange rate in force on its day, so the cost basis and every basis built on it cannot be stated; refused from that day on, externalCapitalUnpricedCost names the pair and the day. 'valueFxRateUnavailable' — an open holding has no current exchange rate into the portfolio currency, so the book value and the return over it cannot be stated; unconvertedCurrencies names the currency (owner ruling D81). 'noWindowOpenBasis' — the requested window opens on a bar that carries no return. 'noWindowOpenEquity' — no equity was standing when the window opened. 'capitalAddedInWindow' — external capital entered the window AND the flow could not be time-weighted, so no denominator spans it. 'windowEquityWipedOut' — the book was wiped out inside the window. 'syntheticPoint' — a fabricated bar. 'unavailable' — an upstream input was missing. 'nonFiniteInput' — a return-determining input reached the boundary as a PRESENT non-finite value (NaN/Infinity), which cannot be told from an absent optional, so the point is refused rather than let either solver publish NaN or a fabricated number. 'mergedFundingEntangled' — MERGED views only: the members draw on the same external money (a shared funding wallet, or a transfer chain moving one deposit between them), so summing their bases would count that deposit once per member; view the portfolios separately for a return on each. 'ledgerCannotFundHoldings' — the recorded deposits were proven short of what the wallet spent AND the wallet balance is negative, so the holdings are funded by money the ledger never recorded and the ratio would be overstated; complete the deposit history. 'mergedFundingUndeclared' — MERGED views only: the funding partition could not inspect one or more members, so it can neither prove nor rule out that they share external money; the fold refuses rather than risk double-counting a deposit, and the remedy is the same as for mergedFundingEntangled (view the portfolios separately). 'mergedMemberRefused' — MERGED views only: at least one member refused its own return with a verdict about its own capital and the members do not share one reason, so no member diagnosis is true of the whole merge; open each portfolio on its own to see which one and why. 'neverDeployed' — era-17: a certified book whose deposits were NEVER put to work (the deployed peak never cleared the 0.05 dust floor): no capital at work means a return on deployed capital does not exist yet. A named abstention, never a percent and never the microExternalBasis heuristic; the earned amount (externalCapitalReturnAmount) still populates and surfaces show it beside the caption.",
      )
      .nullable(),
    externalCapitalReturnBasisKind: z
      .enum(['inception', 'window', 'costBasis'])
      .describe(
        "WHICH quantity the four fields above carry. 'inception' — inception-to-date, the engine's own quantity, with externalCapitalBasis the all-time peak. 'window' — relative to the requested timeframe's opening bar, with externalCapitalBasis the window's OPENING EQUITY. 'costBasis' — the honest cost-basis fallback served where external-capital history is missing but the book is still majority-invested: externalCapitalReturnPercent is a return on the peak OPEN-POSITIONS cost basis (externalCapitalBasis), and externalCapitalHistoryIncomplete is true. Not nullable: a refusal still answers one of the questions. NEVER re-apply a client-side rebase to fields marked 'window' or 'costBasis'.",
      ),
    cfdCapitalMissingCount: z
      .coerce.number()
      .describe(
        "How many of this book's closed-CFD cash rows carry NO capital-at-risk data, scoped like the basis (0 on a funding wallet shared with another portfolio, whose return never takes a CFD close's P&L). Their realized P&L is in the return; their margin is NOT in externalCapitalBasis, so a served return beside a non-zero count rests on an understated basis and a surface captions it.",
      ),
    portfolioName: z.string(),
    timeFrame: z.string().describe("The book age as 'N|unit' (all-history)."),
    lastUpdated: z.string(),
    currency: z.string().optional(),
    showValues: z.boolean().describe('Whether the owner publishes absolute amounts.'),
    totalValue: z.coerce.number().nullish(),
    totalInvested: z.coerce.number().nullish(),
    totalReturn: z.coerce.number().nullish(),
    income: z.coerce.number().nullish(),
    cashBalances: z
      .array(
        z.object({ currency: z.string(), amount: z.coerce.number(), converted: z.coerce.number() }).passthrough(),
      )
      .optional(),
    allocation: z
      .array(
        z
          .object({
            symbol: z.string(),
            name: z.string(),
            percentage: z.coerce.number(),
            value: z.coerce.number().nullish(),
            marketValue: z.coerce.number().nullish(),
            assetType: z.string().optional(),
          })
          .passthrough(),
      )
      .optional(),
    topHoldings: z
      .array(
        z
          .object({
            symbol: z.string(),
            name: z.string(),
            quantity: z.coerce.number().describe('Absent unless the owner shows quantities.').nullish(),
            value: z.coerce.number().describe('Absent unless the owner shows amounts.').nullish(),
            returnPercent: z
              .coerce.number()
              .describe('Null = REFUSED (nothing priced the row) — render a gap, never 0.00%.')
              .nullable(),
            priceStaleSince: z
              .string()
              .describe(
                'YYYY-MM-DD of the market close the row is valued at when it predates the last completed session (owner ruling D94). Null on a live price.',
              )
              .nullish(),
            assetType: z.string().optional(),
          })
          .passthrough(),
      )
      .optional(),
    holdingsHeatmap: z
      .array(
        z
          .object({
            symbol: z.string(),
            name: z
              .string()
              .describe(
                'What the tile is called: the ticker, or for a fund unit (whose symbol is a key made from its name) the name its statement gave it.',
              ),
            weightPercent: z
              .coerce.number()
              .describe(
                "Share of the VISIBLE positions' value, percent — hidden ones re-base the rest.",
              ),
            returnPercent: z
              .coerce.number()
              .describe(
                'Total return since cost. Null = REFUSED (nothing priced the row) — a gap, never 0.',
              )
              .nullable(),
            dayChangePercent: z
              .coerce.number()
              .describe("Today's move. Null when nothing priced the row today — a gap, never 0.")
              .nullable(),
            priceStaleSince: z
              .string()
              .describe(
                "YYYY-MM-DD of the market close the tile's return is measured at when it predates the last completed session (owner ruling D94). Null on a live price.",
              )
              .nullish(),
            assetType: z.string().optional(),
          })
          .passthrough(),
      )
      .optional(),
    unconvertedCurrencies: z
      .array(z.string())
      .describe(
        'Currencies excluded from the totals on this object because no usable exchange rate into its currency was available: the totals are PARTIAL and must be shown as such. Absent when nothing was excluded.',
      )
      .optional(),
    performanceChart: z.boolean().optional(),
    performanceTimeSeries: z
      .array(
        z
          .object({
            date: z.string(),
            value: z.coerce.number().nullish(),
            returnPercent: z.coerce.number().nullish(),
            positionValue: z.coerce.number().nullish(),
            investedCapital: z.coerce.number().nullish(),
            costBasis: z.coerce.number().nullish(),
            walletCashBalance: z.coerce.number().nullish(),
            walletToPortfolioFxRate: z.coerce.number().nullish(),
            cumulativeTwr: z.coerce.number().nullish(),
            twrRefused: z
              .literal(true)
              .describe(
                'Present on every point when the TWR of this series was REFUSED: its cumulativeTwr is absent (a 0 on an older payload is not a measurement), so say it was not measured, never read it as 0%. A boolean, never an amount.',
              )
              .optional(),
            cumulativeMwr: z.coerce.number().nullish(),
            externalCapitalReturnPercent: z.coerce.number().nullish(),
            externalCapitalBasisSource: z
              .enum(['externalFlows', 'deployedCash', 'tradeLedger', 'none'])
              .optional(),
            externalCapitalSuppressedReason: z
              .enum([
                'positionFilter',
                'noBasisYet',
                'noValueAtDate',
                'zeroBasis',
                'negativeValue',
                'dustBasis',
                'equityWipedOut',
                'microExternalBasis',
                'insufficientExternalHistory',
                'ledgerCannotFundHoldings',
                'neverDeployed',
                'cfdCapitalUnavailable',
                'fxRateUnavailable',
                'costFxRateUnavailable',
                'valueFxRateUnavailable',
                'noWindowOpenBasis',
                'noWindowOpenEquity',
                'capitalAddedInWindow',
                'windowEquityWipedOut',
                'syntheticPoint',
                'unavailable',
                'nonFiniteInput',
                'mergedFundingEntangled',
                'mergedFundingUndeclared',
                'mergedMemberRefused',
              ])
              .nullish(),
            externalCapitalReturnBasisKind: z.enum(['inception', 'window', 'costBasis']).optional(),
            cfdCapitalMissingCount: z.coerce.number().optional(),
            netDeposits: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
            cumulativeDeliveredCapital: z
              .coerce.number()
              .describe('Absent unless amounts are shown.')
              .nullish(),
            cumulativeCfdRealized: z
              .coerce.number()
              .describe('Absent unless amounts are shown.')
              .nullish(),
            externalCapitalReturnAmount: z
              .coerce.number()
              .describe('Absent unless amounts are shown.')
              .nullish(),
            externalCapitalBasis: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
            externalCapitalInceptionBasis: z
              .coerce.number()
              .describe('Absent unless amounts are shown.')
              .nullish(),
            externalCapitalDeployed: z
              .coerce.number()
              .describe('Absent unless amounts are shown.')
              .nullish(),
            funded: z
              .boolean()
              .describe(
                'Whether the bar holds invested positions, on every share point (values shown or hidden). The chart rebases a benchmark on the first such bar. Never an amount. Optional only because a replica that predates it sends none; the chart then anchors as it did before.',
              )
              .optional(),
          })
          .passthrough(),
      )
      .optional(),
    performanceTimeframe: z.string().optional(),
    performanceChartReturnType: z.enum(['mwr', 'twr', 'simple']).optional(),
    monthlyReturns: z
      .array(
        z
          .object({ month: z.string().describe('YYYY-MM'), returnPercent: z.coerce.number().nullable() })
          .passthrough(),
      )
      .describe('LEGACY, RANGE-BOUND monthly returns; the heatmap reads allHistory.monthlyReturns.')
      .optional(),
    showMonthlyReturns: z.boolean().optional(),
    dividends: z
      .array(
        z
          .object({
            date: z.string(),
            symbol: z.string(),
            amount: z.coerce.number(),
            currency: z
              .string()
              .describe(
                'The currency `amount` is in: the book’s, or the row’s own when no rate into the book’s currency was in force on its day. Such a row is named and left out of the total, never summed 1:1.',
              ),
          })
          .passthrough(),
      )
      .optional(),
    showDividends: z.boolean().optional(),
    benchmarks: z
      .array(
        z
          .object({
            symbol: z.string(),
            name: z.string().describe('Display name of the benchmark.').optional(),
            timeSeries: z
              .array(
                z
                  .object({
                    date: z.string(),
                    value: z
                      .coerce.number()
                      .describe(
                        'Null only on a payload computed before zero closes were refused: draw a gap.',
                      )
                      .nullable(),
                  })
                  .passthrough(),
              )
              .describe(
                'Chronological. value = close / close(basis.window.from) x 100, each close on `basis` (converted, dividends reinvested). timeSeries[0] is the BASELINE point: value 100, stamped at the window`s start, at or before every other point. It is the close in force when the window opens - on a daily series (bare YYYY-MM-DD dates) the close of the window`s first day, or the last close before it when that day was not a trading day; on an intraday series (ISO instants: 1D, 1W and custom ranges of up to 7 days) the close of the last session BEFORE the first session the window shows (for the live 1D window, that session`s previous close). A line that starts later rebases at the benchmark value in force at its own first point.',
              ),
            returnPercent: z
              .coerce.number()
              .describe(
                'Change over basis.window on basis (its currency, its return kind), percent: the last value - 100. Compare it with a portfolio`s time-weighted return over the same window.',
              )
              .optional(),
            volatility: z
              .coerce.number()
              .describe(
                "Annualized volatility of the series` bar-to-bar returns on basis FROM THE ANCHOR CLOSE (the series returnPercent reads), percent: population sigma x sqrt(bars a trading year holds at the series` resolution) - the sessions its own days measure a year to hold (252 for a listing, 365.25 for a series that prints every day). Absent when the bars span under 7 days (1D, 1W): too few returns to annualize, as for the portfolio's own volatility.",
              )
              .optional(),
            basis: z
              .object({
                currency: z
                  .string()
                  .describe(
                    'ISO 4217 code the series is expressed in: the currency the analytics are served in (the view`s displayCurrency, else the portfolio`s). The listing`s closes are converted at the FX route`s daily rate of each bar`s day, the last known rate carried over a gap - never a later one. A benchmark that cannot be converted is not served. Null only on a payload computed before the conversion existed.',
                  )
                  .nullable(),
                returnKind: z
                  .enum(['price', 'netTotalReturn'])
                  .describe(
                    "'netTotalReturn' (label: TR netto / net TR): the listing`s ledger dividends, net of `withholdingRate`, reinvested at the ex-date close - the basis to hold a portfolio return that counts its dividends against. 'price': closes only, dividends NOT reinvested - served for a listing whose withholding rate is not defined (every non-US listing), a dividend in another currency than the listing, an unreadable ledger, and on a payload computed before net TR existed. A portfolio beats a price benchmark by the dividend yield alone.",
                  ),
                withholdingRate: z
                  .coerce.number()
                  .describe(
                    'The fraction withheld from each dividend before it is reinvested: 0.15 = the standard 15 % US withholding (owner ruling D1). Null on a price return; absent only on a payload computed before it existed.',
                  )
                  .nullish(),
                window: z
                  .object({
                    from: z
                      .string()
                      .describe(
                        'YYYY-MM-DD: the session whose CLOSE the series is anchored on (its value 100).',
                      ),
                    to: z.string().describe('YYYY-MM-DD: the session of the series` last point.'),
                  })
                  .passthrough()
                  .describe(
                    'The period the series and returnPercent measure: returnPercent = close(to) / close(from) - 1.',
                  ),
              })
              .passthrough()
              .describe(
                'What the series measures. Absent on an `unavailable` block, and on an analytics payload computed before the field existed and still served from the compute memo.',
              )
              .optional(),
            portfolioReturnPercent: z
              .coerce.number()
              .describe(
                'The PORTFOLIO`s time-weighted return over this benchmark`s own basis.window - the same start bar, percent. The ONE figure to hold returnPercent against: the portfolio`s headline TWR runs from its purchase prices, while the benchmark opens on the close in force when its window opens (for a window holding the first buy, the close of that session). Null when nothing was at work in the window; absent on an `unavailable` block and on a payload computed before it existed.',
              )
              .nullish(),
            unavailable: z
              .enum(['fx-unavailable', 'no-data'])
              .describe(
                'Set when the requested benchmark could NOT be served - then timeSeries is empty and there is no basis. `fx-unavailable`: its closes cannot be expressed in the analytics currency (no FX rate at the window`s anchor - the FX route publishes no rate for the currency - or its listing currency is unknown). `no-data`: no price source serves a bar for the symbol in the window (an unknown or delisted symbol, or a failed read). Show the benchmark as refused, never as a flat line.',
              )
              .optional(),
          })
          .passthrough(),
      )
      .describe('The owner`s benchmark lines, exactly as the analytics route serves them.')
      .optional(),
    transactions: z
      .array(
        z
          .object({
            date: z.string(),
            type: z.string(),
            symbol: z.string(),
            name: z
              .string()
              .describe(
                "What the row is shown under: the issuer's name for a listed holding (the one the owner's own list shows), the name its statement gave a fund unit (whose symbol is a key made from its name), and the ticker where no name resolved.",
              ),
            quantity: z
              .coerce.number()
              .describe('Absent unless the owner shows how many shares (`showPositions`).')
              .nullish(),
            price: z.coerce.number().describe('Absent unless the owner shows amounts.').nullish(),
            total: z.coerce.number().describe('Absent unless the owner shows amounts.').nullish(),
            currency: z
              .string()
              .describe(
                'The currency `price` and `total` are in: the instrument’s on a cross-currency row of a regular book, the book’s on a merged one (a row with no rate keeps its own). Absent with them.',
              )
              .optional(),
            assetType: z.string().optional(),
          })
          .passthrough(),
      )
      .optional(),
    analyticsAllocation: z
      .object({
        byAsset: z.array(
          z
            .object({
              type: z.string(),
              value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
              percent: z.coerce.number(),
            })
            .passthrough(),
        ),
        bySector: z.array(
          z
            .object({
              sector: z.string(),
              value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
              percent: z.coerce.number(),
            })
            .passthrough(),
        ),
        byGeography: z.array(
          z
            .object({
              region: z.string(),
              value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
              percent: z.coerce.number(),
            })
            .passthrough(),
        ),
        byIndustry: z.array(
          z
            .object({
              industry: z.string(),
              value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
              percent: z.coerce.number(),
            })
            .passthrough(),
        ),
        byPosition: z.array(
          z
            .object({
              symbol: z.string(),
              name: z.string(),
              value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
              percent: z.coerce.number(),
            })
            .passthrough(),
        ),
      })
      .partial()
      .passthrough()
      .optional(),
    showPositionAllocation: z.boolean().optional(),
    showAssetAllocation: z.boolean().optional(),
    showSectorDistribution: z.boolean().optional(),
    showGeographicBreakdown: z.boolean().optional(),
    showIndustryDistribution: z.boolean().optional(),
    description: z.string().describe('The owner description, if they wrote one.').nullish(),
    tags: z.array(z.string()).optional(),
    brokerSynced: z
      .boolean()
      .describe(
        'True when a live broker connection owns the book (every member, for a merged book) - the same rule as the /investments card (`brokerSyncedPortfolioIds`). Null when unknown.',
      )
      .nullish(),
    viewer: PublicShareViewerDto.optional(),
    visibility: ShareSectionVisibilityDto.optional(),
    sinceStartPercent: z
      .coerce.number()
      .describe(
        'THE headline, public on every link: the book`s TWR over its whole record, the measure every period cell of allHistory.periodReturns reads (owner decision D83), so a 1Y longer than the book reads exactly this. Null when the series cannot be measured.',
      )
      .nullable(),
    allHistory: z
      .object({
        periodReturns: z
          .object({
            asOf: z.string().describe('YYYY-MM-DD — the last bar every return below ends on.'),
            oneMonthPercent: z.coerce.number().nullable(),
            yearToDatePercent: z.coerce.number().nullable(),
            oneYearPercent: z.coerce.number().nullable(),
            sinceStartPercent: z
              .coerce.number()
              .describe(
                'The TWR over the whole record (owner decision D83) - the page headline. A period that reaches back past the first bar is clipped to it and reads exactly this figure.',
              )
              .nullable(),
            benchmark: z
              .object({
                symbol: z.string().describe("The owner's FIRST selected benchmark."),
                basis: z
                  .object({
                    currency: z
                      .string()
                      .describe(
                        'ISO 4217 code the series is expressed in: the currency the analytics are served in (the view`s displayCurrency, else the portfolio`s). The listing`s closes are converted at the FX route`s daily rate of each bar`s day, the last known rate carried over a gap - never a later one. A benchmark that cannot be converted is not served. Null only on a payload computed before the conversion existed.',
                      )
                      .nullable(),
                    returnKind: z
                      .enum(['price', 'netTotalReturn'])
                      .describe(
                        "'netTotalReturn' (label: TR netto / net TR): the listing`s ledger dividends, net of `withholdingRate`, reinvested at the ex-date close - the basis to hold a portfolio return that counts its dividends against. 'price': closes only, dividends NOT reinvested - served for a listing whose withholding rate is not defined (every non-US listing), a dividend in another currency than the listing, an unreadable ledger, and on a payload computed before net TR existed. A portfolio beats a price benchmark by the dividend yield alone.",
                      ),
                    withholdingRate: z
                      .coerce.number()
                      .describe(
                        'The fraction withheld from each dividend before it is reinvested: 0.15 = the standard 15 % US withholding (owner ruling D1). Null on a price return; absent only on a payload computed before it existed.',
                      )
                      .nullish(),
                  })
                  .passthrough()
                  .describe(
                    "What the benchmark`s 1Y is measured on: the book`s currency and, for a US listing, a net total return (owner ruling D1) - the label a reader needs next to 'vs SPY'.",
                  )
                  .optional(),
                unavailable: z
                  .enum(['fx-unavailable', 'no-data'])
                  .describe(
                    'Set when the requested benchmark could NOT be served - then timeSeries is empty and there is no basis. `fx-unavailable`: its closes cannot be expressed in the analytics currency (no FX rate at the window`s anchor - the FX route publishes no rate for the currency - or its listing currency is unknown). `no-data`: no price source serves a bar for the symbol in the window (an unknown or delisted symbol, or a failed read). Show the benchmark as refused, never as a flat line.',
                  )
                  .optional(),
                oneYearPercent: z
                  .coerce.number()
                  .describe(
                    'The benchmark`s change over the window the excess is measured on, percent.',
                  )
                  .nullable(),
                portfolioReturnPercent: z
                  .coerce.number()
                  .describe(
                    'The book`s TWR over the SAME window, percent - the excess`s book leg, so the excess is reproducible from what the strip prints (owner decision D83). NOT the 1Y above: it ends on the benchmark`s last close and opens on the first funded close.',
                  )
                  .nullable(),
                windowFrom: z
                  .string()
                  .describe(
                    "YYYY-MM-DD - where both legs open: the 1Y anchor, or a younger book`s first funded close, so a young book`s window is printed as its dates, never as '1 Year' (D83).",
                  )
                  .nullable(),
                windowTo: z
                  .string()
                  .describe(
                    'YYYY-MM-DD - the benchmark`s last close in the window, where both legs end.',
                  )
                  .nullable(),
                excessOneYearPp: z
                  .coerce.number()
                  .describe(
                    'Percentage points: the book`s time-weighted return over the benchmark`s own 1Y window minus the benchmark`s oneYearPercent, both from ONE bar (the book`s 1Y anchor, or its first bar holding positions after it). For a book younger than a year that bar is the close of its first funded day, so the book`s leg is NOT the book`s oneYearPercent above, which runs from its purchase prices.',
                  )
                  .nullable(),
              })
              .passthrough()
              .describe('Null when the owner selected no benchmark.')
              .nullable(),
          })
          .passthrough()
          .nullable(),
        drawdown: z
          .object({
            maxDrawdownPercent: z.coerce.number().describe('Negative, e.g. -18.4.'),
            peakDate: z.string().describe('YYYY-MM-DD'),
            troughDate: z.string().describe('YYYY-MM-DD'),
            recoveryDate: z
              .string()
              .describe('YYYY-MM-DD, or null while still underwater.')
              .nullable(),
          })
          .passthrough()
          .nullable(),
        monthlyReturns: z
          .array(
            z
              .object({
                month: z.string().describe('YYYY-MM'),
                returnPercent: z.coerce.number().nullable(),
              })
              .passthrough(),
          )
          .describe('Every calendar month since inception (the heatmap), unlike the legacy field.')
          .nullable(),
        returnStatistics: z
          .object({
            tradingDays: z
              .coerce.number()
              .int()
              .describe('How many daily returns the figures below are over.'),
            averageDailyReturnPercent: z.coerce.number().nullable(),
            bestDayPercent: z.coerce.number().nullable(),
            worstDayPercent: z.coerce.number().nullable(),
            positiveDaysPercent: z.coerce.number().nullable(),
            longestWinningStreakDays: z.coerce.number().int().nullable(),
          })
          .passthrough()
          .nullable(),
        riskMetrics: z
          .object({
            sharpeRatio: z.coerce.number().nullable(),
            sortinoRatio: z.coerce.number().nullable(),
            sharpeWithheldReason: z
              .enum(['NO_RISK_FREE_RATE', 'MODEL_ONLY', 'UNDER_MIN_SPAN', 'NO_VOLATILITY'])
              .describe(
                'Why sharpeRatio and sortinoRatio are null, or null when measured: NO_RISK_FREE_RATE (the measuring currency has no risk-free series, or none over the window - only PLN and USD carry one), MODEL_ONLY (every holding valued by a model), UNDER_MIN_SPAN (under 7 days), NO_VOLATILITY (fewer than two moving daily returns).',
              )
              .nullable(),
            currency: z
              .string()
              .describe("The currency the risk figures are measured in (the risk model's)."),
            realizedVolatilityPercent: z
              .coerce.number()
              .describe(
                'Annualized REALIZED volatility since inception, percent: the sigma sharpeRatio and sortinoRatio divide by.',
              )
              .nullable(),
            modelVolatilityPercent: z
              .coerce.number()
              .describe(
                "Annualized MODEL volatility, percent: today's holdings over the last modelSessions sessions of their prices.",
              )
              .nullable(),
            modelSessions: z.coerce.number().describe('Sessions behind modelVolatilityPercent.'),
            maxDrawdownPercent: z.coerce.number().nullable(),
            beta: z.coerce.number().nullable(),
            correlation: z.coerce.number().nullable(),
            benchmarkSymbol: z
              .string()
              .describe('What beta and correlation are measured against.')
              .nullable(),
          })
          .passthrough()
          .nullable(),
      })
      .partial()
      .passthrough()
      .optional(),
    tradeActivity: z
      .object({
        from: z.string().describe('YYYY-MM-DD'),
        to: z.string().describe('YYYY-MM-DD'),
        days: z
          .array(z.object({ date: z.string(), count: z.coerce.number().int() }).passthrough())
          .describe('Only days with at least one trade.'),
        totalTrades: z.coerce.number().int(),
        activeDays: z.coerce.number().int(),
      })
      .passthrough()
      .nullish(),
    marketCapBuckets: z
      .array(
        z
          .object({
            bucket: z.enum(['mega', 'large', 'mid', 'small', 'micro', 'other', 'cash']),
            percent: z.coerce.number(),
            value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
          })
          .passthrough(),
      )
      .nullish(),
    dividendBuckets: z
      .array(
        z
          .object({
            bucket: z.enum(['none', 'upTo2', 'from2To4', 'over4', 'other', 'cash']),
            percent: z.coerce.number(),
            value: z.coerce.number().describe('Absent unless amounts are shown.').nullish(),
          })
          .passthrough(),
      )
      .nullish(),
    portfolioInfo: z
      .object({
        positions: z.coerce.number().int().nullable(),
        subscriberCount: z.coerce.number().int().nullable(),
        avgTradesPerMonth: z.coerce.number().nullable(),
        avgHoldDays: z.coerce.number().nullable(),
        lastActiveAt: z.string().describe('YYYY-MM-DD of the latest trade.').nullable(),
        startDate: z.string().describe('YYYY-MM-DD of the first transaction.').nullable(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
const RatioStatsCoverageDto = z
  .object({
    firstSampleDate: z.string().describe('ISO date of the earliest sample inside the window.'),
    lastSampleDate: z.string().describe('ISO date of the latest sample inside the window.'),
    sampleCount: z.coerce.number(),
    coveredYears: z.coerce.number().describe('Years the samples ACTUALLY span.'),
    claimedYears: z
      .coerce.number()
      .describe(
        "Years the window asked for. Below `coveredYears` it means the listing is younger than the window — a '5Y median' over 2 years of history, which the UI must disclose rather than imply.",
      ),
  })
  .passthrough();
const RatioStatsDto = z
  .object({
    avg: z.coerce.number(),
    median: z.coerce.number(),
    min: z.coerce.number(),
    max: z.coerce.number(),
    p25: z
      .coerce.number()
      .describe(
        '25th percentile, linear-interpolated (R-7). Optional only because payloads cached before percentiles were computed may lack it; the current service always emits it.',
      )
      .optional(),
    p75: z.coerce.number().describe('75th percentile (R-7).').optional(),
    coverage: RatioStatsCoverageDto.optional(),
  })
  .passthrough();
const EarningsScenarioInputsDto = z
  .object({
    epsGrowth: z.coerce.number().describe('Annual EPS growth, PERCENT.'),
    expectedPe: z.coerce.number().describe('The P/E the scenario exits at.'),
  })
  .passthrough();
const RevenueScenarioInputsDto = z
  .object({
    revenueGrowth: z.coerce.number().describe('Annual revenue growth, PERCENT.'),
    expectedPs: z.coerce.number().describe('The P/S the scenario exits at.'),
  })
  .passthrough();
const comparisonGetPeerComparison_Body = z.object({
  symbols: z
    .array(z.string().regex(/^[A-Z0-9 ]{1,12}(\.[A-Z0-9]{1,5}){0,2}$/))
    .min(1)
    .max(20)
    .describe(
      'Array of stock symbols to compare. Accepts the wire grammar BASE(.SHARE-CLASS)?(.EXCHANGE)? — so a US dual-class listing carries TWO dot segments (BRK.B.US), and Nordic share classes separate theirs with a space (NOVO B.CO).',
    ),
  metrics: z.array(z.string()).describe('Specific metrics to include in comparison').optional(),
  includeRankings: z
    .boolean()
    .describe('Include percentile rankings in response')
    .optional()
    .default(true),
  mode: z
    .enum(['industry', 'region', 'marketCap'])
    .describe(
      "How to group the peer set. 'industry' (the default) is the classic behaviour. 'region' and 'marketCap' additionally resolve each company's country / cap bucket so the client can group and label the columns.",
    )
    .optional()
    .default('industry'),
});
const MetricRankingDto = z
  .object({
    rank: z.coerce.number().describe('Rank position (1 = best); 0 when this company could not be ranked'),
    outOf: z.coerce.number().describe('How many companies in the group reported this metric'),
    percentile: z
      .coerce.number()
      .describe(
        "Percentile 0-100 — the share of the group scoring LOWER, ties counted half (SCORING_RULESET_V1.md:155-157, the same arithmetic as the F11 scoring kernel). Null when the group cannot support one: nobody reported the metric, this company did not, or the group is under the five-member floor. Never 0 for 'unranked' — 0 is a real percentile and would read as worst-in-group.",
      )
      .nullable(),
  })
  .passthrough();
const RankingCategoryDto = z
  .object({
    growth: MetricRankingDto.describe('Overall growth ranking'),
    profitability: MetricRankingDto.describe('Overall profitability ranking'),
    valuation: MetricRankingDto.describe('Overall valuation ranking'),
    overall: MetricRankingDto.describe('Composite score ranking'),
  })
  .passthrough();
const comparisonGetGroupRankings_Body = z.object({
  symbols: z
    .array(z.string().regex(/^[A-Z0-9 ]{1,12}(\.[A-Z0-9]{1,5}){0,2}$/))
    .min(2)
    .max(20)
    .describe(
      'The whole comparison group — the subject plus every discovered peer. Two symbols minimum: a single company has no group to be ranked within.',
    ),
});
const ComparisonGroupResponseDto = z
  .object({
    id: z.string().describe('Group id (uuid)'),
    userId: z.string().describe('Owner user id (uuid)'),
    name: z.string().describe('Group name, unique per user'),
    symbols: z.array(z.string()).describe('Ordered ticker list'),
    createdAt: z.string().datetime({ offset: true }).describe('Creation timestamp, ISO instant'),
    updatedAt: z
      .string()
      .datetime({ offset: true })
      .describe('Last modification timestamp, ISO instant'),
  })
  .passthrough();
const comparisonGroupsCreate_Body = z.object({
  name: z.string().min(1).max(80).describe('Group name, unique per user'),
  symbols: z
    .array(z.string())
    .min(1)
    .max(20)
    .describe('Ordered ticker list (uppercased and de-duplicated on the way in)'),
});
const comparisonGroupsUpdate_Body = z
  .object({
    name: z.string().min(1).max(80).describe('New group name (must stay unique per user)'),
    symbols: z.array(z.string()).min(1).max(20).describe('Replacement ordered ticker list'),
  })
  .partial();
const AiUsageResponseDto = z
  .object({
    used: z
      .coerce.number()
      .describe(
        'Paid generations this reader made since the start of the current UTC calendar month, across both legs',
      ),
    limit: z
      .coerce.number()
      .describe('The tier cap; null means unlimited. The window lives in the count, not here.')
      .nullable(),
    resetsAt: z
      .string()
      .datetime({ offset: true })
      .describe(
        'ISO instant the window ends and the count starts again - the next step of the billing month from the subscription anchor, or the first of next month (UTC) without one',
      ),
  })
  .passthrough();
const aiSummaryGenerateAnalysis_Body = z.object({
  eventId: z.string().max(128).describe('Transcript event id (SYMBOL_ID, e.g. AAPL_123)'),
  symbol: z.string().max(24).describe('Stock symbol the transcript belongs to'),
  language: z.enum(['en', 'pl', 'es', 'de']).describe("Output language (default 'en')").optional(),
  year: z
    .coerce.number()
    .int()
    .gte(1990)
    .lte(2100)
    .describe('Fiscal year (enables the QoQ second source)')
    .optional(),
  quarter: z
    .coerce.number()
    .int()
    .gte(1)
    .lte(4)
    .describe('Fiscal quarter 1-4 (enables the QoQ second source)')
    .optional(),
});
const DividendSafetyResponseDto = z
  .object({
    narrative: z
      .object({
        verdict: z.enum(['well-covered', 'adequate', 'stretched', 'at-risk']),
        headline: z.string(),
        narrative: z.string().describe('The safety paragraph rendered under the coverage bars'),
        factors: z.array(
          z
            .object({
              label: z.string(),
              text: z
                .string()
                .describe('One sentence on what this factor implies for the dividend'),
              sentiment: z.enum(['positive', 'negative', 'neutral']),
            })
            .passthrough(),
        ),
      })
      .passthrough()
      .describe('Null when nothing has been generated for this symbol/language yet')
      .nullish(),
    generatedAt: z.string().datetime({ offset: true }).nullish(),
    modelUsed: z.string().nullish(),
    cached: z.boolean().describe('True when served from the durable cache'),
  })
  .passthrough();
const aiSummaryGenerateDividendSafety_Body = z.object({
  symbol: z.string().describe('Stock symbol'),
  language: z.string().describe('Output language for free text').optional(),
});
const aiSummaryStreamTranslation_Body = z.object({
  id: z.string().describe('Chat ID from AI SDK').optional(),
  messages: z.array(z.unknown()).describe('AI SDK messages array').optional(),
  trigger: z.string().describe('Trigger type from AI SDK').optional(),
  resourceId: z.string().max(200).describe('Resource ID (e.g., article:uuid#hash)'),
  language: z.enum(['pl', 'es', 'de']).describe('Target language code'),
  fields: z
    .record(z.string().max(5000))
    .describe('Fields to translate (max 64 keys, max 5000 chars each)'),
});
const aiSummaryStreamDocumentTranslation_Body = z.object({
  id: z.string().describe('Chat ID from AI SDK').optional(),
  messages: z.array(z.unknown()).describe('AI SDK messages array').optional(),
  trigger: z.string().describe('Trigger type from AI SDK').optional(),
  resourceId: z.string().max(200).describe('Resource ID (e.g., ir-event:uuid#hash)'),
  language: z.enum(['pl', 'es', 'de']).describe('Target language code'),
  content: z.string().max(200000).describe('Markdown document to translate'),
});
const aiSummaryBatchTranslation_Body = z.object({
  items: z
    .array(
      z.object({
        resourceId: z.string().max(200).describe('Resource ID'),
        fields: z
          .record(z.string().max(5000))
          .describe('Fields to translate (max 64 keys, max 5000 chars each)'),
      }),
    )
    .max(200)
    .describe('Items to translate (max 200)'),
  language: z.enum(['pl', 'es', 'de']).describe('Target language code'),
});
const aiSummaryCheckTranslation_Body = z.object({
  resourceIds: z.array(z.string().max(200)).max(200).describe('Resource IDs to check (max 200)'),
  language: z.enum(['pl', 'es', 'de']).describe('Target language code'),
});
const CompactionSummaryDto = z
  .object({
    markdown: z
      .string()
      .describe(
        'Markdown summary of everything at or before the compaction boundary (facts, decisions, open tasks, key figures).',
      ),
    createdAt: z.string().describe('ISO timestamp of when this summary was generated.'),
  })
  .passthrough();
const chatbotStream_Body = z
  .object({
    id: z
      .string()
      .uuid()
      .describe('Chat id (thread key) — a client-minted UUID (chat-id.ts always emits one).'),
    message: z
      .object({})
      .partial()
      .passthrough()
      .describe(
        'THIN wire form: only the newest AI SDK UIMessage; the backend assembles the history from the thread store. Mutually exclusive with `messages` (this wins when both appear).',
      ),
    messages: z
      .array(z.unknown())
      .describe('LEGACY wire form: the full conversation, resent each turn.'),
    pageContext: z
      .object({ place: z.string(), bag: z.object({}).partial().passthrough() })
      .partial()
      .passthrough()
      .describe('Page context { place, bag } — a hint for defaults + situational awareness.'),
    model: z
      .string()
      .describe('Model id from the allowlist (falls back to the configured default).'),
    trigger: z.string(),
    messageId: z
      .string()
      .describe(
        'Id of the message being (re)generated — sent by the AI SDK on regenerate and on the tool-approval/tool-result resubmit. Accepted but unused (the backend is stateless).',
      ),
    clientNow: z
      .object({ date: z.string(), dateLong: z.string(), time: z.string() })
      .partial()
      .passthrough()
      .describe(
        "Current date/time from the user's browser (their timezone) for the model context.",
      ),
    userName: z
      .string()
      .describe("The user's display name from the client (preferred over the JWT-derived name)."),
    clientPage: z
      .object({ url: z.string(), title: z.string(), snapshot: z.string() })
      .partial()
      .passthrough()
      .describe(
        'The page the user is on (their browser): url, title, and a text snapshot of the visible main content — so the model can answer questions about what is on screen.',
      ),
    disabledCapabilities: z
      .array(z.string())
      .max(8)
      .describe(
        'Copilot capability ids the user switched OFF for this conversation (the shared COPILOT_CAPABILITIES vocabulary). Absent → all capabilities on. Unknown ids are ignored.',
      ),
    mode: z
      .enum(['chat', 'deep_research'])
      .describe(
        "Run mode: 'chat' (default, fast) or 'deep_research' (a longer, fact-checked deep dive that produces a downloadable report). Absent → chat.",
      ),
  })
  .partial();
const CopilotSettingsDto = z
  .object({
    instructions: z
      .string()
      .describe('Global copilot instructions applied to every conversation; null = none.')
      .nullable(),
    memoryEnabled: z
      .boolean()
      .describe(
        'Whether the copilot may keep durable memories. false ⇒ the record_memory tool is absent AND no memory block is injected.',
      ),
  })
  .passthrough();
const copilotSettingsUpdateSettings_Body = z
  .object({
    instructions: z
      .string()
      .max(2048)
      .describe('Global copilot instructions (max 2048 chars); null clears them.')
      .nullable(),
    memoryEnabled: z.boolean().describe('Turn durable memory on or off.'),
  })
  .partial();
const BoardDocResponseDto = z
  .object({
    boardId: z.string().describe('Composer-authored board slug.'),
    title: z.string().describe("The document's title."),
    version: z.coerce.number().describe('The version this document is (1..n).'),
    doc: z
      .object({})
      .partial()
      .passthrough()
      .describe(
        'The reduced BoardDoc (shared-types `applyBoardOps` output: { boardId, title, blocks }) stored verbatim for faithful restore.',
      ),
  })
  .passthrough();
const SearchEngine = z
  .object({
    id: z.string(),
    name: z.string(),
    baseUrl: z.string(),
    hasApiKey: z.boolean().describe('The key itself is never returned'),
    settings: z.union([
      z
        .object({
          kind: z.literal('fourget'),
          options: z
            .object({
              scraper: z.string().min(1).max(40).describe("4get's scraper, e.g. ddg, brave"),
              web: z.boolean(),
              news: z.boolean(),
            })
            .passthrough(),
        })
        .passthrough(),
      z
        .object({
          kind: z.literal('searxng'),
          options: z.object({ web: z.boolean(), news: z.boolean() }).passthrough(),
        })
        .passthrough(),
      z
        .object({
          kind: z.literal('serpapi'),
          options: z
            .object({
              webEngine: z.string().max(60).describe('e.g. google; empty = not used for web'),
              newsEngine: z
                .string()
                .max(60)
                .describe('e.g. google_news; empty = not used for news'),
            })
            .passthrough(),
        })
        .passthrough(),
    ]),
    modes: z
      .array(z.enum(['web', 'news']))
      .describe('The modes it serves, read from settings here so no client restates the rule'),
    timeoutMs: z.coerce.number(),
    maxTries: z.coerce.number(),
    enabled: z.boolean(),
    position: z.coerce.number(),
    restingUntil: z
      .string()
      .datetime({ offset: true })
      .describe('Set while the engine rests after an error')
      .nullable(),
    lastOkAt: z.string().datetime({ offset: true }).nullable(),
    lastErrorAt: z.string().datetime({ offset: true }).nullable(),
    lastError: z.string().nullable(),
    stats: z
      .object({
        tried: z.coerce.number(),
        answered: z.coerce.number(),
        empty: z.coerce.number(),
        failed: z.coerce.number(),
        avgMs: z.coerce.number().nullable(),
      })
      .passthrough()
      .describe('The last 7 days of copilot searches'),
  })
  .passthrough();
const SearchEngines = z.object({ engines: z.array(SearchEngine) }).passthrough();
const CreateSearchEngineDto = z.object({
  name: z.string().min(1).max(60),
  baseUrl: z
    .string()
    .max(500)
    .url()
    .describe("The engine's address; a private address is fine (only admins set it)"),
  apiKey: z
    .string()
    .max(500)
    .describe('Write-only. Empty or absent keeps the stored key.')
    .optional(),
  settings: z.union([
    z.object({
      kind: z.literal('fourget'),
      options: z.object({
        scraper: z.string().min(1).max(40).describe("4get's scraper, e.g. ddg, brave"),
        web: z.boolean(),
        news: z.boolean(),
      }),
    }),
    z.object({
      kind: z.literal('searxng'),
      options: z.object({ web: z.boolean(), news: z.boolean() }),
    }),
    z.object({
      kind: z.literal('serpapi'),
      options: z.object({
        webEngine: z.string().max(60).describe('e.g. google; empty = not used for web'),
        newsEngine: z.string().max(60).describe('e.g. google_news; empty = not used for news'),
      }),
    }),
  ]),
  timeoutMs: z.coerce.number().int().gte(1000).lte(60000),
  maxTries: z
    .coerce.number()
    .int()
    .gte(1)
    .lte(5)
    .describe('Asked up to this many times before the search moves to the next engine'),
  enabled: z.boolean(),
});
const ReorderSearchEnginesDto = z.object({
  ids: z.array(z.string().uuid()).min(1).max(50).describe('Every engine, first first'),
});
const TestSearchEngineDto = z.object({
  id: z
    .string()
    .uuid()
    .describe('A saved engine: its stored key is used when apiKey is empty')
    .optional(),
  baseUrl: z
    .string()
    .max(500)
    .url()
    .describe("The engine's address; a private address is fine (only admins set it)"),
  apiKey: z
    .string()
    .max(500)
    .describe('Write-only. Empty or absent keeps the stored key.')
    .optional(),
  settings: z.union([
    z.object({
      kind: z.literal('fourget'),
      options: z.object({
        scraper: z.string().min(1).max(40).describe("4get's scraper, e.g. ddg, brave"),
        web: z.boolean(),
        news: z.boolean(),
      }),
    }),
    z.object({
      kind: z.literal('searxng'),
      options: z.object({ web: z.boolean(), news: z.boolean() }),
    }),
    z.object({
      kind: z.literal('serpapi'),
      options: z.object({
        webEngine: z.string().max(60).describe('e.g. google; empty = not used for web'),
        newsEngine: z.string().max(60).describe('e.g. google_news; empty = not used for news'),
      }),
    }),
  ]),
  timeoutMs: z.coerce.number().int().gte(1000).lte(60000),
});
const SearchEngineTest = z
  .object({
    modes: z.array(
      z
        .object({
          mode: z.enum(['web', 'news']),
          ok: z.boolean(),
          resultCount: z.coerce.number(),
          datedCount: z.coerce.number(),
          durationMs: z.coerce.number(),
          error: z.string().nullable(),
          sample: z.string().describe('The first result title').nullable(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
const TrySearchDto = z.object({
  query: z.string().min(2).max(400),
  mode: z.enum(['web', 'news']),
  plan: z
    .enum(['chain', 'each'])
    .describe('chain = as the copilot searches; each = every engine, side by side'),
});
const TrySearchResult = z
  .object({
    mode: z.enum(['web', 'news']).describe('The mode searched; web when no engine serves news'),
    runs: z.array(
      z
        .object({
          engineId: z.string(),
          name: z.string(),
          kind: z.enum(['fourget', 'searxng', 'serpapi']),
          enabled: z.boolean(),
          status: z.enum(['answered', 'empty', 'failed', 'skipped', 'notAsked']),
          durationMs: z.coerce.number().describe('Every try and the pauses between them'),
          tries: z.coerce.number(),
          error: z.string().describe('The last failure, also when a later try answered').nullable(),
          results: z.array(
            z
              .object({
                title: z.string(),
                url: z.string(),
                snippet: z.string().optional(),
                publishedAt: z.string().optional(),
                source: z.string().optional(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
    ),
    copilot: z
      .object({})
      .partial()
      .passthrough()
      .describe('What web_search hands the copilot (chain only)')
      .nullable(),
  })
  .passthrough();
const RecentSearches = z
  .object({
    total: z.coerce.number(),
    items: z.array(
      z
        .object({
          searchId: z.string(),
          at: z.string().datetime({ offset: true }),
          query: z.string(),
          mode: z.enum(['web', 'news']),
          outcome: z.enum(['answered', 'empty', 'failed']),
          durationMs: z.coerce.number(),
          attempts: z.array(
            z
              .object({
                engineId: z.string(),
                name: z.string(),
                outcome: z.enum(['answered', 'empty', 'failed']),
                resultCount: z.coerce.number(),
                datedCount: z.coerce.number(),
                durationMs: z.coerce.number(),
                tries: z.coerce.number(),
                error: z.string().nullable(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
    ),
  })
  .passthrough();
const UpdateSearchEngineDto = z
  .object({
    name: z.string().min(1).max(60),
    baseUrl: z
      .string()
      .max(500)
      .url()
      .describe("The engine's address; a private address is fine (only admins set it)"),
    apiKey: z.string().max(500).describe('Write-only. Empty or absent keeps the stored key.'),
    settings: z.union([
      z.object({
        kind: z.literal('fourget'),
        options: z.object({
          scraper: z.string().min(1).max(40).describe("4get's scraper, e.g. ddg, brave"),
          web: z.boolean(),
          news: z.boolean(),
        }),
      }),
      z.object({
        kind: z.literal('searxng'),
        options: z.object({ web: z.boolean(), news: z.boolean() }),
      }),
      z.object({
        kind: z.literal('serpapi'),
        options: z.object({
          webEngine: z.string().max(60).describe('e.g. google; empty = not used for web'),
          newsEngine: z.string().max(60).describe('e.g. google_news; empty = not used for news'),
        }),
      }),
    ]),
    timeoutMs: z.coerce.number().int().gte(1000).lte(60000),
    maxTries: z
      .coerce.number()
      .int()
      .gte(1)
      .lte(5)
      .describe('Asked up to this many times before the search moves to the next engine'),
    enabled: z.boolean(),
  })
  .partial();
const ConversationDetailDto = z
  .object({
    id: z.string().describe('Conversation id (the client Chat id).'),
    title: z.string().nullable(),
    messages: z
      .array(z.unknown())
      .describe('AI SDK UI messages (ChatbotUIMessage[]) stored verbatim for faithful restore.'),
    contextTokens: z
      .coerce.number()
      .describe(
        'Provider-true context token count after the latest turn (the context-meter seed on restore; 0 = never measured).',
      ),
    summary: CompactionSummaryDto.describe(
      'Compaction summary of the messages up to the boundary; null = never compacted.',
    ).nullable(),
    boundarySeq: z
      .coerce.number()
      .describe('Highest message seq the summary covers (0 = never compacted).'),
    boundaryMessageId: z
      .string()
      .describe(
        'Id of the LAST message the summary covers — the transcript renders the compaction marker after it. Null when never compacted.',
      )
      .nullable(),
    pinned: z
      .object({})
      .partial()
      .passthrough()
      .describe(
        'Pinned context for this thread — the plural-key ContextBag subset (symbols, portfolioIds, walletIds, watchlistIds) the user pinned. `{}` = nothing pinned. Drives the composer chips on restore.',
      ),
    instructions: z.string().describe('Thread-scoped user instructions; null = none.').nullable(),
    createdAt: z.string().describe('ISO creation timestamp.'),
    updatedAt: z.string().describe('ISO last-updated timestamp.'),
  })
  .passthrough();
const conversationsUpdateSettings_Body = z
  .object({
    pinned: z
      .object({})
      .partial()
      .passthrough()
      .describe(
        'Pinned context bag: { symbols?, portfolioIds?, walletIds?, watchlistIds? }. Max 20 items in TOTAL across the keys.',
      ),
    instructions: z
      .string()
      .max(2048)
      .describe('Thread instructions (max 2048 chars); null clears them.')
      .nullable(),
  })
  .partial();
const AdminUserDto = z
  .object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    role: z.string(),
    language: z.string().nullable(),
    isDemoUser: z.boolean(),
    onboardingCompleted: z.boolean(),
    lastLoginAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    walletsCount: z.coerce.number().nullable(),
    portfoliosCount: z.coerce.number().nullable(),
    bugReportCount: z
      .coerce.number()
      .describe("Bug reports this user filed through the in-product 'Report a bug' dialogs")
      .nullable(),
    openBugReportCount: z
      .coerce.number()
      .describe(
        'How many of those are still open. Which statuses count as open is decided in one place (the bug-report status lifecycle) and is deliberately not restated here.',
      )
      .nullable(),
    unrepliedBugReportCount: z
      .coerce.number()
      .describe(
        'How many of those nobody has answered yet (`replied_at IS NULL`) — closed reports included, because a report resolved without a reply is still a user who never heard back',
      )
      .nullable(),
    latestBugReportStatus: z
      .string()
      .describe("The triage status of the user's NEWEST report; null when they filed none")
      .nullable(),
    planTier: z
      .string()
      .describe("The tier of the user's LATEST subscription; null when they never subscribed")
      .nullable(),
    subscriptionStatus: z
      .string()
      .describe("That subscription's status (`active`, `canceled`, …); null when never subscribed")
      .nullable(),
    planTierOverride: z
      .enum(['BASIC', 'PREMIUM', 'ULTIMATE'])
      .describe(
        'An admin-GRANTED tier that outranks Stripe, or null when the user has none. This is what the user is actually entitled to whenever it is set — `planTier` above still describes their latest Stripe subscription, which a grant does not touch.',
      )
      .nullable(),
    referralCode: z.string().nullable(),
    onboardingStep: z.string().nullable(),
  })
  .passthrough();
const adminUpdateUserRole_Body = z.object({
  role: z.enum(['user', 'admin']).describe('The role to grant the user'),
});
const adminUpdateUserPlanOverride_Body = z.object({
  planTier: z
    .enum(['BASIC', 'PREMIUM', 'ULTIMATE'])
    .describe(
      'The tier to grant — it outranks the Stripe subscription for every entitlement check. Pass `null` to revoke the grant and hand the user back to whatever Stripe says.',
    )
    .nullable(),
});
const backfillTrigger_Body = z
  .object({
    tier: z.string().describe('recoverable | cn-adr | foreign'),
    symbols: z.array(z.string()).min(1).describe('Explicit symbols (overrides tier)'),
    max: z.coerce.number().int().gte(1).describe('Max filings to process (capped at 200)'),
    dryRun: z.boolean().describe('Estimate only — no LLM cost'),
    force: z.boolean().describe('Re-extract even already-present periods (fix-driven recovery)'),
  })
  .partial();
const backfillTriggerCampaign_Body = z.object({
  scope: z
    .enum(['missing-da', 'held', 'all-foreign-adr', 'ads-ratio', 'missing-da-dev', 'ads-ratio-dev'])
    .describe(
      'Enumeration scope. The `-dev` variants cap the canonical scope to a ~100-symbol ranked sample for a fast test.',
    ),
  force: z.boolean().describe('Re-extract even already-present periods (fix-driven)').optional(),
  maxPerSymbol: z
    .coerce.number()
    .int()
    .gte(1)
    .describe('Filings per symbol (capped at 200 by edge-wire)')
    .optional(),
  maxCostUsd: z.coerce.number().gte(0).describe('Hard spend ceiling in USD').optional(),
  dryRun: z.boolean().describe('Estimate only — no LLM cost, no campaign started').optional(),
});
const EarlyBirdStatusDto = z
  .object({
    cutoffAt: z
      .string()
      .datetime({ offset: true })
      .describe(
        'When the Early Bird cutoff was pressed; null until then. Write-once: it is never moved.',
      )
      .nullable(),
    qualifyingCount: z
      .coerce.number()
      .describe(
        'Distinct users who qualify right now: an active-set subscription first paid before the cutoff',
      ),
    awaitingSyncCount: z
      .coerce.number()
      .describe(
        "Active-set subscriptions with no purchase time yet (predate the column, not synced since). They cannot qualify until 'Sync from Stripe' reads their invoices.",
      ),
    portalConfigurationId: z
      .string()
      .describe(
        'The Stripe customer-portal configuration (bpc_…) Early Bird portal sessions run on; null = the default portal.',
      )
      .nullable(),
  })
  .passthrough();
const EarlyBirdPortalProductDto = z
  .object({
    product: z.string().describe('Stripe product id'),
    prices: z.array(z.string()).describe("Price ids listed under 'Switch plan'"),
  })
  .passthrough();
const EarlyBirdPortalSyncDto = z
  .object({
    configurationId: z.string().describe('The configuration that was written'),
    products: z.array(EarlyBirdPortalProductDto).describe('The list now on Stripe'),
  })
  .passthrough();
const billingCreateCheckout_Body = z.object({
  priceId: z.string().min(1).describe('Stripe Price ID for the plan'),
  currency: z
    .enum(['usd', 'eur', 'gbp', 'pln'])
    .describe(
      "Lowercase ISO currency for the Checkout Session; the Stripe Price must carry a matching currency_options entry. Ignored on the portal-upgrade path — an active subscription keeps its original currency. Omitted: the price's default currency.",
    )
    .optional(),
  successUrl: z
    .string()
    .url()
    .describe(
      'Absolute URL to return the user to after a successful checkout. Honored only when its origin is trusted (CORS allowlist); the server appends the session_id={CHECKOUT_SESSION_ID} placeholder. Falls back to the FRONTEND_URL default otherwise.',
    )
    .optional(),
  cancelUrl: z
    .string()
    .url()
    .describe(
      'Absolute URL to return the user to when checkout is canceled. Honored only when its origin is trusted (CORS allowlist); falls back to the FRONTEND_URL default otherwise.',
    )
    .optional(),
});
const billingCreateProduct_Body = z.object({
  planTier: z
    .enum(['BASIC', 'PREMIUM', 'ULTIMATE'])
    .describe('The entitlement plan this product belongs to (at most one product per plan)'),
  name: z.string().min(1).describe('Display name shown on the public plans page'),
  description: z.string().describe('Optional marketing description').optional(),
});
const billingUpdateProduct_Body = z.object({
  name: z.string().min(1).describe('Display name shown on the public plans page'),
  description: z.string().describe('Optional marketing description').optional(),
});
const billingAssignPrice_Body = z.object({
  stripePriceId: z
    .string()
    .min(1)
    .describe(
      'The Stripe price id to assign. The price is re-fetched server-side — no client-sent amount/currency/interval is ever trusted.',
    ),
  productId: z.string().uuid().describe('The billing product (our catalog) to assign the price to'),
  setAsCurrent: z
    .boolean()
    .describe('Immediately make it the current price of its (product, interval) slot'),
});
const billingUpdateEntitlement_Body = z.object({
  planTier: z.enum(['BASIC', 'PREMIUM', 'ULTIMATE']),
  featureKey: z.enum([
    'REALTIME_PRICES',
    'FUNDAMENTALS',
    'AI_NEWS_TICKERS',
    'IR_SUMMARIES_MONTHLY',
    'OWNERSHIP_DATA',
    'CONGRESSIONAL_TRADES',
    'BROKER_SYNC',
    'AI_STOCK_DAILY_SUMMARY',
    'AI_PEER_ANALYSIS',
    'AI_PORTFOLIO_INSIGHT',
    'EARLY_ACCESS',
    'PRIORITY_SUPPORT',
    'CSV_PDF_EXPORT',
    'TRACKED_TICKERS',
    'PORTFOLIO_COUNT',
    'CUSTOM_BENCHMARKS',
    'CUSTOM_PROJECTIONS',
    'ADVANCED_COMPARISON',
    'STOCK_DIVIDENDS',
    'INVESTOR_CALENDAR',
    'ANALYST_ESTIMATES',
    'PORTFOLIO_SUBSCRIPTIONS',
    'COPILOT',
  ]),
  isEnabled: z.boolean(),
  limitValue: z.coerce.number().int().nullable(),
});
const mergedPortfoliosCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Name for the merged portfolio'),
  description: z.string().describe('Description of the merged portfolio').optional(),
  portfolioIds: z
    .array(z.string().uuid())
    .min(1)
    .describe('Array of portfolio IDs to merge (minimum 1)'),
  currency: z
    .string()
    .max(3)
    .describe(
      'Preferred display currency for the merged portfolio (the create modal defaults to the largest member currency). Honored when it is a supported currency or one a member is held in; otherwise the shared member currency, or USD for a mixed set.',
    )
    .optional(),
  hideMembers: z
    .boolean()
    .describe(
      'Hide the member portfolios from the Investments grid while they are in this merge (default true).',
    )
    .optional()
    .default(true),
  lockMembers: z
    .boolean()
    .describe(
      'Refuse the member portfolios to any other merge while they are in this one (default true). False lets a member be merged again into another merge.',
    )
    .optional()
    .default(true),
  showCashAsPosition: z
    .boolean()
    .describe(
      "Treat the members' free cash as a position of the merged view (default false): a row in the holdings table, a slice on the allocation rings, and part of the value the merged return is measured on. Stored on the merge's own portfolio row (the view has ONE regime, never the members').",
    )
    .optional()
    .default(false),
});
const MergedPortfolioMemberDto = z
  .object({
    id: z.string().describe('Portfolio ID'),
    name: z.string().describe('Portfolio name'),
    currency: z.string().describe('Portfolio currency'),
    currentValue: z.coerce.number().describe('Portfolio current value'),
    convertedValue: z
      .coerce.number()
      .describe(
        "currentValue in the MERGE's currency at spot, the same conversion the merged total folds this member at, so the member rows add up to it; null when no usable rate converts the member currency (never a 1:1 figure under the merge currency).",
      )
      .nullable(),
    addedAt: z.string().datetime({ offset: true }).describe('Date added to merge'),
    fundingWalletIds: z
      .array(z.string())
      .describe(
        'Wallet ids that FUND this member — the same two sources the buy/sell router resolves a funding wallet from (the direct portfolios.wallet_id link, plus any wallet transaction tying a wallet to this portfolio). EMPTY for a manual portfolio that has never had a trade recorded against it, which is exactly when a buy on the merge cannot be routed by wallet and needs an explicit sourcePortfolioId (#662).',
      ),
  })
  .passthrough();
const MergedPortfolioMetricsDto = z
  .object({
    externalCapitalReturnPercent: z
      .coerce.number()
      .describe(
        "Return on EXTERNAL capital, percent. THE canonical return: one rule, one denominator, identical on every surface. READ IT WITH externalCapitalReturnBasisKind — 'inception' means inception-to-date, 'window' means relative to the requested timeframe's opening bar. Null when it is not defined for this book or this request — read externalCapitalSuppressedReason for which. A null is a gap, NEVER 0%.",
      )
      .nullable(),
    externalCapitalReturnAmount: z
      .coerce.number()
      .describe(
        "The return in currency. Under basisKind 'inception' it is total value less the external capital the book consumed; under 'window' it is the CHANGE in that quantity across the window. Divided by externalCapitalBasis it reproduces externalCapitalReturnPercent exactly in both regimes.",
      )
      .nullable(),
    externalCapitalBasis: z
      .coerce.number()
      .describe(
        "THE denominator. Under basisKind 'inception' the running peak of the smaller of the external and deployed capital legs (price-independent: a live price move changes the numerator only). Under 'window' the EQUITY standing at the window's opening bar, which is a materially different figure — do not label it 'invested capital' without checking the kind.",
      )
      .nullable(),
    externalCapitalBasisSource: z
      .enum(['externalFlows', 'deployedCash', 'tradeLedger', 'none'])
      .describe(
        "Which rung of the data-derived ladder produced the basis. 'none' IS the absence value, which is why this field is not nullable.",
      ),
    externalCapitalSuppressedReason: z
      .enum([
        'positionFilter',
        'noBasisYet',
        'noValueAtDate',
        'zeroBasis',
        'negativeValue',
        'dustBasis',
        'equityWipedOut',
        'microExternalBasis',
        'insufficientExternalHistory',
        'ledgerCannotFundHoldings',
        'neverDeployed',
        'cfdCapitalUnavailable',
        'fxRateUnavailable',
        'costFxRateUnavailable',
        'valueFxRateUnavailable',
        'noWindowOpenBasis',
        'noWindowOpenEquity',
        'capitalAddedInWindow',
        'windowEquityWipedOut',
        'syntheticPoint',
        'unavailable',
        'nonFiniteInput',
        'mergedFundingEntangled',
        'mergedFundingUndeclared',
        'mergedMemberRefused',
      ])
      .describe(
        "Why the return is absent. Null IFF externalCapitalReturnPercent is a number. 'positionFilter' — undefined for a filtered selection. 'noBasisYet'/'zeroBasis' — the book had no external capital at risk yet; render a gap. 'noValueAtDate' — no valuation on that date. 'negativeValue' — the portfolio is worth less than nothing, so a percentage-of-capital is not a measurement. 'dustBasis' — the capital at risk is a rounding residue (at or below 0.05), so a ratio over it is noise. 'equityWipedOut' — the loss exceeds the whole book. 'microExternalBasis' — the basis clears the dust floor but is a negligible fraction (below 5%) of the capital the book deployed, so it was funded by internal churn / already-withdrawn deposits and a return on external capital is not meaningfully computable (evaluated per point, on the externalFlows and deployedCash rungs). 'insufficientExternalHistory' — the book has no external-flow history (deployedCash rung) and its wallet cash balance exceeds the deployed basis, so the value is dominated by non-redeployed sell-proceeds cash and the return is an artefact of the missing deposit history; surface the 'please fill in your history' prompt. (When such a book is still majority-invested an honest cost-basis return is served instead — see externalCapitalReturnBasisKind 'costBasis' — and this reason is null.) 'cfdCapitalUnavailable' — the book has no denominator at all AND carries closed-CFD realized-P&L rows whose capital-at-risk is unknown (source statement not re-imported/backfilled yet), so the closes' unknown margin is the cause of the missing basis; a book with any stateable capital serves instead and reports cfdCapitalMissingCount. The remedy is re-importing the broker statement. 'fxRateUnavailable' — a wallet row has no exchange rate in force on its day (none stored for it or the 14 days before), so it is missing from the cash and the external capital alike; refused from that day on, externalCapitalUnpricedCashRows counts the rows. 'costFxRateUnavailable' — a cost row (a buy, an opened option, a delivery in) has no exchange rate in force on its day, so the cost basis and every basis built on it cannot be stated; refused from that day on, externalCapitalUnpricedCost names the pair and the day. 'valueFxRateUnavailable' — an open holding has no current exchange rate into the portfolio currency, so the book value and the return over it cannot be stated; unconvertedCurrencies names the currency (owner ruling D81). 'noWindowOpenBasis' — the requested window opens on a bar that carries no return. 'noWindowOpenEquity' — no equity was standing when the window opened. 'capitalAddedInWindow' — external capital entered the window AND the flow could not be time-weighted, so no denominator spans it. 'windowEquityWipedOut' — the book was wiped out inside the window. 'syntheticPoint' — a fabricated bar. 'unavailable' — an upstream input was missing. 'nonFiniteInput' — a return-determining input reached the boundary as a PRESENT non-finite value (NaN/Infinity), which cannot be told from an absent optional, so the point is refused rather than let either solver publish NaN or a fabricated number. 'mergedFundingEntangled' — MERGED views only: the members draw on the same external money (a shared funding wallet, or a transfer chain moving one deposit between them), so summing their bases would count that deposit once per member; view the portfolios separately for a return on each. 'ledgerCannotFundHoldings' — the recorded deposits were proven short of what the wallet spent AND the wallet balance is negative, so the holdings are funded by money the ledger never recorded and the ratio would be overstated; complete the deposit history. 'mergedFundingUndeclared' — MERGED views only: the funding partition could not inspect one or more members, so it can neither prove nor rule out that they share external money; the fold refuses rather than risk double-counting a deposit, and the remedy is the same as for mergedFundingEntangled (view the portfolios separately). 'mergedMemberRefused' — MERGED views only: at least one member refused its own return with a verdict about its own capital and the members do not share one reason, so no member diagnosis is true of the whole merge; open each portfolio on its own to see which one and why. 'neverDeployed' — era-17: a certified book whose deposits were NEVER put to work (the deployed peak never cleared the 0.05 dust floor): no capital at work means a return on deployed capital does not exist yet. A named abstention, never a percent and never the microExternalBasis heuristic; the earned amount (externalCapitalReturnAmount) still populates and surfaces show it beside the caption.",
      )
      .nullable(),
    externalCapitalReturnBasisKind: z
      .enum(['inception', 'window', 'costBasis'])
      .describe(
        "WHICH quantity the four fields above carry. 'inception' — inception-to-date, the engine's own quantity, with externalCapitalBasis the all-time peak. 'window' — relative to the requested timeframe's opening bar, with externalCapitalBasis the window's OPENING EQUITY. 'costBasis' — the honest cost-basis fallback served where external-capital history is missing but the book is still majority-invested: externalCapitalReturnPercent is a return on the peak OPEN-POSITIONS cost basis (externalCapitalBasis), and externalCapitalHistoryIncomplete is true. Not nullable: a refusal still answers one of the questions. NEVER re-apply a client-side rebase to fields marked 'window' or 'costBasis'.",
      ),
    externalCapitalPeakDeployed: z
      .coerce.number()
      .describe(
        "The running peak of the GROSS capital the book put to work (rawInvestedCapital before the internal-funding subtraction and the external cap) — the SCALE REFERENCE the 'microExternalBasis' rung divides the basis by, never a denominator of any served return. Present only as a finite number; absent when the point never reached the kernel or the producer predates the field. Consumed by the merged aggregate fold, which re-applies the micro rung to the summed pair before a microExternalBasis member may contribute.",
      )
      .optional(),
    externalCapitalDeployed: z
      .coerce.number()
      .describe(
        "EXTERNAL CAPITAL AT WORK at this point — max(0, min(net external capital, cost basis)): the money the user contributed from outside AND currently has invested. THE CAPITAL LINE a chart draws, and never a denominator of any served return. It falls when positions are sold, does not move when idle cash arrives, and a reinvested winner raises it only by what was actually paid in (the cost basis grows, the external cap does not). Distinct from externalCapitalBasis, which is this quantity's running PEAK over a differently-measured deployment leg — right as a denominator, and a ratchet that can never fall. Present only as a finite number; absent when either leg is unknown, and a reader then falls back rather than drawing a fabricated level.",
      )
      .optional(),
    externalCapitalHistoryIncomplete: z
      .boolean()
      .describe(
        "True when the book's recorded deposit history looks incomplete, so the served return rests on an understated basis. Two disjoint cases: (1) a 'costBasis' fallback point, where the external-capital return was refused for a book with NO deposits and an honest cost-basis return is served instead; (2) a normal return on a book with PARTIAL deposits, where recorded inflows fail to cover recorded outflows (the served number is unchanged, only the hint added). A surface shows a subtle 'history incomplete — complete your history' hint. Margin/CFD books are excluded. Absent/false everywhere else.",
      )
      .optional(),
    externalCapitalNegativeCash: z
      .object({
        from: z.string().describe('First day (YYYY-MM-DD) the wallet fell below zero.'),
        to: z
          .string()
          .describe('First day it was back at or above zero; null while it is still below today.')
          .nullable(),
        lowestBalance: z
          .coerce.number()
          .describe(
            'The lowest balance inside the stretch, in the currency these fields are served in.',
          ),
      })
      .passthrough()
      .describe(
        'The stretch the wallet spent below zero, which the served return counts as EXTERNAL capital (an overdraft is money from outside the recorded ledger: a forgotten deposit or a broker credit line). Only on a book without margin or CFDs whose wallet funds no other book; always beside externalCapitalHistoryIncomplete: true. A surface names the days and the lowest balance and suggests adding the missing deposit or marking the portfolio as margin. Absent everywhere else, and on every bar of a series but the last.',
      )
      .optional(),
    externalCapitalUnpricedCashRows: z
      .coerce.number()
      .int()
      .gt(0)
      .describe(
        "How many wallet rows have no exchange rate in force on their day, so they are missing from the cash and the external capital alike. Present only beside externalCapitalSuppressedReason 'fxRateUnavailable' on the terminal; absent everywhere else.",
      )
      .optional(),
    externalCapitalUnpricedCost: z
      .object({
        pair: z.string().describe("The currency pair no rate priced, FROM/TO (e.g. 'USD/PLN')."),
        date: z.string().describe('The day of the first cost row it left unpriced (YYYY-MM-DD).'),
      })
      .passthrough()
      .describe(
        "The first cost row no exchange rate in force could price: the pair and its day. The basis is refused from that day on (owner ruling D53); a manual rate on that trade clears it. Present on the terminal of a book with such a row, whichever refusal reason won (a wallet row with no rate refuses 'fxRateUnavailable' first); absent everywhere else.",
      )
      .optional(),
    cfdCapitalMissingCount: z
      .coerce.number()
      .describe(
        "Σ over the members of summaryMetricsSchema.cfdCapitalMissingCount, each scoped like its basis (0 for a member on a funding wallet shared with another portfolio, whose return takes no close's P&L): how many closed-CFD cash rows across the merge carry no capital-at-risk data and leave the merged basis short. Non-zero beside a served return means the merged basis is understated by those closes' margin; 0 means it is not, never that no close lacks capital data.",
      ),
    totalValue: z
      .coerce.number()
      .describe(
        'What the merge is worth right now: the members’ open positions PLUS their settled wallet cash (de-duplicated by wallet), converted to the merge currency. The cash leg is folded UNCONDITIONALLY — showCashAsPosition governs COMPOSITION (the holdings cash row, the allocation rings, the return basis), never this total. For positions alone read positionValue.',
      ),
    positionValue: z
      .coerce.number()
      .describe(
        "Market value of the members’ open positions alone, in the merge currency. EXCLUDES the settled wallet cash that totalValue folds in, so this — never totalValue — is the leg a merged return is measured on. Every producer serves it; ABSENT only on a persisted merged-compute entry written before it shipped (7-day serveStale window), where a consumer treats it as 'the split is unknown' — never as zero cash.",
      )
      .optional(),
    totalInvested: z
      .coerce.number()
      .describe(
        'Total invested across the covered members. A member whose cost basis is undefined is left out (unavailablePortfolioIds, unavailablePortfolioReasons). Null (unknown, never 0) when none is covered, or when the aggregate basis cannot be computed because a cross-currency external flow had no exchange rate; currentCapitalGain and unrealizedGainLoss measured on it are then withheld too.',
      )
      .nullable(),
    currentCapitalGain: z
      .coerce.number()
      .describe(
        'The degraded pair beside a merge-level canonical refusal: positionValue less totalInvested, the gain on the external capital currently at work (the per-funding-group aggregate invested, a shared deposit counted once). Measured off positionValue, NOT totalValue: totalInvested is min-capped to exclude idle cash, so a cash-inclusive numerator would put a deposit in the return. The same identity /portfolios/total-metrics serves the account band, from the same producer.',
      )
      .nullish(),
    currentCapitalReturnPercent: z
      .coerce.number()
      .describe(
        'currentCapitalGain / totalInvested x 100. Null when totalInvested is not positive — a gap, NEVER 0%.',
      )
      .nullish(),
    totalReturn: z.coerce.number().describe('Total return percentage').optional(),
    unrealizedGainLoss: z.coerce.number().describe('Unrealized gain/loss').optional(),
    realizedGainLoss: z
      .coerce.number()
      .describe(
        "Realized gain/loss of the members whose realized converted (D34); a member whose realized was refused is left out and named in unavailablePortfolioIds with 'realizedFxRateUnavailable'. Null when every member was refused.",
      )
      .nullish(),
    dayChange: z.coerce.number().describe('Day change amount').optional(),
    dayChangePercent: z.coerce.number().describe('Day change percentage').optional(),
    currencyImpactAmount: z
      .coerce.number()
      .describe(
        "Σ over the members of summaryMetricsSchema.currencyImpactAmount: the currency's part of the merged externalCapitalReturnAmount, in the merge currency — each member valued in the merge's currency, so a member held in it contributes its own holdings' foreign moves. Null when any member cannot state its own, or when the merged return is refused.",
      )
      .nullish(),
    income: z.coerce.number().describe('Total income (dividends + interest)').optional(),
    positionCount: z
      .coerce.number()
      .describe(
        'Number of open positions across all member portfolios (flat sum, not deduped by symbol)',
      )
      .optional(),
    externalCapitalEntangledPortfolioIds: z
      .array(z.string())
      .describe(
        "Present ONLY when externalCapitalSuppressedReason is 'mergedFundingEntangled': the member portfolio ids the funding partition proved to share money (a shared cash wallet or a transfer between their wallets). The health panel lists exactly these books; a member not in this list is funded independently and is not the reason the return is withheld.",
      )
      .optional(),
    unconvertedCurrencies: z
      .array(z.string())
      .describe(
        'Currencies excluded from the totals on this object because no usable exchange rate into its currency was available: the totals are PARTIAL and must be shown as such. Absent when nothing was excluded.',
      )
      .optional(),
    unavailablePortfolioIds: z
      .array(z.string())
      .describe(
        'Books that could not be computed on this read and are NOT measured in its totals (their money is at most a stale stored figure). Absent when every book answered.',
      )
      .optional(),
    unavailablePortfolioReasons: z
      .record(z.enum(['costFxRateUnavailable', 'incoherentHistory', 'realizedFxRateUnavailable']))
      .describe(
        "Why a book in unavailablePortfolioIds is left out, when it answered but its cost basis is undefined: 'costFxRateUnavailable' (a trade has no exchange rate for its day, D53) or 'incoherentHistory' (its history sells more than it bought). Such a book is left out of every figure measured on a cost basis (Invested, the degraded gain and percent, the return); the value totals (totalValue, positionValue) still include it. 'realizedFxRateUnavailable' (a realization has no exchange rate for its own day, D34): the book is left out of the realized total ONLY, which is null when no book is left. A book absent from this map threw on this read and is in no total.",
      )
      .optional(),
  })
  .passthrough();
const MergedPortfolioResponseDto = z
  .object({
    id: z.string().describe('Merged portfolio unique identifier'),
    userId: z.string().describe('User ID who owns this merged portfolio'),
    name: z.string().describe('Merged portfolio name'),
    description: z.string().describe('Merged portfolio description').nullish(),
    currency: z.string().describe('Currency the merge is shown in (editable at any time)'),
    hideMembers: z
      .boolean()
      .describe('Members are hidden from the Investments grid while in this merge'),
    lockMembers: z.boolean().describe('Members are refused by any other merge while in this one'),
    showCashAsPosition: z
      .boolean()
      .describe(
        "The members' free cash is a position of this merged view (the merge's own switch — a row, a slice and part of the return basis)",
      ),
    memberCount: z.coerce.number().describe('Number of member portfolios'),
    members: z.array(MergedPortfolioMemberDto).describe('Member portfolios'),
    metrics: MergedPortfolioMetricsDto.describe('Aggregated metrics').optional(),
    createdAt: z.string().datetime({ offset: true }).describe('Creation timestamp'),
    updatedAt: z.string().datetime({ offset: true }).describe('Last update timestamp'),
  })
  .passthrough();
const mergedPortfoliosUpdate_Body = z
  .object({
    name: z.string().max(100).describe('New name for the merged portfolio'),
    description: z.string().describe('New description for the merged portfolio'),
    currency: z
      .string()
      .min(3)
      .max(3)
      .regex(/^[A-Z]{3}$/)
      .describe(
        'New display currency: any supported currency, or one a member is held in. The members keep their own books, so it can change at any time.',
      ),
    hideMembers: z.boolean().describe('Hide the member portfolios from the Investments grid'),
    lockMembers: z.boolean().describe('Refuse the member portfolios to any other merge'),
    showCashAsPosition: z
      .boolean()
      .describe("Treat the members' free cash as a position of the merged view (see create)"),
  })
  .partial();
const PortfolioMoverDto = z
  .object({
    symbol: z.string().describe('Stock ticker symbol'),
    name: z.string().describe('Company name').optional(),
    direction: z.enum(['up', 'down']).describe('Direction of movement'),
    changePercent: z.coerce.number().describe('Percentage change'),
    reason: z.string().describe('Brief reason for the move'),
  })
  .passthrough();
const PortfolioInsightDto = z
  .object({
    id: z.string().uuid().describe('Insight ID'),
    portfolioId: z.string().uuid().describe('Portfolio ID'),
    tradingDay: z.string().describe('Trading day for this insight'),
    period: z.enum(['today', '1W', '1M']).describe('Time period for this insight'),
    briefSummary: z
      .string()
      .describe('Brief 2-3 sentence summary explaining the portfolio movement'),
    movers: z.array(PortfolioMoverDto).describe('Top movers in the portfolio'),
    detailedAnalysis: z
      .string()
      .describe("Detailed analysis (populated when 'Read more' is clicked)")
      .optional(),
    dayChangePercent: z.coerce.number().describe('Portfolio day change percentage'),
    createdAt: z.string().datetime({ offset: true }).describe('When this insight was created'),
    updatedAt: z.string().datetime({ offset: true }).describe('When this insight was last updated'),
    isStale: z.boolean().describe('Whether this insight is stale (>30 min old)'),
  })
  .passthrough();
const RiskAiAnalysisDto = z
  .object({
    narrative: z
      .object({
        headline: z.string().describe('2-3 sentences for the verdict hero.'),
        takeaways: z.array(z.string()).describe('2-4 short observations, most important first.'),
      })
      .passthrough()
      .describe(
        'PROSE ONLY - every figure it mentions is one the risk model serves. null = nothing has been generated for the portfolio as it stands NOW (the cache key follows the figures, so a narrative written for an older risk picture is not served).',
      )
      .nullable(),
    generatedAt: z.string().datetime({ offset: true }).nullable(),
    model: z.string().describe('The language model that wrote it.').nullable(),
  })
  .passthrough();
const portfolioCoreGenerateRiskAiAnalysis_Body = z
  .object({
    window: z
      .enum(['1M', '3M', '6M', '1Y', '3Y'])
      .describe(
        'Risk window the narrative describes (default 1Y) - the same one as the risk model.',
      )
      .default('1Y'),
    language: z
      .enum(['en', 'pl', 'es', 'de'])
      .describe('Language the narrative is written in (default en).')
      .default('en'),
    displayCurrency: z
      .string()
      .min(3)
      .max(3)
      .regex(/^[A-Z]{3}$/)
      .describe(
        'Currency the risk model is read in - pass the one given to the risk-model route. Defaults to the portfolio currency.',
      ),
    benchmark: z
      .string()
      .min(1)
      .max(32)
      .describe(
        'Benchmark symbol the risk model is measured against - pass the one given to the risk-model route. Omit for the automatic choice.',
      ),
  })
  .partial();
const chartingPresetsCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Preset name'),
  snapshot: z
    .record(z.string().max(50000).nullable())
    .describe('Snapshot of localStorage key-value pairs (allowlisted keys)'),
});
const chartingPresetsUpdate_Body = z.object({
  snapshot: z
    .record(z.string().max(50000).nullable())
    .describe('Snapshot of localStorage key-value pairs (allowlisted keys)'),
  name: z.string().min(1).max(100).describe('Preset name').optional(),
});
const metricPresetsCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Set name'),
  snapshot: z
    .object({
      metrics: z
        .array(z.string().min(1).max(100))
        .min(1)
        .max(12)
        .describe('The Overview tiles in order: built-in tile keys or metric-catalog series ids'),
      layout: z
        .array(
          z.object({
            i: z.string().min(1).describe('Category id'),
            w: z.union([z.literal(1), z.literal(2)]).describe('Column span (1 or 2)'),
            h: z.union([z.literal(1), z.literal(2)]).describe('Row span (1 or 2)'),
          }),
        )
        .max(32)
        .describe('Dashboard layout (widget positions)')
        .optional(),
    })
    .describe('The tiles of the set'),
});
const metricPresetsUpdate_Body = z
  .object({
    name: z.string().min(1).max(100).describe('Set name'),
    snapshot: z
      .object({
        metrics: z
          .array(z.string().min(1).max(100))
          .min(1)
          .max(12)
          .describe('The Overview tiles in order: built-in tile keys or metric-catalog series ids'),
        layout: z
          .array(
            z.object({
              i: z.string().min(1).describe('Category id'),
              w: z.union([z.literal(1), z.literal(2)]).describe('Column span (1 or 2)'),
              h: z.union([z.literal(1), z.literal(2)]).describe('Row span (1 or 2)'),
            }),
          )
          .max(32)
          .describe('Dashboard layout (widget positions)')
          .optional(),
      })
      .describe('Replaces the tiles whole'),
  })
  .partial();
const ScreenerPresetFiltersDto = z
  .object({
    minPoints: z
      .coerce.number()
      .gte(0)
      .lte(100)
      .describe('Minimum Stoxly Points (0-100). Absent means unfiltered.'),
    minPercentile: z
      .coerce.number()
      .gte(0)
      .lte(100)
      .describe('Minimum universe percentile (0-100). Absent means unfiltered.'),
    minFvDiscountPct: z
      .coerce.number()
      .gte(-1000)
      .lte(100)
      .describe(
        "Minimum discount to fair value in percent. Negative values are a real screen ('include names trading above FV'), hence the -1000 floor.",
      ),
    minOverview: z.coerce.number().gte(0).lte(15).describe('Minimum Overview tab sum (0-15).'),
    minDividends: z.coerce.number().gte(0).lte(10).describe('Minimum Dividends tab sum (0-10).'),
    minGrowth: z.coerce.number().gte(0).lte(12).describe('Minimum Growth tab sum (0-12).'),
    minSolidity: z.coerce.number().gte(0).lte(12).describe('Minimum Solidity tab sum (0-12).'),
    industries: z
      .array(z.string())
      .max(50)
      .describe('Industry names to keep. Empty or absent means every industry.'),
    countries: z
      .array(z.string())
      .max(50)
      .describe('Country names to keep. Empty or absent means every country.'),
    capBuckets: z
      .array(z.string())
      .max(50)
      .describe('Market-cap buckets to keep (mega, large, mid, small, micro).'),
    payingOnly: z.boolean().describe('Keep only dividend payers.'),
    sortKey: z
      .string()
      .describe(
        'Sort column (symbol, points, overview, dividends, growth, solidity, percentile, fvDiscount, marketCap, pe, dividendYield). Not enum-constrained here: the screener kernel re-validates on load and falls back to its default, so a key retired by a later ruleset leaves the preset loadable.',
      ),
    sortDir: z
      .string()
      .describe("Sort direction ('asc' or 'desc'); anything else falls back on load."),
  })
  .partial()
  .passthrough();
const ScreenerPresetDto = z
  .object({
    id: z.string().describe('Preset id (uuid)'),
    name: z.string().max(100).describe('Preset name, unique per user'),
    filters: ScreenerPresetFiltersDto.describe('The saved filter set'),
    createdAt: z.string().datetime({ offset: true }).describe('Creation timestamp, ISO instant'),
    updatedAt: z
      .string()
      .datetime({ offset: true })
      .describe('Last modification timestamp, ISO instant'),
  })
  .passthrough();
const screenerPresetsCreate_Body = z.object({
  name: z.string().min(1).max(100).describe('Preset name, unique per user'),
  filters: ScreenerPresetFiltersDto.describe('The screener filter set to save'),
});
const SavedNewsFilterDto = z
  .object({
    minActionability: z.coerce.number().gte(0).lte(5).nullable(),
    actionabilityLabels: z.array(z.string()).nullable(),
    articleTypes: z.array(z.string()).nullable(),
  })
  .partial()
  .passthrough();
const AlertRuleParamsDto = z
  .object({
    direction: z
      .enum(['above', 'below'])
      .describe('price_threshold, fair_value — which side of the level fires.'),
    price: z.coerce.number().gte(0).describe('price_threshold — the absolute level.'),
    thresholdPct: z
      .coerce.number()
      .gte(0)
      .lte(100)
      .describe('fair_value — distance from fair value in percent, as a magnitude.'),
    daysBefore: z
      .coerce.number()
      .int()
      .gte(0)
      .lte(365)
      .describe('ex_dividend, calendar_event — how many days of notice.'),
    side: z.enum(['buy', 'sell', 'any']).describe('insider — which side.'),
    minValueUsd: z
      .coerce.number()
      .gte(0)
      .describe('insider — minimum reported value; null means no minimum.')
      .nullable(),
    eventTypes: z
      .array(z.string())
      .describe(
        'calendar_event (earnings|dividend) and ir_event (press-release|filing|transcript). A type outside the producing set is REJECTED, not dropped — it would be a rule that can never match. NULL or omitted means every type, for ir_event.',
      )
      .nullable(),
    filter: SavedNewsFilterDto.describe('news_filter — the saved facets.'),
  })
  .partial()
  .passthrough();
const alertsCreate_Body = z.object({
  type: z.enum([
    'price_threshold',
    'fair_value',
    'ex_dividend',
    'insider',
    'calendar_event',
    'news_filter',
    'ir_event',
  ]),
  symbol: z
    .string()
    .max(32)
    .describe(
      'Required by every type except news_filter, which is the ONE feed-wide type: it is matched on ingest, so a rule with no symbol has a real stream to see. A polled type with no symbol would have no universe to poll and could never fire, so it is rejected.',
    )
    .nullish(),
  params: AlertRuleParamsDto,
  cooldownMinutes: z.coerce.number().int().gte(0).lte(43200).optional(),
});
const alertsUpdate_Body = z
  .object({
    symbol: z.string().max(32).nullable(),
    params: AlertRuleParamsDto,
    isActive: z.boolean(),
    cooldownMinutes: z.coerce.number().int().gte(0).lte(43200),
  })
  .partial();
const CampaignWithStatsDto = z
  .object({
    id: z.string(),
    code: z.string().describe('URL-safe campaign code'),
    name: z.string().describe('Display name'),
    isActive: z.boolean(),
    tag: z.string().describe('Grouping tag; null = untagged').nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    registrations: z.coerce.number().int().describe('Attributed signups'),
    conversions: z.coerce.number().int().describe('Latest subscription in a conversion status'),
    churned: z.coerce.number().int().describe('Latest subscription canceled'),
    retained: z.coerce.number().int().describe('Converted AND attributed at least `retentionDays` ago'),
    pointsEarned: z
      .coerce.number()
      .int()
      .describe(
        'POINTS (not users): 1 per 3 months of PAID time, summed over every attributed user — can exceed registrations, and a refund can lower it',
      ),
    pointsStalled: z.coerce.number().int().describe('USERS churned while carrying banked months'),
    pointsPending: z
      .coerce.number()
      .int()
      .describe('USERS still subscribed, each accruing their next point'),
    conversionRate: z.coerce.number().describe('conversions / registrations; 0 when no registrations'),
    conversionRateLow: z
      .coerce.number()
      .describe('Wilson 95% lower bound of the conversion rate — ranks small samples honestly'),
    avgDurationDays: z.coerce.number().int().describe('Mean attribution age in days'),
    estMrrCents: z
      .coerce.number()
      .int()
      .describe('Estimated MRR in minor units from currently-active subs'),
  })
  .passthrough();
const campaignsCreate_Body = z.object({
  code: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/)
    .describe('URL-safe campaign code'),
  name: z.string().min(1).max(255).describe('Display name'),
  tag: z.string().min(1).max(40).describe('Grouping tag').optional(),
});
const CampaignDto = z
  .object({
    id: z.string(),
    code: z.string().describe('URL-safe campaign code'),
    name: z.string().describe('Display name'),
    isActive: z.boolean(),
    tag: z.string().describe('Grouping tag; null = untagged').nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    deletedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .passthrough();
const CampaignStatsDto = z
  .object({
    registrations: z.coerce.number().int().describe('Attributed signups'),
    conversions: z.coerce.number().int().describe('Latest subscription in a conversion status'),
    churned: z.coerce.number().int().describe('Latest subscription canceled'),
    retained: z.coerce.number().int().describe('Converted AND attributed at least `retentionDays` ago'),
    pointsEarned: z
      .coerce.number()
      .int()
      .describe(
        'POINTS (not users): 1 per 3 months of PAID time, summed over every attributed user — can exceed registrations, and a refund can lower it',
      ),
    pointsStalled: z.coerce.number().int().describe('USERS churned while carrying banked months'),
    pointsPending: z
      .coerce.number()
      .int()
      .describe('USERS still subscribed, each accruing their next point'),
    conversionRate: z.coerce.number().describe('conversions / registrations; 0 when no registrations'),
    conversionRateLow: z
      .coerce.number()
      .describe('Wilson 95% lower bound of the conversion rate — ranks small samples honestly'),
    avgDurationDays: z.coerce.number().int().describe('Mean attribution age in days'),
    estMrrCents: z
      .coerce.number()
      .int()
      .describe('Estimated MRR in minor units from currently-active subs'),
  })
  .passthrough();
const CampaignChartPointDto = z
  .object({
    date: z.string().describe('Bucket start, YYYY-MM-DD'),
    registrations: z.coerce.number().int(),
    conversions: z.coerce.number().int(),
    churned: z.coerce.number().int(),
    pointsEarned: z
      .coerce.number()
      .int()
      .describe('Points earned by this cohort — can exceed registrations'),
  })
  .passthrough();
const CampaignPointsSeriesPointDto = z
  .object({
    bucket: z.string().describe('Bucket start, YYYY-MM-DD'),
    earned: z
      .coerce.number()
      .int()
      .describe('Points earned in this bucket; null for a future bucket')
      .nullable(),
    lost: z
      .coerce.number()
      .int()
      .describe('Points taken back by refunds in this bucket; null for a future bucket')
      .nullable(),
    total: z
      .coerce.number()
      .int()
      .describe('Running total at the end of this bucket; null for a future bucket')
      .nullable(),
    scheduled: z
      .coerce.number()
      .int()
      .describe(
        'Points the renewals due in this bucket would add if every subscriber pays; null for a past bucket',
      )
      .nullable(),
    scheduledTotal: z
      .coerce.number()
      .int()
      .describe(
        "Running total after those renewals, counted from today's total; null for a past bucket",
      )
      .nullable(),
  })
  .passthrough();
const CampaignPlanMixEntryDto = z
  .object({ planTier: z.string(), count: z.coerce.number().int(), estMrrCents: z.coerce.number().int() })
  .passthrough();
const campaignsExportHistory_Body = z
  .object({
    from: z.string().describe('First UTC day to export, YYYY-MM-DD'),
    to: z.string().describe('Last UTC day to export, YYYY-MM-DD'),
  })
  .partial();
const CampaignExportQueuedDto = z
  .object({
    queued: z.coerce.number().int().describe('Daily export jobs queued'),
    from: z.string().describe('First day queued, YYYY-MM-DD'),
    to: z.string().describe('Last day queued, YYYY-MM-DD'),
  })
  .passthrough();
const campaignsUpdate_Body = z
  .object({
    name: z.string().max(255).describe('Display name'),
    isActive: z.boolean().describe('Active status'),
    tag: z.string().min(1).max(40).describe('Grouping tag').nullable(),
  })
  .partial();
const CampaignReferredUserDto = z
  .object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    registeredAt: z.string().datetime({ offset: true }),
    attributedAt: z.string().datetime({ offset: true }),
    status: z.enum(['registered', 'subscribed', 'churned']),
    planTier: z.string().nullable(),
    durationDays: z.coerce.number().int().describe('Days since attribution'),
    pointState: z
      .enum(['earned', 'pending', 'stalled'])
      .describe('Badge flavour for `points`; null = never subscribed')
      .nullable(),
    points: z.coerce.number().int().describe('Points earned; a refund can lower it').nullable(),
    paidMonths: z
      .coerce.number()
      .int()
      .describe(
        'Whole months of PAID time — the union of the paid invoice windows across all subscriptions',
      )
      .nullable(),
    refundedMonths: z.coerce.number().int().describe('Whole months cut from refunded invoices').nullable(),
    pointProgressMonths: z
      .coerce.number()
      .int()
      .describe('Months banked toward the next point')
      .nullable(),
  })
  .passthrough();
const CampaignUsersResponseDto = z
  .object({
    users: z.array(CampaignReferredUserDto),
    total: z.coerce.number().int(),
    page: z.coerce.number().int(),
    limit: z.coerce.number().int(),
  })
  .passthrough();
const bugReportsCreate_Body = z
  .object({
    description: z.string(),
    email: z.string(),
    source: z.string(),
    context: z.string(),
    includeImportedFiles: z.string(),
    kind: z.string(),
  })
  .partial();
const ReporterBugReportDto = z
  .object({
    number: z.coerce.number(),
    createdAt: z.string().datetime({ offset: true }),
    statusChangedAt: z.string().datetime({ offset: true }).nullable(),
    reporterStatus: z.enum(['in_progress', 'needs_info', 'fixed', 'closed']),
    kind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .nullish(),
    description: z.string(),
    mailedAnswerAt: z
      .string()
      .datetime({ offset: true })
      .describe(
        'Set when the answer went out by mail before the panel existed, so the thread cannot show it. The thread prints one line saying so, dated with this instant. Optional for the same deploy-skew reason as the kind field: absent must degrade to no line, not no page.',
      )
      .nullish(),
    shippedIn: z.string().nullable(),
    unreadCount: z.coerce.number(),
    lastActivityAt: z
      .string()
      .datetime({ offset: true })
      .describe('The newest of a published message, a status change, or the report itself'),
    lastSignal: z
      .object({
        signal: z.enum(['resolved', 'still_open']),
        at: z.string().datetime({ offset: true }),
      })
      .passthrough()
      .nullable(),
    signals: z
      .object({
        allowed: z.array(z.enum(['resolved', 'still_open'])),
        availableAt: z
          .string()
          .datetime({ offset: true })
          .describe(
            "When 'still occurs' returns after its per-plan cooldown; null when it is not cooling. 'No longer occurs' never cools and is offered once per report.",
          )
          .nullable(),
        bumpedAt: z
          .string()
          .datetime({ offset: true })
          .describe(
            "The reporter's last 'still occurs'. A wait they caused is shown as a dead button with the date; a wait that is simply too early hides the button instead.",
          )
          .nullable(),
        blocked: z
          .enum(['status', 'window_closed'])
          .describe(
            'Why `allowed` is empty: `status` = this state takes no signal, `window_closed` = more than 14 days since the fix, open a new report',
          )
          .nullable(),
      })
      .passthrough(),
  })
  .passthrough();
const ReporterMessageDto = z
  .object({
    id: z.string(),
    sentAt: z.string().datetime({ offset: true }),
    sender: z.enum(['admin', 'user', 'system']),
    body: z.string().describe('Empty on a signal message'),
    signal: z.enum(['resolved', 'still_open']).nullable(),
    statusChange: z
      .enum(['in_progress', 'needs_info', 'fixed', 'closed'])
      .describe('On a system line: the state the report moved to, never the raw triage status')
      .nullable(),
    readAt: z.string().datetime({ offset: true }).nullable(),
    attachments: z
      .array(
        z
          .object({
            id: z.string(),
            originalFilename: z.string(),
            contentType: z.string().nullable(),
            fileSize: z.coerce.number().describe('Bytes'),
          })
          .passthrough(),
      )
      .describe('Files sent WITH this message'),
  })
  .passthrough();
const bugReportsSignal_Body = z.object({
  signal: z.enum(['resolved', 'still_open']),
  body: z.string().max(5000).optional(),
});
const BugReportReporterIssuesDto = z
  .object({
    total: z.coerce.number().describe('Reports this person has filed, ever'),
    open: z.coerce.number().describe('Still open, per the bug-report status lifecycle'),
    unreplied: z
      .coerce.number()
      .describe('Never answered (`replied_at IS NULL`), closed reports included'),
    latestStatus: z.string().describe("Their newest report's status"),
  })
  .passthrough();
const BugReportLastSignalDto = z
  .object({ signal: z.enum(['resolved', 'still_open']), at: z.string().datetime({ offset: true }) })
  .passthrough();
const BugReportListItemDto = z
  .object({
    id: z.string(),
    userId: z.string().nullable(),
    email: z
      .string()
      .describe('Reply-to address the reporter gave, falling back to their account email')
      .nullable(),
    userEmail: z.string().describe('The reporter account email').nullable(),
    userName: z.string().nullable(),
    source: z.enum(['onboarding', 'sidebar', 'healthPanel', 'support']),
    status: z.enum([
      'new',
      'investigating',
      'needs_info',
      'queued',
      'in_progress',
      'fixed_pending_release',
      'resolved',
      'wont_fix',
      'invalid',
      'obsolete',
    ]),
    description: z.string().describe('The reproduction steps the user wrote'),
    adminNote: z.string().nullable(),
    category: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('Triage classification, or null when nobody has classified it yet')
      .nullable(),
    replyMessage: z.string().describe('The reply drafted for the reporter').nullable(),
    repliedAt: z
      .string()
      .datetime({ offset: true })
      .describe('When we replied; null means the reporter is still owed one')
      .nullable(),
    attachmentCount: z.coerce.number(),
    reporterIssues: BugReportReporterIssuesDto.describe(
      "The reporter's OTHER outstanding work — null when the account is gone, absent when served by a backend older than the field. Tallied over the person, not this row, so triage can see it is looking at one of someone's four open reports. Same query the users list's Issues column reads, so the two pages cannot state different counts for the same person.",
    ).nullish(),
    reporterPlanTier: z
      .string()
      .describe(
        'The tier the reporter is ACTUALLY served — an admin grant, else their granting subscription, else BASIC. Null when the account is gone, absent when served by a backend older than the field.',
      )
      .nullish(),
    number: z.coerce.number().describe('The reporter-facing number (#N)').optional(),
    shippedIn: z
      .string()
      .describe('The release that shipped the fix; null until a reply batch stamps it')
      .nullish(),
    reportedKind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('The kind the REPORTER picked when filing - a claim, not triage (`category`)')
      .nullish(),
    issueUrl: z
      .string()
      .describe(
        'The GitHub issue or PR this report is worked in, or null. Internal - never served to the reporter.',
      )
      .nullish(),
    kind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe(
        'THE kind: `category` when triage has classified it, else what the reporter called it. One label, the admin overrides it, and the reporter is served this same value.',
      )
      .nullish(),
    statusChangedAt: z.string().datetime({ offset: true }).nullish(),
    lastSender: z
      .enum(['admin', 'user', 'system'])
      .describe('Sender of the latest published message; `user` means the report needs a reply')
      .nullish(),
    lastSignal: BugReportLastSignalDto.describe(
      'The latest reporter signal; survives a later system message',
    ).nullish(),
    reporterActivityAt: z
      .string()
      .datetime({ offset: true })
      .describe(
        "The newest of: filed, the reporter's last message, their last 'still occurs'. Our own messages, status lines and 'no longer occurs' do not count. The default order.",
      )
      .optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const BugReplyBatchStateDto = z
  .object({
    id: z.string(),
    stagedAt: z.string().datetime({ offset: true }).nullable(),
    appliedAt: z
      .string()
      .datetime({ offset: true })
      .describe('Null until a production container start has published this batch')
      .nullable(),
    entries: z
      .array(
        z
          .object({
            number: z.coerce.number(),
            outcome: z.enum([
              'drafted',
              'draft_kept',
              'no_message',
              'unknown_number',
              'failed',
              'applied',
            ]),
          })
          .passthrough(),
      )
      .describe('Per-entry verdict: an applied batch can still carry a `failed` entry')
      .optional(),
  })
  .passthrough();
const bugReportsAdminStageBatch_Body = z.object({
  id: z.string().regex(/^\d{4}-\d{2}-\d{2}-[a-z0-9-]+$/),
  entries: z
    .array(
      z.object({
        number: z.coerce.number().int().gt(0).lte(2147483647),
        status: z.enum(['resolved', 'wont_fix', 'invalid', 'obsolete']).optional(),
        language: z.enum(['en', 'pl', 'es', 'de']).optional(),
        adminNote: z.string().min(1).optional(),
        message: z.string().min(1).max(10000).optional(),
      }),
    )
    .min(1),
});
const StagedBugReplyBatchDto = z
  .object({
    id: z.string().describe('The batch id, which is also its file name'),
    stagedAt: z.string().datetime({ offset: true }).describe('When this batch was FIRST staged'),
    entries: z.array(
      z
        .object({
          number: z.coerce.number().describe('The reporter-facing report number this entry named'),
          outcome: z
            .enum(['drafted', 'draft_kept', 'no_message', 'unknown_number', 'failed'])
            .describe(
              '`drafted`: a draft was created. `draft_kept`: one was already there and its text was kept, edits included. `no_message`: the entry answers nothing, so there is nothing to draft. `unknown_number`: no report carries that number. `failed`: this entry threw and the rest of the batch still ran.',
            ),
        })
        .passthrough(),
    ),
    closingWithoutWork: z
      .array(z.coerce.number())
      .describe(
        'Report numbers this batch would CLOSE as fixed while nothing is linked to them - no issue, no PR. A warning for the human staging it, never a refusal: a report can be fixed in passing. It is raised HERE because staging is the last moment a person is looking, and the apply runs unattended on a container start.',
      )
      .optional(),
  })
  .passthrough();
const UpdateBugReportDraftDto = z.object({
  body: z.string().min(1).max(10000).describe('The corrected reply text'),
});
const BugReportMessageDto = z
  .object({
    id: z.string(),
    sentAt: z.string().datetime({ offset: true }),
    subject: z.string().nullable(),
    body: z.string().describe('Empty on a signal message: the signal is the content'),
    language: z.string().describe('The locale this message was written in'),
    templateStatus: z
      .string()
      .describe('Status template that seeded the text; null when typed from scratch')
      .nullable(),
    templateCategory: z
      .string()
      .describe('Category the template was picked for; null when typed from scratch')
      .nullable(),
    edited: z
      .boolean()
      .describe('Whether the admin changed the seeded text before sending; null when not a mail')
      .nullable(),
    sentBy: z
      .string()
      .describe('The admin who sent it; null once their account is gone, or for an automated send')
      .nullable(),
    sender: z.enum(['admin', 'user', 'system']).optional(),
    publishedAt: z
      .string()
      .datetime({ offset: true })
      .describe('Null = a draft the reporter cannot see')
      .nullish(),
    readAt: z.string().datetime({ offset: true }).describe('When the reporter opened it').nullish(),
    signal: z.enum(['resolved', 'still_open']).nullish(),
    batchId: z
      .string()
      .describe('The reply batch that wrote this message; null for anything an admin typed')
      .nullish(),
    channel: z
      .enum(['mail', 'in_app'])
      .describe(
        'How an admin message reached the reporter: mailed by hand, or posted in the app. Null on a user or system message.',
      )
      .nullish(),
  })
  .passthrough();
const BugReportDetailDto = z
  .object({
    id: z.string(),
    userId: z.string().nullable(),
    email: z
      .string()
      .describe('Reply-to address the reporter gave, falling back to their account email')
      .nullable(),
    userEmail: z.string().describe('The reporter account email').nullable(),
    userName: z.string().nullable(),
    source: z.enum(['onboarding', 'sidebar', 'healthPanel', 'support']),
    status: z.enum([
      'new',
      'investigating',
      'needs_info',
      'queued',
      'in_progress',
      'fixed_pending_release',
      'resolved',
      'wont_fix',
      'invalid',
      'obsolete',
    ]),
    description: z.string().describe('The reproduction steps the user wrote'),
    adminNote: z.string().nullable(),
    category: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('Triage classification, or null when nobody has classified it yet')
      .nullable(),
    replyMessage: z.string().describe('The reply drafted for the reporter').nullable(),
    repliedAt: z
      .string()
      .datetime({ offset: true })
      .describe('When we replied; null means the reporter is still owed one')
      .nullable(),
    attachmentCount: z.coerce.number(),
    reporterIssues: BugReportReporterIssuesDto.describe(
      "The reporter's OTHER outstanding work — null when the account is gone, absent when served by a backend older than the field. Tallied over the person, not this row, so triage can see it is looking at one of someone's four open reports. Same query the users list's Issues column reads, so the two pages cannot state different counts for the same person.",
    ).nullish(),
    reporterPlanTier: z
      .string()
      .describe(
        'The tier the reporter is ACTUALLY served — an admin grant, else their granting subscription, else BASIC. Null when the account is gone, absent when served by a backend older than the field.',
      )
      .nullish(),
    number: z.coerce.number().describe('The reporter-facing number (#N)').optional(),
    shippedIn: z
      .string()
      .describe('The release that shipped the fix; null until a reply batch stamps it')
      .nullish(),
    reportedKind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('The kind the REPORTER picked when filing - a claim, not triage (`category`)')
      .nullish(),
    issueUrl: z
      .string()
      .describe(
        'The GitHub issue or PR this report is worked in, or null. Internal - never served to the reporter.',
      )
      .nullish(),
    kind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe(
        'THE kind: `category` when triage has classified it, else what the reporter called it. One label, the admin overrides it, and the reporter is served this same value.',
      )
      .nullish(),
    statusChangedAt: z.string().datetime({ offset: true }).nullish(),
    lastSender: z
      .enum(['admin', 'user', 'system'])
      .describe('Sender of the latest published message; `user` means the report needs a reply')
      .nullish(),
    lastSignal: BugReportLastSignalDto.describe(
      'The latest reporter signal; survives a later system message',
    ).nullish(),
    reporterActivityAt: z
      .string()
      .datetime({ offset: true })
      .describe(
        "The newest of: filed, the reporter's last message, their last 'still occurs'. Our own messages, status lines and 'no longer occurs' do not count. The default order.",
      )
      .optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    context: z
      .object({})
      .partial()
      .passthrough()
      .describe('Client + server diagnostics captured when the report was filed')
      .nullable(),
    attachments: z.array(
      z
        .object({
          id: z.string(),
          origin: z
            .enum(['upload', 'import_reference'])
            .describe(
              '`upload` = dropped in the dialog; `import_reference` = a broker statement the user had already uploaded, linked without copying bytes',
            ),
          originalFilename: z.string(),
          contentType: z.string().nullable(),
          fileSize: z.coerce.number(),
          downloadable: z
            .boolean()
            .describe('False when the object was never stored (S3 disabled or PUT failed)'),
          createdAt: z.string().datetime({ offset: true }),
        })
        .passthrough(),
    ),
    messages: z
      .array(BugReportMessageDto)
      .describe(
        'Messages actually sent to the reporter, newest first. Absent when served by a backend older than the field; empty when nobody has written yet.',
      )
      .optional(),
    quietStatusChange: z
      .literal(true)
      .describe('Present when the PATCH accepts `announceStatus`')
      .optional(),
  })
  .passthrough();
const UpdateBugReportDto = z
  .object({
    status: z
      .enum([
        'new',
        'investigating',
        'needs_info',
        'queued',
        'in_progress',
        'fixed_pending_release',
        'resolved',
        'wont_fix',
        'invalid',
        'obsolete',
      ])
      .describe(
        'New triage status. Goes through the one status write, which posts the reporter a thread message + notification for the working statuses. `needs_info` requires `text`.',
      ),
    text: z
      .string()
      .min(1)
      .max(10000)
      .describe(
        'The question posted to the reporter with `needs_info` (required there, 400 without). Ignored by statuses that post a fixed line or nothing.',
      ),
    announceStatus: z
      .boolean()
      .describe(
        '`false`: move the status WITHOUT its thread line and notification, because a message sent with this save carries the news. Absent or `true`: announce as usual. Never suppresses the needs_info question.',
      ),
    adminNote: z.string().max(5000).describe('Internal triage note — never shown to the reporter'),
    issueUrl: z
      .string()
      .max(500)
      .describe(
        'The GitHub issue or PR this report is worked in. Validated as an issue/PR URL - a branch name or a bare number is a 400. `null` unlinks it. Internal: the reporter learns about the work from the STATUS and its message, never from this.',
      )
      .nullable(),
    category: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('What kind of report this is. `null` puts it back to unclassified.')
      .nullable(),
    replyMessage: z
      .string()
      .max(10000)
      .describe('The reply drafted for the reporter. Distinct from the internal `adminNote`.')
      .nullable(),
    replied: z
      .boolean()
      .describe(
        'Mark the report replied or un-mark it. `true` stamps `repliedAt` on the TRANSITION only — re-sending it on a later save leaves the original instant alone, because the Replied column sorts by it to answer who has been waiting longest. Independent of `replyMessage`: a reply sent from a mail client leaves no draft here, and a draft written today may not go out until tomorrow.',
      ),
  })
  .partial();
const CreateBugReportMessageDto = z.object({
  subject: z.string().max(200).describe('Subject line as sent; may be empty for an in-app message'),
  body: z.string().min(1).max(10000).describe('Message body as sent, verbatim'),
  language: z.enum(['en', 'pl', 'es', 'de']).describe('The locale the message was written in'),
  templateStatus: z
    .enum([
      'new',
      'investigating',
      'needs_info',
      'queued',
      'in_progress',
      'fixed_pending_release',
      'resolved',
      'wont_fix',
      'invalid',
      'obsolete',
    ])
    .describe('Status template that seeded the text; omit or null when typed from scratch')
    .nullish(),
  templateCategory: z
    .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
    .describe('Category the template was picked for; omit or null when typed from scratch')
    .nullish(),
  edited: z
    .boolean()
    .describe(
      'Whether the admin changed the seeded text before sending. Decided by the SENDER, not derived later: the template that seeded it is free to change afterwards.',
    ),
  channel: z
    .enum(['mail', 'in_app'])
    .describe(
      'Mailed by hand (`mail`) or posted in the app (`in_app`). ABSENT MEANS `mail`: the panel omits it for a mail so a server older than this field (which 400s an unknown key) keeps recording, and `in_app` is the only value it ever sends.',
    )
    .optional(),
});
const bugReportsAgentPendingRelease_Body = z.object({
  issueUrls: z
    .array(z.string().max(500))
    .min(1)
    .max(200)
    .describe('The merged pull request and the issues it closes'),
  mergedPr: z
    .string()
    .max(500)
    .describe('The pull request whose merge this is, recorded on every report it moves')
    .optional(),
});
const AgentPendingReleaseResultDto = z
  .object({
    moved: z
      .array(z.coerce.number())
      .describe('The report numbers now waiting for the release; empty when nothing matched'),
  })
  .passthrough();
const AgentBugReportDto = z
  .object({
    number: z.coerce.number(),
    status: z.enum([
      'new',
      'investigating',
      'needs_info',
      'queued',
      'in_progress',
      'fixed_pending_release',
      'resolved',
      'wont_fix',
      'invalid',
      'obsolete',
    ]),
    kind: z
      .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
      .describe('The admin category when triaged, else what the reporter called it')
      .nullable(),
    description: z.string().describe('What the reporter wrote when filing'),
    createdAt: z.string().datetime({ offset: true }),
    statusChangedAt: z.string().datetime({ offset: true }).nullable(),
    adminNote: z.string().describe('Internal triage notes, including earlier agents').nullable(),
    issueUrl: z.string().describe('The issue or PR this report is being worked in').nullable(),
    attachmentCount: z.coerce.number(),
    messages: z
      .array(
        z
          .object({
            sentAt: z.string().datetime({ offset: true }),
            sender: z.enum(['admin', 'user', 'system']),
            body: z.string(),
            signal: z
              .string()
              .describe('The button the reporter pressed, if this row is one')
              .nullable(),
          })
          .passthrough(),
      )
      .describe('The published conversation, oldest first'),
  })
  .passthrough();
const bugReportsAgentUpdate_Body = z.object({
  status: z.enum([
    'investigating',
    'queued',
    'in_progress',
    'needs_info',
    'invalid',
    'wont_fix',
    'obsolete',
  ]),
  note: z
    .string()
    .min(1)
    .max(5000)
    .describe('Internal note, appended under the admin note')
    .optional(),
  text: z
    .string()
    .min(1)
    .max(10000)
    .describe('The question for the reporter; required with needs_info')
    .optional(),
  branch: z
    .string()
    .min(1)
    .max(200)
    .describe('The branch the agent is working on, recorded in the note')
    .optional(),
  issueUrl: z
    .string()
    .max(500)
    .describe(
      "The GitHub issue or PR opened for this report. Set it in the SAME call that moves the status: the two are one act - 'somebody is working on this, and here is the work'.",
    )
    .nullish(),
  expectCreatedAt: z
    .string()
    .datetime({ offset: true })
    .describe(
      "WHAT THE CALLER BELIEVES IT IS MOVING: the filing instant it read for this number, from GET /agent/bug-reports/{number}. A mismatch is a 409 and nothing is written. Report numbers are a per-database sequence, so an agent that triaged one database and writes to another moves a stranger's report and rings them. REQUIRED: while it was optional the whole protection was a docblock asking to be obeyed, and a caller that never read the report was refused by nothing.",
    ),
});
const BroadcastDto = z
  .object({
    id: z.string(),
    name: z.string(),
    audience: z.enum(['all', 'free', 'paid']),
    sender: z
      .object({
        name: z.string(),
        email: z.string(),
        replyTo: z.string().describe('null = replies go to the sender address').nullable(),
      })
      .passthrough()
      .describe('null = sent from the server default')
      .nullable(),
    templateId: z.string().describe('null = the default template').nullable(),
    status: z.enum(['draft', 'scheduled', 'sending', 'sent', 'partly_sent', 'failed']),
    content: z.record(
      z
        .object({
          subject: z.string(),
          previewText: z.string(),
          body: z.string(),
          aiDraft: z
            .boolean()
            .describe('Machine-translated and not yet edited or test-sent by a person'),
          sourceHash: z.string().describe('The English hash it was translated from').nullable(),
        })
        .passthrough(),
    ),
    languages: z
      .array(
        z
          .object({
            language: z.enum(['en', 'pl', 'es', 'de']),
            hasText: z.boolean(),
            aiDraft: z.boolean(),
            stale: z.boolean().describe('Translated from an English text that has changed since'),
            tested: z.boolean().describe('A test email went out with exactly the current text'),
          })
          .passthrough(),
      )
      .describe('Every language, English first'),
    englishHash: z.string().describe('null while English has no text').nullable(),
    versions: z
      .array(
        z
          .object({
            language: z.enum(['en', 'pl', 'es', 'de']),
            readerLanguages: z.array(z.enum(['en', 'pl', 'es', 'de'])),
            recipients: z.coerce.number().int(),
            step: z.enum([
              'pending',
              'segment_created',
              'contacts_imported',
              'broadcast_created',
              'sent',
            ]),
            error: z.string().nullable(),
            sentAt: z.string().datetime({ offset: true }).nullable(),
          })
          .passthrough(),
      )
      .describe('Filled when the broadcast is sent'),
    recipients: z
      .coerce.number()
      .int()
      .describe('Summed over the sent versions; null before a send')
      .nullable(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    sentAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const CreateBroadcastDto = z.object({
  name: z.string().min(1).max(200).describe('Admin-only name'),
  audience: z.enum(['all', 'free', 'paid']),
});
const BroadcastAudienceCountsDto = z
  .object({
    counts: z.record(z.record(z.coerce.number().int())),
    restrictedTo: z
      .array(z.string())
      .describe('The recipient allowlist when one is set: nobody else can receive a broadcast')
      .nullable(),
    sendingBlocked: z.string().describe('Why no broadcast can be sent right now').nullable(),
  })
  .passthrough();
const BroadcastSendersDto = z
  .object({
    defaultSender: z
      .object({
        name: z.string(),
        email: z.string(),
        replyTo: z.string().describe('null = replies go to the sender address').nullable(),
      })
      .passthrough()
      .describe('Who a broadcast with no sender picked goes out from'),
    recent: z
      .array(
        z
          .object({
            name: z.string(),
            email: z.string(),
            replyTo: z.string().describe('null = replies go to the sender address').nullable(),
            lastUsedAt: z.string().datetime({ offset: true }),
          })
          .passthrough(),
      )
      .describe('Senders of earlier broadcasts, most recent first'),
    domains: z
      .array(z.string())
      .describe('Domains Resend may send from; empty when Resend could not be asked'),
  })
  .passthrough();
const BroadcastSettingsDto = z
  .object({
    translationModel: z.string().describe("The id of the model 'Translate from English' uses"),
    defaultTemplateId: z.string().describe('The template new broadcasts start with'),
    footer: z
      .record(z.object({ why: z.string(), unsubscribe: z.string() }).passthrough())
      .describe('The footer lines in use per language (saved, else the built-in copy)'),
    defaultTranslationModel: z.string(),
    translationAvailable: z.boolean().describe('Translation has an API key on this server'),
    models: z
      .array(z.object({ id: z.string(), name: z.string() }).passthrough())
      .describe('The models translation can use, by id and name'),
  })
  .passthrough();
const UpdateBroadcastSettingsDto = z
  .object({
    translationModel: z
      .string()
      .min(1)
      .max(100)
      .describe('One of the models GET /admin/broadcasts/settings lists'),
    defaultTemplateId: z.string().uuid().describe('The template new broadcasts start with'),
    footer: z
      .record(z.object({ why: z.string().max(300), unsubscribe: z.string().max(300) }))
      .describe('The two footer lines per language; an empty line falls back to English'),
  })
  .partial();
const PreviewBroadcastDto = z.object({
  language: z.enum(['en', 'pl', 'es', 'de']),
  subject: z.string().max(200),
  previewText: z.string().max(300),
  body: z.string().max(50000).describe('Markdown, plus `[button: Label](https://…)` lines'),
  templateId: z
    .string()
    .uuid()
    .describe('Render with this template; omitted = the default')
    .nullish(),
  templateHtml: z
    .string()
    .max(100000)
    .describe('Render with this unsaved template HTML (the template editor)')
    .optional(),
});
const BroadcastPreviewDto = z.object({ html: z.string() }).passthrough();
const BroadcastImageDto = z
  .object({ url: z.string().describe('Where readers load the image from; it never expires') })
  .passthrough();
const UpdateBroadcastDto = z
  .object({
    name: z.string().min(1).max(200),
    templateId: z.string().uuid().describe('The email template; null = the default one').nullable(),
    sender: z
      .object({
        name: z.string().min(1).max(100),
        email: z.string().max(200).email().describe('On a domain verified in Resend'),
        replyTo: z
          .string()
          .max(200)
          .email()
          .describe('Where replies go, any domain; null or omitted = the sender address')
          .nullish(),
      })
      .describe('Who it is sent from; null = the default, Stoxly <no-reply@stoxly.ai>')
      .nullable(),
    audience: z.enum(['all', 'free', 'paid']),
    content: z
      .record(
        z
          .object({
            subject: z.string().max(200),
            previewText: z.string().max(300),
            body: z
              .string()
              .max(50000)
              .describe('Markdown, plus `[button: Label](https://…)` lines'),
            sourceHash: z
              .string()
              .max(64)
              .describe(
                'The English hash this version is current with; send it to mark a translation up to date',
              )
              .nullish(),
          })
          .nullable(),
      )
      .describe('Per language: the new text, or null to remove that language'),
  })
  .partial();
const BroadcastSentHtmlDto = z.object({ html: z.string() }).passthrough();
const TestBroadcastDto = z.object({
  to: z.array(z.string().email()).min(1).max(10),
  languages: z
    .array(z.enum(['en', 'pl', 'es', 'de']))
    .min(1)
    .max(4),
});
const TranslateBroadcastDto = z.object({ language: z.enum(['en', 'pl', 'es', 'de']) });
const SendBroadcastDto = z
  .object({
    scheduledAt: z
      .string()
      .datetime({ offset: true })
      .describe('Omit or null to send now; a future instant to schedule')
      .nullable(),
  })
  .partial();
const BroadcastTemplatesDto = z
  .object({
    placeholders: z.array(
      z.object({ key: z.string(), required: z.boolean(), note: z.string() }).passthrough(),
    ),
    templates: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          html: z.string(),
          isDefault: z.boolean(),
          isBuiltIn: z.boolean().describe('The seeded Stoxly layout: editable, not deletable'),
          used: z.coerce.number().int().describe('Broadcasts that picked it'),
          updatedAt: z.string().datetime({ offset: true }),
        })
        .passthrough(),
    ),
  })
  .passthrough();
const CreateBroadcastTemplateDto = z.object({
  name: z.string().min(1).max(100),
  html: z
    .string()
    .min(1)
    .max(100000)
    .describe('The layout inside <body>; must contain {{{body}}} and {{{unsubscribe_url}}}'),
});
const BroadcastTemplateDto = z
  .object({
    id: z.string(),
    name: z.string(),
    html: z.string(),
    isDefault: z.boolean(),
    isBuiltIn: z.boolean().describe('The seeded Stoxly layout: editable, not deletable'),
    used: z.coerce.number().int().describe('Broadcasts that picked it'),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const UpdateBroadcastTemplateDto = z
  .object({ name: z.string().min(1).max(100), html: z.string().min(1).max(100000) })
  .partial();

export const schemas = {
  ProfileResponseDto,
  usersUpdateProfile_Body,
  ChartViewPreferenceDto,
  ChartsPreferencesDto,
  UiPreferencesDto,
  PreferencesResponseDto,
  usersUpdatePreferences_Body,
  CustomTimeframeResponseDto,
  usersCreateCustomTimeframe_Body,
  usersCreateCustomBenchmark_Body,
  usersDeleteAccount_Body,
  OnboardingStatusResponseDto,
  usersUpdateOnboardingProgress_Body,
  CfdOpenPositionHintDto,
  usersSaveOnboardingDraft_Body,
  usersResetOnboardingSession_Body,
  importUnknownLabelsConfirm_Body,
  ShareVisibility,
  ShareSectionsDto,
  PrivacySettingsDto,
  portfolioCoreUpdateShareById_Body,
  portfolioCoreCreateShare_Body,
  startDate,
  PortfolioTransactionResponseDto,
  PaginatedPortfolioTransactionsResponseDto,
  portfolioCoreUpdateTransaction_Body,
  portfolioCoreBuy_Body,
  portfolioCoreSell_Body,
  portfolioCoreDividend_Body,
  portfolioCoreAssignment_Body,
  portfolioCoreExercise_Body,
  portfolioCoreExpire_Body,
  portfolioCoreCompareProjections_Body,
  portfolioCoreCreate_Body,
  portfolioCoreUpdate_Body,
  portfolioCoreCreateCfdPosition_Body,
  portfolioCoreUpdateCfdPosition_Body,
  portfolioCoreCloseCfdPosition_Body,
  portfolioRiskLimitsUpdate_Body,
  BondTermsDto,
  TreasuryBondHoldingDto,
  TreasuryRedemptionQuoteDto,
  treasuryBondsBook_Body,
  PortfolioEventDto,
  portfolioEventsRecord_Body,
  RecordPortfolioEventResultDto,
  bondTermsSet_Body,
  bondTermsPreview_Body,
  BondTermsPreviewDto,
  fundUnitsBook_Body,
  BookedFundUnitsDto,
  NotificationDto,
  NotificationPreferencesDto,
  notificationPreferencesUpdate_Body,
  notificationSymbolsUpdate_Body,
  notificationSymbolsReplaceAll_Body,
  ForecastLegDto,
  EarningsLegDto,
  internalFundamentalsNotifyUpdate_Body,
  internalFundamentalsIngestIssuerBalance_Body,
  internalFundamentalsIngestAdsRatio_Body,
  trackedTickersSetActiveTickers_Body,
  companyProfilesAdminEnqueueBatch_Body,
  ProfileRunDetailDto,
  walletsCreate_Body,
  WalletResponseDto,
  WalletTransactionResponseDto,
  PaginationMetaDto,
  walletsUpdate_Body,
  walletsDeposit_Body,
  walletsWithdraw_Body,
  walletsTransfer_Body,
  walletsUpdateTransaction_Body,
  PositionValuationDto,
  positionValuationsSet_Body,
  PositionValuationSeriesDto,
  TreasurySeriesDto,
  FundUnitSeriesDto,
  FundUnitSeriesListDto,
  FundUnitSeriesPriceDto,
  ProjectionYearDto,
  projectionsCreateProjection_Body,
  ProjectionYearResponseDto,
  ProjectionResponseDto,
  projectionsUpdateProjection_Body,
  ShareViewerAccess,
  FollowingResponseDto,
  snaptradeGetConnectionPortal_Body,
  snaptradeHandleCallback_Body,
  snaptradeUpsertOpeningLot_Body,
  SyncResultDto,
  ImportUnknownLabelDto,
  UpdateImportUnknownLabelDto,
  WatchlistItemResponseDto,
  WatchlistResponseDto,
  watchlistsCreate_Body,
  watchlistsUpdate_Body,
  watchlistsAddItem_Body,
  QualityPairSeriesDto,
  TabPercentilesDto,
  FactorProfileDto,
  FairValueBasisDto,
  WeightVectorDto,
  ScreenerTabScoreDto,
  positionsUpdatePosition_Body,
  Position,
  positionsUpdatePositionCore_Body,
  RenamedFundUnitDto,
  newsletterSubscribe_Body,
  contactCreateInquiry_Body,
  PublicShareViewerDto,
  ShareSectionVisibilityDto,
  PublicPortfolioResponseDto,
  RatioStatsCoverageDto,
  RatioStatsDto,
  EarningsScenarioInputsDto,
  RevenueScenarioInputsDto,
  comparisonGetPeerComparison_Body,
  MetricRankingDto,
  RankingCategoryDto,
  comparisonGetGroupRankings_Body,
  ComparisonGroupResponseDto,
  comparisonGroupsCreate_Body,
  comparisonGroupsUpdate_Body,
  AiUsageResponseDto,
  aiSummaryGenerateAnalysis_Body,
  DividendSafetyResponseDto,
  aiSummaryGenerateDividendSafety_Body,
  aiSummaryStreamTranslation_Body,
  aiSummaryStreamDocumentTranslation_Body,
  aiSummaryBatchTranslation_Body,
  aiSummaryCheckTranslation_Body,
  CompactionSummaryDto,
  chatbotStream_Body,
  CopilotSettingsDto,
  copilotSettingsUpdateSettings_Body,
  BoardDocResponseDto,
  SearchEngine,
  SearchEngines,
  CreateSearchEngineDto,
  ReorderSearchEnginesDto,
  TestSearchEngineDto,
  SearchEngineTest,
  TrySearchDto,
  TrySearchResult,
  RecentSearches,
  UpdateSearchEngineDto,
  ConversationDetailDto,
  conversationsUpdateSettings_Body,
  AdminUserDto,
  adminUpdateUserRole_Body,
  adminUpdateUserPlanOverride_Body,
  backfillTrigger_Body,
  backfillTriggerCampaign_Body,
  EarlyBirdStatusDto,
  EarlyBirdPortalProductDto,
  EarlyBirdPortalSyncDto,
  billingCreateCheckout_Body,
  billingCreateProduct_Body,
  billingUpdateProduct_Body,
  billingAssignPrice_Body,
  billingUpdateEntitlement_Body,
  mergedPortfoliosCreate_Body,
  MergedPortfolioMemberDto,
  MergedPortfolioMetricsDto,
  MergedPortfolioResponseDto,
  mergedPortfoliosUpdate_Body,
  PortfolioMoverDto,
  PortfolioInsightDto,
  RiskAiAnalysisDto,
  portfolioCoreGenerateRiskAiAnalysis_Body,
  chartingPresetsCreate_Body,
  chartingPresetsUpdate_Body,
  metricPresetsCreate_Body,
  metricPresetsUpdate_Body,
  ScreenerPresetFiltersDto,
  ScreenerPresetDto,
  screenerPresetsCreate_Body,
  SavedNewsFilterDto,
  AlertRuleParamsDto,
  alertsCreate_Body,
  alertsUpdate_Body,
  CampaignWithStatsDto,
  campaignsCreate_Body,
  CampaignDto,
  CampaignStatsDto,
  CampaignChartPointDto,
  CampaignPointsSeriesPointDto,
  CampaignPlanMixEntryDto,
  campaignsExportHistory_Body,
  CampaignExportQueuedDto,
  campaignsUpdate_Body,
  CampaignReferredUserDto,
  CampaignUsersResponseDto,
  bugReportsCreate_Body,
  ReporterBugReportDto,
  ReporterMessageDto,
  bugReportsSignal_Body,
  BugReportReporterIssuesDto,
  BugReportLastSignalDto,
  BugReportListItemDto,
  BugReplyBatchStateDto,
  bugReportsAdminStageBatch_Body,
  StagedBugReplyBatchDto,
  UpdateBugReportDraftDto,
  BugReportMessageDto,
  BugReportDetailDto,
  UpdateBugReportDto,
  CreateBugReportMessageDto,
  bugReportsAgentPendingRelease_Body,
  AgentPendingReleaseResultDto,
  AgentBugReportDto,
  bugReportsAgentUpdate_Body,
  BroadcastDto,
  CreateBroadcastDto,
  BroadcastAudienceCountsDto,
  BroadcastSendersDto,
  BroadcastSettingsDto,
  UpdateBroadcastSettingsDto,
  PreviewBroadcastDto,
  BroadcastPreviewDto,
  BroadcastImageDto,
  UpdateBroadcastDto,
  BroadcastSentHtmlDto,
  TestBroadcastDto,
  TranslateBroadcastDto,
  SendBroadcastDto,
  BroadcastTemplatesDto,
  CreateBroadcastTemplateDto,
  BroadcastTemplateDto,
  UpdateBroadcastTemplateDto,
};

export const endpoints = makeApi([
  {
    method: 'get',
    path: '/api',
    alias: 'appGetHello',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/early-bird',
    alias: 'adminAnalyticsGetEarlyBird',
    requestFormat: 'json',
    response: EarlyBirdStatusDto,
  },
  {
    method: 'post',
    path: '/api/admin/analytics/early-bird/cutoff',
    alias: 'adminAnalyticsSetEarlyBirdCutoff',
    requestFormat: 'json',
    response: EarlyBirdStatusDto,
    errors: [
      {
        status: 409,
        description: `The cutoff is already set; it is never moved`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/admin/analytics/early-bird/portal-configuration',
    alias: 'adminAnalyticsSetEarlyBirdPortalConfiguration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          configurationId: z
            .string()
            .regex(/^bpc_[A-Za-z0-9]+$/)
            .describe(
              'A Stripe customer-portal configuration id (bpc_…). Re-fetched server-side; must not be the default configuration and must have no hosted login page.',
            ),
        }),
      },
    ],
    response: EarlyBirdPortalSyncDto,
  },
  {
    method: 'delete',
    path: '/api/admin/analytics/early-bird/portal-configuration',
    alias: 'adminAnalyticsClearEarlyBirdPortalConfiguration',
    requestFormat: 'json',
    response: EarlyBirdStatusDto,
  },
  {
    method: 'post',
    path: '/api/admin/analytics/early-bird/portal-configuration/sync',
    alias: 'adminAnalyticsSyncEarlyBirdPortal',
    requestFormat: 'json',
    response: EarlyBirdPortalSyncDto,
  },
  {
    method: 'get',
    path: '/api/admin/analytics/early-bird/portal-configurations',
    alias: 'adminAnalyticsListEarlyBirdPortalConfigurations',
    requestFormat: 'json',
    response: z.array(
      z
        .object({
          id: z.string().describe('Stripe portal configuration id (bpc_…)'),
          name: z.string().nullable(),
          isDefault: z.boolean().describe('The dashboard-managed default; never eligible'),
          loginPageEnabled: z
            .boolean()
            .describe('Has a hosted login page (shareable URL); never eligible'),
          priceCount: z.coerce.number().describe("Prices currently under its 'Switch plan' list"),
          eligible: z.boolean().describe('May early birds run on it'),
          ineligibleReason: z.string().nullable(),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/registrations/overview',
    alias: 'adminAnalyticsGetRegistrationOverview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        totalUsers: z.coerce.number().describe('All-time non-demo user count'),
        newInRange: z.coerce.number().describe('New non-demo users registered in the selected range'),
        prevRange: z
          .coerce.number()
          .describe('New users in the equal-length window before the range (delta base)'),
        deltaPct: z
          .coerce.number()
          .describe('% change vs the previous window; null when there is no prior baseline')
          .nullable(),
        activeInRange: z.coerce.number().describe('Users with a login in the range'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/registrations/trend',
    alias: 'adminAnalyticsGetRegistrationTrend',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'groupBy',
        type: 'Query',
        schema: z.enum(['day', 'week', 'month']).optional().default('day'),
      },
    ],
    response: z.array(
      z
        .object({
          date: z.string().describe('Bucket start, YYYY-MM-DD'),
          newUsers: z.coerce.number().describe('New non-demo users registered in this bucket'),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/subscriptions/list',
    alias: 'adminAnalyticsGetSubscriptionList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'planTier',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        subscriptions: z
          .array(
            z
              .object({
                id: z.string(),
                userId: z.string(),
                userEmail: z.string(),
                userName: z.string().nullable(),
                planTier: z.string().describe('BASIC | PREMIUM | ULTIMATE'),
                status: z.string().describe('active | trialing | past_due | canceled | …'),
                billingInterval: z.string().describe('MONTH | YEAR'),
                monthlyValueCents: z
                  .coerce.number()
                  .describe(
                    'Monthly-normalized, discount-net value in cents (0 when the price is unknown)',
                  ),
                discountPercent: z
                  .coerce.number()
                  .describe(
                    'Recurring discount currently applied, percent of list price; null when none',
                  )
                  .nullable(),
                currency: z.string().nullable(),
                startedAt: z.string().datetime({ offset: true }),
                canceledAt: z.string().datetime({ offset: true }).nullable(),
                cancelAtPeriodEnd: z
                  .boolean()
                  .describe('Scheduled to end at currentPeriodEnd (still active until then)'),
                currentPeriodEnd: z.string().datetime({ offset: true }).nullable(),
              })
              .passthrough(),
          )
          .describe('Subscriptions, newest first'),
        total: z.coerce.number(),
        page: z.coerce.number(),
        limit: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/subscriptions/overview',
    alias: 'adminAnalyticsGetSubscriptionOverview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        activeCount: z.coerce.number().describe('Subscriptions currently in `active` status'),
        trialingCount: z.coerce.number().describe('Subscriptions currently in `trialing` status'),
        pastDueCount: z.coerce.number().describe('Subscriptions currently in `past_due` status'),
        payingCount: z.coerce.number().describe('Subscriptions in a revenue status (active | past_due)'),
        pendingCancelCount: z
          .coerce.number()
          .describe(
            'Active subscriptions scheduled to cancel at period end (still paying, not churn)',
          ),
        canceledInRange: z.coerce.number().describe('Subscriptions whose service ENDED within the range'),
        newInRange: z.coerce.number().describe('Subscriptions created within the range'),
        mrrCents: z
          .coerce.number()
          .describe(
            'Current MRR in cents — active + past_due, net of recurring discounts, trials excluded (the Stripe dashboard definition); single-currency list-price estimate',
          ),
        arrCents: z.coerce.number().describe('Annual run-rate in cents (mrrCents × 12)'),
        arpuCents: z
          .coerce.number()
          .describe('Average MRR per paying subscription (cents); null when there are none')
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/subscriptions/plan-mix',
    alias: 'adminAnalyticsGetSubscriptionPlanMix',
    requestFormat: 'json',
    response: z.array(
      z
        .object({
          planTier: z.string().describe('BASIC | PREMIUM | ULTIMATE | unknown'),
          count: z.coerce.number().describe('Currently-paying subscriptions on this tier'),
          estMrrCents: z.coerce.number().describe('Estimated MRR for this tier (cents)'),
        })
        .passthrough(),
    ),
  },
  {
    method: 'post',
    path: '/api/admin/analytics/subscriptions/resync',
    alias: 'adminAnalyticsResyncSubscriptions',
    requestFormat: 'json',
    response: z
      .object({
        synced: z.coerce.number().describe('Stripe subscriptions re-read and written to the local row'),
        skipped: z
          .coerce.number()
          .describe('Stripe subscriptions skipped (no price, or a customer we have no user for)'),
        invoicesSynced: z
          .coerce.number()
          .describe(
            'PAID Stripe invoices mirrored into stripe_invoices (the campaign points source)',
          ),
        invoicesSkipped: z
          .coerce.number()
          .describe(
            'Paid invoices not mirrored: settled at zero, no service period, or no local customer',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/analytics/subscriptions/trend',
    alias: 'adminAnalyticsGetSubscriptionTrend',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'groupBy',
        type: 'Query',
        schema: z.enum(['day', 'week', 'month']).optional().default('day'),
      },
    ],
    response: z.array(
      z
        .object({
          date: z.string().describe('Bucket start, YYYY-MM-DD'),
          newSubs: z.coerce.number().describe('Subscriptions started in this bucket'),
          churnedSubs: z.coerce.number().describe('Subscriptions canceled in this bucket'),
          netNewMrrCents: z
            .coerce.number()
            .describe('MRR of subs started minus MRR of subs canceled this bucket (cents)'),
        })
        .passthrough(),
    ),
  },
  {
    method: 'post',
    path: '/api/admin/backfill',
    alias: 'backfillTrigger',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: backfillTrigger_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/backfill',
    alias: 'backfillList',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/backfill/:jobId',
    alias: 'backfillStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'jobId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/backfill/campaign',
    alias: 'backfillTriggerCampaign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: backfillTriggerCampaign_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/backfill/campaign',
    alias: 'backfillListCampaigns',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/backfill/campaign/:id',
    alias: 'backfillCampaignStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/backfill/campaign/:id/stop',
    alias: 'backfillStopCampaign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/backfill/foreign-adrs',
    alias: 'backfillPreviewForeignAdrs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'scope',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/broadcast-templates',
    alias: 'broadcastTemplatesAdminList',
    requestFormat: 'json',
    response: BroadcastTemplatesDto,
  },
  {
    method: 'post',
    path: '/api/admin/broadcast-templates',
    alias: 'broadcastTemplatesAdminCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateBroadcastTemplateDto,
      },
    ],
    response: BroadcastTemplateDto,
    errors: [
      {
        status: 400,
        description: `Missing {{{body}}} or {{{unsubscribe_url}}}`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/admin/broadcast-templates/:templateId',
    alias: 'broadcastTemplatesAdminUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateBroadcastTemplateDto,
      },
      {
        name: 'templateId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastTemplateDto,
  },
  {
    method: 'delete',
    path: '/api/admin/broadcast-templates/:templateId',
    alias: 'broadcastTemplatesAdminRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'templateId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 409,
        description: `The default or the built-in template`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts',
    alias: 'broadcastsAdminList',
    requestFormat: 'json',
    response: z.array(BroadcastDto),
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts',
    alias: 'broadcastsAdminCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateBroadcastDto,
      },
    ],
    response: BroadcastDto,
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts/:id',
    alias: 'broadcastsAdminGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
    errors: [
      {
        status: 404,
        description: `Unknown broadcast`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/admin/broadcasts/:id',
    alias: 'broadcastsAdminUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateBroadcastDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
    errors: [
      {
        status: 409,
        description: `Not a draft`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/admin/broadcasts/:id',
    alias: 'broadcastsAdminRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 409,
        description: `Not a draft`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/:id/cancel',
    alias: 'broadcastsAdminCancel',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/:id/retry',
    alias: 'broadcastsAdminRetry',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/:id/send',
    alias: 'broadcastsAdminSend',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            scheduledAt: z
              .string()
              .datetime({ offset: true })
              .describe('Omit or null to send now; a future instant to schedule')
              .nullable(),
          })
          .partial(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
    errors: [
      {
        status: 409,
        description: `Not a draft, or sending is not configured here`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts/:id/sent/:language',
    alias: 'broadcastsAdminSentHtml',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'language',
        type: 'Path',
        schema: z.enum(['en', 'pl', 'es', 'de']),
      },
    ],
    response: z.object({ html: z.string() }).passthrough(),
    errors: [
      {
        status: 404,
        description: `Nothing was sent in that language`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/:id/test',
    alias: 'broadcastsAdminSendTest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestBroadcastDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/:id/translate',
    alias: 'broadcastsAdminTranslate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TranslateBroadcastDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BroadcastDto,
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts/audience',
    alias: 'broadcastsAdminAudience',
    requestFormat: 'json',
    response: BroadcastAudienceCountsDto,
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/images',
    alias: 'broadcastsAdminUploadImage',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ file: z.instanceof(File) }).passthrough(),
      },
    ],
    response: z
      .object({ url: z.string().describe('Where readers load the image from; it never expires') })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/admin/broadcasts/preview',
    alias: 'broadcastsAdminPreview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PreviewBroadcastDto,
      },
    ],
    response: z.object({ html: z.string() }).passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts/senders',
    alias: 'broadcastsAdminSenders',
    requestFormat: 'json',
    response: BroadcastSendersDto,
  },
  {
    method: 'get',
    path: '/api/admin/broadcasts/settings',
    alias: 'broadcastsAdminSettings',
    requestFormat: 'json',
    response: BroadcastSettingsDto,
  },
  {
    method: 'put',
    path: '/api/admin/broadcasts/settings',
    alias: 'broadcastsAdminUpdateSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateBroadcastSettingsDto,
      },
    ],
    response: BroadcastSettingsDto,
    errors: [
      {
        status: 400,
        description: `A model the settings do not list`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports',
    alias: 'bugReportsAdminList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z
          .enum([
            'new',
            'investigating',
            'needs_info',
            'queued',
            'in_progress',
            'fixed_pending_release',
            'resolved',
            'wont_fix',
            'invalid',
            'obsolete',
            'open',
            'closed',
          ])
          .optional(),
      },
      {
        name: 'category',
        type: 'Query',
        schema: z
          .enum([
            'bug',
            'data_issue',
            'feature_request',
            'complaint',
            'question',
            'billing',
            'unclassified',
          ])
          .optional(),
      },
      {
        name: 'replied',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'needsReply',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'hasIssue',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().max(200).optional(),
      },
      {
        name: 'sort',
        type: 'Query',
        schema: z
          .enum([
            'reporterActivityAt',
            'createdAt',
            'status',
            'category',
            'repliedAt',
            'source',
            'reporter',
            'reporterIssues',
            'attachmentCount',
          ])
          .optional(),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
    ],
    response: z
      .object({
        reports: z.array(BugReportListItemDto),
        total: z.coerce.number(),
        page: z.coerce.number(),
        limit: z.coerce.number(),
        statusCounts: z
          .record(z.coerce.number())
          .describe('Report count per status across ALL reports, not just this page'),
        categoryCounts: z
          .record(z.coerce.number())
          .describe(
            'Report count per category across ALL reports, with untriaged ones under `unclassified`',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports/:id',
    alias: 'bugReportsAdminGetOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: BugReportDetailDto,
  },
  {
    method: 'patch',
    path: '/api/admin/bug-reports/:id',
    alias: 'bugReportsAdminUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateBugReportDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: BugReportListItemDto,
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports/:id/attachments/download',
    alias: 'bugReportsAdminDownloadAllAttachments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'origin',
        type: 'Query',
        schema: z.enum(['upload', 'import_reference']).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/bug-reports/:id/messages',
    alias: 'bugReportsAdminAddMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateBugReportMessageDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: BugReportMessageDto,
    errors: [
      {
        status: 404,
        description: `Unknown bug report`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports/:id/reply-template',
    alias: 'bugReportsAdminReplyTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum([
          'new',
          'investigating',
          'needs_info',
          'queued',
          'in_progress',
          'fixed_pending_release',
          'resolved',
          'wont_fix',
          'invalid',
          'obsolete',
        ]),
      },
      {
        name: 'category',
        type: 'Query',
        schema: z
          .enum(['bug', 'data_issue', 'feature_request', 'complaint', 'question', 'billing'])
          .optional(),
      },
      {
        name: 'channel',
        type: 'Query',
        schema: z.enum(['mail', 'in_app']).optional(),
      },
    ],
    response: z
      .object({
        available: z
          .boolean()
          .describe('False when this status writes to nobody, or a category is needed'),
        reason: z
          .enum(['noTemplate', 'noTemplateForCategory', 'categoryRequired'])
          .describe(
            '`noTemplate`: this status deliberately does not write to the reporter, whatever the report is about. `noTemplateForCategory`: the status writes to other kinds of report but has nothing true to say about this one. `categoryRequired`: the wording depends on the kind of report and none is set yet. Null when a draft was produced.',
          )
          .nullable(),
        locale: z
          .string()
          .describe("The language the draft is written in, resolved from the reporter's account")
          .nullable(),
        subject: z.string().nullable(),
        body: z.string().describe('Plain text, ready to drop into the reply field').nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports/attachments/:attachmentId/download',
    alias: 'bugReportsAdminDownloadAttachment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'attachmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        url: z.string().describe('Presigned S3 URL, valid for 15 minutes'),
        filename: z.string(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/bug-reports/batches',
    alias: 'bugReportsAdminListBatches',
    requestFormat: 'json',
    response: z.array(BugReplyBatchStateDto),
  },
  {
    method: 'post',
    path: '/api/admin/bug-reports/batches',
    alias: 'bugReportsAdminStageBatch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bugReportsAdminStageBatch_Body,
      },
    ],
    response: StagedBugReplyBatchDto,
    errors: [
      {
        status: 400,
        description: `The batch fails its schema`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `&#x60;batch_applied&#x60; (a deploy already published it) or &#x60;batch_applying&#x60; (in flight)`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/admin/bug-reports/messages/:messageId',
    alias: 'bugReportsAdminUpdateDraft',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          body: z.string().min(1).max(10000).describe('The corrected reply text'),
        }),
      },
      {
        name: 'messageId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: BugReportMessageDto,
    errors: [
      {
        status: 404,
        description: `Unknown message`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `&#x60;message_published&#x60;`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/admin/bulk-sync/clear',
    alias: 'bulkSyncClearJob',
    requestFormat: 'json',
    parameters: [
      {
        name: 'dataType',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/bulk-sync/recover-year-ends',
    alias: 'bulkSyncTriggerYearEndRecovery',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/bulk-sync/status',
    alias: 'bulkSyncGetStatus',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/bulk-sync/trigger',
    alias: 'bulkSyncTriggerSync',
    requestFormat: 'json',
    parameters: [
      {
        name: 'dataType',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/bulk-sync/trigger-all',
    alias: 'bulkSyncTriggerAll',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/campaigns',
    alias: 'campaignsFindAll',
    requestFormat: 'json',
    parameters: [
      {
        name: 'retentionDays',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(90),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: z.array(CampaignWithStatsDto),
  },
  {
    method: 'post',
    path: '/api/admin/campaigns',
    alias: 'campaignsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: campaignsCreate_Body,
      },
    ],
    response: CampaignDto,
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/:id',
    alias: 'campaignsFindById',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'retentionDays',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(90),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: CampaignWithStatsDto,
  },
  {
    method: 'patch',
    path: '/api/admin/campaigns/:id',
    alias: 'campaignsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: campaignsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CampaignDto,
  },
  {
    method: 'delete',
    path: '/api/admin/campaigns/:id',
    alias: 'campaignsRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CampaignDto,
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/:id/users',
    alias: 'campaignsGetCampaignUsers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
    ],
    response: CampaignUsersResponseDto,
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/chart',
    alias: 'campaignsGetChartData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'campaignId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'groupBy',
        type: 'Query',
        schema: z.enum(['day', 'week', 'month']).optional().default('day'),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: z.array(CampaignChartPointDto),
  },
  {
    method: 'post',
    path: '/api/admin/campaigns/export',
    alias: 'campaignsExportHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: campaignsExportHistory_Body,
      },
    ],
    response: CampaignExportQueuedDto,
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/overview',
    alias: 'campaignsGetOverview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'retentionDays',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(90),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: CampaignStatsDto,
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/plan-mix',
    alias: 'campaignsGetPlanMix',
    requestFormat: 'json',
    parameters: [
      {
        name: 'campaignId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: z.array(CampaignPlanMixEntryDto),
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/points-series',
    alias: 'campaignsGetPointsSeries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'campaignId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'groupBy',
        type: 'Query',
        schema: z.enum(['day', 'week', 'month']).optional().default('week'),
      },
      {
        name: 'pointMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(60).optional().default(3),
      },
      {
        name: 'horizonMonths',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(24).optional().default(12),
      },
      {
        name: 'tag',
        type: 'Query',
        schema: z.string().max(40).optional(),
      },
    ],
    response: z.array(CampaignPointsSeriesPointDto),
  },
  {
    method: 'get',
    path: '/api/admin/campaigns/tags',
    alias: 'campaignsGetTags',
    requestFormat: 'json',
    response: z.array(z.string()),
  },
  {
    method: 'post',
    path: '/api/admin/company-profiles/batch',
    alias: 'companyProfilesAdminEnqueueBatch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: companyProfilesAdminEnqueueBatch_Body,
      },
    ],
    response: z
      .object({
        outcomes: z.array(
          z
            .object({
              symbol: z.string(),
              outcome: z.enum([
                'enqueued',
                'skipped_active',
                'skipped_unknown',
                'skipped_unsupported',
              ]),
            })
            .passthrough(),
        ),
        enqueued: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/company-profiles/profiles',
    alias: 'companyProfilesAdminListProfiles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(25),
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        rows: z.array(
          z
            .object({
              runId: z.string().describe('Run id of the serving profile (links to the run detail)'),
              symbol: z.string(),
              name: z.string().nullable(),
              researchModel: z.string().nullable(),
              costUsd: z.coerce.number().nullable(),
              sourceCount: z.coerce.number(),
              supplierCount: z.coerce.number(),
              peerCount: z.coerce.number(),
              generatedAt: z.string().datetime({ offset: true }),
            })
            .passthrough(),
        ),
        total: z.coerce.number(),
        page: z.coerce.number(),
        totalPages: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/company-profiles/runs',
    alias: 'companyProfilesAdminListRuns',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(25),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['queued', 'researching', 'ready', 'failed']).optional(),
      },
    ],
    response: z
      .object({
        rows: z.array(
          z
            .object({
              id: z.string(),
              symbol: z.string(),
              instrumentId: z.string().nullable(),
              status: z.enum(['queued', 'researching', 'ready', 'failed']),
              promptVersion: z.string(),
              researchModel: z.string().nullable(),
              costUsd: z.coerce.number().describe('Vendor-reported cost of the run (USD)').nullable(),
              error: z.string().nullable(),
              createdAt: z.string().datetime({ offset: true }),
              updatedAt: z.string().datetime({ offset: true }),
            })
            .passthrough(),
        ),
        total: z.coerce.number(),
        page: z.coerce.number(),
        totalPages: z.coerce.number(),
        counts: z
          .object({
            queued: z.coerce.number(),
            researching: z.coerce.number(),
            ready: z.coerce.number(),
            failed: z.coerce.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/company-profiles/runs/:id',
    alias: 'companyProfilesAdminGetRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ProfileRunDetailDto,
  },
  {
    method: 'post',
    path: '/api/admin/company-profiles/runs/:id/retry',
    alias: 'companyProfilesAdminRetryRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ProfileRunDetailDto,
  },
  {
    method: 'get',
    path: '/api/admin/company-profiles/top-held',
    alias: 'companyProfilesAdminTopHeld',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(500).optional().default(200),
      },
    ],
    response: z.array(
      z
        .object({
          symbol: z.string(),
          name: z.string(),
          holders: z.coerce.number().describe('Distinct users holding an open position'),
          lastStatus: z.enum(['queued', 'researching', 'ready', 'failed']).nullable(),
          lastRunAt: z.string().datetime({ offset: true }).nullable(),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/admin/company-profiles/top-market-cap',
    alias: 'companyProfilesAdminTopMarketCap',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(1000).optional().default(1000),
      },
      {
        name: 'scope',
        type: 'Query',
        schema: z.enum(['us', 'global']).optional().default('us'),
      },
    ],
    response: z.array(
      z
        .object({
          symbol: z.string(),
          name: z.string(),
          marketCapUsd: z.coerce.number().describe('Absolute USD market cap (Finnhub ranking signal)'),
          lastStatus: z.enum(['queued', 'researching', 'ready', 'failed']).nullable(),
          lastRunAt: z.string().datetime({ offset: true }).nullable(),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/admin/deletion-feedback',
    alias: 'adminGetDeletionFeedback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
    ],
    response: z
      .object({
        items: z.array(
          z
            .object({
              id: z.string(),
              reason: z.string().describe('Predefined reason code or null').nullable(),
              comment: z.string().describe('Free-text feedback or null').nullable(),
              createdAt: z.string().datetime({ offset: true }),
            })
            .passthrough(),
        ),
        total: z.coerce.number(),
        countsByReason: z.array(
          z.object({ reason: z.string().nullable(), count: z.coerce.number() }).passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/export/users-xlsx',
    alias: 'adminDownloadAllUsersXlsx',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/import-unknown-labels',
    alias: 'importUnknownLabelsAdminList',
    requestFormat: 'json',
    response: z.object({ labels: z.array(ImportUnknownLabelDto) }).passthrough(),
  },
  {
    method: 'patch',
    path: '/api/admin/import-unknown-labels/:id',
    alias: 'importUnknownLabelsAdminSetStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateImportUnknownLabelDto,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Not a lifecycle status`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No record has that id`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/imported-files/:fileId/download',
    alias: 'adminGetImportedFileDownloadUrl',
    requestFormat: 'json',
    parameters: [
      {
        name: 'fileId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/search-engines',
    alias: 'searchEnginesAdminList',
    requestFormat: 'json',
    response: SearchEngines,
  },
  {
    method: 'post',
    path: '/api/admin/search-engines',
    alias: 'searchEnginesAdminCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateSearchEngineDto,
      },
    ],
    response: SearchEngines,
    errors: [
      {
        status: 409,
        description: `An engine with this name already exists`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/admin/search-engines/:engineId',
    alias: 'searchEnginesAdminUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateSearchEngineDto,
      },
      {
        name: 'engineId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: SearchEngines,
  },
  {
    method: 'delete',
    path: '/api/admin/search-engines/:engineId',
    alias: 'searchEnginesAdminRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'engineId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/admin/search-engines/order',
    alias: 'searchEnginesAdminReorder',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReorderSearchEnginesDto,
      },
    ],
    response: SearchEngines,
  },
  {
    method: 'get',
    path: '/api/admin/search-engines/recent',
    alias: 'searchEnginesAdminRecent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'outcome',
        type: 'Query',
        schema: z.enum(['answered', 'empty', 'failed']).optional(),
      },
      {
        name: 'mode',
        type: 'Query',
        schema: z.enum(['web', 'news']).optional(),
      },
    ],
    response: RecentSearches,
  },
  {
    method: 'post',
    path: '/api/admin/search-engines/search',
    alias: 'searchEnginesAdminTrySearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TrySearchDto,
      },
    ],
    response: TrySearchResult,
  },
  {
    method: 'post',
    path: '/api/admin/search-engines/test',
    alias: 'searchEnginesAdminTest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestSearchEngineDto,
      },
    ],
    response: SearchEngineTest,
  },
  {
    method: 'get',
    path: '/api/admin/snaptrade-logs',
    alias: 'adminGetSnaptradeResponseLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'userId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'endpoint',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/snaptrade-logs/:logId/download',
    alias: 'adminGetSnaptradeLogDownloadUrl',
    requestFormat: 'json',
    parameters: [
      {
        name: 'logId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/snaptrade-logs/run/:runId/download',
    alias: 'adminGetSnaptradeRunDownload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'runId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/snaptrade-webhook-logs',
    alias: 'adminGetSnaptradeWebhookLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'eventType',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'processingStatus',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'appUserId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/stats',
    alias: 'adminGetDashboardStats',
    requestFormat: 'json',
    response: z
      .object({
        totalUsers: z.coerce.number(),
        activeUsers: z.coerce.number(),
        totalWallets: z.coerce.number(),
        totalPortfolios: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/user-health',
    alias: 'adminUserHealthGetUsersHealth',
    description: `The SAME fold the user reads on /investments, batched for the users list: score, band, scored-finding count and the findings themselves (severity + full count, no &#x60;items&#x60;).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'userIds',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        users: z.array(
          z
            .object({
              userId: z.string(),
              score: z
                .coerce.number()
                .describe(
                  "The same 0-100 'Stan portfeli' score the user sees, or null when the fold could not run for this user — null is NOT 100, and the column must not paint it as healthy.",
                )
                .nullable(),
              band: z
                .enum(['good', 'warn', 'bad'])
                .describe(
                  'The colour band `healthBand` earns for `score`; null exactly when score is.',
                )
                .nullable(),
              issueCount: z
                .coerce.number()
                .describe(
                  "SCORED findings — the 'n things to review' figure, one per finding however many offenders it covers.",
                ),
              scoreExact: z
                .boolean()
                .describe(
                  'Whether the ACCOUNT-wide return was consulted. False means the one account-level refusal (`mergedFundingEntangled`) could not be evaluated, so `score` is an UPPER bound for that user — see AdminUserHealthFoldService for why it is not fetched here.',
                ),
              issues: z
                .array(
                  z
                    .object({
                      type: z.enum([
                        'delisted_instrument',
                        'missing_cost_basis',
                        'stalled_import',
                        'duplicate_transactions',
                        'stale_prices',
                        'stale_fx',
                        'unresolved_instrument',
                        'missing_trade_day_fx',
                        'expired_option_open',
                        'expired_future_open',
                        'stopped_price_series',
                        'valued_at_cost',
                        'unclassified_fee',
                        'cfd_estimate',
                        'cfd_unpriced',
                        'cfd_stale_open',
                        'delivery_close_missing',
                        'commission_estimated',
                        'sale_without_quantity',
                        'opening_lot_estimated',
                        'risk_unmodelled',
                        'risk_ratio_unmeasured',
                        'duplicate_lot',
                        'orphan_top_up',
                        'hand_book_duplicate',
                        'hand_lot_vs_statement',
                        'subscription_not_allotted',
                        'holdings_vs_statement',
                        'broken_connection',
                        'connection_in_progress',
                        'connection_unknown_status',
                        'negative_cash',
                        'margin_cash_debt',
                        'orphan_wallet',
                        'portfolio_without_wallet',
                        'unverified_symbols',
                        'canonical_refusal',
                        'history_incomplete',
                        'overdraft_as_capital',
                        'cfd_margin_missing',
                        'unconverted_currency',
                        'book_not_computed',
                        'opening_date_invented',
                        'currency_effect_unmeasured',
                        'shared_wallet_return',
                        'twr_refused',
                        'own_return_refused',
                        'contribution_payer_not_stated',
                        'sells_exceed_buys',
                        'price_unavailable',
                        'fx_refused',
                      ]),
                      severity: z.enum(['high', 'mid', 'low']),
                      count: z
                        .coerce.number()
                        .describe('FULL cardinality of the offenders behind this issue.'),
                    })
                    .passthrough(),
                )
                .describe(
                  "Every finding behind the score. EMPTY means the fold ran and found nothing — a detection that FAILED is absent rather than reported clean, so an empty list is never 'we could not look'; that state is `score: null`.",
                ),
            })
            .passthrough(),
        ),
        checkedAt: z.string().describe('ISO-8601 instant the checks ran.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/users',
    alias: 'adminGetUsers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(200).optional().default(20),
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'role',
        type: 'Query',
        schema: z.enum(['user', 'admin']).optional(),
      },
      {
        name: 'plan',
        type: 'Query',
        schema: z.enum(['BASIC', 'PREMIUM', 'ULTIMATE', 'PAID']).optional(),
      },
      {
        name: 'issues',
        type: 'Query',
        schema: z.enum(['any', 'open', 'unreplied']).optional(),
      },
      {
        name: 'sort',
        type: 'Query',
        schema: z
          .enum(['email', 'role', 'issues', 'wallets', 'portfolios', 'lastLoginAt', 'createdAt'])
          .optional(),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
    ],
    response: z
      .object({
        users: z.array(AdminUserDto),
        total: z.coerce.number(),
        page: z.coerce.number(),
        limit: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/users/:id',
    alias: 'adminGetUserById',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AdminUserDto,
  },
  {
    method: 'delete',
    path: '/api/admin/users/:id',
    alias: 'adminDeleteUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 403,
        description: `Cannot delete yourself, admins, or demo users`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/activity',
    alias: 'adminGetUserActivity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        counts: z
          .object({
            wallets: z.coerce.number(),
            portfolios: z.coerce.number(),
            watchlists: z.coerce.number(),
            trackedTickers: z.coerce.number(),
            importedFiles: z.coerce.number(),
            chatConversations: z.coerce.number(),
            researchReports: z.coerce.number(),
            notifications: z.coerce.number(),
            unreadNotifications: z.coerce.number(),
            snaptradeConnections: z.coerce.number(),
            projections: z.coerce.number(),
            metricPresets: z.coerce.number(),
            chartingPresets: z.coerce.number(),
            logins: z.coerce.number().describe('Total logins recorded in `session_logs`'),
          })
          .passthrough(),
        sessions: z
          .array(
            z
              .object({
                id: z.string(),
                loginAt: z.string().datetime({ offset: true }),
                logoutAt: z.string().datetime({ offset: true }).nullable(),
                isActive: z.boolean(),
                loginMethod: z.string().describe('e.g. google | email | magiclink'),
                ipAddress: z.string(),
                userAgent: z.string().nullable(),
              })
              .passthrough(),
          )
          .describe('Most recent logins, newest first'),
        attribution: z
          .object({
            campaignId: z.string(),
            campaignCode: z.string(),
            campaignName: z.string(),
            assignedAt: z.string().datetime({ offset: true }),
            assignedReason: z.string(),
            attributionModel: z.string(),
          })
          .passthrough()
          .nullable(),
        adminActions: z
          .array(
            z
              .object({
                id: z.string(),
                action: z
                  .string()
                  .describe('e.g. IMPERSONATE | CHANGE_ROLE | SNAPTRADE_SYNC | EXPORT_*'),
                entityType: z.string(),
                createdAt: z.string().datetime({ offset: true }),
                ipAddress: z.string().nullable(),
                actorId: z.string().describe('The admin who performed the action').nullable(),
                actorEmail: z.string().nullable(),
              })
              .passthrough(),
          )
          .describe('Privileged admin actions targeting this user, newest first'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/apology-template',
    alias: 'adminGetApologyTemplate',
    description: `Returns TEXT ONLY. Nothing is sent from here, and nothing here grants the free days the message promises — that grant is made by hand in Stripe. Because the copy states the extension is already applied, the order is: grant first, then send.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'extensionDays',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(365),
      },
    ],
    response: z
      .object({
        locale: z
          .enum(['en', 'pl', 'es', 'de'])
          .describe(
            "The language the text below is written in, resolved from the user's account (an unrecognised or missing one falls back to English). The panel names it so an admin pasting the message knows what they are sending.",
          ),
        subject: z.string(),
        body: z
          .string()
          .describe(
            'Plain text. The line breaks ARE the message — there is no HTML version, so any surface showing this must preserve them.',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/billing',
    alias: 'adminGetUserBilling',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        stripeCustomerId: z
          .string()
          .describe('The Stripe customer id (`cus_…`); null when the user never reached checkout')
          .nullable(),
        currentPlanTier: z.string().describe('Resolved tier — BASIC when no active subscription'),
        currentStatus: z
          .string()
          .describe("The active subscription's status, or `none` when there is no active one"),
        subscriptions: z
          .array(
            z
              .object({
                id: z.string(),
                stripeSubscriptionId: z.string(),
                stripePriceId: z.string(),
                planTier: z.string().describe('BASIC | PREMIUM | ULTIMATE'),
                status: z.string().describe('active | trialing | past_due | canceled | unpaid | …'),
                billingInterval: z.string().describe('MONTH | YEAR'),
                currentPeriodStart: z.string().datetime({ offset: true }).nullable(),
                currentPeriodEnd: z.string().datetime({ offset: true }).nullable(),
                trialStart: z.string().datetime({ offset: true }).nullable(),
                trialEnd: z.string().datetime({ offset: true }).nullable(),
                cancelAtPeriodEnd: z.boolean(),
                cancelAt: z.string().datetime({ offset: true }).nullable(),
                canceledAt: z.string().datetime({ offset: true }).nullable(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                unitAmount: z
                  .coerce.number()
                  .describe(
                    'Price in minor units (cents); null when the price is not in our catalog',
                  )
                  .nullable(),
                currency: z.string().nullable(),
                productName: z.string().describe('Our catalog product name').nullable(),
              })
              .passthrough(),
          )
          .describe('Every subscription, newest first'),
        payments: z
          .array(
            z
              .object({
                id: z.string(),
                number: z.string().describe('Human-readable invoice number').nullable(),
                amountPaid: z.coerce.number().describe('Amount actually paid, in minor units'),
                amountDue: z.coerce.number().describe('Amount billed, in minor units'),
                currency: z.string(),
                status: z.string().describe('draft | open | paid | uncollectible | void'),
                created: z.string().datetime({ offset: true }),
                periodStart: z.string().datetime({ offset: true }).nullable(),
                periodEnd: z.string().datetime({ offset: true }).nullable(),
                description: z.string().nullable(),
                invoicePdfUrl: z.string().nullable(),
                hostedInvoiceUrl: z.string().nullable(),
              })
              .passthrough(),
          )
          .describe('Stripe invoices, newest first'),
        lifetimeValueCents: z
          .coerce.number()
          .describe('Sum of `amountPaid` across all invoices, in minor units'),
        paymentsError: z
          .string()
          .describe(
            'Why the Stripe payment history is missing (a Stripe API failure). Null on success — an empty `payments` with a null error means the user genuinely has no invoices.',
          )
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/admin/users/:id/impersonate',
    alias: 'adminImpersonateUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        token: z.string(),
        expiresAt: z
          .string()
          .datetime({ offset: true })
          .describe('ISO instant the magic link stops working.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/imported-files',
    alias: 'adminGetUserImportedFiles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'patch',
    path: '/api/admin/users/:id/plan-override',
    alias: 'adminUpdateUserPlanOverride',
    description: `The grant OUTRANKS the Stripe subscription for every entitlement check and is written to &#x60;users.plan_tier_override&#x60; — no subscription row is created, so revenue reporting and &#x60;resyncAllFromStripe&#x60; are unaffected and revoking restores the paid tier exactly. The change takes effect on the target&#x27;s next request; their browser can cache the old entitlements for up to two minutes.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: adminUpdateUserPlanOverride_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AdminUserDto,
    errors: [
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/admin/users/:id/role',
    alias: 'adminUpdateUserRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: adminUpdateUserRole_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AdminUserDto,
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/snaptrade-bundle/download',
    alias: 'adminDownloadUserSnaptradeBundle',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/admin/users/:id/snaptrade-logs',
    alias: 'adminGetUserSnaptradeResponseLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/users/:id/snaptrade-refresh',
    alias: 'adminTriggerSnaptradeBrokerRefresh',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/admin/users/:id/snaptrade-sync',
    alias: 'adminTriggerSnaptradeSync',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/agent/bug-reports/:number',
    alias: 'bugReportsAgentGetOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
      {
        name: 'X-Agent-Token',
        type: 'Header',
        schema: z.string().optional(),
      },
    ],
    response: AgentBugReportDto,
    errors: [
      {
        status: 401,
        description: `No agent token, no admin session`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Unknown report number`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/agent/bug-reports/:number',
    alias: 'bugReportsAgentUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bugReportsAgentUpdate_Body,
      },
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
      {
        name: 'X-Agent-Token',
        type: 'Header',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `No agent token, no admin session`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Unknown report number`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `expectCreatedAt does not match - this is not the report the caller read`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/agent/bug-reports/pending-release',
    alias: 'bugReportsAgentPendingRelease',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bugReportsAgentPendingRelease_Body,
      },
      {
        name: 'X-Agent-Token',
        type: 'Header',
        schema: z.string().optional(),
      },
    ],
    response: AgentPendingReleaseResultDto,
    errors: [
      {
        status: 401,
        description: `No agent token, no admin session`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/ai-summary/analysis',
    alias: 'aiSummaryGetAnalysis',
    description: `Cache-only and ownership-aware: never generates and never fetches the transcript. &#x60;analysis&#x60; is null when nothing was generated yet OR when this metered reader has not bought this analysis (a purchase is one press, permanent; unlimited tiers always read) — POST /ai-summary/analysis is the (quota-gated) purchase path.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'eventId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'language',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        analysis: z
          .object({
            tone: z.enum(['optimistic', 'neutral', 'cautious', 'defensive']),
            toneRationale: z.string().describe('One sentence justifying the tone judgement'),
            verdict: z
              .object({
                rating: z.enum(['buy', 'hold', 'sell']),
                confidencePct: z.coerce.number().gte(0).lte(100),
                headline: z.string(),
                metrics: z.array(
                  z
                    .object({
                      label: z.string(),
                      value: z.string(),
                      delta: z.string(),
                      assessment: z.enum(['positive', 'negative', 'neutral', 'mixed']),
                    })
                    .passthrough(),
                ),
              })
              .passthrough(),
            qaStartIndex: z
              .coerce.number()
              .describe('Index of the first Q&A segment; null when the call has no Q&A')
              .nullable(),
            quotes: z.array(
              z
                .object({
                  segmentIndex: z.coerce.number().describe('Index into the transcript segment list'),
                  quote: z.string().describe('Verbatim quote from that segment'),
                  tag: z.enum(['positive', 'risk', 'evasive']),
                })
                .passthrough(),
            ),
            sections: z.array(
              z
                .object({
                  key: z.enum(['summary', 'sequential', 'guidance', 'qa']),
                  title: z.string(),
                  tag: z.string().describe('Short chip label'),
                  points: z.array(
                    z
                      .object({
                        lead: z.string().describe('Bolded lead-in phrase'),
                        text: z.string(),
                        sentiment: z.enum(['positive', 'negative', 'neutral', 'mixed']),
                      })
                      .passthrough(),
                  ),
                })
                .passthrough(),
            ),
          })
          .passthrough()
          .nullable(),
        generatedAt: z
          .string()
          .describe('ISO timestamp of the (re)generation; null when analysis is null')
          .nullable(),
        modelUsed: z.string().nullable(),
        sourcesCount: z
          .coerce.number()
          .describe('Deterministic source count (1 = transcript; 2 = + previous quarter)')
          .nullable(),
        cached: z.boolean().describe('True when served from the durable cache'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/ai-summary/analysis',
    alias: 'aiSummaryGenerateAnalysis',
    description: `The purchase path: the press is admitted against the IR_SUMMARIES_MONTHLY allowance and recorded FIRST (one unit per analysis per reader, permanent, idempotent), then the analysis is served from the durable cache or generated by ONE schema-constrained Gemini call.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryGenerateAnalysis_Body,
      },
    ],
    response: z
      .object({
        analysis: z
          .object({
            tone: z.enum(['optimistic', 'neutral', 'cautious', 'defensive']),
            toneRationale: z.string().describe('One sentence justifying the tone judgement'),
            verdict: z
              .object({
                rating: z.enum(['buy', 'hold', 'sell']),
                confidencePct: z.coerce.number().gte(0).lte(100),
                headline: z.string(),
                metrics: z.array(
                  z
                    .object({
                      label: z.string(),
                      value: z.string(),
                      delta: z.string(),
                      assessment: z.enum(['positive', 'negative', 'neutral', 'mixed']),
                    })
                    .passthrough(),
                ),
              })
              .passthrough(),
            qaStartIndex: z
              .coerce.number()
              .describe('Index of the first Q&A segment; null when the call has no Q&A')
              .nullable(),
            quotes: z.array(
              z
                .object({
                  segmentIndex: z.coerce.number().describe('Index into the transcript segment list'),
                  quote: z.string().describe('Verbatim quote from that segment'),
                  tag: z.enum(['positive', 'risk', 'evasive']),
                })
                .passthrough(),
            ),
            sections: z.array(
              z
                .object({
                  key: z.enum(['summary', 'sequential', 'guidance', 'qa']),
                  title: z.string(),
                  tag: z.string().describe('Short chip label'),
                  points: z.array(
                    z
                      .object({
                        lead: z.string().describe('Bolded lead-in phrase'),
                        text: z.string(),
                        sentiment: z.enum(['positive', 'negative', 'neutral', 'mixed']),
                      })
                      .passthrough(),
                  ),
                })
                .passthrough(),
            ),
          })
          .passthrough()
          .nullable(),
        generatedAt: z
          .string()
          .describe('ISO timestamp of the (re)generation; null when analysis is null')
          .nullable(),
        modelUsed: z.string().nullable(),
        sourcesCount: z
          .coerce.number()
          .describe('Deterministic source count (1 = transcript; 2 = + previous quarter)')
          .nullable(),
        cached: z.boolean().describe('True when served from the durable cache'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/ai-summary/analysis/tones',
    alias: 'aiSummaryGetAnalysisTones',
    description: `Batch, cache-only tone chips (never generates). Lives here rather than on the events endpoint because the analyses belong to ai-summary and investor-relations must not read AI tables (the one-way module edge) — the client joins by eventId.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'eventIds',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        tones: z.array(
          z
            .object({
              eventId: z.string().describe('Transcript event id'),
              tone: z.enum(['optimistic', 'neutral', 'cautious', 'defensive']),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/ai-summary/dividend-safety',
    alias: 'aiSummaryGetDividendSafety',
    description: `Cache-only and ownership-aware: never generates. &#x60;narrative&#x60; is null when nothing was generated yet OR when this metered reader has not bought it (the same monthly allowance as the call analysis) — POST /ai-summary/dividend-safety is the (quota-gated) purchase path. The narrative describes ONLY the figures the page already serves; the disclaimer copy lives in the client catalogs.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'language',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: DividendSafetyResponseDto,
  },
  {
    method: 'post',
    path: '/api/ai-summary/dividend-safety',
    alias: 'aiSummaryGenerateDividendSafety',
    description: `The purchase path for the narrative: the press is admitted against the same monthly AI allowance as the call analysis and recorded FIRST (one unit per symbol per reader, permanent, idempotent), then the narrative is served from the durable cache or generated by ONE schema-constrained call over the SERVED coverage figures. 404 for a symbol with no dividend history — a non-payer has no safety picture to narrate.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryGenerateDividendSafety_Body,
      },
    ],
    response: DividendSafetyResponseDto,
  },
  {
    method: 'post',
    path: '/api/ai-summary/translation/batch',
    alias: 'aiSummaryBatchTranslation',
    description: `Non-streaming batch translation. Returns all translations at once.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryBatchTranslation_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/ai-summary/translation/check',
    alias: 'aiSummaryCheckTranslation',
    description: `Returns cached translations for given resource IDs. Null for uncached.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryCheckTranslation_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/ai-summary/translation/document/stream',
    alias: 'aiSummaryStreamDocumentTranslation',
    description: `Streams a markdown document translation (earnings-call transcripts) via SSE, preserving the markdown structure for progressive rendering.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryStreamDocumentTranslation_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/ai-summary/translation/stream',
    alias: 'aiSummaryStreamTranslation',
    description: `Streams field-by-field translation via SSE for typewriter animation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: aiSummaryStreamTranslation_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/ai-summary/usage',
    alias: 'aiSummaryGetUsage',
    description: `Purchases made in the reader&#x27;s current allowance window - a month stepped from the active subscription&#x27;s period start, or the UTC calendar month without one (one purchase per analysis per reader, permanent; the call analysis and the dividend narrative share the allowance) - the tier cap they count against (&#x60;limit: null&#x60; &#x3D; unlimited), and &#x60;resetsAt&#x60;, the window&#x27;s end. Per reader: without a signed-in user the answer is &#x60;{ used: 0, limit: 0 }&#x60;, never another reader&#x27;s figures.`,
    requestFormat: 'json',
    response: AiUsageResponseDto,
  },
  {
    method: 'get',
    path: '/api/alerts',
    alias: 'alertsFindAll',
    requestFormat: 'json',
    response: z.array(
      z
        .object({
          id: z.string(),
          type: z.enum([
            'price_threshold',
            'fair_value',
            'ex_dividend',
            'insider',
            'calendar_event',
            'news_filter',
            'ir_event',
          ]),
          symbol: z.string().nullable(),
          params: AlertRuleParamsDto,
          isActive: z.boolean(),
          cooldownMinutes: z.coerce.number(),
          lastTriggeredAt: z.string().describe('ISO instant of the last delivery.').nullable(),
          triggerCount: z.coerce.number(),
          createdAt: z.string(),
          pausedByPlan: z
            .boolean()
            .describe(
              'True when the reader’s plan does not cover this rule’s data, so the evaluator is skipping it. Derived per request; independent of isActive, which is the user’s own switch.',
            ),
        })
        .passthrough(),
    ),
  },
  {
    method: 'post',
    path: '/api/alerts',
    alias: 'alertsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: alertsCreate_Body,
      },
    ],
    response: z
      .object({
        id: z.string(),
        type: z.enum([
          'price_threshold',
          'fair_value',
          'ex_dividend',
          'insider',
          'calendar_event',
          'news_filter',
          'ir_event',
        ]),
        symbol: z.string().nullable(),
        params: AlertRuleParamsDto,
        isActive: z.boolean(),
        cooldownMinutes: z.coerce.number(),
        lastTriggeredAt: z.string().describe('ISO instant of the last delivery.').nullable(),
        triggerCount: z.coerce.number(),
        createdAt: z.string(),
        pausedByPlan: z
          .boolean()
          .describe(
            'True when the reader’s plan does not cover this rule’s data, so the evaluator is skipping it. Derived per request; independent of isActive, which is the user’s own switch.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'patch',
    path: '/api/alerts/:id',
    alias: 'alertsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: alertsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        id: z.string(),
        type: z.enum([
          'price_threshold',
          'fair_value',
          'ex_dividend',
          'insider',
          'calendar_event',
          'news_filter',
          'ir_event',
        ]),
        symbol: z.string().nullable(),
        params: AlertRuleParamsDto,
        isActive: z.boolean(),
        cooldownMinutes: z.coerce.number(),
        lastTriggeredAt: z.string().describe('ISO instant of the last delivery.').nullable(),
        triggerCount: z.coerce.number(),
        createdAt: z.string(),
        pausedByPlan: z
          .boolean()
          .describe(
            'True when the reader’s plan does not cover this rule’s data, so the evaluator is skipping it. Derived per request; independent of isActive, which is the user’s own switch.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/alerts/:id',
    alias: 'alertsRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/alerts/calendar/dividends',
    alias: 'alertsDividendCalendar',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/analyst/:id',
    alias: 'analystEntitiesGetAnalyst',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        benzingaId: z.string(),
        fullName: z.string(),
        firmName: z.string().nullable(),
        firmId: z.string().nullable(),
        smartScore: z.coerce.number().describe('Benzinga smart score').nullable(),
        totalRatings: z.coerce.number().nullable(),
        totalRatingsPercentile: z.coerce.number().nullable(),
        successRate: z.coerce.number().describe('Overall success rate %').nullable(),
        avgReturn: z.coerce.number().describe('Overall average return %').nullable(),
        avgReturnPercentile: z.coerce.number().nullable(),
      })
      .passthrough()
      .nullable(),
  },
  {
    method: 'post',
    path: '/api/auth/email-availability',
    alias: 'authCheckEmailAvailability',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string().email().describe('Email address to check') }),
      },
    ],
    response: z
      .object({
        available: z
          .boolean()
          .describe('True when no account with this email exists in the local user store'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/auth/logout-all',
    alias: 'authLogoutAll',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/auth/me',
    alias: 'authGetMe',
    requestFormat: 'json',
    response: z
      .object({
        user: z
          .object({
            id: z.string(),
            email: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            language: z.string(),
            role: z.string().describe('User role'),
            isDemoUser: z.boolean().describe('Whether user is a demo account'),
            createdAt: z.string().datetime({ offset: true }),
            updatedAt: z.string().datetime({ offset: true }),
            lastLoginAt: z.string().datetime({ offset: true }).optional(),
          })
          .passthrough(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/billing/admin/catalog',
    alias: 'billingGetAdminCatalog',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices',
    alias: 'billingAssignPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: billingAssignPrice_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/billing/admin/catalog/prices/:id',
    alias: 'billingDeletePrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/archive',
    alias: 'billingArchivePrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/clear-early-bird',
    alias: 'billingClearEarlyBirdPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/clear-review',
    alias: 'billingClearPriceReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/reassign',
    alias: 'billingReassignPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          productId: z
            .string()
            .uuid()
            .describe('The billing product (our catalog) to move the price to'),
        }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/set-current',
    alias: 'billingSetCurrentPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/set-early-bird',
    alias: 'billingSetEarlyBirdPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/prices/:id/unarchive',
    alias: 'billingUnarchivePrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/products',
    alias: 'billingCreateProduct',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: billingCreateProduct_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/billing/admin/catalog/products/:id',
    alias: 'billingUpdateProduct',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: billingUpdateProduct_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/billing/admin/catalog/products/:id',
    alias: 'billingDeleteProduct',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/catalog/refresh',
    alias: 'billingRefreshCatalog',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/admin/entitlements',
    alias: 'billingGetAdminEntitlements',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/billing/admin/entitlements',
    alias: 'billingUpdateEntitlement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: billingUpdateEntitlement_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/admin/stripe/prices/:id/preview',
    alias: 'billingPreviewStripePrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/admin/stripe/products',
    alias: 'billingBrowseStripeProducts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'startingAfter',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/admin/stripe/products/:id/prices',
    alias: 'billingBrowseStripePrices',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/admin/sync-entitlements',
    alias: 'billingSyncEntitlements',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/cancel',
    alias: 'billingCancelSubscription',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/checkout',
    alias: 'billingCreateCheckout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: billingCreateCheckout_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/entitlements',
    alias: 'billingGetEntitlements',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/invoices',
    alias: 'billingGetInvoices',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/plans',
    alias: 'billingGetPlans',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/plans/me',
    alias: 'billingGetMyPlans',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/portal',
    alias: 'billingCreatePortal',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            returnUrl: z
              .string()
              .url()
              .describe(
                'Absolute URL to return the user to after the Stripe Customer Portal. Honored only when its origin is trusted (CORS allowlist); falls back to the FRONTEND_URL default otherwise.',
              ),
          })
          .partial(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/resume',
    alias: 'billingResumeSubscription',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/billing/subscription',
    alias: 'billingGetSubscription',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/billing/webhook',
    alias: 'billingHandleWebhook',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/bond-terms/preview',
    alias: 'bondTermsPreview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bondTermsPreview_Body,
      },
    ],
    response: BondTermsPreviewDto,
  },
  {
    method: 'post',
    path: '/api/bug-reports',
    alias: 'bugReportsCreate',
    description: `Multipart form: &#x60;files&#x60; (repeatable), &#x60;description&#x60;, &#x60;email&#x60;, &#x60;source&#x60;, &#x60;context&#x60; (JSON string), &#x60;includeImportedFiles&#x60; (&#x27;true&#x27;/&#x27;false&#x27;), &#x60;kind&#x60; (optional, a bug-report category the reporter picked). Sent with a raw multipart POST, not the typed client.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bugReportsCreate_Body,
      },
    ],
    response: z
      .object({
        id: z.string().describe('Id of the stored report'),
        createdAt: z.string().datetime({ offset: true }).describe('When the report was stored'),
        attachmentCount: z
          .coerce.number()
          .describe(
            'How many artifacts were stored with the report (dialog uploads + linked import files)',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Malformed field or file type`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 413,
        description: `Attachment exceeds 50 MB`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/bug-reports',
    alias: 'bugReportsList',
    requestFormat: 'json',
    response: z.array(ReporterBugReportDto),
  },
  {
    method: 'get',
    path: '/api/bug-reports/:number',
    alias: 'bugReportsDetail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
    ],
    response: z
      .object({
        report: ReporterBugReportDto,
        messages: z.array(ReporterMessageDto).describe('Published messages, oldest first'),
        attachments: z
          .array(
            z
              .object({
                id: z.string(),
                originalFilename: z.string(),
                contentType: z.string().nullable(),
                fileSize: z.coerce.number().describe('Bytes'),
              })
              .passthrough(),
          )
          .describe('Files the REPORT was filed with; a reply carries its own'),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Unknown, or not the caller report`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/bug-reports/:number/attachments/:attachmentId/download',
    alias: 'bugReportsDownloadAttachment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
      {
        name: 'attachmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        url: z.string().describe('Presigned S3 URL, valid for 15 minutes'),
        originalFilename: z.string(),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Unknown report, not the caller report, or not an upload of that report`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/bug-reports/:number/messages',
    alias: 'bugReportsPostMessage',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ body: z.string().min(1).max(5000) }),
      },
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
    ],
    response: ReporterMessageDto,
    errors: [
      {
        status: 409,
        description: `The report is not waiting for a reply`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/bug-reports/:number/read',
    alias: 'bugReportsMarkRead',
    requestFormat: 'json',
    parameters: [
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
    ],
    response: z.object({ marked: z.coerce.number() }).passthrough(),
  },
  {
    method: 'post',
    path: '/api/bug-reports/:number/signal',
    alias: 'bugReportsSignal',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bugReportsSignal_Body,
      },
      {
        name: 'number',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
    ],
    response: ReporterMessageDto,
    errors: [
      {
        status: 409,
        description: `&#x60;signal_not_allowed&#x60; or &#x60;window_closed&#x60;`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `&#x60;cooldown&#x60; with &#x60;retryAt&#x60;`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/bug-reports/unread-count',
    alias: 'bugReportsUnreadCount',
    requestFormat: 'json',
    response: z
      .object({
        count: z.coerce.number().describe('Published messages from support the reader has not opened'),
        awaitingReply: z
          .coerce.number()
          .describe('Their reports sitting in needs_info - support asked and has had no answer'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/calendar/dividends',
    alias: 'finnhubCalendarGetDividends',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/dividends/batch',
    alias: 'finnhubCalendarGetDividendsBatch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/earnings',
    alias: 'finnhubCalendarGetEarningsCalendar',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/earnings-calls',
    alias: 'finnhubCalendarGetEarningsCallsLive',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/earnings-calls/batch',
    alias: 'finnhubCalendarGetEarningsCallsBatch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/earnings/batch',
    alias: 'finnhubCalendarGetEarningsBatch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/economic',
    alias: 'finnhubCalendarGetEconomicCalendar',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'countries',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/calendar/holidays',
    alias: 'finnhubCalendarGetMarketHolidays',
    requestFormat: 'json',
    parameters: [
      {
        name: 'exchange',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/charting-presets',
    alias: 'chartingPresetsFindAll',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/charting-presets',
    alias: 'chartingPresetsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: chartingPresetsCreate_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/charting-presets/:id',
    alias: 'chartingPresetsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: chartingPresetsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/charting-presets/:id',
    alias: 'chartingPresetsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/charting-presets/:id/default',
    alias: 'chartingPresetsSetDefault',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/charting-presets/assignments/:symbol',
    alias: 'chartingPresetsResolveAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        presetId: z.string().describe('null = the factory setup').nullable(),
        source: z
          .enum(['assigned', 'default', 'factory'])
          .describe(
            "'assigned' = a saved preset chosen for this company; 'default' = the account default; 'factory' = the factory setup (chosen for this company, or there is no default)",
          ),
      })
      .passthrough(),
  },
  {
    method: 'put',
    path: '/api/charting-presets/assignments/:symbol',
    alias: 'chartingPresetsAssign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          presetId: z
            .string()
            .uuid()
            .describe('The saved preset to open this company on; null = the factory setup')
            .nullable(),
        }),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        presetId: z.string().describe('null = the factory setup').nullable(),
        source: z
          .enum(['assigned', 'default', 'factory'])
          .describe(
            "'assigned' = a saved preset chosen for this company; 'default' = the account default; 'factory' = the factory setup (chosen for this company, or there is no default)",
          ),
      })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/charting-presets/assignments/:symbol',
    alias: 'chartingPresetsUnassign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        presetId: z.string().describe('null = the factory setup').nullable(),
        source: z
          .enum(['assigned', 'default', 'factory'])
          .describe(
            "'assigned' = a saved preset chosen for this company; 'default' = the account default; 'factory' = the factory setup (chosen for this company, or there is no default)",
          ),
      })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/charting-presets/default',
    alias: 'chartingPresetsClearDefault',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/chatbot/models',
    alias: 'chatbotGetModels',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/chatbot/stream',
    alias: 'chatbotStream',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: chatbotStream_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/chatbot/threads/:id/artifacts/:artifactId',
    alias: 'chatbotArtifactsGetArtifact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'artifactId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        title: z.string().describe('Human title the model gave the document.'),
        html: z.string().describe('The complete self-contained HTML document.'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/chatbot/threads/:id/artifacts/:artifactId/repair',
    alias: 'chatbotArtifactsRepairArtifact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            error: z
              .string()
              .max(500)
              .describe(
                'The script error the viewer observed (handler-internal crashes the server cannot reproduce). A locally-reproducible load-time crash takes precedence.',
              ),
          })
          .partial(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'artifactId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        title: z.string().describe('Human title the model gave the document.'),
        html: z.string().describe('The complete self-contained HTML document.'),
      })
      .passthrough(),
    errors: [
      {
        status: 422,
        description: `Nothing to repair, or the fix still crashes.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/chatbot/threads/:id/boards',
    alias: 'chatbotBoardsListBoards',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        boards: z.array(
          z
            .object({
              id: z.string().describe('Board PK (uuid).'),
              boardId: z
                .string()
                .describe('Composer-authored board slug (the URL segment and op target).'),
              title: z.string().describe('Board title (from the latest version).'),
              currentVersion: z.coerce.number().describe('The latest version number (versions are 1..n).'),
              blockCount: z.coerce.number().describe("Number of blocks in the latest version's document."),
              updatedAt: z
                .string()
                .describe('ISO last-materialized timestamp (the gallery sorts by this).'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/chatbot/threads/:id/boards/:boardId',
    alias: 'chatbotBoardsGetBoard',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'boardId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'version',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional(),
      },
    ],
    response: BoardDocResponseDto,
  },
  {
    method: 'get',
    path: '/api/chatbot/threads/:id/boards/:boardId/versions',
    alias: 'chatbotBoardsListVersions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'boardId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        versions: z.array(
          z
            .object({
              version: z.coerce.number().describe('Version number (1..n, append-only).'),
              sourceSeq: z
                .coerce.number()
                .describe(
                  'Message seq of the compose run that produced this version; null when it was minted by a restore.',
                )
                .nullable(),
              blockCount: z.coerce.number().describe("Number of blocks in this version's document."),
              createdAt: z.string().describe('ISO creation timestamp.'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/chatbot/threads/:id/boards/:boardId/versions/:version/restore',
    alias: 'chatbotBoardsRestoreVersion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'boardId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'version',
        type: 'Path',
        schema: z.coerce.number().int(),
      },
    ],
    response: BoardDocResponseDto,
  },
  {
    method: 'post',
    path: '/api/chatbot/threads/:id/compact',
    alias: 'chatbotCompactThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        summary: CompactionSummaryDto.describe(
          'The summary now stored on the thread (the compaction marker renders it).',
        ),
        boundarySeq: z
          .coerce.number()
          .describe('Highest message seq the summary covers — messages after it stay verbatim.'),
        contextTokens: z
          .coerce.number()
          .describe(
            'Re-estimated context size after compaction (summary + surviving tail); the meter drops to this until the next turn reports provider truth.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/chatbot/threads/:id/retitle',
    alias: 'chatbotRetitleThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        title: z
          .string()
          .describe('The newly generated title, already persisted on the conversation.'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/chatbot/threads/:id/stop',
    alias: 'chatbotStopThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        stopped: z
          .boolean()
          .describe(
            'true when a turn was running on this thread and has been stopped — its partial reply is persisted by the time this returns (bounded wait); false when nothing was running.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/chatbot/threads/:id/stream',
    alias: 'chatbotResumeThreadStream',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/company-profiles/:symbol',
    alias: 'companyProfilesGetProfile',
    description: `Fresh profile → served as-is. Stale → served while a background refresh runs. Missing → a research run is enqueued and an empty envelope returns; poll this endpoint.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        status: z.enum(['queued', 'researching', 'ready', 'failed', 'none']),
        refreshing: z
          .boolean()
          .describe('True when a stale profile is served while a refresh runs'),
        generatedAt: z.string().datetime({ offset: true }).nullable(),
        profile: z.object({}).partial().passthrough().nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/company-profiles/:symbol/display',
    alias: 'companyProfilesGetProfileDisplay',
    description: `Narrative, revenue streams and (full profiles only) the supply chain, for product surfaces. NEVER enqueues a research run — unlike the envelope above, this is safe to call on every page render. No ready profile → &#x60;available: false&#x60;.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        available: z.boolean().describe('False = no ready profile; every field below is empty.'),
        generatedAt: z.string().datetime({ offset: true }).nullable(),
        promptVersion: z.string().describe('e.g. `profiler/v7-peers`.').nullable(),
        narrative: z
          .string()
          .describe('One-paragraph executive summary from the research (English).')
          .nullable(),
        revenueStreams: z.array(
          z
            .object({
              name: z.string(),
              share: z
                .object({
                  pct: z.coerce.number().describe('Percent, 0-100.'),
                  basis: z
                    .enum(['reported', 'third_party_estimate', 'imputed'])
                    .describe(
                      'How the figure was established. `reported` = the company stated it; the other two are estimates and the UI marks them as such.',
                    ),
                })
                .passthrough()
                .nullable(),
              description: z.string(),
            })
            .passthrough(),
        ),
        suppliers: z.array(
          z
            .object({
              name: z.string(),
              symbol: z.string().nullable(),
              components: z.array(z.string()),
              supplyShare: z
                .object({
                  pct: z.coerce.number().describe('Percent, 0-100.'),
                  basis: z
                    .enum(['reported', 'third_party_estimate', 'imputed'])
                    .describe(
                      'How the figure was established. `reported` = the company stated it; the other two are estimates and the UI marks them as such.',
                    ),
                })
                .passthrough()
                .describe("Share of the SUBJECT's supply of those components.")
                .nullable(),
              commodityKeys: z.array(z.string()),
            })
            .passthrough(),
        ),
        commodities: z.array(
          z
            .object({
              commodityKey: z.string(),
              label: z.string(),
              cogsShare: z
                .object({
                  pct: z.coerce.number().describe('Percent, 0-100.'),
                  basis: z
                    .enum(['reported', 'third_party_estimate', 'imputed'])
                    .describe(
                      'How the figure was established. `reported` = the company stated it; the other two are estimates and the UI marks them as such.',
                    ),
                })
                .passthrough()
                .describe("Share of the subject's cost of goods sold.")
                .nullable(),
              sourcingRegions: z.array(z.string()),
            })
            .passthrough(),
        ),
        runStatus: z
          .enum(['queued', 'researching', 'ready', 'failed'])
          .describe(
            'Latest run for the symbol: queued/researching = in flight (poll), failed = last attempt broke.',
          )
          .nullable(),
        requestable: z
          .boolean()
          .describe(
            'True when the caller may request a profile: eligible stock, none ready, none in flight.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/company-profiles/:symbol/request',
    alias: 'companyProfilesRequestProfile',
    description: `Enqueues the same profiling run the admin batch uses. Refused with 409 (&#x60;PROFILE_ALREADY_EXISTS&#x60; / &#x60;PROFILE_RECENTLY_FAILED&#x60;) or 429 (&#x60;PROFILE_REQUEST_LIMIT&#x60;, the per-user daily cap); a run already in flight is returned as-is.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({ symbol: z.string(), status: z.enum(['queued', 'researching']), runId: z.string() })
      .passthrough(),
    errors: [
      {
        status: 409,
        description: `Profile exists or the last run is cooling down`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Per-user daily request cap reached`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/comparison-groups',
    alias: 'comparisonGroupsList',
    requestFormat: 'json',
    response: z.array(ComparisonGroupResponseDto),
  },
  {
    method: 'post',
    path: '/api/comparison-groups',
    alias: 'comparisonGroupsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: comparisonGroupsCreate_Body,
      },
    ],
    response: ComparisonGroupResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid payload, or the per-user group cap is reached`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The user already has a group with this name`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/comparison-groups/:id',
    alias: 'comparisonGroupsGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ComparisonGroupResponseDto,
    errors: [
      {
        status: 404,
        description: `No such group, or it belongs to another user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/comparison-groups/:id',
    alias: 'comparisonGroupsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: comparisonGroupsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ComparisonGroupResponseDto,
    errors: [
      {
        status: 404,
        description: `No such group, or it belongs to another user`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The new name is already taken by another group`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/comparison-groups/:id',
    alias: 'comparisonGroupsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        id: z.string().describe('Id of the group that was removed'),
        deleted: z.boolean().describe('Always true — a failed delete is a 404, never `false`'),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `No such group, or it belongs to another user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/comparison/peers',
    alias: 'comparisonGetPeerComparison',
    description: `Compare multiple stocks with comprehensive metrics and rankings`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: comparisonGetPeerComparison_Body,
      },
    ],
    response: z
      .object({
        companies: z
          .array(
            z
              .object({
                symbol: z.string().describe('Stock symbol'),
                name: z.string().describe('Company name'),
                sector: z.string().describe('Sector'),
                industry: z.string().describe('Industry'),
                country: z
                  .string()
                  .describe(
                    "ISO country code of the listing/domicile. Resolved only in 'region' / 'marketCap' comparison mode; null in 'industry' mode (nobody asked for it) or when the registry has no profile for the symbol.",
                  )
                  .nullable(),
                capBucket: z
                  .string()
                  .describe(
                    "Capitalisation bucket (mega | large | mid | small | micro). Resolved only in 'region' / 'marketCap' comparison mode; null otherwise.",
                  )
                  .nullable(),
                marketCap: z.coerce.number().describe('Market capitalization'),
                price: z.coerce.number().describe('Current stock price'),
                growth: z
                  .object({
                    ttmRevenueGrowth: z
                      .coerce.number()
                      .describe('TTM revenue growth percentage')
                      .nullable(),
                    currentYearRevenueGrowth: z
                      .coerce.number()
                      .describe('Current year expected revenue growth')
                      .nullable(),
                    nextYearRevenueGrowth: z
                      .coerce.number()
                      .describe('Next year revenue growth')
                      .nullable(),
                    ttmEpsGrowth: z.coerce.number().describe('TTM EPS growth percentage').nullable(),
                    currentYearEpsGrowth: z
                      .coerce.number()
                      .describe('Current year expected EPS growth')
                      .nullable(),
                    nextYearEpsGrowth: z.coerce.number().describe('Next year EPS growth').nullable(),
                    currentQuarterYoyGrowth: z
                      .coerce.number()
                      .describe('Current quarter YoY growth')
                      .nullable(),
                    twoYearStackedGrowth: z
                      .coerce.number()
                      .describe('2-year stacked growth rate')
                      .nullable(),
                    threeYearCagr: z.coerce.number().describe('3-year CAGR').nullable(),
                  })
                  .passthrough()
                  .describe('Growth metrics'),
                profitability: z
                  .object({
                    grossMargin: z.coerce.number().describe('Gross margin percentage').nullable(),
                    operatingMargin: z.coerce.number().describe('Operating margin percentage').nullable(),
                    netMargin: z.coerce.number().describe('Net margin percentage').nullable(),
                    ebitdaMargin: z.coerce.number().describe('EBITDA margin percentage').nullable(),
                    roe: z.coerce.number().describe('Return on Equity (ROE)').nullable(),
                    roa: z.coerce.number().describe('Return on Assets (ROA)').nullable(),
                    roic: z.coerce.number().describe('Return on Invested Capital (ROIC)').nullable(),
                    fcfMargin: z.coerce.number().describe('Free cash flow margin').nullable(),
                  })
                  .passthrough()
                  .describe('Profitability metrics'),
                valuation: z
                  .object({
                    ttmPE: z.coerce.number().describe('TTM Price-to-Earnings ratio').nullable(),
                    forwardPE: z.coerce.number().describe('Forward P/E ratio').nullable(),
                    ttmPS: z.coerce.number().describe('TTM Price-to-Sales ratio').nullable(),
                    forwardPS: z.coerce.number().describe('Forward P/S ratio').nullable(),
                    priceToBook: z.coerce.number().describe('Price-to-Book ratio').nullable(),
                    pegRatio: z.coerce.number().describe('PEG ratio').nullable(),
                    evToEbitda: z.coerce.number().describe('Enterprise Value to EBITDA').nullable(),
                    evToRevenue: z.coerce.number().describe('Enterprise Value to Revenue').nullable(),
                    fcfYield: z.coerce.number().describe('Free cash flow yield').nullable(),
                  })
                  .passthrough()
                  .describe('Valuation metrics'),
                dataQualityScore: z.coerce.number().describe('Data quality score (0-100)'),
                dataQualityIssues: z
                  .array(
                    z
                      .object({
                        type: z
                          .enum([
                            'insufficient_quarters',
                            'missing_financials',
                            'stale_data',
                            'currency_conversion_failed',
                          ])
                          .describe('Type of data quality issue'),
                        affectedMetrics: z.array(z.string()).describe('Affected metrics category'),
                        message: z.string().describe('Human-readable message'),
                      })
                      .passthrough(),
                  )
                  .describe('Data quality issues that may affect metric accuracy')
                  .optional(),
                lastUpdated: z
                  .string()
                  .datetime({ offset: true })
                  .describe('Last updated timestamp, ISO instant'),
              })
              .passthrough(),
          )
          .describe('Array of company metrics'),
        rankings: z
          .array(
            z
              .object({
                symbol: z.string().describe('Symbol identifier'),
                peerGroup: RankingCategoryDto.describe('Rankings within peer group'),
                sector: RankingCategoryDto.describe('Rankings within sector'),
                market: RankingCategoryDto.describe('Rankings within entire market'),
                metrics: z.record(MetricRankingDto).describe('Individual metric rankings'),
              })
              .passthrough(),
          )
          .describe('Rankings for each company (omitted when includeRankings is false)')
          .optional(),
        metadata: z
          .object({
            requestedSymbols: z
              .array(z.string())
              .describe('Symbols requested, in the caller’s order'),
            successfulSymbols: z.array(z.string()).describe('Symbols that resolved'),
            failedSymbols: z
              .array(
                z
                  .object({
                    symbol: z.string().describe('The symbol that failed'),
                    reason: z
                      .enum(['timeout', 'no_data', 'api_error', 'insufficient_data', 'unknown'])
                      .describe('Reason for failure'),
                    message: z.string().describe('Human-readable error message'),
                  })
                  .passthrough(),
              )
              .describe('Symbols that did not resolve, with why'),
            timestamp: z
              .string()
              .datetime({ offset: true })
              .describe('When this comparison was computed, ISO instant'),
            cacheTTL: z.coerce.number().describe('Response cache lifetime in milliseconds'),
            medians: z
              .object({
                marketCap: z.coerce.number().describe('Median market capitalization').nullable(),
                ttmPE: z.coerce.number().describe('Median TTM P/E').nullable(),
                ttmRevenueGrowth: z.coerce.number().describe('Median TTM revenue growth (%)').nullable(),
                netMargin: z.coerce.number().describe('Median net margin (%)').nullable(),
                roe: z.coerce.number().describe('Median return on equity (%)').nullable(),
              })
              .passthrough()
              .describe('Group medians over the returned peer set'),
          })
          .passthrough()
          .describe('Comparison metadata'),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/comparison/rankings',
    alias: 'comparisonGetGroupRankings',
    description: `Percentile rankings for a whole peer group in one request — the percentile floor needs the full group, which a batched metrics fetch can never assemble.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: comparisonGetGroupRankings_Body,
      },
    ],
    response: z
      .object({
        rankings: z
          .array(
            z
              .object({
                symbol: z.string().describe('Symbol identifier'),
                peerGroup: RankingCategoryDto.describe('Rankings within peer group'),
                sector: RankingCategoryDto.describe('Rankings within sector'),
                market: RankingCategoryDto.describe('Rankings within entire market'),
                metrics: z.record(MetricRankingDto).describe('Individual metric rankings'),
              })
              .passthrough(),
          )
          .describe(
            'One entry per symbol that resolved, ranked against the whole group. Empty when fewer than two symbols resolved. A percentile inside an entry is still null when the group is under the five-member floor — how many companies reported the metric is on the entry itself (`outOf`).',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/contact/inquire',
    alias: 'contactCreateInquiry',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: contactCreateInquiry_Body,
      },
    ],
    response: z
      .object({
        success: z.boolean().describe('Success status of the operation'),
        message: z.string().describe('Response message'),
        data: z
          .object({ id: z.string(), createdAt: z.string() })
          .passthrough()
          .describe('Contact inquiry data')
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/conversations',
    alias: 'conversationsList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(20),
      },
    ],
    response: z
      .object({
        data: z
          .array(
            z
              .object({
                id: z.string().describe('Conversation id (the client Chat id).'),
                title: z.string().describe('Auto-generated thread title.').nullable(),
                messageCount: z.coerce.number().describe('Number of persisted messages in the thread.'),
                createdAt: z.string().describe('ISO creation timestamp.'),
                updatedAt: z
                  .string()
                  .describe('ISO last-updated timestamp (list sorts by this, desc).'),
              })
              .passthrough(),
          )
          .describe('This page of conversations.'),
        meta: z
          .object({
            total: z.coerce.number().describe('Total number of conversations for the user.'),
            page: z.coerce.number().describe('Current page number (1-based).'),
            limit: z.coerce.number().describe('Conversations per page.'),
            totalPages: z.coerce.number().describe('Total number of pages.'),
          })
          .passthrough()
          .describe('Pagination metadata.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/conversations/:id',
    alias: 'conversationsGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ConversationDetailDto,
  },
  {
    method: 'delete',
    path: '/api/conversations/:id',
    alias: 'conversationsRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'patch',
    path: '/api/conversations/:id',
    alias: 'conversationsRename',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ title: z.string().min(1).max(200).describe('New conversation title.') }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ConversationDetailDto,
  },
  {
    method: 'get',
    path: '/api/conversations/:id/attachments/:attachmentId',
    alias: 'conversationsGetAttachmentUrl',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'attachmentId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        url: z.string().describe('Presigned S3 URL for the attachment (expires in 15 minutes).'),
      })
      .passthrough(),
  },
  {
    method: 'patch',
    path: '/api/conversations/:id/settings',
    alias: 'conversationsUpdateSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: conversationsUpdateSettings_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        pinned: z
          .object({})
          .partial()
          .passthrough()
          .describe('The stored pinned bag after the write (validated; `{}` = nothing pinned).'),
        instructions: z.string().describe('Thread instructions; null = none.').nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/copilot/memories',
    alias: 'copilotSettingsListMemories',
    requestFormat: 'json',
    response: z
      .object({
        memories: z.array(
          z
            .object({
              id: z.string().describe('Memory id (the delete target).'),
              content: z.string().describe('The memory text, verbatim.'),
              createdAt: z.string().describe('ISO creation timestamp.'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/copilot/memories',
    alias: 'copilotSettingsClearMemories',
    requestFormat: 'json',
    response: z
      .object({ deleted: z.coerce.number().describe('Number of memories deleted.') })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/copilot/memories/:id',
    alias: 'copilotSettingsDeleteMemory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/copilot/settings',
    alias: 'copilotSettingsGetSettings',
    requestFormat: 'json',
    response: CopilotSettingsDto,
  },
  {
    method: 'patch',
    path: '/api/copilot/settings',
    alias: 'copilotSettingsUpdateSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: copilotSettingsUpdateSettings_Body,
      },
    ],
    response: CopilotSettingsDto,
  },
  {
    method: 'get',
    path: '/api/exchange-rates',
    alias: 'exchangeRatesGetLatestRates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        result: z.string().describe('Result status from the API'),
        base_code: z.string().describe('Base currency code'),
        conversion_rates: z
          .record(z.coerce.number())
          .describe('Object containing conversion rates for all supported currencies'),
        time_last_update_unix: z.coerce.number().describe('Unix timestamp of last update'),
      })
      .passthrough(),
    errors: [
      {
        status: 503,
        description: `Exchange rates unavailable`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/exchange-rates/history',
    alias: 'exchangeRatesGetHistoricalRates',
    description: `Fetch historical rates for a specific date (from API) or date range (from cache only). Note: Date ranges only return previously cached data. The ExchangeRate API only supports single date queries.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'targetCurrency',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'date',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        baseCurrency: z.string().describe('Base currency code'),
        targetCurrency: z.string().describe('Target currency code'),
        rate: z.coerce.number().describe('Exchange rate'),
        date: z.string().describe('Date of the exchange rate'),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Rate not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/exchange-rates/history/range',
    alias: 'exchangeRatesGetDailyRateSeries',
    description: `DB-first daily series for ONE currency pair over a date range, gap-filled from the ECB (frankfurter) and persisted, so the second request for a range is a pure database read. A rate MULTIPLIES a baseCurrency amount to give targetCurrency. The series covers BUSINESS DAYS ONLY: weekends and market holidays are ABSENT, never forward-filled server-side — forward-fill the last known rate in the client. Distinct from &#x60;GET /exchange-rates/history?startDate&amp;endDate&#x60;, which is cache-only and never fetches.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'targetCurrency',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        baseCurrency: z.string().describe('Base currency, normalized (GBX/GBp -> GBP)'),
        targetCurrency: z.string().describe('Target currency, normalized (GBX/GBp -> GBP)'),
        startDate: z.string().describe('Requested range start (inclusive)'),
        endDate: z.string().describe('Requested range end (inclusive)'),
        coverage: z
          .literal('business-days')
          .describe(
            'The calendar the series is published on. `business-days`: ONLY days the FX source actually published are present — weekends and market holidays are ABSENT, not filled. The series is deliberately NOT gap-free; forward-fill the last known rate client-side (see apps/web/src/core/fx). Empty when baseCurrency === targetCurrency.',
          ),
        points: z
          .array(
            z
              .object({
                date: z.string().describe('Calendar day (YYYY-MM-DD, UTC)'),
                rate: z
                  .coerce.number()
                  .describe(
                    'MULTIPLY a baseCurrency amount by this to get targetCurrency (a row (USD, EUR, 0.9187) means 1 USD = 0.9187 EUR). Inverses are never stored nor derived.',
                  ),
              })
              .passthrough(),
          )
          .describe('The daily rates, ascending by date.'),
        fetched: z
          .coerce.number()
          .describe('How many points this request fetched from the FX source and persisted.'),
        partial: z
          .boolean()
          .describe(
            'True when a known gap could NOT be filled (quota ceiling reached, or the FX source failed). The points returned are still valid — the series is just incomplete, and a later request will try again.',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid currency code or date range`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/filings/events',
    alias: 'filingsGetEvents',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.coerce.number().int().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        events: z.array(
          z
            .object({
              id: z.string(),
              source: z.string().describe("Regulator feed, e.g. 'gpw_espi'"),
              symbol: z.string().describe("Trading symbol, e.g. 'SNT.WA'").nullable(),
              issuerName: z.string(),
              reportSystem: z.string().describe("e.g. 'ESPI' | 'EBI'").nullable(),
              reportNumber: z
                .string()
                .describe("Issuer's report number, e.g. '15/2026'")
                .nullable(),
              title: z.string(),
              summaryEn: z.string().describe('Canonical LLM summary (English)').nullable(),
              summaryPl: z.string().describe('Polish rendering of the summary').nullable(),
              classification: z
                .string()
                .describe(
                  "Event type from the legal-basis classification, e.g. 'inside_information' | 'managers_transactions' | 'major_holdings' | 'corporate_action' | 'periodic_cover' | 'corporate_governance' | 'other_regulatory'; null = unclassified",
                )
                .nullable(),
              publishedAt: z.string().datetime({ offset: true }),
              sourceUrl: z.string().describe('Link to the official report at the source'),
            })
            .passthrough(),
        ),
        hasMore: z.boolean().describe('True when more events exist beyond this page'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/bonds/:symbol',
    alias: 'finnhubEtfGetEtfBondData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/country/:symbol',
    alias: 'finnhubEtfGetEtfCountryExposure',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/holdings/:symbol',
    alias: 'finnhubEtfGetEtfHoldings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/performance/:symbol',
    alias: 'finnhubEtfGetEtfFundPerformance',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/profile/:symbol',
    alias: 'finnhubEtfGetEtfProfile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/etf/sector/:symbol',
    alias: 'finnhubEtfGetEtfSectorExposure',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/finnhub/financials/earnings/:symbol',
    alias: 'finnhubFinancialsGetEarnings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/firm/:id',
    alias: 'analystEntitiesGetFirm',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        benzingaId: z.string(),
        name: z.string(),
        analystCount: z.coerce.number().describe('Number of analysts at the firm'),
        totalRatings: z.coerce.number().describe('Total rated calls across analysts').nullable(),
        avgSuccessRate: z.coerce.number().describe('Rating-weighted avg success rate %').nullable(),
        avgReturn: z.coerce.number().describe('Rating-weighted avg return %').nullable(),
      })
      .passthrough()
      .nullable(),
  },
  {
    method: 'get',
    path: '/api/fund-units/series',
    alias: 'fundUnitsSearchSeries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: z.string().min(2).max(80),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(50).optional(),
      },
    ],
    response: FundUnitSeriesListDto,
  },
  {
    method: 'get',
    path: '/api/fund-units/series/:seriesId/price',
    alias: 'fundUnitsSeriesVnav',
    requestFormat: 'json',
    parameters: [
      {
        name: 'seriesId',
        type: 'Path',
        schema: z.coerce.number().int().gte(1),
      },
      {
        name: 'day',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: FundUnitSeriesPriceDto,
  },
  {
    method: 'get',
    path: '/api/health',
    alias: 'healthGetHealth',
    requestFormat: 'json',
    response: z.object({ status: z.literal('ok') }).passthrough(),
  },
  {
    method: 'get',
    path: '/api/images/logo/:symbol',
    alias: 'imagesGetLogo',
    description: `Returns a resized and optimized company logo image. Images are cached for 7 days. Supports WebP (default) and PNG formats.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'size',
        type: 'Query',
        schema: z.coerce.number().int().optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['webp', 'png']).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No logo available for this symbol`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `Failed to fetch or process logo`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/analyst-ratings',
    alias: 'analystRatingsGetAnalystRatings',
    description: `Returns Finnhub upgrade/downgrade rows (coverage never regresses) enriched with the real Benzinga price target where the security matches; &#x27;—&#x27; (null PT) on no match. Includes the Benzinga consensus when available. 30-min Redis-cached. PAGED, newest first: the merge reaches ~1000 rows for a US mega-cap, so &#x60;ratings&#x60; is a window and &#x60;total&#x60; is the archive.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        ratings: z
          .array(
            z
              .object({
                firm: z.string().describe('Analyst firm'),
                analyst: z.string().describe('Analyst (Benzinga only)').nullable(),
                action: z.string().describe('Rating action'),
                fromGrade: z.string().nullable(),
                toGrade: z.string().nullable(),
                gradeTime: z.coerce.number().describe('Rating date as unix seconds'),
                date: z.string().describe('Rating date (YYYY-MM-DD)'),
                priceTarget: z
                  .coerce.number()
                  .describe('Benzinga price target (trading currency)')
                  .nullable(),
                previousPriceTarget: z.coerce.number().describe('Previous price target').nullable(),
                priceTargetAction: z.string().describe('Price-target action').nullable(),
                pricePercentChange: z.coerce.number().describe('Pre-computed PT % change').nullable(),
                importance: z.coerce.number().describe('Benzinga importance (0–5)').nullable(),
                newsUrl: z.string().nullable(),
                currency: z.string().nullable(),
                benzingaAnalystId: z
                  .string()
                  .describe('Benzinga analyst id (for the entity dialog)')
                  .nullable(),
                benzingaFirmId: z.string().describe('Benzinga firm id').nullable(),
                source: z.enum(['finnhub', 'benzinga', 'merged']).describe('Provenance of the row'),
              })
              .passthrough(),
          )
          .describe('Merged rating rows (newest first)'),
        consensus: z
          .object({
            consensusRating: z.string().nullable(),
            consensusRatingValue: z.coerce.number().nullable(),
            consensusPriceTarget: z.coerce.number().nullable(),
            lowPriceTarget: z.coerce.number().nullable(),
            highPriceTarget: z.coerce.number().nullable(),
            strongBuy: z.coerce.number().nullable(),
            buy: z.coerce.number().nullable(),
            hold: z.coerce.number().nullable(),
            sell: z.coerce.number().nullable(),
            strongSell: z.coerce.number().nullable(),
            ratingsContributors: z.coerce.number().nullable(),
            priceTargetContributors: z.coerce.number().nullable(),
          })
          .passthrough()
          .describe(
            'Current consensus, derived from the Finnhub price-target and latest recommendation-trend legs (never an all-time vendor aggregate)',
          )
          .nullable(),
        tradingCurrency: z.string().describe('Currency of the price targets').nullable(),
        finnhubCount: z.coerce.number().describe('Finnhub base row count (coverage floor)'),
        benzingaCount: z.coerce.number().describe('Benzinga rows that enriched/added a price target'),
        total: z
          .coerce.number()
          .describe(
            'Merged rows available for this symbol — the count `ratings` is a window INTO, not its length. A US mega-cap really does reach ~1000 rows, which is why the feed pages.',
          ),
        limit: z.coerce.number().describe('Window size served (rows in `ratings`, at most)'),
        offset: z.coerce.number().describe('Window start, 0-based, into the newest-first list'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/detail',
    alias: 'instrumentsGetInstrumentDetail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        id: z.string(),
        symbol: z.string(),
        name: z.string(),
        exchange: z.string().nullable(),
        type: z.string(),
        currency: z
          .string()
          .describe('Normalized MAJOR-unit trading currency label (GBX folds to GBP).')
          .nullable(),
        isin: z
          .string()
          .describe('International Securities Identification Number, when known.')
          .nullable(),
        wkn: z
          .string()
          .describe(
            'German Wertpapierkennnummer, derived from a DE-issued ISIN (fox F0). Null for non-German ISINs — a WKN assigned by German exchanges to a foreign listing is not derivable from the ISIN and is never fabricated.',
          )
          .nullable(),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Instrument not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/fundamentals/complete',
    alias: 'finnhubFundamentalsGetCompleteFundamentals',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'period',
        type: 'Query',
        schema: z.enum(['annual', 'quarterly', 'ttm', 'ytd']).optional().default('quarterly'),
      },
      {
        name: 'preliminary',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'includeNews',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'includePeers',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'bypassCache',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'currency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'frequency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/fundamentals/financial-forecasts',
    alias: 'finnhubFundamentalsGetFinancialForecasts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'periods',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        recommendations: z
          .array(z.unknown())
          .describe('Recommendation trend rows (buy/hold/sell counts over time)'),
        priceTarget: z
          .object({
            symbol: z.string().optional(),
            targetHigh: z.coerce.number().describe('High target (trading currency)'),
            targetLow: z.coerce.number().describe('Low target (trading currency)'),
            targetMean: z.coerce.number().describe('Mean target (trading currency)'),
            targetMedian: z.coerce.number().describe('Median target (trading currency)'),
            numberAnalysts: z.coerce.number().optional(),
            lastUpdated: z.string().optional(),
          })
          .passthrough()
          .describe('Consensus price target')
          .nullable(),
        forecasts: z
          .array(
            z
              .object({
                period: z.string().describe('Display period label'),
                targetDate: z.string().describe('Period end date (ISO)'),
                isEstimate: z
                  .boolean()
                  .describe('true = analyst estimate, false = reported actual'),
                revenue: ForecastLegDto.describe('Revenue (trading currency)').nullable(),
                eps: EarningsLegDto.describe(
                  'EPS (per-ADS, trading currency), with the basis it is measured on',
                ).nullable(),
                netIncome: EarningsLegDto.describe(
                  'Net income (trading currency), with the basis it is measured on',
                ).nullable(),
                grossIncome: ForecastLegDto.describe(
                  'Gross income / gross profit (trading currency)',
                ).nullable(),
                pretaxIncome: ForecastLegDto.describe(
                  'Pretax income (trading currency)',
                ).nullable(),
                ebitda: ForecastLegDto.describe('EBITDA (trading currency)').nullable(),
                ebit: ForecastLegDto.describe('EBIT (trading currency)').nullable(),
                sgaExpense: ForecastLegDto.describe('SG&A expense (trading currency)').nullable(),
                dps: ForecastLegDto.describe(
                  'Dividend per share (per-ADS, trading currency)',
                ).nullable(),
                ocf: ForecastLegDto.describe('Operating cash flow (trading currency)').nullable(),
                capex: ForecastLegDto.describe(
                  'Capital expenditure — positive spend magnitude (trading currency)',
                ).nullable(),
                fcf: ForecastLegDto.describe(
                  'Free cash flow — Finnhub consensus, not OCF − Capex (trading currency)',
                ).nullable(),
                revenueGrowth: z.coerce.number().describe('YoY revenue growth %').nullish(),
                epsGrowth: z.coerce.number().describe('YoY EPS growth %').nullish(),
                ebitdaGrowth: z.coerce.number().describe('YoY EBITDA growth %').nullish(),
                ebitGrowth: z.coerce.number().describe('YoY EBIT growth %').nullish(),
                netIncomeGrowth: z.coerce.number().describe('YoY net-income growth %').nullish(),
                grossIncomeGrowth: z.coerce.number().describe('YoY gross-income growth %').nullish(),
                pretaxIncomeGrowth: z.coerce.number().describe('YoY pretax-income growth %').nullish(),
                dpsGrowth: z.coerce.number().describe('YoY dividend-per-share growth %').nullish(),
                ocfGrowth: z.coerce.number().describe('YoY operating-cash-flow growth %').nullish(),
                capexGrowth: z.coerce.number().describe('YoY capex growth %').nullish(),
                fcfGrowth: z.coerce.number().describe('YoY free-cash-flow growth %').nullish(),
              })
              .passthrough(),
          )
          .describe('Forecast series (oldest → newest)'),
        annualForecasts: z
          .array(
            z
              .object({
                period: z.string().describe('Display period label'),
                targetDate: z.string().describe('Period end date (ISO)'),
                isEstimate: z
                  .boolean()
                  .describe('true = analyst estimate, false = reported actual'),
                revenue: ForecastLegDto.describe('Revenue (trading currency)').nullable(),
                eps: EarningsLegDto.describe(
                  'EPS (per-ADS, trading currency), with the basis it is measured on',
                ).nullable(),
                netIncome: EarningsLegDto.describe(
                  'Net income (trading currency), with the basis it is measured on',
                ).nullable(),
                grossIncome: ForecastLegDto.describe(
                  'Gross income / gross profit (trading currency)',
                ).nullable(),
                pretaxIncome: ForecastLegDto.describe(
                  'Pretax income (trading currency)',
                ).nullable(),
                ebitda: ForecastLegDto.describe('EBITDA (trading currency)').nullable(),
                ebit: ForecastLegDto.describe('EBIT (trading currency)').nullable(),
                sgaExpense: ForecastLegDto.describe('SG&A expense (trading currency)').nullable(),
                dps: ForecastLegDto.describe(
                  'Dividend per share (per-ADS, trading currency)',
                ).nullable(),
                ocf: ForecastLegDto.describe('Operating cash flow (trading currency)').nullable(),
                capex: ForecastLegDto.describe(
                  'Capital expenditure — positive spend magnitude (trading currency)',
                ).nullable(),
                fcf: ForecastLegDto.describe(
                  'Free cash flow — Finnhub consensus, not OCF − Capex (trading currency)',
                ).nullable(),
                revenueGrowth: z.coerce.number().describe('YoY revenue growth %').nullish(),
                epsGrowth: z.coerce.number().describe('YoY EPS growth %').nullish(),
                ebitdaGrowth: z.coerce.number().describe('YoY EBITDA growth %').nullish(),
                ebitGrowth: z.coerce.number().describe('YoY EBIT growth %').nullish(),
                netIncomeGrowth: z.coerce.number().describe('YoY net-income growth %').nullish(),
                grossIncomeGrowth: z.coerce.number().describe('YoY gross-income growth %').nullish(),
                pretaxIncomeGrowth: z.coerce.number().describe('YoY pretax-income growth %').nullish(),
                dpsGrowth: z.coerce.number().describe('YoY dividend-per-share growth %').nullish(),
                ocfGrowth: z.coerce.number().describe('YoY operating-cash-flow growth %').nullish(),
                capexGrowth: z.coerce.number().describe('YoY capex growth %').nullish(),
                fcfGrowth: z.coerce.number().describe('YoY free-cash-flow growth %').nullish(),
              })
              .passthrough(),
          )
          .describe(
            'Annual income-statement series for the net-income / margin-waterfall / dividend charts (per forecast year). Equals `forecasts` when that series is already annual.',
          ),
        tradingCurrency: z.string().describe('Currency of all monetary values').nullable(),
        reportingCurrency: z
          .string()
          .describe("Issuer's reporting currency (differs from tradingCurrency for ADRs)")
          .nullish(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/fundamentals/metric-catalog',
    alias: 'finnhubFundamentalsGetMetricCatalog',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'currency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        total: z.coerce.number().describe('Total number of catalog entries'),
        currency: z
          .string()
          .describe(
            'Display currency applied to currency-class values (uppercase ISO), or null for the reporting/trading basis',
          )
          .nullable(),
        categories: z
          .array(
            z
              .object({
                category: z.string().describe('Registry category slug'),
                count: z.coerce.number().describe('Number of catalog entries in this category'),
              })
              .passthrough(),
          )
          .describe('Per-category entry counts'),
        metrics: z
          .array(
            z
              .object({
                key: z.string().describe('Canonical metric key (the kernel registry key)'),
                seriesKey: z
                  .string()
                  .describe(
                    'The served series id the chart draws for this row — equals `key` unless the series rides a registry alias (e.g. revenueGrowthYoY is drawn from the served revenueYoY)',
                  ),
                name: z.string().describe('Human-readable metric name (EN)'),
                category: z
                  .string()
                  .describe(
                    "Registry category slug ('raw-financials', 'valuation-multiples', 'margins', ...), or 'other' for a served key outside the registry",
                  ),
                unit: z
                  .string()
                  .describe(
                    "Unit from the kernel registry ('ratio' | 'percent' | 'currency' | 'currency-per-share' | 'shares' | 'days' | 'score'); null when the key is not registered",
                  )
                  .nullable(),
                latestValue: z
                  .coerce.number()
                  .describe(
                    'Latest reported value (the TTM/LTM snapshot where precomputed, else the last non-estimate datapoint); null when no history exists',
                  )
                  .nullable(),
                latestPeriodEnd: z
                  .string()
                  .describe(
                    'ISO end date (YYYY-MM-DD) of the period behind the latest reported datapoint',
                  )
                  .nullable(),
                latestPeriodLabel: z
                  .string()
                  .describe(
                    "Period stamp for `latestValue`, naming its BASIS — 'Q3 '25' when the value is that single reported period, 'TTM · Q3 '25' when it is the trailing-twelve-month snapshot ending there (flows, per-share flows and the TTM-denominated valuation ratios)",
                  )
                  .nullable(),
                historyPoints: z.coerce.number().describe('Count of reported (non-estimate) datapoints'),
                hasHistory: z
                  .boolean()
                  .describe('True when at least one reported datapoint exists'),
                hasForecast: z
                  .boolean()
                  .describe(
                    "True when analyst consensus exists for this metric for this symbol (a non-null forecast leg on an estimate row) — drives the fox forecast ('e') column availability",
                  ),
              })
              .passthrough(),
          )
          .describe('Catalog entries, ordered by category then name'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/fundamentals/period-bounds',
    alias: 'finnhubFundamentalsGetPeriodBounds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'frequency',
        type: 'Query',
        schema: z.enum(['annual', 'quarterly']).optional(),
      },
    ],
    response: z.object({ first: z.string().nullable(), last: z.string().nullable() }).passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/overview-metrics',
    alias: 'finnhubFundamentalsGetOverviewMetrics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        reportingCadence: z.string(),
        displayCurrency: z.string().nullable(),
        lifecycleStatus: z.string().nullable(),
        successorSymbol: z.string().nullable(),
        priceStale: z.boolean(),
        distributionYield: z.coerce.number().nullable(),
        isBank: z.boolean(),
        isDepositLender: z.boolean(),
        isMiner: z.boolean(),
        isMortgageReit: z.boolean(),
        overviewKind: z
          .enum(['company', 'bank', 'insurer', 'reit'])
          .describe(
            'Which Overview metric set the issuer opens on: bank (deposit-taking), insurer, reit (equity or mortgage), else company',
          ),
        marketCap: z.coerce.number().nullable(),
        enterpriseValue: z.coerce.number().nullable(),
        sharesOutstanding: z.coerce.number().nullable(),
        revenue: z.coerce.number().nullable(),
        netIncome: z.coerce.number().nullable(),
        ttmEps: z.coerce.number().nullable(),
        bookValuePerShare: z.coerce.number().nullable(),
        revenuePerShare: z.coerce.number().nullable(),
        operatingCashFlowPerShare: z.coerce.number().nullable(),
        freeCashFlowPerShare: z.coerce.number().nullable(),
        employees: z.coerce.number().nullable(),
        peRatio: z.coerce.number().nullable(),
        pbRatio: z.coerce.number().nullable(),
        psRatio: z.coerce.number().nullable(),
        evToSales: z.coerce.number().nullable(),
        evToEbitda: z.coerce.number().nullable(),
        pFcf: z.coerce.number().nullable(),
        evToGrossProfit: z.coerce.number().nullable(),
        grossMargin: z.coerce.number().nullable(),
        ebitdaMargin: z.coerce.number().nullable(),
        operatingMargin: z.coerce.number().nullable(),
        pretaxMargin: z.coerce.number().nullable(),
        netMargin: z.coerce.number().nullable(),
        fcfMargin: z.coerce.number().nullable(),
        roa: z.coerce.number().nullable(),
        rota: z.coerce.number().nullable(),
        roe: z.coerce.number().nullable(),
        roic: z.coerce.number().nullable(),
        assetTurnover: z.coerce.number().nullable(),
        priceTarget: z.coerce.number().nullable(),
        forwardPe: z.coerce.number().nullable(),
        forwardPs: z.coerce.number().nullable(),
        pegRatio: z.coerce.number().nullable(),
        forwardEvSales: z.coerce.number().nullable(),
        forwardEvEbitda: z.coerce.number().nullable(),
        forwardPFcf: z.coerce.number().nullable(),
        cash: z.coerce.number().nullable(),
        netDebt: z.coerce.number().nullable(),
        revenueGrowth3yr: z.coerce.number().nullable(),
        revenueGrowth5yr: z.coerce.number().nullable(),
        revenueGrowth10yr: z.coerce.number().nullable(),
        epsGrowth3yr: z.coerce.number().nullable(),
        epsGrowth5yr: z.coerce.number().nullable(),
        epsGrowth10yr: z.coerce.number().nullable(),
        revenueGrowthFwd: z.coerce.number().nullable(),
        ebitdaGrowthFwd: z.coerce.number().nullable(),
        epsGrowthFwd: z.coerce.number().nullable(),
        epsLtGrowthEst: z.coerce.number().nullable(),
        dividendYield: z.coerce.number().nullable(),
        payoutRatio: z.coerce.number().nullable(),
        dividendPerShare: z.coerce.number().nullable(),
        dpsGrowth3yr: z.coerce.number().nullable(),
        dpsGrowth5yr: z.coerce.number().nullable(),
        dpsGrowth10yr: z.coerce.number().nullable(),
        metricNotMeaningful: z
          .record(z.string())
          .describe(
            'metric id → not-meaningful reason for a hard-identity null (negative_equity | not_meaningful)',
          ),
      })
      .partial()
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/congressional-trading',
    alias: 'instrumentOwnershipGetCongressionalTrading',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        data: z.array(
          z
            .object({
              name: z.string().describe('Filing member of Congress.'),
              filingDate: z.string().describe('Disclosure filing date (YYYY-MM-DD).'),
              transactionDate: z.string().describe('Trade date (YYYY-MM-DD).'),
              owner: z.string().describe('Whose account traded — self, spouse, joint, child.'),
              transactionType: z.string().describe('Purchase or sale, as filed.'),
              amount: z
                .coerce.number()
                .describe('Legacy single-value amount. Prefer the amountFrom/amountTo range.')
                .nullable(),
              amountFrom: z.coerce.number().describe('Lower bound of the disclosed range.').nullable(),
              amountTo: z.coerce.number().describe('Upper bound of the disclosed range.').nullable(),
              party: z.string().describe('Party affiliation.').optional(),
              state: z.string().describe('State represented.').optional(),
              chamber: z.string().describe('House or Senate.'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/executives',
    alias: 'instrumentOwnershipGetCompanyExecutives',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string().optional(),
        executive: z.array(
          z
            .object({
              name: z.string(),
              age: z.coerce.number().nullable(),
              compensation: z.coerce.number().describe('Total compensation in `currency`.').nullable(),
              currency: z.string().nullable(),
              position: z
                .string()
                .describe("Role, e.g. 'Chief Executive Officer, Director'.")
                .nullable(),
              sex: z.string().nullable(),
              since: z.string().describe('In role / at the company since.').nullable(),
              securitiesOwned: z
                .coerce.number()
                .describe('Shares held, matched from the most recent Form 4.'),
              ownershipFilingDate: z
                .string()
                .describe('Filing date behind securitiesOwned, or null when no Form 4 matched.')
                .nullable(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/fund',
    alias: 'instrumentOwnershipGetFundOwnership',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        ownership: z.array(
          z
            .object({
              name: z.string().describe('Holder name as filed.'),
              share: z.coerce.number().describe('Shares held.'),
              change: z.coerce.number().describe('Share change versus the previous filing.'),
              filingDate: z.string().describe('Filing date (YYYY-MM-DD).'),
              portfolioPercent: z
                .coerce.number()
                .describe("% of the FUND's own portfolio this position represents."),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/history',
    alias: 'ownershipHistoryGetOwnershipHistory',
    description: `The daily &#x60;(symbol x date)&#x60; series of the two headline ownership percentages, ascending by date. The values are the EXACT aggregates the ownership page renders (snapshotted on the day they were observed), so the chart and the page cannot drift. A percent of null means &#x27;sampled, no value&#x27; — never a fabricated 0; a MISSING date means the daily sweep did not run.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'months',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        points: z
          .array(
            z
              .object({
                date: z
                  .string()
                  .describe('UTC calendar day the observation was taken (YYYY-MM-DD).'),
                institutionalOwnershipPercent: z
                  .coerce.number()
                  .describe(
                    "% of shares outstanding held by institutional filers on that day. NULL means 'sampled, no value' — the vendor table was empty or shares outstanding was unavailable. Never a fabricated 0: 0% institutional ownership is a real, alarming claim, not an absence.",
                  )
                  .nullable(),
                insiderOwnershipPercent: z
                  .coerce.number()
                  .describe(
                    "% held by individual (insider / 13D-G) major holders on that day. NULL means 'sampled, no value' — never a fabricated 0.",
                  )
                  .nullable(),
              })
              .passthrough(),
          )
          .describe(
            'The daily series, ASCENDING by date, bounded by the requested window. A MISSING date means the daily sweep did not run — a sampled day with no vendor value is present with null percents.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/holders',
    alias: 'instrumentOwnershipGetHolderPage',
    description: `Server-side search/sort/paging over the FULL holder table the vendor already returns, so the wire carries one page instead of several thousand filers. The institutions view reads the SAME full holder table the institutional-ownership KPI aggregates, so the two numbers on the card can never disagree. &#x60;total&#x60; counts the SEARCH result; &#x60;totalFilers&#x60; counts the full, unsearched table; &#x60;concentration&#x60; (Top 3/10/25) is computed over that same full table.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'view',
        type: 'Query',
        schema: z.enum(['institutions', 'funds']).optional(),
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'sortKey',
        type: 'Query',
        schema: z.enum(['name', 'share', 'percent', 'change', 'date']).optional(),
      },
      {
        name: 'sortDir',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        view: z.enum(['institutions', 'funds']).describe('Which holder family this page covers.'),
        rows: z.array(
          z
            .object({
              name: z.string().describe('Holder name as filed.'),
              share: z.coerce.number().describe('Shares held.'),
              percent: z
                .coerce.number()
                .describe(
                  '% of shares outstanding this holder owns. Null when shares outstanding is unavailable — never a fabricated 0.',
                )
                .nullable(),
              change: z.coerce.number().describe('Share change versus the previous filing.'),
              filingDate: z.string().describe('Filing date (YYYY-MM-DD).'),
              reportDate: z
                .string()
                .describe(
                  '13F report period. Absent on the institutions and funds tables, which the vendor publishes with a filing date only.',
                )
                .optional(),
              portfolioPercent: z
                .coerce.number()
                .describe(
                  "% of the FUND's own portfolio this position represents (fund rows only).",
                )
                .optional(),
              badge: z
                .enum(['new-position', 'unchanged', 'stale-report'])
                .describe(
                  'Provenance marker: new-position (the whole stake arrived this quarter), unchanged (held steady), stale-report (filed for an older 13F than the newest on file). Null for an ordinary move.',
                )
                .nullable(),
            })
            .passthrough(),
        ),
        total: z.coerce.number().describe("Rows matching the search — the denominator of '10 of 4 812'."),
        page: z.coerce.number().describe('1-based page number.'),
        pageSize: z.coerce.number(),
        totalFilers: z
          .coerce.number()
          .describe(
            'Holders on file in the FULL, unsearched table — the denominator that does not move when the reader types a search. Institutions view only; null elsewhere.',
          )
          .nullable(),
        asOfReportDate: z
          .string()
          .describe(
            'Newest 13F report period present in the table, or null when no row carries one (the plain holder table does not).',
          )
          .nullable(),
        concentration: z
          .array(
            z
              .object({
                topN: z.coerce.number().describe('Band size — 3, 10 or 25.'),
                percent: z
                  .coerce.number()
                  .describe('% of shares outstanding held by the largest topN holders, or null.')
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('Top 3 / 10 / 25 concentration, computed over the FULL table (not the page).'),
        unavailableReason: z
          .literal('vendor-share-class-fold')
          .describe(
            "Why the table is WITHHELD rather than empty, or null when the rows are the real thing. `vendor-share-class-fold`: the data provider serves one share class's holder table under every ticker of a dual-class issuer (Berkshire Class B answers with the Class A line), so the rows describe a different security and are not served. An empty table with this set must never be read as 'no holdings are on file'.",
          )
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/insider-transactions',
    alias: 'instrumentOwnershipGetInsiderTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        data: z.array(
          z
            .object({
              name: z.string().describe('Insider name as filed.'),
              share: z.coerce.number().describe('Shares held after the transaction.'),
              change: z.coerce.number().describe('Signed share change; negative is a disposal.'),
              filingDate: z.string().describe('Form 4 filing date (YYYY-MM-DD).'),
              transactionDate: z.string().describe('Trade date (YYYY-MM-DD).'),
              transactionCode: z
                .string()
                .describe('SEC transaction code, e.g. S (sale), P (purchase), A (award).'),
              transactionPrice: z.coerce.number().describe('Price per share; 0 for a grant/award.'),
              source: z
                .enum(['finnhub', 'sec_edgar'])
                .describe(
                  "Provenance. 'finnhub' rows are authoritative; 'sec_edgar' rows are preliminary.",
                )
                .optional(),
              isPreliminary: z
                .boolean()
                .describe("True for edge-wire-extracted rows Finnhub hasn't covered yet.")
                .optional(),
              insiderCik: z.string().describe('Edge-wire only: the insider CIK.').optional(),
              isOfficer: z.boolean().optional(),
              isDirector: z.boolean().optional(),
              isTenPercentOwner: z.boolean().optional(),
              officerTitle: z.string().describe('Officer title where disclosed.').nullish(),
              isDerivative: z.boolean().optional(),
              aff10b5One: z
                .boolean()
                .describe(
                  '10b5-1 pre-planned trade flag — a scheduled sale, not a discretionary signal.',
                )
                .optional(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/institutional',
    alias: 'instrumentOwnershipGetInstitutionalOwnership',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        data: z.array(
          z
            .object({
              name: z.string().describe('Holder name as filed.'),
              share: z.coerce.number().describe('Shares held.'),
              change: z.coerce.number().describe('Share change versus the previous filing.'),
              filingDate: z.string().describe('Filing date (YYYY-MM-DD).'),
              reportDate: z.string().describe('13F report period (YYYY-MM-DD).'),
            })
            .passthrough(),
        ),
        totalFilers: z
          .coerce.number()
          .describe(
            'Raw 13F filer count of the newest report, before multi-entity filers are merged for display (so it can exceed data.length). Null when no report carries an ownership table.',
          )
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/ownership/stock',
    alias: 'instrumentOwnershipGetStockOwnership',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        ownership: z
          .array(
            z
              .object({
                name: z.string().describe('Holder name as filed.'),
                share: z.coerce.number().describe('Shares held.'),
                change: z.coerce.number().describe('Share change versus the previous filing.'),
                filingDate: z.string().describe('Filing date (YYYY-MM-DD).'),
              })
              .passthrough(),
          )
          .describe(
            'Top holders trimmed to the requested limit (display rows only — NOT the aggregate basis).',
          ),
        institutionalOwnershipPercent: z
          .coerce.number()
          .describe(
            '% of shares outstanding held by institutional filers, computed over the FULL holder table (issue #552 — a top-N sum structurally undercounts). Null when the table is empty or shares outstanding is unavailable.',
          )
          .nullish(),
        insiderOwnershipPercent: z
          .coerce.number()
          .describe(
            "% held by individual (insider / 13D-G) major holders and curated strategic vehicles (family holdcos, controlling state treasuries) from the same full table. NOT the company's executives: PKN.WA's 49.90% is the Polish State Treasury, one row, while every officer on /ownership/executives holds zero shares.",
          )
          .nullish(),
        freeFloatOwnershipPercent: z
          .coerce.number()
          .describe(
            'The residual part of the SAME split: 100 − institutional − insider. Served rather than derived by each reader, because the three parts are one division and two browsers deriving the third independently is how HUT.US came to show 100.00 + 10.99 + 0.00. All four fields are present together or all null: a filed table exceeding shares outstanding withholds the whole split, it does not clamp it.',
          )
          .nullish(),
        insiderHolderCount: z
          .coerce.number()
          .describe(
            'How many FILED ROWS `insiderOwnershipPercent` is the sum of — the population that percentage actually measures, so a caption under it counts the same thing the number does.',
          )
          .nullish(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/peers',
    alias: 'instrumentsGetPeers',
    description: `Multi-source peer discovery using ensemble model. Aggregates peers from Finnhub, FMP, Yahoo Finance, and Massive Data with weighted voting.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({ peers: z.array(z.string()).describe('List of peer company symbols') })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/profile',
    alias: 'instrumentsGetProfile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        ticker: z.string().describe('Company symbol/ticker'),
        name: z.string().describe('Company name'),
        country: z.string().describe('Country of headquarters'),
        currency: z.string().describe('Currency used in financials'),
        estimateCurrency: z
          .string()
          .describe(
            "Currency Finnhub's analyst estimates are quoted in (may differ from reporting)",
          ),
        exchange: z.string().describe('Exchange where stock is listed'),
        finnhubIndustry: z.string().describe('Finnhub industry classification'),
        ipo: z.string().describe('IPO date'),
        logo: z.string().describe('Company logo URL'),
        marketCapitalization: z.coerce.number().describe('Market capitalization'),
        phone: z.string().describe('Phone number'),
        shareOutstanding: z.coerce.number().describe('Number of outstanding shares'),
        weburl: z.string().describe('Company website URL'),
        gicsSector: z.string().describe('GICS sector classification'),
        gicsIndustryGroup: z.string().describe('GICS industry group classification'),
        gicsIndustry: z.string().describe('GICS industry classification'),
        gicsSubIndustry: z.string().describe('GICS sub-industry classification'),
        description: z.string().describe('Company description/business summary'),
        address: z.string().describe('Company address'),
        city: z.string().describe('Company city'),
        state: z.string().describe('Company state'),
        zip: z.string().describe('Company zip code'),
        employeeTotal: z.coerce.number().describe('Number of employees'),
        ceo: z.string().describe('CEO name'),
      })
      .partial()
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/quote',
    alias: 'instrumentsGetQuote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION']),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Instrument symbol'),
        price: z
          .coerce.number()
          .describe('Current price (omitted for some crypto/option quotes)')
          .optional(),
        bid: z.coerce.number().describe('Bid price').optional(),
        ask: z.coerce.number().describe('Ask price').optional(),
        bidSize: z.coerce.number().describe('Bid size').optional(),
        askSize: z.coerce.number().describe('Ask size').optional(),
        previousClose: z.coerce.number().describe('Previous close price').optional(),
        change: z.coerce.number().describe('Price change').optional(),
        changePercent: z.coerce.number().describe('Price change percentage').optional(),
        volume: z.coerce.number().describe('Trading volume').optional(),
        timestamp: z.coerce.number().describe('Quote timestamp'),
        type: z.enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION']).describe('Asset type'),
        currency: z.string().describe('Trading currency (ISO 4217 code)').optional(),
        source: z
          .enum(['finnhub', 'yahoo', 'eodhd'])
          .describe(
            "Provenance of the served value (quote failover Phase 5): which approved source priced it — 'finnhub' | 'yahoo' | 'eodhd'. Additive-optional; absent on crypto/option quotes.",
          )
          .optional(),
        asOf: z
          .coerce.number()
          .describe("The serving source's own freshness stamp for the value, epoch ms.")
          .optional(),
        stale: z
          .boolean()
          .describe(
            'True when the served value is a DEMOTED last-known print (engine `stale-held`): it self-describes an older session than the one a live surface would show — a halted, delisted or illiquid listing (NTZ after its NYSE suspension), or every live leg down. `asOf` says which session it describes. Always present on a stock/ETF quote; absent on crypto/option quotes.',
          )
          .optional(),
        impliedVolatility: z.coerce.number().describe('Implied volatility (options only)').optional(),
        openInterest: z.coerce.number().describe('Open interest (options only)').optional(),
        greeks: z
          .object({
            delta: z.coerce.number().describe('Delta'),
            gamma: z.coerce.number().describe('Gamma'),
            theta: z.coerce.number().describe('Theta'),
            vega: z.coerce.number().describe('Vega'),
            rho: z.coerce.number().describe('Rho'),
          })
          .partial()
          .passthrough()
          .describe('Option Greeks (options only)')
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/:symbol/valuation',
    alias: 'valuationGetValuation',
    description: `Builds (and 5-min Redis-caches) the kernel-sourced valuation context, then returns the seeded bull/base/bear projection grid, DCF fair value + margin of safety per scenario, and the earnings scenario defaults. The baseline (current EPS/P-E/net-margin) equals /fundamentals + /overview.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'currency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'method',
        type: 'Query',
        schema: z.enum(['pe', 'fpe', 'ps']).optional(),
      },
      {
        name: 'scenario',
        type: 'Query',
        schema: z.enum(['bull', 'base', 'bear']).optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Upper-cased.'),
        displayCurrency: z.string(),
        tradingCurrency: z.string(),
        reportingCurrency: z.string(),
        asOfYear: z
          .coerce.number()
          .describe(
            "FISCAL year of the projection grid's BASE column — the earliest year still under forecast, which for a Jan-Aug filer (NVDA, MSFT, WMT, BABA) is NOT the calendar year. Every column label on the DCF grid is derived from it, so it is the period name the forecasts tab prints for the same row ('FY 2027').",
          ),
        valuationMethod: z
          .enum(['pe', 'fpe', 'ps'])
          .describe('The method this response was built with.'),
        suggestedMethod: z
          .object({
            method: z
              .enum(['pe', 'fpe', 'ps'])
              .describe('The method whose multiple has a meaningful base for THIS company.'),
            reason: z
              .enum(['lossMaker', 'ttmDistorted', 'consensusDecline', 'aligned', 'unknown'])
              .describe(
                "Why — a CODE, never a sentence: the client renders it in the reader's language. `unknown` means the bases could not be compared (typically no forward estimate) and the client should show no hint rather than a fabricated one.",
              ),
          })
          .passthrough()
          .describe(
            "Which method FITS this company, independent of the one requested. The client uses it to mark a 'suggested' option and explain the choice.",
          ),
        baseline: z
          .object({
            price: z.coerce.number().describe('Latest price, in the display currency.'),
            ttmEps: z
              .coerce.number()
              .describe(
                "Trailing-twelve-month EPS, display currency. NULL when the metric kernel refused the SHARE basis: a mirror / cross-listed / depositary / preferred LINE is served the PARENT security’s per-share package by the vendor (MCD.NE carried McDonald’s per-ordinary 12,31 against a CA$22,25 receipt quote), and there is no honest per-share figure for the paper the ticker names. Never 0 — 0 would read as 'earned nothing'.",
              )
              .nullable(),
            peRatio: z
              .coerce.number()
              .describe(
                'The metric-kernel trailing P/E — the SAME ratio /overview and the fundamentals chart render. It is not price / ttmEps: the kernel uses its own EPS reference period, so recomputing it from the two fields beside it drifts from canonical. NULL when the kernel refused it — a refused SHARE basis (see `ttmEps`) or non-positive trailing earnings. Never 0: a P/E of zero says the market pays nothing for the earnings, which is a claim, not an absence.',
              )
              .nullable(),
            netMargin: z.coerce.number().describe('Net margin as a PERCENT (e.g. 26.6), not a fraction.'),
            forwardPe: z
              .coerce.number()
              .describe(
                'Forward P/E, or null when there is no forward EPS estimate to build it from.',
              )
              .nullable(),
            sharesOutstanding: z
              .coerce.number()
              .describe('Share count on the ADS-consistent basis used throughout.'),
          })
          .passthrough(),
        currentData: z
          .object({
            revenue: z.coerce.number(),
            netIncome: z.coerce.number(),
            netMargin: z
              .coerce.number()
              .describe(
                'Net margin as a DECIMAL FRACTION here (0.266) — deliberately unlike `baseline.netMargin`, which is a percent. This block feeds the shared projection kernel, which works in fractions.',
              ),
            eps: z.coerce.number(),
            peRatio: z.coerce.number(),
            psRatio: z.coerce.number(),
            price: z.coerce.number(),
            sharesOutstanding: z.coerce.number(),
          })
          .passthrough(),
        analystEstimates: z
          .object({
            revenue: z.array(z.coerce.number()),
            eps: z.array(z.coerce.number()),
            revenueHigh: z.array(z.coerce.number()),
            revenueLow: z.array(z.coerce.number()),
            epsHigh: z.array(z.coerce.number()),
            epsLow: z.array(z.coerce.number()),
            numberOfAnalysts: z.coerce.number(),
            epsBasis: z
              .enum(['usGaap', 'reported', 'adjusted'])
              .describe(
                "Basis of the consensus EPS figures in this block. 'adjusted' = the street's analyst-comparable basis. 'usGaap'/'reported' = the issuer's own filed basis (the second one meaning the reporting framework is NOT established — never read it as IFRS). null = no claim: the rows disagreed, or the vendor publishes no basis for this issuer. A client MUST render nothing on null rather than substitute a default.",
              )
              .nullable(),
          })
          .passthrough(),
        companyFinancials: z
          .object({
            beta: z.coerce.number(),
            marketCap: z.coerce.number(),
            totalDebt: z.coerce.number(),
            interestExpense: z.coerce.number(),
            effectiveTaxRate: z.coerce.number(),
            netMargin: z.coerce.number(),
          })
          .partial()
          .passthrough(),
        projectionInputs: z
          .object({
            bull: z
              .object({
                revenueGrowth: z.array(z.coerce.number()),
                netIncomeGrowth: z.array(z.coerce.number()),
                peLow: z.array(z.coerce.number()),
                peHigh: z.array(z.coerce.number()),
                psLow: z.array(z.coerce.number()),
                psHigh: z.array(z.coerce.number()),
              })
              .passthrough(),
            base: z
              .object({
                revenueGrowth: z.array(z.coerce.number()),
                netIncomeGrowth: z.array(z.coerce.number()),
                peLow: z.array(z.coerce.number()),
                peHigh: z.array(z.coerce.number()),
                psLow: z.array(z.coerce.number()),
                psHigh: z.array(z.coerce.number()),
              })
              .passthrough(),
            bear: z
              .object({
                revenueGrowth: z.array(z.coerce.number()),
                netIncomeGrowth: z.array(z.coerce.number()),
                peLow: z.array(z.coerce.number()),
                peHigh: z.array(z.coerce.number()),
                psLow: z.array(z.coerce.number()),
                psHigh: z.array(z.coerce.number()),
              })
              .passthrough(),
          })
          .passthrough(),
        ladderProvenance: z
          .object({
            pe: z
              .enum(['currentMultiple', 'historicalMedian', 'assumedConstant'])
              .describe(
                "Anchor of the P/E ladder — this company's own multiple, its own realized median, or a generic constant.",
              ),
            ps: z
              .enum(['currentMultiple', 'historicalMedian', 'assumedConstant'])
              .describe('Anchor of the P/S ladder — same three, resolved by the same function.'),
          })
          .passthrough()
          .describe(
            "Where each seeded multiple LADDER anchored. `currentMultiple` is this company's own (forward) P/E or P/S; `historicalMedian` is its own realized median when the current one is unusable; `assumedConstant` is a generic placeholder and is NOT a fact about this company — a surface must flag or refuse it rather than present it as a measurement.",
          ),
        projectedData: z
          .object({
            bull: z.array(
              z
                .object({
                  fiscalYear: z
                    .coerce.number()
                    .describe(
                      'FISCAL year of THIS row — counted off `asOfYear`, never off a clock. A consumer that derives the period from its own calendar gets it wrong for every Jan-Aug filer (R8-A7-02).',
                    ),
                  revenue: z.coerce.number(),
                  netIncome: z.coerce.number(),
                  netMargin: z
                    .coerce.number()
                    .describe('Decimal fraction, matching `currentData.netMargin`.'),
                  eps: z.coerce.number(),
                  priceLow: z.coerce.number(),
                  priceHigh: z.coerce.number(),
                })
                .passthrough(),
            ),
            base: z.array(
              z
                .object({
                  fiscalYear: z
                    .coerce.number()
                    .describe(
                      'FISCAL year of THIS row — counted off `asOfYear`, never off a clock. A consumer that derives the period from its own calendar gets it wrong for every Jan-Aug filer (R8-A7-02).',
                    ),
                  revenue: z.coerce.number(),
                  netIncome: z.coerce.number(),
                  netMargin: z
                    .coerce.number()
                    .describe('Decimal fraction, matching `currentData.netMargin`.'),
                  eps: z.coerce.number(),
                  priceLow: z.coerce.number(),
                  priceHigh: z.coerce.number(),
                })
                .passthrough(),
            ),
            bear: z.array(
              z
                .object({
                  fiscalYear: z
                    .coerce.number()
                    .describe(
                      'FISCAL year of THIS row — counted off `asOfYear`, never off a clock. A consumer that derives the period from its own calendar gets it wrong for every Jan-Aug filer (R8-A7-02).',
                    ),
                  revenue: z.coerce.number(),
                  netIncome: z.coerce.number(),
                  netMargin: z
                    .coerce.number()
                    .describe('Decimal fraction, matching `currentData.netMargin`.'),
                  eps: z.coerce.number(),
                  priceLow: z.coerce.number(),
                  priceHigh: z.coerce.number(),
                })
                .passthrough(),
            ),
          })
          .passthrough(),
        dcf: z
          .object({
            bull: z
              .object({
                presentValues: z
                  .array(z.coerce.number())
                  .describe('Discounted cash flow per projected year.'),
                terminalValue: z.coerce.number(),
                pvOfTerminalValue: z.coerce.number(),
                enterpriseValue: z.coerce.number(),
                fairValuePerShare: z.coerce.number(),
                currentPrice: z.coerce.number(),
                upsidePercent: z.coerce.number(),
                marginOfSafety: z.coerce.number(),
                wacc: z.coerce.number().describe('Discount rate as a decimal fraction (0.089).'),
                terminalGrowthRate: z
                  .coerce.number()
                  .describe('Terminal growth rate as a decimal fraction.'),
                terminalMethod: z.enum(['exitMultiple', 'gordonGrowth', 'psMultiple', 'none']),
                isValid: z
                  .boolean()
                  .describe(
                    'False when the inputs cannot support a fair value; read `validationMessage`.',
                  ),
                validationMessage: z.string(),
              })
              .passthrough(),
            base: z
              .object({
                presentValues: z
                  .array(z.coerce.number())
                  .describe('Discounted cash flow per projected year.'),
                terminalValue: z.coerce.number(),
                pvOfTerminalValue: z.coerce.number(),
                enterpriseValue: z.coerce.number(),
                fairValuePerShare: z.coerce.number(),
                currentPrice: z.coerce.number(),
                upsidePercent: z.coerce.number(),
                marginOfSafety: z.coerce.number(),
                wacc: z.coerce.number().describe('Discount rate as a decimal fraction (0.089).'),
                terminalGrowthRate: z
                  .coerce.number()
                  .describe('Terminal growth rate as a decimal fraction.'),
                terminalMethod: z.enum(['exitMultiple', 'gordonGrowth', 'psMultiple', 'none']),
                isValid: z
                  .boolean()
                  .describe(
                    'False when the inputs cannot support a fair value; read `validationMessage`.',
                  ),
                validationMessage: z.string(),
              })
              .passthrough(),
            bear: z
              .object({
                presentValues: z
                  .array(z.coerce.number())
                  .describe('Discounted cash flow per projected year.'),
                terminalValue: z.coerce.number(),
                pvOfTerminalValue: z.coerce.number(),
                enterpriseValue: z.coerce.number(),
                fairValuePerShare: z.coerce.number(),
                currentPrice: z.coerce.number(),
                upsidePercent: z.coerce.number(),
                marginOfSafety: z.coerce.number(),
                wacc: z.coerce.number().describe('Discount rate as a decimal fraction (0.089).'),
                terminalGrowthRate: z
                  .coerce.number()
                  .describe('Terminal growth rate as a decimal fraction.'),
                terminalMethod: z.enum(['exitMultiple', 'gordonGrowth', 'psMultiple', 'none']),
                isValid: z
                  .boolean()
                  .describe(
                    'False when the inputs cannot support a fair value; read `validationMessage`.',
                  ),
                validationMessage: z.string(),
              })
              .passthrough(),
          })
          .passthrough(),
        exitMultiple: z.coerce.number().nullable(),
        forwardEps: z.coerce.number(),
        baseYearIsEstimate: z
          .boolean()
          .describe(
            'True when the projection base year is an analyst estimate rather than a filing.',
          ),
        peStats: z
          .object({
            '1Y': RatioStatsDto.nullable(),
            '5Y': RatioStatsDto.nullable(),
            MAX: RatioStatsDto.nullable(),
          })
          .passthrough()
          .describe('Historical P/E percentiles.'),
        psStats: z
          .object({
            '1Y': RatioStatsDto.nullable(),
            '5Y': RatioStatsDto.nullable(),
            MAX: RatioStatsDto.nullable(),
          })
          .passthrough()
          .describe('Historical P/S percentiles.'),
        earnings: z
          .object({
            defaults: z
              .object({
                bull: EarningsScenarioInputsDto,
                base: EarningsScenarioInputsDto,
                bear: EarningsScenarioInputsDto,
              })
              .passthrough(),
            defaultsProvenance: z
              .object({
                baseGrowth: z.enum(['nextFyConsensus', 'multiYearCagr', 'assumedFallback']),
                baseGrowthCapped: z
                  .boolean()
                  .describe('True when the seeded growth hit the sanity cap and was clamped.'),
                basePe: z.enum(['forwardPe', 'ttmPe']),
                rangePe: z
                  .enum(['historicalQuantiles', 'dispersionBand'])
                  .describe(
                    'Where the bull/bear multiples came from. `historicalQuantiles` means this company had enough history to use its OWN percentile band; `dispersionBand` is the synthetic fallback.',
                  ),
              })
              .passthrough(),
            scenarios: z
              .object({
                bull: EarningsScenarioInputsDto,
                base: EarningsScenarioInputsDto,
                bear: EarningsScenarioInputsDto,
              })
              .passthrough(),
            results: z
              .object({
                bull: z
                  .object({
                    futureEps: z.coerce.number(),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
                base: z
                  .object({
                    futureEps: z.coerce.number(),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
                bear: z
                  .object({
                    futureEps: z.coerce.number(),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
              })
              .passthrough(),
            chartData: z
              .object({
                bull: z.array(z.coerce.number()),
                base: z.array(z.coerce.number()),
                bear: z.array(z.coerce.number()),
              })
              .passthrough(),
            horizonYears: z.coerce.number(),
            discountRatePct: z
              .coerce.number()
              .describe(
                "Required return used to discount the future price to today, PERCENT to 1dp — the same floored CAPM cost of equity the DCF discounts at, so both surfaces answer 'what is it worth today' with one rate.",
              ),
            currentEps: z
              .coerce.number()
              .describe(
                'Trailing EPS the scenarios start from — NULL when the kernel refused the share basis (see ValuationBaselineDto.ttmEps). The scenario numbers beside it are then all zero.',
              )
              .nullable(),
            currentPe: z
              .coerce.number()
              .describe(
                'Trailing P/E — the same kernel ratio as `baseline.peRatio`. NULL when the kernel refused it; never 0.',
              )
              .nullable(),
            currentFyConsensusEps: z.coerce.number().nullable(),
            analystEpsGrowth: z.coerce.number().describe('PERCENT, 1dp.').nullable(),
            projectedEpsCagr: z.coerce.number().describe('PERCENT, 1dp.').nullable(),
          })
          .passthrough(),
        revenue: z
          .object({
            defaults: z
              .object({
                bull: RevenueScenarioInputsDto,
                base: RevenueScenarioInputsDto,
                bear: RevenueScenarioInputsDto,
              })
              .passthrough(),
            defaultsProvenance: z
              .object({
                baseGrowth: z.enum(['nextFyConsensus', 'multiYearCagr', 'assumedFallback']),
                baseGrowthCapped: z.boolean(),
                basePs: z.enum(['forwardPs', 'ttmPs']),
                rangePs: z.enum(['historicalQuantiles', 'dispersionBand']),
              })
              .passthrough(),
            scenarios: z
              .object({
                bull: RevenueScenarioInputsDto,
                base: RevenueScenarioInputsDto,
                bear: RevenueScenarioInputsDto,
              })
              .passthrough(),
            results: z
              .object({
                bull: z
                  .object({
                    futureRps: z.coerce.number().describe('Revenue per share at the end of the horizon.'),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
                base: z
                  .object({
                    futureRps: z.coerce.number().describe('Revenue per share at the end of the horizon.'),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
                bear: z
                  .object({
                    futureRps: z.coerce.number().describe('Revenue per share at the end of the horizon.'),
                    futurePrice: z.coerce.number(),
                    totalReturnPct: z.coerce.number(),
                    annualizedReturnPct: z.coerce.number(),
                    presentValue: z.coerce.number(),
                    presentValueUpsidePct: z.coerce.number(),
                  })
                  .passthrough(),
              })
              .passthrough(),
            chartData: z
              .object({
                bull: z.array(z.coerce.number()),
                base: z.array(z.coerce.number()),
                bear: z.array(z.coerce.number()),
              })
              .passthrough(),
            horizonYears: z.coerce.number(),
            discountRatePct: z
              .coerce.number()
              .describe('PERCENT to 1dp; same rate as the earnings block.'),
            currentRps: z.coerce.number(),
            currentPs: z.coerce.number(),
            analystRevenueGrowth: z.coerce.number().describe('PERCENT, 1dp.').nullable(),
            projectedRevenueCagr: z.coerce.number().describe('PERCENT, 1dp.').nullable(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/coverage-report',
    alias: 'finnhubFundamentalsGetCoverageReport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['json', 'markdown']).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/crypto/:symbol/history',
    alias: 'cryptoGetCryptoHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'dateFrom',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'dateTo',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/crypto/:symbol/quote',
    alias: 'cryptoGetCryptoQuote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/metrics/:symbol',
    alias: 'finnhubFundamentalsGetFundamentalMetrics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        metrics: z
          .object({
            symbol: z.string().describe('Stock symbol'),
            metricType: z.string().describe('Metric type').optional(),
            metric: z
              .object({
                marketCapitalization: z.coerce.number().describe('Market capitalization'),
                enterpriseValue: z.coerce.number().describe('Enterprise value'),
                peRatio: z.coerce.number().describe('Price to earnings ratio'),
                pegRatio: z.coerce.number().describe('Price to earnings growth ratio'),
                psRatio: z.coerce.number().describe('Price to sales ratio'),
                pbRatio: z.coerce.number().describe('Price to book ratio'),
                evToSales: z.coerce.number().describe('Enterprise value to sales'),
                evToEBITDA: z.coerce.number().describe('Enterprise value to EBITDA'),
                evToEBIT: z.coerce.number().describe('Enterprise value to EBIT'),
                evToFCF: z.coerce.number().describe('Enterprise value to free cash flow'),
                fcfYield: z.coerce.number().describe('Free cash flow yield'),
                dividendYield: z.coerce.number().describe('Dividend yield'),
                dividendPerShare: z.coerce.number().describe('Dividend per share'),
                payoutRatio: z.coerce.number().describe('Payout ratio'),
                grossMargin: z.coerce.number().describe('Gross margin'),
                operatingMargin: z.coerce.number().describe('Operating margin'),
                ebitdaMargin: z.coerce.number().describe('EBITDA margin'),
                netProfitMargin: z.coerce.number().describe('Net profit margin'),
                returnOnAssets: z.coerce.number().describe('Return on assets'),
                returnOnEquity: z.coerce.number().describe('Return on equity'),
                returnOnInvestedCapital: z.coerce.number().describe('Return on invested capital'),
                assetTurnover: z.coerce.number().describe('Asset turnover'),
                inventoryTurnover: z.coerce.number().describe('Inventory turnover'),
                currentRatio: z.coerce.number().describe('Current ratio'),
                quickRatio: z.coerce.number().describe('Quick ratio'),
                cashRatio: z.coerce.number().describe('Cash ratio'),
                debtToEquity: z.coerce.number().describe('Debt to equity ratio'),
                debtToAssets: z.coerce.number().describe('Debt to assets ratio'),
                netDebtToEBITDA: z.coerce.number().describe('Net debt to EBITDA'),
                revenuePerShare: z.coerce.number().describe('Revenue per share'),
                eps: z.coerce.number().describe('Earnings per share'),
                bookValuePerShare: z.coerce.number().describe('Book value per share'),
                tangibleBookValuePerShare: z.coerce.number().describe('Tangible book value per share'),
                cashPerShare: z.coerce.number().describe('Cash per share'),
                operatingCashFlowPerShare: z.coerce.number().describe('Operating cash flow per share'),
                freeCashFlowPerShare: z.coerce.number().describe('Free cash flow per share'),
                beta: z.coerce.number().describe('Beta'),
                '52WeekHigh': z.coerce.number().describe('52 week high'),
                '52WeekLow': z.coerce.number().describe('52 week low'),
                '10DayAverageTradingVolume': z.coerce.number().describe('10 day average trading volume'),
                '3MonthAverageTradingVolume': z.coerce.number().describe('3 month average trading volume'),
                sharesOutstanding: z.coerce.number().describe('Shares outstanding'),
                interestCoverage: z.coerce.number().describe('Interest coverage ratio'),
                receivablesTurnover: z.coerce.number().describe('Receivables turnover'),
                revenueGrowthYoY: z.coerce.number().describe('Revenue growth TTM YoY'),
                earningsGrowthYoY: z.coerce.number().describe('Earnings/EPS growth TTM YoY'),
                netIncomeGrowthYoY: z.coerce.number().describe('Net income growth TTM YoY'),
                ebitdaGrowthYoY: z.coerce.number().describe('EBITDA growth TTM YoY'),
                revenueGrowth3Y: z.coerce.number().describe('Revenue growth CAGR 3 years'),
                revenueGrowth5Y: z.coerce.number().describe('Revenue growth CAGR 5 years'),
                revenueGrowth10Y: z.coerce.number().describe('Revenue growth CAGR 10 years'),
                epsGrowth3Y: z.coerce.number().describe('EPS growth CAGR 3 years'),
                epsGrowth5Y: z.coerce.number().describe('EPS growth CAGR 5 years'),
                epsGrowth10Y: z.coerce.number().describe('EPS growth CAGR 10 years'),
                forwardPe: z.coerce.number().describe('Forward P/E ratio (NTM)'),
                forwardEvSales: z.coerce.number().describe('Forward EV/Sales (NTM)'),
                forwardEvEbitda: z.coerce.number().describe('Forward EV/EBITDA (NTM)'),
                forwardPFcf: z.coerce.number().describe('Forward P/FCF (NTM)'),
                evToGrossProfit: z.coerce.number().describe('EV/Gross Profit (TTM)'),
                ebitdaGrowthFwd2Y: z.coerce.number().describe('EBITDA forward 2-year growth (annualized)'),
                epsLtGrowthEst: z.coerce.number().describe('EPS long-term growth estimate (5-year)'),
                pretaxMargin: z.coerce.number().describe('Pre-tax profit margin TTM'),
                fcfMargin: z.coerce.number().describe('Free cash flow margin TTM'),
                returnOnTangibleAssets: z.coerce.number().describe('Return on tangible assets TTM'),
                dividendGrowthRate5Y: z.coerce.number().describe('Dividend growth rate 5 years'),
                dpsGrowth3Y: z.coerce.number().describe('DPS growth CAGR 3 years'),
                dpsGrowth5Y: z.coerce.number().describe('DPS growth CAGR 5 years'),
                netDebt: z.coerce.number().describe('Net debt'),
              })
              .partial()
              .passthrough()
              .describe('Financial metrics'),
            series: z
              .object({
                annual: z.record(
                  z.array(z.object({ period: z.string(), v: z.coerce.number() }).passthrough()),
                ),
                quarterly: z.record(
                  z.array(z.object({ period: z.string(), v: z.coerce.number() }).passthrough()),
                ),
              })
              .partial()
              .passthrough()
              .describe('Time series data')
              .optional(),
          })
          .passthrough()
          .describe('Basic financial metrics'),
        priceTarget: z
          .object({
            symbol: z.string().describe('Stock symbol'),
            targetHigh: z.coerce.number().describe('Highest analyst target price'),
            targetLow: z.coerce.number().describe('Lowest analyst target price'),
            targetMean: z.coerce.number().describe('Mean of all analyst targets'),
            targetMedian: z.coerce.number().describe('Median of all analyst targets'),
            numberAnalysts: z.coerce.number().describe('Number of analysts'),
            lastUpdated: z.string().describe('Last updated date').optional(),
          })
          .passthrough()
          .describe('Price target data')
          .optional(),
        recommendations: z
          .array(
            z
              .object({
                symbol: z.string().describe('Stock symbol'),
                buy: z.coerce.number().describe('Number of buy recommendations'),
                hold: z.coerce.number().describe('Number of hold recommendations'),
                period: z.string().describe('Period (date)'),
                sell: z.coerce.number().describe('Number of sell recommendations'),
                strongBuy: z.coerce.number().describe('Number of strong buy recommendations'),
                strongSell: z.coerce.number().describe('Number of strong sell recommendations'),
              })
              .passthrough(),
          )
          .describe('Recommendation trends')
          .optional(),
        trailingValuation: z
          .object({
            marketCap: z.coerce.number().nullable(),
            enterpriseValue: z.coerce.number().nullable(),
            peRatio: z.coerce.number().nullable(),
            psRatio: z.coerce.number().nullable(),
            pbRatio: z.coerce.number().nullable(),
            evToSales: z.coerce.number().nullable(),
            evToEBITDA: z.coerce.number().nullable(),
            evToEBIT: z.coerce.number().nullable(),
            evToFCF: z.coerce.number().nullable(),
            fcfYield: z.coerce.number().nullable(),
            dividendYield: z.coerce.number().nullable(),
          })
          .partial()
          .passthrough()
          .describe('Trailing valuation metrics')
          .optional(),
        forwardValuation: z
          .object({
            forwardPE: z.coerce.number(),
            forwardPS: z.coerce.number(),
            forwardEVToSales: z.coerce.number(),
            forwardEVToEBITDA: z.coerce.number(),
          })
          .partial()
          .passthrough()
          .describe('Forward valuation metrics')
          .optional(),
        margins: z
          .object({
            grossMargin: z.coerce.number().nullable(),
            operatingMargin: z.coerce.number().nullable(),
            ebitdaMargin: z.coerce.number().nullable(),
            netProfitMargin: z.coerce.number().nullable(),
            fcfMargin: z.coerce.number().nullable(),
          })
          .partial()
          .passthrough()
          .describe('Margin metrics')
          .optional(),
        capitalEfficiency: z
          .object({
            returnOnAssets: z.coerce.number().nullable(),
            returnOnEquity: z.coerce.number().nullable(),
            returnOnInvestedCapital: z.coerce.number().nullable(),
            assetTurnover: z.coerce.number().nullable(),
            inventoryTurnover: z.coerce.number().nullable(),
          })
          .partial()
          .passthrough()
          .describe('Capital efficiency metrics')
          .optional(),
        financialHealth: z
          .object({
            currentRatio: z.coerce.number().nullable(),
            quickRatio: z.coerce.number().nullable(),
            cashRatio: z.coerce.number().nullable(),
            debtToEquity: z.coerce.number().nullable(),
            debtToAssets: z.coerce.number().nullable(),
            netDebtToEBITDA: z.coerce.number().nullable(),
          })
          .partial()
          .passthrough()
          .describe('Financial health metrics')
          .optional(),
        perShare: z
          .object({
            revenuePerShare: z.coerce.number().nullable(),
            eps: z.coerce.number().nullable(),
            bookValuePerShare: z.coerce.number().nullable(),
            tangibleBookValuePerShare: z.coerce.number().nullable(),
            cashPerShare: z.coerce.number().nullable(),
            operatingCashFlowPerShare: z.coerce.number().nullable(),
            freeCashFlowPerShare: z.coerce.number().nullable(),
          })
          .partial()
          .passthrough()
          .describe('Per share metrics')
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/options/:symbol/history',
    alias: 'optionsGetOptionHistory',
    description: `Fetches historical daily close prices for an option contract. Tries Massive API first, falls back to internal transaction data.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/quotes/stream',
    alias: 'instrumentsStreamQuotes',
    description: `Streams real-time quotes as they arrive. Each quote is sent as a separate SSE event. Connection closes when all quotes are fetched.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/search/popular-today',
    alias: 'instrumentsGetPopularToday',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(20).optional(),
      },
    ],
    response: z
      .object({
        date: z.string().describe('The UTC day the ranking covers (YYYY-MM-DD).'),
        symbols: z.array(
          z
            .object({
              symbol: z.string(),
              name: z.string().describe('Instrument display name, if known.').nullable(),
              searchCount: z
                .coerce.number()
                .describe('How many times the symbol was picked from search today (UTC).'),
              rank: z.coerce.number().describe('1-based rank, best first.'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/instruments/search/track-selection',
    alias: 'instrumentsTrackSearchSelection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          symbol: z
            .string()
            .min(1)
            .max(30)
            .regex(/^(?!.*\.\.)[A-Za-z0-9.^_&\/-]+(?: [A-Za-z0-9.^_&\/-]+)*$/)
            .describe('The picked exchange-suffixed symbol.'),
        }),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/instruments/search/unified',
    alias: 'instrumentsSearchUnified',
    requestFormat: 'json',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'types',
        type: 'Query',
        schema: z.array(z.enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION'])).optional(),
      },
    ],
    response: z
      .object({
        count: z.coerce.number().describe('Total number of results'),
        results: z
          .array(
            z
              .object({
                symbol: z.string().describe('Instrument symbol'),
                name: z.string().describe('Instrument name'),
                type: z.enum(['STOCK', 'ETF', 'CRYPTO', 'BOND', 'OPTION']).describe('Asset type'),
                exchange: z.string().describe('Exchange or market').optional(),
                source: z
                  .enum(['FINNHUB', 'YAHOO', 'MARKETDATA', 'COINAPI', 'STATIC'])
                  .describe('Data source API'),
                lifecycleStatus: z
                  .enum(['delisted', 'renamed'])
                  .describe(
                    "Registry lifecycle when NOT live: 'delisted' (identity kept for historical positions — badge it) or 'renamed' (retired code; see successorSymbol)",
                  )
                  .optional(),
                successorSymbol: z
                  .string()
                  .describe("The live symbol a 'renamed' row now trades as (CCC.WA → MDV.WA)")
                  .nullish(),
              })
              .passthrough(),
          )
          .describe('Search results'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/:symbol/dividends',
    alias: 'stocksGetDividends',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z.array(
      z
        .object({
          symbol: z.string().describe('Stock symbol'),
          date: z.string().describe('Dividend date'),
          amount: z.coerce.number().describe('Dividend amount'),
          adjustedAmount: z.coerce.number().describe('Adjusted dividend amount'),
          payDate: z.string().describe('Payment date'),
          recordDate: z.string().describe('Record date'),
          declarationDate: z.string().describe('Declaration date'),
          currency: z.string().describe('Currency'),
          isSpecial: z
            .boolean()
            .describe(
              'True when this payout falls outside the regular payment schedule (off-cycle)',
            )
            .optional(),
          scheduledDate: z
            .string()
            .describe(
              "Calendar date of the regular-schedule slot this payment fills (the issuer's dividend period); null when no schedule could be established",
            )
            .nullish(),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/:symbol/dividends/analysis',
    alias: 'stocksGetDividendAnalysis',
    description: `Split-adjusted history + overview-sourced scalar metrics + server-computed series analysis (streak / growth / frequency / declining flag). One consistent source for the research /dividends page: split-adjusted always, with the incomplete current calendar year excluded from every YoY/streak/decline comparison.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        history: z
          .array(
            z
              .object({
                symbol: z.string().describe('Stock symbol'),
                date: z.string().describe('Dividend date'),
                amount: z.coerce.number().describe('Dividend amount'),
                adjustedAmount: z.coerce.number().describe('Adjusted dividend amount'),
                payDate: z.string().describe('Payment date'),
                recordDate: z.string().describe('Record date'),
                declarationDate: z.string().describe('Declaration date'),
                currency: z.string().describe('Currency'),
                isSpecial: z
                  .boolean()
                  .describe(
                    'True when this payout falls outside the regular payment schedule (off-cycle)',
                  )
                  .optional(),
                scheduledDate: z
                  .string()
                  .describe(
                    "Calendar date of the regular-schedule slot this payment fills (the issuer's dividend period); null when no schedule could be established",
                  )
                  .nullish(),
              })
              .passthrough(),
          )
          .describe('Split-adjusted dividend history'),
        metrics: z
          .object({
            dividendYield: z.coerce.number().describe('Dividend yield (%)').nullable(),
            dividendPerShare: z
              .coerce.number()
              .describe('Dividend per share (display basis: per-ADS / class / trading currency)')
              .nullable(),
            payoutRatio: z.coerce.number().describe('Payout ratio (%)').nullable(),
            dpsGrowth3Y: z.coerce.number().describe('3-year DPS growth CAGR (%)').nullable(),
            dpsGrowth5Y: z.coerce.number().describe('5-year DPS growth CAGR (%)').nullable(),
            dpsGrowth10Y: z
              .coerce.number()
              .describe(
                '10-year DPS growth CAGR (%), split-adjusted, computed from COMPLETE calendar years. Null unless both ends of the 10-year window carry real positive totals.',
              )
              .nullable(),
          })
          .passthrough()
          .describe('Scalar dividend metrics (overview-sourced)'),
        analysis: z
          .object({
            paymentFrequency: z.string().describe('Detected payment cadence'),
            consecutiveYearsOfIncreases: z
              .coerce.number()
              .describe(
                'Consecutive COMPLETE years of split-adjusted REGULAR dividend increases. Off-cycle specials are excluded from both the year total and the payment count, so a one-off extra payment can neither inflate a year nor (via the per-payment average) read as a cut.',
              ),
            consecutiveIncreasesTruncated: z
              .boolean()
              .describe(
                'True when the streak ran out of HISTORY instead of out of increases — it reached the oldest complete year the vendor supplied without meeting a cut. The count is then a FLOOR (KO: 35 visible of 64 real, the feed starts 1990-03-09), not a fact about the company, and must not be rendered as one.',
              ),
            oneYearGrowth: z
              .coerce.number()
              .describe('Trailing 1-year growth (%) on the split-adjusted basis')
              .nullable(),
            averageDividend: z.coerce.number().describe('Average split-adjusted dividend per payment'),
            latestDividend: z.coerce.number().describe('Most recent split-adjusted dividend'),
            totalDividendsLastYear: z
              .coerce.number()
              .describe('Total split-adjusted dividends paid in the trailing 12 months'),
            hasSpecialDividends: z
              .boolean()
              .describe('Whether any special (outsized) dividends were detected'),
            isDeclining: z
              .boolean()
              .describe('Whether complete-year split-adjusted dividends genuinely trend down'),
            trendDirection: z.string().describe('Trend direction over complete years'),
          })
          .passthrough()
          .describe('Server-computed dividend series analysis'),
        nextEstimate: z
          .object({
            exDate: z.string().describe('Projected ex-dividend date (YYYY-MM-DD)'),
            recordDate: z
              .string()
              .describe(
                'Projected record date; null when the vendor never supplied historical ones',
              )
              .nullable(),
            payDate: z
              .string()
              .describe(
                'Projected payment date; null when the vendor never supplied historical ones',
              )
              .nullable(),
            declarationDate: z
              .string()
              .describe('Projected declaration date (coupled to the earnings calendar where known)')
              .nullable(),
            declarationBasis: z
              .enum(['declared', 'earnings-calendar', 'historical-lead'])
              .describe(
                'How the declaration date was derived; `declared` means the issuer announced it and nothing here was projected. Null when there was no basis for one',
              )
              .nullable(),
            isDeclared: z
              .boolean()
              .describe(
                'True when the issuer has already DECLARED this payment and nothing here is a projection: every date is a fact and must not be labelled as an estimate.',
              ),
            amount: z
              .coerce.number()
              .describe(
                'Estimated per-share amount (forward consensus DPS split by cadence, else last)',
              )
              .nullable(),
            currency: z.string().describe('Payment currency').nullable(),
          })
          .passthrough()
          .describe(
            'The next expected dividend, projected from the detected cadence. Null when no estimate can be substantiated (no cadence / too short a history / a quiet payer).',
          )
          .nullish(),
        coverage: z
          .object({
            earnings: z
              .coerce.number()
              .describe('Net income per unit of dividend (8.2 = earnings cover the payout 8.2x)')
              .nullable(),
            freeCashFlow: z.coerce.number().describe('Free cash flow per unit of dividend').nullable(),
            netDebtToEbitda: z
              .coerce.number()
              .describe('Net debt / EBITDA — leverage context for the payout (may be negative)')
              .nullable(),
          })
          .passthrough()
          .describe('Coverage ratios behind the safety panel'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/:symbol/history',
    alias: 'stocksGetHistoricalPrices',
    description: `Fetches historical price data for stocks, ETFs, or bonds. Supports up to 20 years of historical data and handles future dates by capping them to the current date.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'resolution',
        type: 'Query',
        schema: z.enum(['1', '5', '15', '30', '60', 'D', 'W', 'M']).optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Stock/ETF/Bond symbol'),
        closePrices: z.array(z.coerce.number()).describe('Array of close prices'),
        highPrices: z.array(z.coerce.number()).describe('Array of high prices'),
        lowPrices: z.array(z.coerce.number()).describe('Array of low prices'),
        openPrices: z.array(z.coerce.number()).describe('Array of open prices'),
        volumes: z.array(z.coerce.number()).describe('Array of volumes'),
        timestamps: z.array(z.coerce.number()).describe('Array of timestamps'),
        status: z.string().describe('Data status'),
        currency: z.string().describe('Trading currency (e.g., USD, GBP)').optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/:symbol/quote',
    alias: 'stocksGetQuote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Stock symbol'),
        currentPrice: z.coerce.number().describe('Current price'),
        highPrice: z.coerce.number().describe('High price of the day'),
        lowPrice: z.coerce.number().describe('Low price of the day'),
        openPrice: z.coerce.number().describe('Open price of the day'),
        previousClose: z.coerce.number().describe('Previous close price'),
        change: z.coerce.number().describe('Price change').optional(),
        percentChange: z.coerce.number().describe('Percent change').optional(),
        timestamp: z.coerce.number().describe('Timestamp'),
        marketSession: z
          .string()
          .describe('Market session: pre-market, regular, post-market, or closed')
          .optional(),
        marketOpen: z.boolean().describe('Whether the market is currently open').optional(),
        bid: z.coerce.number().describe('Current bid price (if available)').optional(),
        ask: z.coerce.number().describe('Current ask price (if available)').optional(),
        bidSize: z.coerce.number().describe('Bid size (if available)').optional(),
        askSize: z.coerce.number().describe('Ask size (if available)').optional(),
        timezone: z.string().describe('Exchange timezone').optional(),
        volume: z.coerce.number().describe('Volume traded during session').optional(),
        currency: z
          .string()
          .describe('Trading currency (ISO 4217 code, e.g., USD, GBP, EUR)')
          .optional(),
        regularMarketPrice: z
          .coerce.number()
          .describe(
            "Regular trading session's price reference. Equals currentPrice during regular session; outside regular session it's the regular-session close (frozen until next session opens). Used by UI to compute extended-hours change.",
          )
          .optional(),
        extendedHoursChange: z
          .coerce.number()
          .describe(
            "currentPrice - regularMarketPrice. Only populated outside regular session and when |change| ≥ 0.05% (UI hides 'AH +0.00%' badges).",
          )
          .optional(),
        extendedHoursChangePercent: z
          .coerce.number()
          .describe('(extendedHoursChange / regularMarketPrice) * 100')
          .optional(),
        marketSessionPhase: z
          .enum(['regular', 'pre-market', 'after-hours', 'closed'])
          .describe(
            "Trading session phase: 'regular' | 'pre-market' | 'after-hours' | 'closed'. Drives extended-hours badge icon (sun for pre-market, moon for after-hours).",
          )
          .optional(),
        source: z
          .enum(['finnhub', 'yahoo', 'eodhd'])
          .describe(
            "Provenance of the served value (quote failover Phase 5): 'finnhub' | 'yahoo' | 'eodhd'. Additive-optional.",
          )
          .optional(),
        asOf: z
          .coerce.number()
          .describe("The serving source's own freshness stamp for the value, epoch ms.")
          .optional(),
        stale: z
          .boolean()
          .describe(
            'True when the served value is a DEMOTED last-known print (engine `stale-held`) rather than a live quote — a halted/delisted/illiquid listing, or every live leg down. `asOf` says which session it describes.',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/market-status',
    alias: 'stocksGetMarketStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'exchange',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        exchange: z.string(),
        session: z
          .string()
          .describe('pre-market, regular, post-market, or closed; null outside every window')
          .nullish(),
        isOpen: z.boolean(),
        timezone: z.string().optional(),
        timestamp: z.coerce.number(),
        holiday: z.string().describe('Holiday name when the market is shut for one').nullish(),
        tradingHours: z
          .object({ preMarket: z.string(), regular: z.string(), postMarket: z.string() })
          .passthrough()
          .optional(),
        tradingSessionDate: z
          .string()
          .describe('Current trading session date (YYYY-MM-DD)')
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/instruments/stocks/market-status/batch',
    alias: 'stocksGetBatchMarketStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        statuses: z.array(
          z
            .object({
              symbol: z.string(),
              exchange: z.string().describe('Finnhub exchange code the symbol resolves to.'),
              phase: z
                .enum(['regular', 'pre-market', 'after-hours', 'closed'])
                .describe(
                  "Session phase: 'regular' | 'pre-market' | 'after-hours' | 'closed'. Null when the state is UNKNOWN (the fleet has never polled this exchange) — never a fabricated closed.",
                )
                .nullable(),
              isOpen: z.boolean().nullable(),
              timezone: z.string().nullable(),
            })
            .passthrough(),
        ),
        timestamp: z.coerce.number().describe('Server epoch-ms the batch was resolved at.'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/internal/filings/ingest-event',
    alias: 'internalFilingsIngestEvent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.unknown(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/internal/fundamentals/ingest-ads-ratio',
    alias: 'internalFundamentalsIngestAdsRatio',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: internalFundamentalsIngestAdsRatio_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/internal/fundamentals/ingest-extraction',
    alias: 'internalFundamentalsIngestExtraction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.unknown(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/internal/fundamentals/ingest-issuer-balance',
    alias: 'internalFundamentalsIngestIssuerBalance',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: internalFundamentalsIngestIssuerBalance_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/internal/fundamentals/notify-update',
    alias: 'internalFundamentalsNotifyUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: internalFundamentalsNotifyUpdate_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/internal/fundamentals/quarterly-data',
    alias: 'internalFundamentalsGetQuarterlyData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'fyEndDate',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/internal/insider-transactions/ingest',
    alias: 'internalInsiderTransactionsIngest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.unknown(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/investor-relations/content',
    alias: 'investorRelationsGetContent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'url',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/investor-relations/event-reactions',
    alias: 'investorRelationsGetEventReactions',
    description: `Joins the earnings calendar (EPS actual/estimate, session hour) with daily candles into a per-announcement close-to-close reaction, keyed by fiscal (year, quarter) / date for client-side joining onto IR events.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        reactions: z.array(
          z
            .object({
              year: z.coerce.number().describe('Fiscal year').nullable(),
              quarter: z.coerce.number().describe('Fiscal quarter 1-4').nullable(),
              date: z.string().describe('Announcement date (YYYY-MM-DD)').nullable(),
              hour: z
                .string()
                .describe(
                  "Announcement session: 'bmo' | 'amc' | 'dmh'; null when the vendor omits it",
                )
                .nullable(),
              epsActual: z.coerce.number().nullable(),
              epsEstimate: z.coerce.number().nullable(),
              epsSurprise: z.coerce.number().describe('epsActual - epsEstimate').nullable(),
              epsSurprisePct: z
                .coerce.number()
                .describe('Surprise as percent of |estimate|; null when the estimate is 0/absent')
                .nullable(),
              priceReactionPct: z
                .coerce.number()
                .describe(
                  'Close-to-close move across the announcement in percent (AMC: next session vs announcement day; BMO/DMH: announcement day vs previous session)',
                )
                .nullable(),
              baselineDate: z
                .string()
                .describe('Session whose close is the pre-announcement baseline (YYYY-MM-DD)')
                .nullable(),
              reactionDate: z
                .string()
                .describe('Session whose close carries the reaction (YYYY-MM-DD)')
                .nullable(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/investor-relations/events',
    alias: 'investorRelationsGetEvents',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'types',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(
      z
        .object({
          type: z.string().describe("Event family: 'transcript' | 'press-release' | 'filing'"),
          id: z.string().describe('Vendor id (transcript id / access number / URL)').optional(),
          title: z.string().optional(),
          date: z.string().describe('Event datetime (vendor format)').optional(),
          url: z.string().describe('Document URL (press releases / filings)').optional(),
          summary: z.string().describe('Vendor summary (press releases)').optional(),
          symbol: z.string().optional(),
          year: z.coerce.number().describe('Fiscal year (earnings-call transcripts)').optional(),
          quarter: z.coerce.number().describe('Fiscal quarter 1-4 (earnings-call transcripts)').optional(),
          audioUrl: z
            .string()
            .describe('Direct vendor recording URL (mp3/m3u8); null when the call has no audio')
            .nullish(),
          audioDurationSec: z
            .coerce.number()
            .describe('Recording length in seconds; null until a transcript open probed it')
            .nullish(),
          wordCount: z
            .coerce.number()
            .describe(
              'Word count of the underlying document/transcript; null until the content was first fetched (fox F2, cache-only on the list). Call tone rides the ai-summary tones batch instead (module boundary: IR never reads AI tables).',
            )
            .nullish(),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/investor-relations/transcript',
    alias: 'investorRelationsGetTranscript',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        id: z.string().describe('Vendor transcript id'),
        title: z.string(),
        time: z.string().describe('Call datetime (vendor format)'),
        year: z.coerce.number(),
        quarter: z.coerce.number(),
        transcript: z.array(
          z
            .object({
              name: z.string(),
              speech: z.array(z.string()).describe('Paragraphs of this turn'),
            })
            .partial()
            .passthrough(),
        ),
        audio: z
          .string()
          .describe('Direct vendor recording URL (decision 5: served as-is, never proxied)')
          .nullable(),
        audioDurationSec: z
          .coerce.number()
          .describe('Recording length in seconds probed from the MP3 header; null when unknowable')
          .nullable(),
        wordCount: z
          .coerce.number()
          .describe('Word count of the transcript (fox F2 volume chip)')
          .nullable(),
        speakers: z
          .array(
            z
              .object({
                name: z.string(),
                role: z
                  .string()
                  .describe(
                    'Executive position (roster join) or the transcript participant description; null when unknown',
                  )
                  .nullable(),
                isExecutive: z
                  .boolean()
                  .describe('True when the name matched the company executives roster'),
              })
              .passthrough(),
          )
          .describe('Per-speaker role resolution (executives join + participant parse)'),
      })
      .partial()
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/investor-relations/transcript-audio-sync',
    alias: 'investorRelationsGetTranscriptAudioSync',
    description: `Lazy: the first read of a transcript enqueues the ASR forced-alignment job and returns &#x60;pending&#x60;; the finished alignment is cached permanently. &#x60;unavailable&#x60; is terminal for this transcript — the viewer then shows the text with no stamps rather than guessed ones.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        transcriptId: z.string().describe('Vendor transcript id the alignment belongs to'),
        status: z
          .enum(['pending', 'ready', 'unavailable'])
          .describe(
            'pending = the ASR job is queued or running; ready = segments are authoritative; unavailable = no timestamps will arrive for this transcript',
          ),
        paragraphs: z
          .array(
            z
              .object({
                segmentIndex: z
                  .coerce.number()
                  .describe('Index of the speaker turn, as the transcript viewer renders them'),
                paragraphIndex: z.coerce.number().describe('Index of the paragraph within that turn'),
                startSec: z
                  .coerce.number()
                  .describe(
                    'Seconds from the start of the recording; null when the aligner had no confident match. Expected for short interjections that carry too few words to anchor (correctness-or-null — never a guessed time)',
                  )
                  .nullable(),
                confidence: z
                  .coerce.number()
                  .describe('Match confidence 0-1 for this anchor; null when unmatched')
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('Per-paragraph anchors; empty unless status is ready'),
        totalParagraphs: z.coerce.number().describe('Paragraphs in the transcript').nullable(),
        matchedParagraphs: z
          .coerce.number()
          .describe('How many paragraphs the aligner anchored confidently')
          .nullable(),
        unavailableReason: z
          .string()
          .describe(
            'Machine-readable reason when status is unavailable (no-audio, audio-not-a-recording, asr-no-speech, aligner-not-configured, ...)',
          )
          .nullable(),
        progress: z
          .object({
            stage: z
              .enum(['downloading', 'transcribing', 'matching'])
              .describe(
                'downloading = fetching the vendor recording; transcribing = uploading pieces to the ASR (done/total count them); matching = anchoring paragraphs onto the word timeline',
              ),
            done: z.coerce.number().describe('Pieces transcribed so far').nullable(),
            total: z.coerce.number().describe('Pieces in total').nullable(),
            elapsedSec: z.coerce.number().describe('Seconds since the worker picked the job up'),
          })
          .passthrough()
          .describe(
            'Live worker progress; null unless status is pending and a worker holds the job',
          )
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/me/subscriptions',
    alias: 'followingGetSubscriptions',
    requestFormat: 'json',
    response: FollowingResponseDto,
  },
  {
    method: 'post',
    path: '/api/merged-portfolios',
    alias: 'mergedPortfoliosCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: mergedPortfoliosCreate_Body,
      },
    ],
    response: MergedPortfolioResponseDto,
  },
  {
    method: 'get',
    path: '/api/merged-portfolios',
    alias: 'mergedPortfoliosFindAll',
    requestFormat: 'json',
    parameters: [
      {
        name: 'light',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(MergedPortfolioResponseDto),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id',
    alias: 'mergedPortfoliosFindOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'light',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: MergedPortfolioResponseDto,
  },
  {
    method: 'put',
    path: '/api/merged-portfolios/:id',
    alias: 'mergedPortfoliosUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: mergedPortfoliosUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: MergedPortfolioResponseDto,
  },
  {
    method: 'delete',
    path: '/api/merged-portfolios/:id',
    alias: 'mergedPortfoliosDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id/analytics',
    alias: 'mergedPortfoliosGetAnalytics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'timeframe',
        type: 'Query',
        schema: z
          .enum(['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y', '20Y', 'YTD', 'All'])
          .optional(),
      },
      {
        name: 'fromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'benchmarks',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/merged-portfolios/:id/members',
    alias: 'mergedPortfoliosAddMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          portfolioId: z.string().uuid().describe('Portfolio ID to add to the merge'),
        }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: MergedPortfolioResponseDto,
  },
  {
    method: 'delete',
    path: '/api/merged-portfolios/:id/members/:portfolioId',
    alias: 'mergedPortfoliosRemoveMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'portfolioId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.union([
      MergedPortfolioResponseDto,
      z
        .object({ deleted: z.boolean().describe('True when the merged portfolio was dissolved') })
        .passthrough(),
    ]),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id/positions',
    alias: 'mergedPortfoliosGetPositions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.array(
      z
        .object({
          id: z.string().describe('Position ID'),
          sourcePortfolioId: z.string().describe('Source portfolio ID'),
          sourcePortfolioName: z.string().describe('Source portfolio name'),
          symbol: z.string().describe('Symbol'),
          name: z.string().describe("Resolved company display name (e.g. 'Apple Inc')").optional(),
          assetType: z.string().describe('Asset type'),
          quantity: z.coerce.number().describe('Quantity'),
          avgCostBasis: z
            .coerce.number()
            .describe('Average cost basis — null when costUnavailable')
            .nullable(),
          totalCostBasis: z
            .coerce.number()
            .describe('Total cost basis — null when costUnavailable')
            .nullable(),
          instrumentAvgCostBasis: z
            .coerce.number()
            .describe(
              "Per-share cost in the INSTRUMENT's own currency — a display-independent historical fact, rendered as the Avg Cost sub-line. Null when the aggregate spans members that disagree on that cost or on the instrument currency it is stated in (a blend of two facts is not one), or when a member could not resolve its trade-time rate.",
            )
            .nullish(),
          instrumentCurrentPrice: z
            .coerce.number()
            .describe(
              "The live quote in the INSTRUMENT's own trading currency, major unit — the reading `currentPrice` was converted from, so a figure quoted in that currency (the analyst price target) can be measured against it. Per-symbol, so one value serves the whole aggregate. Null when no quote priced the row, and when the members value a unit differently (a model values each book on its own, so the row price is a blend).",
            )
            .nullish(),
          currentPrice: z
            .coerce.number()
            .describe(
              'Unit price: the common price of the members, or, when their unit prices differ (a fund at its VNAV in one book and at its purchase price in another), the row value divided by its quantity, so price times quantity is the market value.',
            )
            .optional(),
          priceStaleSince: z
            .string()
            .describe(
              "YYYY-MM-DD of the market CLOSE currentPrice is, when no live quote priced the row: a dead line's last close (owner ruling D94) or the candle mirror's last bar. Null on a live quote and on a stored fallback (priceUnavailable says that one), and when the prices of the members differ (a blended price is no close of one day).",
            )
            .nullable(),
          marketValue: z.coerce.number().describe('Market value').optional(),
          priceUnavailable: z
            .boolean()
            .describe(
              '#1186 — no quote priced any contributing member row; the stored cost is standing in. Gain and percentage are then ABSENT, not 0.',
            )
            .optional(),
          fxRefused: z
            .boolean()
            .describe(
              'The member row received a quote but its instrumentCurrency→purchaseCurrency pair had no usable rate, so the hop was REFUSED: the row is served NATIVE (every money field in displayCurrency = instrumentCurrency), unblended, and left out of the merged totals. Distinct from priceUnavailable (no quote at all). Served only when true.',
            )
            .optional(),
          costUnavailable: z
            .boolean()
            .describe(
              'An fxRefused row with no instrument-currency cost on record: avgCostBasis, totalCostBasis, unrealizedGainLoss and percentageChange are withheld (null / absent), marketValue kept. Served only when true.',
            )
            .optional(),
          displayCurrency: z
            .string()
            .describe(
              'Currency the money fields on this row are denominated in. Absent on every row in the aggregation target; set to the row’s OWN currency on an FX-refused member served unconverted (never folded at parity).',
            )
            .optional(),
          unrealizedGainLoss: z
            .coerce.number()
            .describe('Unrealized gain/loss — absent when priceUnavailable.')
            .optional(),
          percentageChange: z.coerce.number().describe('Percentage change').optional(),
          dayChange: z.coerce.number().describe('Day change').optional(),
          dayChangePercent: z.coerce.number().describe('Day change percentage').optional(),
          preMarketPrice: z.coerce.number().describe('Last pre-market price for the symbol').optional(),
          preMarketChangePercent: z
            .coerce.number()
            .describe('Last pre-market % change vs. previous regular close')
            .optional(),
          afterHoursPrice: z.coerce.number().describe('Last after-hours price for the symbol').optional(),
          afterHoursChangePercent: z
            .coerce.number()
            .describe("Last after-hours % change vs. today's regular close")
            .optional(),
          sector: z.string().describe('Sector').optional(),
          industry: z.string().describe('Industry').optional(),
          country: z.string().describe('Country').optional(),
          logo: z.string().describe('Company logo URL').optional(),
          optionSymbol: z.string().describe('OCC option symbol').optional(),
          optionType: z.string().describe('Option type (CALL / PUT)').optional(),
          strikePrice: z.coerce.number().describe('Strike price').optional(),
          expirationDate: z.string().describe('Expiration date (options only)').optional(),
          underlyingSymbol: z.string().describe('Underlying symbol (options only)').optional(),
          contractSize: z.coerce.number().describe('Option contract size (default 100)').optional(),
          metrics: z.record(z.coerce.number()).describe('Flat fundamental metrics map').optional(),
          purchaseCurrency: z
            .string()
            .describe(
              "Lots' cost-basis currency (the member currency at BUY time). Omitted when the aggregated member portfolios disagree on the basis — the sell flow then disambiguates via the source-portfolio picker.",
            )
            .optional(),
          instrumentCurrency: z
            .string()
            .describe('Instrument trading currency (stock currency from exchange)')
            .optional(),
          conversionRate: z
            .coerce.number()
            .describe(
              'The spot FX rate the CURRENT figures on this row were converted at. Absent — never 1 — when no conversion applied, or when the contributing members converted at different rates: publishing one of them would attribute the whole aggregate to a rate that produced only part of it.',
            )
            .optional(),
          instrumentConversionRate: z
            .coerce.number()
            .describe(
              'The OTHER spot leg — instrumentCurrency into purchaseCurrency, applied while pricing the row. It is the ONLY conversion a book read in its own account currency performs, so a client reading conversionRate alone reports such a book as converting nothing. Absent — never 1 — when the instrument already trades in the account currency, when no rate was resolvable, or when the contributing members disagree.',
            )
            .optional(),
          historicalConversionRate: z
            .coerce.number()
            .describe(
              "The trade-day FX rate this row's COST was converted at. Absent when the cost fell back to spot, or when the contributing members disagree on the rate.",
            )
            .optional(),
          costAtSpotRate: z
            .boolean()
            .describe(
              "At least one contributing member converted its COST at today's spot rate (no trade-day rate, or a stored one refused as not this pair FX), so part of this basis moves with the rate. Distinct from an absent historicalConversionRate, which members that merely disagree on a genuine rate also produce. Served only when true.",
            )
            .optional(),
          isCfd: z
            .boolean()
            .describe('True for CFD rows (a position flavor, not an assetType)')
            .optional(),
          cfdDirection: z.string().describe('CFD direction — BUY (long) / SELL (short)').optional(),
          cfdMargin: z
            .coerce.number()
            .describe('Broker-blocked margin, in the display currency')
            .optional(),
          cfdEntryPriceUnknown: z
            .boolean()
            .describe('True when the broker reported no usable CFD entry price')
            .optional(),
          cfdContractSize: z.coerce.number().describe('Units per lot (CFD rows)').optional(),
          cfdSwapAccrued: z.coerce.number().describe('Accrued financing, signed (CFD rows)').optional(),
          cfdOverridePrice: z
            .coerce.number()
            .describe("The user's stored current price (CFD rows)")
            .nullish(),
          cfdUnpriced: z
            .boolean()
            .describe('True when no live source nor override priced the CFD row')
            .optional(),
          futureExpiredUnpriced: z
            .boolean()
            .describe(
              'True on an open FUTURE past its expiry that no statement settled: held at its stored mark, listed by Portfolio Health as expired_future_open (R47, §4.18.1)',
            )
            .optional(),
          cfdPriceSource: z
            .enum(['stoxly_estimate', 'user_override', 'broker_statement', 'unpriced'])
            .optional(),
          cfdPriceSourceName: z
            .string()
            .describe('The live source behind a Stoxly estimate')
            .optional(),
          cfdPriceAsOf: z.string().describe('ISO instant the CFD price is as of').optional(),
          cfdStaleAsOf: z
            .string()
            .describe(
              'Statement date of the newest upload that no longer listed this CFD (kept open, captioned); null = listed',
            )
            .nullish(),
          isOpen: z.boolean().describe('Is position open'),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id/snapshot',
    alias: 'mergedPortfoliosGetSnapshot',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id/summary-metrics',
    alias: 'mergedPortfoliosGetSummaryMetrics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'light',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/merged-portfolios/:id/transactions',
    alias: 'mergedPortfoliosGetTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(0).optional(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .enum([
            'BUY',
            'SELL',
            'FEE',
            'DIVIDEND',
            'BUY_TO_OPEN',
            'BUY_TO_CLOSE',
            'SELL_TO_OPEN',
            'SELL_TO_CLOSE',
            'EXERCISE',
            'ASSIGNMENT',
            'EXPIRE',
            'SPLIT',
            'DELIVERY_INBOUND',
            'DELIVERY_OUTBOUND',
            'INTEREST',
            'TAX_REFUND',
            'FEE_REFUND',
          ])
          .optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        data: z.array(
          z
            .object({
              id: z.string().describe('Transaction ID'),
              sourcePortfolioId: z.string().describe('Source portfolio ID'),
              sourcePortfolioName: z.string().describe('Source portfolio name'),
              walletId: z
                .string()
                .describe('Funding/crediting wallet ID (#560 edit prefill)')
                .optional(),
              positionId: z
                .string()
                .describe('Member position ID backing this transaction')
                .optional(),
              type: z.string().describe('Transaction type'),
              symbol: z.string().describe('Symbol'),
              name: z
                .string()
                .describe(
                  'What the row is shown under: the ticker, or for a fund unit (whose symbol is a key made from its name) the name the member position’s statement gave it.',
                ),
              quantity: z.coerce.number().describe('Quantity'),
              price: z.coerce.number().describe('Price'),
              total: z.coerce.number().describe('Total amount'),
              fees: z
                .coerce.number()
                .describe(
                  'Fees, in `currency`: the commission carried from `sourceFeesCurrency` at that currency’s rate. Absent when the row has none or when that pair has no rate (see `sourceFees`).',
                )
                .optional(),
              taxes: z
                .coerce.number()
                .describe('Withholding taxes, converted like `total` (gross = total + taxes)')
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency the money fields on this row are denominated in: the aggregation target normally, or the row’s own currency when its pair had no usable rate (the row is then served unconverted rather than folded at parity).',
                ),
              sourceCurrency: z
                .string()
                .describe(
                  'Settlement currency of the source row (historical fact; does NOT follow the aggregation target). `currency` is the conversion target all money fields are in.',
                ),
              sourceTotalAmount: z
                .coerce.number()
                .describe('Stored (unconverted) total amount, denominated in sourceCurrency'),
              sourcePrice: z
                .coerce.number()
                .describe(
                  'Stored (unconverted) per-unit price, exactly as the member row stores it, denominated in sourcePriceCurrency — NOT a totalAmount/quantity re-derivation. The edit dialog prefills from it and the update path recomputes totalAmount = quantity x price x exchangeRate.',
                ),
              sourcePriceCurrency: z
                .string()
                .describe(
                  'Currency `sourcePrice` is denominated in — the instrument currency on a cross-currency row, the settlement currency when the stored rate is 1/NULL (the plain-path rule).',
                ),
              sourceFees: z.coerce.number().describe('Stored (unconverted) fees').optional(),
              sourceFeesCurrency: z
                .string()
                .describe(
                  'Currency `sourceFees` is denominated in (`feesCurrencyOf`): the PRICE currency for a kernel fee, the settlement currency for a commission the statement books apart. The converted `fees` above is that fee carried into `currency` at the rate of THIS currency on the row’s day, and is absent when that pair has no rate.',
                ),
              sourceTaxes: z
                .coerce.number()
                .describe(
                  'Stored (unconverted) withholding taxes, denominated like sourceTotalAmount',
                )
                .optional(),
              sourceTaxLotInfo: z
                .object({
                  lots: z.array(z.unknown()),
                  method: z.string(),
                  totalCostBasis: z.coerce.number(),
                  realizedGainLoss: z.coerce.number().optional(),
                })
                .passthrough()
                .describe(
                  "The stored tax-lot info exactly as the member row carries it (values in the source transaction's currency), while `taxLotInfo` carries the converted totals.",
                )
                .optional(),
              executedAt: z.string().describe('Execution date'),
              notes: z.string().describe('Notes').optional(),
              instrumentCurrency: z
                .string()
                .describe('Instrument trading currency (stock currency from exchange)')
                .optional(),
              assetType: z
                .string()
                .describe(
                  "Asset class of the traded instrument, off the member row's linked position - the same relation `instrumentCurrency` above reads. Absent when no position is linked.",
                )
                .optional(),
              exchangeRate: z
                .coerce.number()
                .describe('Historical exchange rate (portfolio currency per stock currency)')
                .optional(),
              taxLotInfo: z
                .object({
                  lots: z.array(z.unknown()),
                  method: z.string(),
                  totalCostBasis: z.coerce.number(),
                  realizedGainLoss: z.coerce.number(),
                })
                .passthrough()
                .describe(
                  'Tax-lot info for SELL transactions. realizedGainLoss and totalCostBasis are in the merged-portfolio target currency.',
                )
                .optional(),
            })
            .passthrough(),
        ),
        total: z.coerce.number(),
        page: z.coerce.number(),
        limit: z.coerce.number(),
        totalPages: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/metric-presets',
    alias: 'metricPresetsFindAll',
    requestFormat: 'json',
    response: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          defaultFor: z
            .array(z.enum(['company', 'bank', 'insurer', 'reit']))
            .describe(
              'The issuer kinds that open on this set; each kind has at most one default set',
            ),
          snapshot: z
            .object({
              metrics: z.array(z.string()),
              layout: z
                .array(z.object({ i: z.string(), w: z.coerce.number(), h: z.coerce.number() }).passthrough())
                .optional(),
            })
            .passthrough(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
        })
        .passthrough(),
    ),
  },
  {
    method: 'post',
    path: '/api/metric-presets',
    alias: 'metricPresetsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: metricPresetsCreate_Body,
      },
    ],
    response: z
      .object({
        id: z.string(),
        name: z.string(),
        defaultFor: z
          .array(z.enum(['company', 'bank', 'insurer', 'reit']))
          .describe(
            'The issuer kinds that open on this set; each kind has at most one default set',
          ),
        snapshot: z
          .object({
            metrics: z.array(z.string()),
            layout: z
              .array(z.object({ i: z.string(), w: z.coerce.number(), h: z.coerce.number() }).passthrough())
              .optional(),
          })
          .passthrough(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough(),
  },
  {
    method: 'put',
    path: '/api/metric-presets/:id',
    alias: 'metricPresetsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: metricPresetsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        id: z.string(),
        name: z.string(),
        defaultFor: z
          .array(z.enum(['company', 'bank', 'insurer', 'reit']))
          .describe(
            'The issuer kinds that open on this set; each kind has at most one default set',
          ),
        snapshot: z
          .object({
            metrics: z.array(z.string()),
            layout: z
              .array(z.object({ i: z.string(), w: z.coerce.number(), h: z.coerce.number() }).passthrough())
              .optional(),
          })
          .passthrough(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough(),
  },
  {
    method: 'delete',
    path: '/api/metric-presets/:id',
    alias: 'metricPresetsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/metric-presets/:id/default/:kind',
    alias: 'metricPresetsSetDefault',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'kind',
        type: 'Path',
        schema: z.enum(['company', 'bank', 'insurer', 'reit']),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/metric-presets/default/:kind',
    alias: 'metricPresetsClearDefault',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kind',
        type: 'Path',
        schema: z.enum(['company', 'bank', 'insurer', 'reit']),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/news/articles',
    alias: 'newsGetArticles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'cursor',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(100).optional(),
      },
      {
        name: 'min_actionability',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(5).optional(),
      },
      {
        name: 'actionability_label',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'sentiment_tone',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'time_sensitivity',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'article_type',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'source_tier',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(3).optional(),
      },
      {
        name: 'from_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'sort',
        type: 'Query',
        schema: z.enum(['analyzed_at', 'published_at']).optional(),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/articles/:id',
    alias: 'newsGetArticle',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Article not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global',
    alias: 'newsGetGlobalNews',
    requestFormat: 'json',
    parameters: [
      {
        name: 'cursor',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(100).optional(),
      },
      {
        name: 'significance_level',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'event_type',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'news_scope',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'region_scopes',
        type: 'Query',
        schema: z.array(z.enum(['US', 'EU', 'UK', 'APAC', 'OTHER'])).optional(),
      },
      {
        name: 'sector_scopes',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'communication_services',
              'consumer_discretionary',
              'consumer_staples',
              'energy',
              'financials',
              'healthcare',
              'industrials',
              'materials',
              'real_estate',
              'technology',
              'utilities',
            ]),
          )
          .optional(),
      },
      {
        name: 'source',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'from_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'sort',
        type: 'Query',
        schema: z.enum(['published_at', 'analyzed_at']).optional(),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global-events',
    alias: 'newsListGlobalEvents',
    requestFormat: 'json',
    parameters: [
      {
        name: 'cursor',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional(),
      },
      {
        name: 'lifecycle',
        type: 'Query',
        schema: z.array(z.enum(['ongoing', 'ended'])).optional(),
      },
      {
        name: 'region_scopes',
        type: 'Query',
        schema: z.array(z.enum(['GLOBAL', 'US', 'EU', 'UK', 'APAC', 'OTHER'])).optional(),
      },
      {
        name: 'sector_scopes',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'communication_services',
              'consumer_discretionary',
              'consumer_staples',
              'energy',
              'financials',
              'healthcare',
              'industrials',
              'materials',
              'real_estate',
              'technology',
              'utilities',
            ]),
          )
          .optional(),
      },
      {
        name: 'importance',
        type: 'Query',
        schema: z.array(z.enum(['high', 'medium', 'low'])).optional(),
      },
      {
        name: 'q',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'date',
        type: 'Query',
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
      },
      {
        name: 'start_date',
        type: 'Query',
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
      },
      {
        name: 'end_date',
        type: 'Query',
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
      },
      {
        name: 'developments_limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(5).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Unknown filter value`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global-events/:id',
    alias: 'newsGetGlobalEvent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Global event not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global-events/:id/developments/:developmentId',
    alias: 'newsGetGlobalEventDevelopment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'developmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Global event or development not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global/:id',
    alias: 'newsGetGlobalNewsArticle',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Global news article not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/global/market-contexts',
    alias: 'newsGetMarketContexts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'scope',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'sector',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/insights/daily',
    alias: 'newsListTrackedDailyInsights',
    requestFormat: 'json',
    parameters: [
      {
        name: 'from_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(100).optional(),
      },
      {
        name: 'periods',
        type: 'Query',
        schema: z.enum(['daily', 'all']).optional().default('daily'),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/livestreams/channels',
    alias: 'livestreamsGetChannels',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/news/livestreams/live',
    alias: 'livestreamsCheckLive',
    requestFormat: 'json',
    parameters: [
      {
        name: 'handle',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/insights/daily',
    alias: 'newsListDailyInsights',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'from_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(100).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/insights/daily/:date',
    alias: 'newsGetDailyInsight',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'date',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Daily insight not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/insights/monthly',
    alias: 'newsGetMonthlyInsights',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/insights/overall',
    alias: 'newsGetOverallInsight',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Ticker not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/insights/weekly',
    alias: 'newsGetWeeklyInsights',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/narrative-history',
    alias: 'newsGetNarrativeHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
    ],
    response: z
      .object({
        ticker: z.string().describe('Normalized upstream ticker.'),
        periodType: z
          .string()
          .describe("The archived period type — always 'overall' on this route."),
        versions: z
          .array(
            z
              .object({
                version: z.coerce.number().describe('Upstream version number of the rollup narrative.'),
                summary: z.string().describe('The narrative text of this version.'),
                sentiment: z.string().nullable(),
                sourceArticleCount: z.coerce.number().nullable(),
                sourceSummaryCount: z.coerce.number().nullable(),
                updatedAt: z
                  .string()
                  .describe('Upstream generation instant of this version (ISO), when known.')
                  .nullable(),
                firstSeenAt: z
                  .string()
                  .describe('When this version was first archived locally (ISO).'),
              })
              .passthrough(),
          )
          .describe('Versions, newest first.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/pulse',
    alias: 'newsGetTickerPulse',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        ticker: z.string().describe('Normalized upstream ticker (exchange suffix stripped).'),
        computedAt: z.string().describe('ISO instant the aggregate was computed at.'),
        volume: z
          .object({
            count24h: z
              .coerce.number()
              .describe('Articles published in the last 24 hours (exact, timestamp-cut).'),
            baselineDailyAvg: z
              .coerce.number()
              .describe(
                'Average articles/day over the 7 calendar days preceding the 24h window (dates [now-8d .. now-2d]). Null when the baseline is unknown.',
              )
              .nullable(),
            ratio: z
              .coerce.number()
              .describe('count24h over the floored baseline; null when no baseline exists.')
              .nullable(),
            level: z
              .enum(['quiet', 'normal', 'elevated', 'high'])
              .describe(
                "Rule-based volume classification: 'quiet' | 'normal' | 'elevated' | 'high'.",
              ),
          })
          .passthrough(),
        sentiment: z
          .object({
            windowDays: z
              .coerce.number()
              .describe('Nominal trailing window the distribution covers, in days.'),
            total: z.coerce.number().describe('Articles in the window (rated + unrated).'),
            rated: z.coerce.number().describe('Articles carrying a sentiment tone.'),
            extremelyBullish: z.coerce.number(),
            moderatelyBullish: z.coerce.number(),
            slightlyBullish: z.coerce.number(),
            neutral: z.coerce.number(),
            slightlyBearish: z.coerce.number(),
            moderatelyBearish: z.coerce.number(),
            extremelyBearish: z.coerce.number(),
            unrated: z
              .coerce.number()
              .describe('Articles whose tone is null/unknown — never guessed into a bucket.'),
            mood: z
              .enum([
                'strongly_bullish',
                'bullish',
                'cautiously_bullish',
                'neutral',
                'mixed',
                'cautiously_bearish',
                'bearish',
                'strongly_bearish',
              ])
              .describe(
                'Rule-based mood over the rated distribution (e.g. cautiously_bearish). Null below 3 rated articles — insufficient data is reported, never fabricated.',
              )
              .nullable(),
          })
          .passthrough(),
      })
      .passthrough(),
    errors: [
      {
        status: 503,
        description: `News API not configured`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/stats',
    alias: 'newsGetTickerStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Ticker not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tickers/:ticker/timeline',
    alias: 'newsGetTimeline',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ticker',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'cursor',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(200).optional(),
      },
      {
        name: 'min_score',
        type: 'Query',
        schema: z.coerce.number().gte(1).lte(5).optional(),
      },
      {
        name: 'actionability_label',
        type: 'Query',
        schema: z.enum(['urgent', 'high', 'watch', 'background', 'noise']).optional(),
      },
      {
        name: 'event_type',
        type: 'Query',
        schema: z
          .enum([
            'earnings_report',
            'earnings_beat',
            'earnings_miss',
            'guidance_update',
            'partnership',
            'acquisition',
            'product_launch',
            'regulatory_approval',
            'regulatory_penalty',
            'executive_change',
            'market_share',
            'contract_win',
            'contract_loss',
            'legal_development',
            'other',
          ])
          .optional(),
      },
      {
        name: 'impact_magnitude',
        type: 'Query',
        schema: z.enum(['high', 'medium', 'low']).optional(),
      },
      {
        name: 'is_material',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'from_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to_date',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'order',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/timeline/:id',
    alias: 'newsGetTimelineEvent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Timeline event not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tracked-tickers',
    alias: 'trackedTickersGetTrackedTickers',
    requestFormat: 'json',
    response: z
      .object({
        data: z
          .object({
            tickers: z.array(
              z
                .object({
                  id: z.string().describe('Tracked ticker ID'),
                  symbol: z.string().describe('Stock ticker symbol'),
                  newsSymbol: z
                    .string()
                    .describe(
                      'The provider entity this listing denotes (instruments.news_symbol), or null when no news source has been identified for it. Two spellings of one security share a value; compare THIS, never `symbol`, to decide whether a paper is already tracked.',
                    )
                    .nullable(),
                  listingSymbols: z
                    .array(z.string())
                    .describe(
                      "Every listing symbol that resolves to this row's newsSymbol, including its own. Decide whether a paper is already tracked by membership in THIS, never by comparing `symbol`.",
                    ),
                  createdAt: z.string().describe('When the ticker was added to tracking'),
                  totalArticleCount: z
                    .coerce.number()
                    .describe('Total number of articles scanned for this ticker'),
                  totalAnalyzedArticleCount: z
                    .coerce.number()
                    .describe('Total number of analyzed articles for this ticker'),
                  lastFetchedAt: z
                    .string()
                    .describe('Last time news was fetched for this ticker')
                    .optional(),
                  isPlanSlotActive: z
                    .boolean()
                    .describe(
                      'Did the reader keep this company when their list was over the plan? Only decides anything while the list is bigger than the plan.',
                    ),
                  isPlanCovered: z
                    .boolean()
                    .describe(
                      'Does the plan serve this company right now? Every row while the whole list fits the plan; over the cap, the oldest papers among the rows the reader kept.',
                    ),
                  isActive: z.boolean().describe('Whether the ticker is active in news-api'),
                  isDefault: z
                    .boolean()
                    .describe('True if the ticker is a platform default (e.g. Mag 7)'),
                })
                .passthrough(),
            ),
            limits: z
              .object({
                current: z.coerce.number().describe('Current number of tracked tickers'),
                max: z.coerce.number().describe('Maximum tickers allowed for this tier'),
                canAddMore: z.boolean().describe('Whether the user can add more tickers'),
              })
              .passthrough(),
            summary: z
              .object({
                activeTickers: z.coerce.number().describe('Number of active tracked tickers'),
                totalArticleCount: z
                  .coerce.number()
                  .describe('Total articles scanned across all tracked tickers'),
                totalAnalyzedArticleCount: z
                  .coerce.number()
                  .describe('Total analyzed articles across all tracked tickers'),
              })
              .passthrough(),
          })
          .passthrough(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/news/tracked-tickers',
    alias: 'trackedTickersAddTrackedTicker',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ symbol: z.string().min(1).describe('Stock ticker symbol to track') }),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden - demo user or tier limit reached`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Conflict - the paper is already tracked, under trackedAs`,
        schema: z
          .object({
            statusCode: z.coerce.number(),
            message: z.string(),
            trackedAs: z
              .string()
              .describe('The spelling the reader already tracks this paper under'),
            defaultTicker: z
              .boolean()
              .describe(
                'The paper is one of the platform defaults (Mag 7): already tracked on every account, free, and never addable as a row.',
              )
              .optional(),
            reEnabled: z
              .boolean()
              .describe(
                'ALWAYS false. A platform default can no longer be switched off, so no add can turn one back on; the field is kept because it is part of the generated client contract.',
              )
              .optional(),
          })
          .passthrough(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/news/tracked-tickers/:symbol',
    alias: 'trackedTickersRemoveTrackedTicker',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden - demo user`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Ticker not tracked`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/news/tracked-tickers/active',
    alias: 'trackedTickersSetActiveTickers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: trackedTickersSetActiveTickers_Body,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden - demo user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/news/tracked-tickers/plan-slot-notice',
    alias: 'trackedTickersGetPlanSlotNotice',
    requestFormat: 'json',
    response: z
      .object({
        data: z
          .object({
            required: z
              .boolean()
              .describe(
                'Is the reader over their cap and not yet told at this tier? The dialog shows when true.',
              ),
            tracked: z
              .coerce.number()
              .describe('Companies the reader tracks, excluding the platform defaults'),
            covered: z.coerce.number().describe('How many of those the plan currently covers'),
          })
          .passthrough(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/news/tracked-tickers/plan-slot-notice/ack',
    alias: 'trackedTickersAcknowledgePlanSlotNotice',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/newsletter/subscribe',
    alias: 'newsletterSubscribe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: newsletterSubscribe_Body,
      },
    ],
    response: z
      .object({
        success: z.boolean().describe('Success status of the operation'),
        message: z.string().describe('Response message'),
        data: z
          .object({ id: z.string(), email: z.string() })
          .passthrough()
          .describe('Newsletter subscription data')
          .optional(),
      })
      .passthrough(),
    errors: [
      {
        status: 409,
        description: `Email already subscribed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/notifications',
    alias: 'notificationsList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'cursor',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'unreadOnly',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'news',
              'earnings',
              'insider_trade',
              'analyst_rating',
              'billing_payment_failed',
              'billing_payment_succeeded',
              'billing_trial_ending',
              'billing_plan_changed',
              'corporate_action',
              'broker_connection_broken',
              'alert',
              'bug_report_update',
              'subscribed_portfolio_trade',
            ]),
          )
          .optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z
          .string()
          .regex(/^[A-Z0-9 ]{1,12}(\.[A-Z0-9]{1,5}){0,2}$/)
          .optional(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2}))?$/)
          .optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2}))?$/)
          .optional(),
      },
    ],
    response: z
      .object({
        items: z.array(NotificationDto),
        nextCursor: z.string().nullish(),
        unreadCount: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'patch',
    path: '/api/notifications/:id/read',
    alias: 'notificationsMarkRead',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: NotificationDto,
  },
  {
    method: 'get',
    path: '/api/notifications/preferences',
    alias: 'notificationPreferencesGet',
    requestFormat: 'json',
    response: NotificationPreferencesDto,
  },
  {
    method: 'patch',
    path: '/api/notifications/preferences',
    alias: 'notificationPreferencesUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: notificationPreferencesUpdate_Body,
      },
    ],
    response: NotificationPreferencesDto,
  },
  {
    method: 'post',
    path: '/api/notifications/read-all',
    alias: 'notificationsMarkAllRead',
    requestFormat: 'json',
    response: z.object({ updated: z.coerce.number() }).passthrough(),
  },
  {
    method: 'post',
    path: '/api/notifications/seen-all',
    alias: 'notificationsMarkAllSeen',
    requestFormat: 'json',
    response: z.object({ updated: z.coerce.number() }).passthrough(),
  },
  {
    method: 'get',
    path: '/api/notifications/symbols',
    alias: 'notificationSymbolsList',
    requestFormat: 'json',
    response: z
      .object({
        items: z.array(
          z
            .object({
              symbol: z.string().describe('Normalized ticker symbol'),
              enabled: z
                .boolean()
                .describe('true = notifications enabled (default), false = muted by user'),
              inPortfolio: z
                .boolean()
                .describe('Whether the symbol is held in any portfolio position'),
              inWatchlist: z.boolean().describe('Whether the symbol is on any watchlist'),
            })
            .passthrough(),
        ),
        totalEnabled: z.coerce.number(),
        totalMuted: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'patch',
    path: '/api/notifications/symbols',
    alias: 'notificationSymbolsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: notificationSymbolsUpdate_Body,
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Normalized ticker symbol'),
        enabled: z
          .boolean()
          .describe('true = notifications enabled (default), false = muted by user'),
      })
      .passthrough(),
  },
  {
    method: 'put',
    path: '/api/notifications/symbols',
    alias: 'notificationSymbolsReplaceAll',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: notificationSymbolsReplaceAll_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/notifications/unread-count',
    alias: 'notificationsUnreadCount',
    requestFormat: 'json',
    response: z.object({ count: z.coerce.number(), unseen: z.coerce.number() }).passthrough(),
  },
  {
    method: 'get',
    path: '/api/peers/:symbol/stream',
    alias: 'peerDiscoveryStreamPeers',
    description: `Server-Sent Events stream that runs Algo and AI peer discovery in parallel. AAPL is publicly accessible for demo; all other symbols require authentication. Use scope&#x3D;ai_only to retry just the AI task.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'scope',
        type: 'Query',
        schema: z.enum(['all', 'ai_only']).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolio-health',
    alias: 'portfolioHealthGetHealth',
    description: `Every ledger/moat problem detectable for the authenticated user: delisted or renamed holdings, positions with no acquisition cost, stalled or failed imports, possible duplicate transactions, stale price coverage, stale FX, unrecognised instruments, costs converted at today&#x27;s rate, option contracts still open past their expiry, lots counted twice (a zero-cost lot beside the row an import wrote for the same position), automatic top-up deposits whose purchase was deleted (still counted as cash and capital), holdings typed by hand that an imported account in another book holds lot for lot (counted twice unless they really sit at two brokers), trades typed by hand that a statement&#x27;s import already books (a phantom holding), holdings the newest broker statement lists in another quantity than the book holds on its day (one item per ticker), price series that stopped, and books whose chart holds a large share of their value at purchase price because the stored price history is missing or starts after the buy, and fee rows that name no fee (charged to the return, listed as unclassified). Each issue carries the FULL &#x60;count&#x60; plus a bounded &#x60;items&#x60; sample (at most 10) with &#x60;truncated&#x60; set accordingly. A detection that fails is ABSENT from &#x60;issues&#x60; rather than reported as clean.`,
    requestFormat: 'json',
    response: z
      .object({
        issues: z
          .array(
            z
              .object({
                type: z
                  .enum([
                    'delisted_instrument',
                    'missing_cost_basis',
                    'stalled_import',
                    'duplicate_transactions',
                    'stale_prices',
                    'stale_fx',
                    'unresolved_instrument',
                    'missing_trade_day_fx',
                    'expired_option_open',
                    'expired_future_open',
                    'stopped_price_series',
                    'valued_at_cost',
                    'unclassified_fee',
                    'cfd_estimate',
                    'cfd_unpriced',
                    'cfd_stale_open',
                    'delivery_close_missing',
                    'commission_estimated',
                    'sale_without_quantity',
                    'opening_lot_estimated',
                    'risk_unmodelled',
                    'risk_ratio_unmeasured',
                    'duplicate_lot',
                    'orphan_top_up',
                    'hand_book_duplicate',
                    'hand_lot_vs_statement',
                    'subscription_not_allotted',
                    'holdings_vs_statement',
                  ])
                  .describe('Stable FE i18n key segment.'),
                severity: z.enum(['high', 'mid', 'low']),
                portfolioId: z
                  .string()
                  .describe('Owning portfolio, or null when the issue is account-scoped.')
                  .nullable(),
                portfolioName: z.string().describe('Name of `portfolioId`.').nullable(),
                count: z
                  .coerce.number()
                  .describe('FULL cardinality — never the length of the bounded `items`.'),
                items: z
                  .array(
                    z
                      .object({
                        label: z
                          .string()
                          .describe("Human label, e.g. 'CDR.WA - CD Projekt' or a file name."),
                        note: z
                          .string()
                          .describe(
                            "Detail line in ENGLISH, e.g. 'delisted 2026-03-01' or an import error message. Derived from `noteCode` + `noteParams` (`healthNote`), so the two cannot disagree. Read it for support, logs and the dismissal key; render `noteCode` to the user.",
                          )
                          .nullable(),
                        noteCode: z
                          .enum([
                            'delistedRenamedTo',
                            'delistedOn',
                            'delistedStillDeclared',
                            'delistedNoHoldingsSheet',
                            'costBasisCfdEntryUnknown',
                            'costBasisNone',
                            'importPendingSince',
                            'importRefusedShorterHistory',
                            'importFailed',
                            'priceNoCoverage',
                            'priceNotServable',
                            'priceNoDatedBar',
                            'priceLastBar',
                            'instrumentIsinNoListing',
                            'instrumentNoRecord',
                            'seriesNoBarSince',
                            'tradeDayFxNoRate',
                            'tradeDayFxNoOpeningLot',
                            'tradeDayFxRefused',
                            'optionExpiredStillOpen',
                            'futureExpiredStillOpen',
                            'fxNoRecentRate',
                            'overdraftSince',
                            'overdraftBetween',
                            'cfdClosesWithoutMargin',
                            'currenciesLeftOut',
                            'unclassifiedFeeRows',
                            'basisUnknownCostRate',
                            'basisUnknownHistory',
                            'costNoRateInForce',
                            'cashRowsNoRate',
                            'openingDateOverridable',
                            'openingDateFixed',
                            'cfdStoxlyEstimateFrom',
                            'cfdStoxlyEstimate',
                            'cfdNoLivePrice',
                            'cfdContractSizeUnknown',
                            'cfdOpenPriceUnknown',
                            'cfdNotOnLatestStatement',
                            'twrNoCollateral',
                            'twrUnpricedFlow',
                            'twrUnpricedCash',
                            'twrEquityWipedOut',
                            'deliveryCloseRows',
                            'commissionEstimatedRows',
                            'saleWithoutQuantityRows',
                            'openingLotsEstimated',
                            'riskUnmodelledLegs',
                            'ownReturnNoOwnCapital',
                            'ownReturnTraceCapital',
                            'ownReturnWipedOut',
                            'ownReturnNotSeparable',
                            'ownReturnNegativeValue',
                            'ownReturnHistoryMissing',
                            'ownReturnNeverInvested',
                            'ownReturnNoRate',
                            'ownReturnNotMeasurable',
                            'riskRatiosNoRiskFreeRate',
                            'riskRatiosModelOnly',
                            'fxCostWithheld',
                            'salesEnteredTwice',
                            'lotBookedTwice',
                            'orphanTopUp',
                            'handLotsNotOnStatement',
                            'windowExportStartsOn',
                            'handHoldingAlsoImported',
                            'duplicateTransactions',
                            'subscriptionNotAllotted',
                            'statementHoldingDiffers',
                            'statementHoldingDust',
                          ])
                          .describe(
                            'What `note` SAYS, as a code the client translates. Null only when the detail is a string we did not author (a vendor import error) or when there is no detail at all.',
                          )
                          .nullable(),
                        noteParams: z
                          .record(z.string())
                          .describe(
                            'Values `noteCode` interpolates (dates, a symbol, an FX pair). Strings only.',
                          )
                          .nullable(),
                        noteRows: z
                          .array(
                            z
                              .object({
                                symbol: z.string(),
                                date: z.string().describe('Trade day, ISO 8601 (YYYY-MM-DD), UTC.'),
                                quantity: z.coerce.number(),
                                side: z.enum(['sell', 'buy']),
                              })
                              .passthrough(),
                          )
                          .describe(
                            'The ledger rows the note names (hand-typed trades a statement already books, #3835), at most 10; `noteParams.count` is the full number. Absent on every other item.',
                          )
                          .optional(),
                        entityKind: z
                          .enum(['position', 'transaction', 'file', 'symbol', 'currency'])
                          .describe('What the item points at (drives the FE deep link).'),
                        entityId: z
                          .string()
                          .describe(
                            'Id of the entity when the item HAS one (a grouped item has none).',
                          )
                          .nullable(),
                        symbol: z.string().describe('Ticker when the item has one.').nullable(),
                        walletId: z
                          .string()
                          .describe(
                            "Wallet the item's row lives in, on an item that names a wallet-ledger row (an orphan top-up, whose delete is addressed by wallet). Absent on every other item.",
                          )
                          .optional(),
                        zeroPriceBuyIds: z
                          .array(z.string())
                          .describe(
                            "A missing_cost_basis position's BUY rows at price 0, present only when they are all of its undeclared opening rows: declaring them gifts (`isGift: true` on the transaction edit) settles its cost. Absent on every other item.",
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe('A bounded sample of the offenders (at most 10).'),
                truncated: z.boolean().describe('True when `count` exceeds the returned `items`.'),
              })
              .passthrough(),
          )
          .describe(
            'Every issue detected for the authenticated user. A detection that FAILED is absent rather than reported as clean — the response never claims more than it proved.',
          ),
        checkedAt: z.string().describe('ISO-8601 instant the checks ran.'),
        walletReconciliations: z
          .array(
            z
              .object({
                walletId: z.string().describe('The wallet the statement was imported into.'),
                from: z.string().describe("ISO day the statement's window starts."),
                basis: z
                  .enum(['closing-at', 'window-net'])
                  .describe(
                    '`closing-at`: the statement declares the closing balance; `window-net`: only the net of its window, with no opening balance.',
                  ),
                drift: z
                  .coerce.number()
                  .describe(
                    'Imported cash minus the declared figure when recorded; 0 means they matched.',
                  )
                  .nullable(),
              })
              .passthrough(),
          )
          .describe(
            "Each wallet's latest cash reconciliation against an imported statement; a wallet no statement declared cash for is absent.",
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios',
    alias: 'portfolioCoreCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreCreate_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/portfolios/:id',
    alias: 'portfolioCoreUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/portfolios/:id',
    alias: 'portfolioCoreRemove',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id',
    alias: 'portfolioCoreFindOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/analytics',
    alias: 'portfolioCoreGetAnalytics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'timeframe',
        type: 'Query',
        schema: z
          .enum(['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y', '20Y', 'YTD', 'All'])
          .optional(),
      },
      {
        name: 'fromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'benchmarks',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'positions',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'positionIds',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeOptions',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/assignment',
    alias: 'portfolioCoreAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreAssignment_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/benchmark-closes',
    alias: 'portfolioCoreGetBenchmarkCloses',
    description: `The trailing year plus a week of the benchmark as the analytics serve it: expressed in the portfolio&#x60;s currency (or displayCurrency) and, for a US listing, as a net total return (dividends net of the 15 % US withholding, reinvested on the ex-date). The Performance tab&#x60;s &#x27;vs benchmark&#x27; strip measures its 1Y off these, exactly as the public share page does - one computation, never raw prices.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        basis: z
          .object({
            currency: z
              .string()
              .describe(
                'ISO 4217 code the series is expressed in: the currency the analytics are served in (the view`s displayCurrency, else the portfolio`s). The listing`s closes are converted at the FX route`s daily rate of each bar`s day, the last known rate carried over a gap - never a later one. A benchmark that cannot be converted is not served. Null only on a payload computed before the conversion existed.',
              )
              .nullable(),
            returnKind: z
              .enum(['price', 'netTotalReturn'])
              .describe(
                "'netTotalReturn' (label: TR netto / net TR): the listing`s ledger dividends, net of `withholdingRate`, reinvested at the ex-date close - the basis to hold a portfolio return that counts its dividends against. 'price': closes only, dividends NOT reinvested - served for a listing whose withholding rate is not defined (every non-US listing), a dividend in another currency than the listing, an unreadable ledger, and on a payload computed before net TR existed. A portfolio beats a price benchmark by the dividend yield alone.",
              ),
            withholdingRate: z
              .coerce.number()
              .describe(
                'The fraction withheld from each dividend before it is reinvested: 0.15 = the standard 15 % US withholding (owner ruling D1). Null on a price return; absent only on a payload computed before it existed.',
              )
              .nullish(),
          })
          .passthrough()
          .describe('Absent on a refused benchmark.')
          .optional(),
        unavailable: z
          .enum(['fx-unavailable', 'no-data'])
          .describe(
            'Set when the requested benchmark could NOT be served - then timeSeries is empty and there is no basis. `fx-unavailable`: its closes cannot be expressed in the analytics currency (no FX rate at the window`s anchor - the FX route publishes no rate for the currency - or its listing currency is unknown). `no-data`: no price source serves a bar for the symbol in the window (an unknown or delisted symbol, or a failed read). Show the benchmark as refused, never as a flat line.',
          )
          .optional(),
        points: z
          .array(z.object({ date: z.string(), close: z.coerce.number() }).passthrough())
          .describe(
            'Chronological, YYYY-MM-DD. `close` is the served index on `basis` (proportional to the converted, dividend-reinvested close): only ratios between two points are meaningful. Empty when `unavailable` says why.',
          ),
      })
      .passthrough()
      .nullable(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/bond-terms/:symbol',
    alias: 'bondTermsGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: BondTermsDto,
  },
  {
    method: 'put',
    path: '/api/portfolios/:id/bond-terms/:symbol',
    alias: 'bondTermsSet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: bondTermsSet_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: BondTermsDto,
  },
  {
    method: 'put',
    path: '/api/portfolios/:id/bond-terms/:symbol/rates/:periodStart',
    alias: 'bondTermsSetRate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          ratePct: z.coerce.number().describe("The period's rate, percent (7.5 = 7.5 %)"),
        }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'periodStart',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: BondTermsDto,
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/buy',
    alias: 'portfolioCoreBuy',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreBuy_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/cfd-review',
    alias: 'portfolioCoreGetCfdReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        walletId: z
          .string()
          .describe("The portfolio's wallet — the funding wallet a 'not a CFD' re-booking needs")
          .nullable(),
        open: z.array(
          z
            .object({
              positionId: z.string(),
              kind: z
                .enum(['CFD', 'FUTURE'])
                .describe(
                  'CFD, or an exchange-traded FUTURE (the same row flavour, §4.18.1): a future is never re-booked as shares, and its underlying is the one its contract code states.',
                ),
              symbol: z
                .string()
                .describe('CFD symbol as the broker names it (GOLD, US100, ORCL.US)'),
              direction: z.enum(['BUY', 'SELL']).describe('BUY = long, SELL = short').nullable(),
              quantity: z
                .coerce.number()
                .describe('Volume in lots (positive; direction carries the sign)'),
              openPrice: z
                .coerce.number()
                .describe(
                  'Open price in the underlying quote units. NULL when the statement reported no usable entry price (`entryPriceUnknown`) — the stored placeholder is never served as a price.',
                )
                .nullable(),
              margin: z.coerce.number().describe('Broker-blocked margin, account currency').nullable(),
              currency: z.string().describe('Account currency of margin / P&L'),
              instrumentCurrency: z
                .string()
                .describe(
                  "The INSTRUMENT's quote currency (the open price's unit) — what a 'not a CFD' re-booking must submit the shares in. NULL when the row carries none (pre-2026-09-07 imports).",
                )
                .nullable(),
              unrealizedAtStatement: z
                .coerce.number()
                .describe(
                  "The broker's own unrealized P&L (plus accrued swap) as of the statement — the only value an open CFD carries until it is priced. NULL for a manual row.",
                )
                .nullable(),
              statementDate: z
                .string()
                .describe('ISO date the statement values are as-of')
                .nullable(),
              openedAt: z.string().describe('ISO instant the contract was opened').nullable(),
              entryPriceUnknown: z
                .boolean()
                .describe('The statement reported no usable entry price for this row'),
              bonusShareSuspect: z
                .boolean()
                .describe(
                  "Looks like a bonus / spin-off SHARE allocation the parser tagged as a CFD (open price at or below 0.001, or unknown entry with no margin blocked): CRQ.PL / S2B.PL class. The review offers 'not a CFD' on these.",
                ),
              underlying: z
                .object({
                  quoteSymbol: z
                    .string()
                    .describe(
                      "Where the CFD's price would come from: the curated map's quote symbol for a bare symbol ('BTC', 'EUR/USD'), the ticker itself for a stock-CFD (ORCL.US).",
                    )
                    .nullable(),
                  source: z
                    .enum(['STOCK', 'CRYPTO', 'FX', 'YAHOO', 'UNPRICED', 'UNKNOWN'])
                    .describe(
                      'STOCK = a dotted stock-CFD priced off its own ticker; CRYPTO/FX = a curated map source; UNPRICED = curated but no source (GOLD, US100…); UNKNOWN = a bare symbol the map does not know.',
                    ),
                  note: z.string().describe("The curated map's human note, when any").nullable(),
                })
                .passthrough(),
            })
            .passthrough(),
        ),
        closed: z
          .object({
            count: z
              .coerce.number()
              .describe('Closed-CFD realized P&L rows (`Realized P&L #` wallet rows)'),
            realizedPnl: z.coerce.number().describe('Σ realized P&L of those rows, wallet currency'),
            currency: z.string().describe('Wallet currency of `realizedPnl`'),
            missingCapitalCount: z
              .coerce.number()
              .describe(
                "Rows whose `cfd_capital` is NULL (unknown) — one such row refuses the whole book's return. `[]` (deliberately empty) rows are NOT counted.",
              ),
          })
          .passthrough(),
        hints: z
          .array(CfdOpenPositionHintDto)
          .describe('Likely-open CFDs the statement cannot show (dividend-equivalent evidence)'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/dividend',
    alias: 'portfolioCoreDividend',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreDividend_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/dividends/projection',
    alias: 'portfolioCoreGetDividendProjection',
    description: `Server-side per-holding tier ladder: explicit analyst annual DPS estimates (applied only when the requesting user also holds ANALYST_ESTIMATES), then TTM DPS compounded at the historical growth (5y/3y CAGR, then 1y series growth), then flat, then excluded (non-payers). Merged portfolios resolve to the member-position union. Amounts are converted server-side into displayCurrency; a holding with no usable FX rate is flagged unconvertible and left out of the aggregate (never a silent 1:1).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
    ],
    response: z
      .object({
        displayCurrency: z.string().describe('Currency every income amount is converted into'),
        horizonYears: z.coerce.number().describe('Number of projected years'),
        analystTierApplied: z
          .boolean()
          .describe(
            'True when the analyst tier was considered (requesting user holds ANALYST_ESTIMATES); false = every holding used the historical/flat ladder',
          ),
        holdings: z.array(
          z
            .object({
              symbol: z.string().describe('Instrument symbol'),
              shares: z
                .coerce.number()
                .describe('Open shares held (merged portfolios: summed across members)'),
              basis: z
                .enum(['analyst', 'historical', 'flat', 'excluded'])
                .describe('Which projection tier produced this holding'),
              dividendCurrency: z
                .string()
                .describe('Currency the DPS values are denominated in (null when unresolvable)')
                .nullable(),
              growthRatePercent: z
                .coerce.number()
                .describe('Growth rate (%/yr) used to extrapolate beyond explicit estimate rows')
                .nullable(),
              dpsByYear: z.array(
                z
                  .object({
                    year: z.coerce.number().describe('Calendar year of the fiscal-year end'),
                    dps: z
                      .coerce.number()
                      .describe('Projected dividend per share (native dividend currency)'),
                    dpsHigh: z
                      .coerce.number()
                      .describe(
                        'Analyst high estimate (explicit rows; band carried forward when extrapolated)',
                      )
                      .nullable(),
                    dpsLow: z.coerce.number().describe('Analyst low estimate').nullable(),
                  })
                  .passthrough(),
              ),
              incomeByYearDisplayCcy: z
                .array(
                  z
                    .object({
                      year: z.coerce.number().describe('Calendar year'),
                      income: z
                        .coerce.number()
                        .describe('Projected income in the display currency (shares x DPS x FX)'),
                      incomeHigh: z
                        .coerce.number()
                        .describe('High-band income (equals income when no analyst band exists)'),
                      incomeLow: z
                        .coerce.number()
                        .describe('Low-band income (equals income when no analyst band exists)'),
                    })
                    .passthrough(),
                )
                .describe(
                  'Per-year income in the display currency; EMPTY when unconvertible or excluded',
                ),
              unconvertible: z
                .boolean()
                .describe(
                  'True when no usable FX rate existed for dividendCurrency -> displayCurrency; the holding then contributes nothing to the aggregate (never a fabricated 1:1)',
                ),
            })
            .passthrough(),
        ),
        aggregate: z
          .object({
            years: z.array(z.coerce.number()).describe('Projection years (current year .. +horizon-1)'),
            base: z.array(z.coerce.number()).describe('Base projected income per year (display currency)'),
            high: z.array(z.coerce.number()).describe('High-band income per year'),
            low: z.array(z.coerce.number()).describe('Low-band income per year'),
            currentAnnualIncome: z
              .coerce.number()
              .describe('Projected income for the CURRENT year (= base[0])'),
            yieldPercent: z
              .coerce.number()
              .describe('currentAnnualIncome / total market value, percent (null when value <= 0)')
              .nullable(),
            yieldOnCostPercent: z
              .coerce.number()
              .describe('currentAnnualIncome / total cost basis, percent (null when basis <= 0)')
              .nullable(),
            basisCounts: z
              .object({
                analyst: z.coerce.number(),
                historical: z.coerce.number(),
                flat: z.coerce.number(),
                excluded: z.coerce.number().describe('Non-payers (no/zero DPS) among equity holdings'),
              })
              .passthrough(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/events',
    alias: 'portfolioEventsRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioEventsRecord_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: RecordPortfolioEventResultDto,
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/exercise',
    alias: 'portfolioCoreExercise',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreExercise_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/expire',
    alias: 'portfolioCoreExpire',
    description: `Closes an OPTION position whose expiration date has passed: writes an EXPIRE row at zero cash dated on the expiry day, closes the position and realizes the premium (a lapsed long loses what it paid, a lapsed short keeps what it received). Refused (400) while the contract has not lapsed yet, when it is already closed, or when it is not an option.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreExpire_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        positionId: z.string().describe('The option position that was closed.'),
        symbol: z.string().describe('Position symbol (the underlying ticker).'),
        contracts: z.coerce.number().describe('Contracts closed, unsigned.'),
        expiredAt: z.string().describe('ISO-8601 instant the contract lapsed (its expiry day).'),
        realizedGainLoss: z
          .coerce.number()
          .describe(
            'Booked result in `currency`: negative for a lapsed long (premium paid), positive for a lapsed short (premium kept).',
          ),
        currency: z.string().describe('Portfolio currency `realizedGainLoss` is denominated in.'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/fund-units',
    alias: 'fundUnitsBook',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: fundUnitsBook_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BookedFundUnitsDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/import-price-check',
    alias: 'portfolioCoreImportPriceCheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        portfolioId: z.string().describe('Portfolio the check ran against'),
        anomalies: z.array(
          z
            .object({
              transactionId: z.string().describe('Transaction id'),
              positionId: z.string().describe('Position id the transaction belongs to'),
              symbol: z.string().describe('Position symbol the price was checked against'),
              isClosed: z.boolean().describe('Whether the position is closed (sold review table)'),
              transactionType: z.string().describe('Transaction type (BUY or SELL)'),
              executedAt: z.string().describe('Execution date (ISO)'),
              transactionPrice: z.coerce.number().describe('Per-share price recorded on the transaction'),
              currency: z
                .string()
                .describe('Instrument trading currency the price is denominated in'),
              reason: z
                .enum(['price_out_of_range', 'future_date', 'no_market_data', 'unapplied_split'])
                .describe('Why the transaction is flagged'),
              marketLow: z.coerce.number().describe('Day low the price was compared against').optional(),
              marketHigh: z.coerce.number().describe('Day high the price was compared against').optional(),
              marketClose: z
                .coerce.number()
                .describe('Day close the price was compared against')
                .optional(),
              candleDate: z.string().describe('Day (YYYY-MM-DD) of the candle used').optional(),
              deviationRatio: z
                .coerce.number()
                .describe('How many times outside the nearest range bound the price sits (>= 1)')
                .optional(),
              splitFactor: z
                .coerce.number()
                .describe(
                  'unapplied_split only: the registry split factor the row is missing (quantity × factor, price ÷ factor)',
                )
                .optional(),
              splitDate: z
                .string()
                .describe(
                  'unapplied_split only: effective date (YYYY-MM-DD) of the latest un-applied split',
                )
                .optional(),
            })
            .passthrough(),
        ),
        checkedTransactionCount: z
          .coerce.number()
          .describe('Number of BUY/SELL transactions that were checked'),
        skippedSymbols: z
          .array(z.string())
          .describe('Symbols skipped entirely (non-equity asset types such as options/crypto)'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/insight',
    alias: 'portfolioInsightsGetInsight',
    description: `Returns the cached AI-generated insight for the specified period. Returns null if no insight exists.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'period',
        type: 'Query',
        schema: z.enum(['today', '1W', '1M']).optional().default('today'),
      },
    ],
    response: PortfolioInsightDto,
    errors: [
      {
        status: 404,
        description: `Portfolio not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/insight/generate',
    alias: 'portfolioInsightsGenerateInsight',
    description: `Generates a brief AI-powered insight explaining why the portfolio moved. Supports today, 1W, and 1M periods.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'period',
        type: 'Query',
        schema: z.enum(['today', '1W', '1M']).optional().default('today'),
      },
    ],
    response: PortfolioInsightDto,
    errors: [
      {
        status: 404,
        description: `Portfolio not found or no positions`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/insight/generate/detailed',
    alias: 'portfolioInsightsGenerateDetailedInsight',
    description: `Generates a detailed 2-3 paragraph analysis expanding on the brief insight. Requires brief insight to exist first.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'period',
        type: 'Query',
        schema: z.enum(['today', '1W', '1M']).optional().default('today'),
      },
    ],
    response: z
      .object({ analysis: z.string().describe('Detailed 2-3 paragraph analysis') })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Portfolio not found or no brief insight exists`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/lots',
    alias: 'portfolioCoreGetPositionLots',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'sourcePortfolioId',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Symbol the lots belong to'),
        positionCurrency: z.string().describe('Currency the lot cost basis is denominated in'),
        lots: z
          .array(
            z
              .object({
                lotId: z
                  .string()
                  .describe(
                    'Stable lot id (the opening row identity). Pass back as `lotId` on POST :id/sell with taxLotMethod SPECIFIC.',
                  ),
                acquiredAt: z.string().describe('Acquisition time of the lot (ISO 8601)'),
                quantity: z.coerce.number().describe('Remaining (unsold) quantity in the lot'),
                costPerShare: z
                  .coerce.number()
                  .describe('Cost per share in the position currency (what was actually paid)'),
                unitPrice: z
                  .coerce.number()
                  .describe('Unit open price in the instrument currency (display), when known')
                  .optional(),
              })
              .passthrough(),
          )
          .describe('Remaining open lots, oldest first'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/positions',
    alias: 'portfolioCoreGetPositions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'includeClosed',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'currency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'splitByPortfolio',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeSymbolMetrics',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fxOverride',
        type: 'Query',
        schema: z
          .string()
          .describe(
            "Substitute your own CURRENT exchange rate, as a comma-separated CODE:rate list (e.g. 'USD:3.8250,EUR:4.2500'). CODE is the SOURCE currency and the rate is expressed in units of this response's display currency per 1 unit of CODE. It replaces the spot rate: cost basis, invested capital and realized gain keep their trade-day rates, except where no trade-day rate was ever knowable and the current rate was already standing in for it (such a row reports no historicalConversionRate). A refusal is a 400 whose body carries a machine-readable 'code'. FX_OVERRIDE_OUT_OF_BAND: the rate misses the plausible band for CODE against the display currency, and the body states the min and max it missed. FX_OVERRIDE_OFF_REFERENCE: the rate is a fold too far from our own recent rate for the pair; the body states the min and max derived from it. FX_OVERRIDE_INVERTED: the rate is the reciprocal of a real one - CODE per display unit instead of display units per CODE. FX_OVERRIDE_SUB_UNIT_CODE: CODE is a sub-unit (GBX/GBp, ZAC/ZAc, ILA); state the rate against its parent currency, which is what every conversion it could reach is keyed on. FX_OVERRIDE_NO_DISPLAY_CURRENCY: the request named no display currency, so the rate is quoted against nothing. A malformed list (missing separator, non-decimal rate, a currency named twice) is a 400 with a message and no code.",
          )
          .optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/positions/:positionId/move',
    alias: 'portfolioCoreMovePosition',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          targetPortfolioId: z
            .string()
            .uuid()
            .describe(
              'The portfolio the position moves to. Must be another regular portfolio of the same owner, not a merged view and not one a broker connection keeps in sync.',
            ),
        }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('The symbol that moved, as the sending portfolio held it.'),
        name: z
          .string()
          .describe(
            'The label the position is shown under: a fund unit by its name (its symbol is a key cut from the name), anything else by its ticker. What a confirmation names it by; `symbol` stays the key.',
          ),
        assetType: z
          .string()
          .describe('The class the position was held under (STOCK, ETF, CRYPTO).'),
        quantity: z.coerce.number().describe('Units moved: the whole open position.'),
        value: z
          .coerce.number()
          .describe(
            'The market value the move was booked at, in `currency`: quantity x the market price of the moment. This is the external flow both portfolios record (no gain is realized).',
          ),
        currency: z
          .string()
          .describe(
            'The currency of the SENDING portfolio, which `value` is in (a USD lot in a EUR portfolio moves at its EUR value on the day).',
          ),
        sourcePortfolioId: z.string().uuid().describe('The portfolio the position left.'),
        targetPortfolioId: z.string().uuid().describe('The portfolio the position arrived in.'),
        targetPositionId: z.string().uuid().describe('The position row that now holds the shares.'),
        moveReference: z
          .string()
          .describe(
            'The reference both transactions carry (`manual:move:<uuid>`): what makes them one move.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/positions/cfd',
    alias: 'portfolioCoreCreateCfdPosition',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreCreateCfdPosition_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/positions/cfd-hints',
    alias: 'portfolioCoreGetCfdHints',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.array(CfdOpenPositionHintDto),
  },
  {
    method: 'patch',
    path: '/api/portfolios/:id/positions/cfd/:positionId',
    alias: 'portfolioCoreUpdateCfdPosition',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreUpdateCfdPosition_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/positions/cfd/:positionId/close',
    alias: 'portfolioCoreCloseCfdPosition',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreCloseCfdPosition_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        positionId: z.string(),
        symbol: z.string(),
        closedVolume: z.coerce.number().describe('Lots closed by this call'),
        remainingVolume: z.coerce.number().describe('Lots still open after this call (0 = fully closed)'),
        realizedPnl: z
          .coerce.number()
          .describe('Realized P&L of the closed lots (price leg), account currency'),
        currency: z.string().describe('Currency of realizedPnl'),
        walletTransactionId: z.string().describe('The `Realized P&L #` wallet row id'),
        capitalRecorded: z
          .boolean()
          .describe(
            'False when no margin was known for the closed lots — the row carries an empty capital fragment list, so the return denominator sees no capital-at-risk for it',
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/positions/cfd/:positionId/reclassify',
    alias: 'portfolioCoreReclassifyCfdAsShares',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/positions/closed',
    alias: 'portfolioCoreGetClosedPositions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-alerts',
    alias: 'portfolioRiskAlertsGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(200).optional().default(50),
      },
    ],
    response: z
      .object({
        events: z
          .array(
            z
              .object({
                id: z.string(),
                limitId: z
                  .string()
                  .describe('Catalog id of the limit (volatility, position, sector, beta, ...).'),
                kind: z
                  .enum(['breached', 'recovered'])
                  .describe(
                    'breached = the reading crossed the limit; recovered = it came back inside it.',
                  ),
                current: z
                  .coerce.number()
                  .describe(
                    "The reading that day, in the limit's display unit (percent limits: 24.4 = 24.4%).",
                  ),
                limit: z
                  .coerce.number()
                  .describe('The limit it was measured against that day, same unit.'),
                occurredAt: z.string().datetime({ offset: true }),
              })
              .passthrough(),
          )
          .describe('Limit transitions, newest first.'),
        snapshots: z
          .array(
            z
              .object({
                date: z.string().describe('UTC day of the evaluation.'),
                score: z.coerce.number().describe('Risk score 0-100, higher = safer.'),
                verdict: z.string().describe('controlled | watch | action.'),
                volatility: z
                  .coerce.number()
                  .describe(
                    "The risk model's modelVolatility that day (today's holdings over the window), in percent.",
                  ),
              })
              .passthrough(),
          )
          .describe('One point per evaluated day over the last 90 days, oldest first.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-analysis/ai',
    alias: 'portfolioCoreGetRiskAiAnalysis',
    description: `Cache-only: never calls the language model. &#x60;narrative&#x60; is null when nothing has been generated for the portfolio as its risk stands NOW (the cache follows the risk figures) or when the risk model cannot speak for the book. POST the same path to generate one.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'window',
        type: 'Query',
        schema: z.enum(['1M', '3M', '6M', '1Y', '3Y']).optional().default('1Y'),
      },
      {
        name: 'language',
        type: 'Query',
        schema: z.enum(['en', 'pl', 'es', 'de']).optional().default('en'),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .string()
          .min(3)
          .max(3)
          .regex(/^[A-Z]{3}$/)
          .optional(),
      },
      {
        name: 'benchmark',
        type: 'Query',
        schema: z.string().min(1).max(32).optional(),
      },
    ],
    response: RiskAiAnalysisDto,
    errors: [
      {
        status: 404,
        description: `Portfolio not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/risk-analysis/ai',
    alias: 'portfolioCoreGenerateRiskAiAnalysis',
    description: `Served from the durable cache when the portfolio&#x60;s risk picture has not changed, else ONE schema-constrained model call over the SERVED risk figures (score, limits, contributors, stress test, reduction plan). 422 RISK_MODEL_UNAVAILABLE for a book the risk model cannot speak for; 503 INSIGHT_GENERATION_FAILED when the model call fails.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreGenerateRiskAiAnalysis_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: RiskAiAnalysisDto,
    errors: [
      {
        status: 404,
        description: `Portfolio not found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Not enough price history to model the risk`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-limits',
    alias: 'portfolioRiskLimitsGet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        limits: z
          .array(
            z
              .object({
                id: z
                  .enum([
                    'volatility',
                    'position',
                    'sector',
                    'beta',
                    'var',
                    'drawdown',
                    'effectiveBets',
                    'benchmarkCorrelation',
                    'sectorRisk',
                    'stress',
                    'currency',
                  ])
                  .describe(
                    'Catalog id of the limit (volatility, position, sector, beta, var, ...).',
                  ),
                value: z
                  .coerce.number()
                  .describe(
                    "The threshold, in the catalog entry's display unit: percent limits are percent numbers (20 = 20%), beta / correlation / effective bets are plain numbers.",
                  ),
              })
              .passthrough(),
          )
          .describe('The active limits in display order.'),
        isDefault: z
          .boolean()
          .describe(
            'true = nothing is stored for this portfolio; `limits` is the catalog default set.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'put',
    path: '/api/portfolios/:id/risk-limits',
    alias: 'portfolioRiskLimitsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioRiskLimitsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        limits: z
          .array(
            z
              .object({
                id: z
                  .enum([
                    'volatility',
                    'position',
                    'sector',
                    'beta',
                    'var',
                    'drawdown',
                    'effectiveBets',
                    'benchmarkCorrelation',
                    'sectorRisk',
                    'stress',
                    'currency',
                  ])
                  .describe(
                    'Catalog id of the limit (volatility, position, sector, beta, var, ...).',
                  ),
                value: z
                  .coerce.number()
                  .describe(
                    "The threshold, in the catalog entry's display unit: percent limits are percent numbers (20 = 20%), beta / correlation / effective bets are plain numbers.",
                  ),
              })
              .passthrough(),
          )
          .describe('The active limits in display order.'),
        isDefault: z
          .boolean()
          .describe(
            'true = nothing is stored for this portfolio; `limits` is the catalog default set.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-metrics',
    alias: 'portfolioCoreGetRiskMetrics',
    description: `Returns risk metrics (Sharpe, Sortino, Calmar, volatility, max drawdown, positions in profit (field winRate), MWR) over the optional fromDate/toDate window, or all time without one. Over a window the MWR is that window&#x60;s own IRR, and null under a position filter; it is annualized only over a year or more, and under a year it is the period&#x60;s own return. Sharpe and Sortino are the daily form against the portfolio currency&#x60;s risk-free rate (NBP for PLN, the 13-week T-bill for USD) and null in any other currency. Designed for parallel loading: fetch /analytics (chart data) and /risk-metrics (metrics cards) simultaneously for faster UX.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positions',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'fromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toDate',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        sharpeRatio: z
          .coerce.number()
          .describe(
            'Sharpe ratio, the standard daily form: the mean daily return in excess of the risk-free rate per session, times sessions per year, over the annualized volatility. The risk-free rate is the series currency`s, averaged over the window: NBP`s reference rate for PLN, the 13-week T-bill yield (^IRX) for USD. Null when it cannot be measured: a currency with no risk-free rate (every other one) or a window older than its rate series, under 7 days of bars, no volatility (fewer than two daily returns), or a book only a model values (every holding priced by a valuation model, no market calendar: its volatility measures the model, not market risk) - never 0.',
          )
          .nullable(),
        sharpeWithheldReason: z
          .enum(['NO_RISK_FREE_RATE', 'MODEL_ONLY', 'UNDER_MIN_SPAN', 'NO_VOLATILITY'])
          .describe(
            'Why sharpeRatio and sortinoRatio are null, or null when measured: NO_RISK_FREE_RATE (the measuring currency has no risk-free series, or none over the window - only PLN and USD carry one), MODEL_ONLY (every holding valued by a model), UNDER_MIN_SPAN (under 7 days), NO_VOLATILITY (fewer than two moving daily returns).',
          )
          .nullable(),
        sortinoRatio: z
          .coerce.number()
          .describe(
            'Sortino ratio: the same numerator as sharpeRatio over the annualized downside deviation below the risk-free rate per session. Null where sharpeRatio is (no risk-free rate for the currency, under 7 days, a book only a model values), or with no day below that rate (no downside) - never 0.',
          )
          .nullable(),
        calmarRatio: z
          .coerce.number()
          .describe(
            'Calmar ratio - annualized return divided by max drawdown. Null for a period under a year (its return would be a part-year extrapolated), and when it cannot be measured: no annualized return, or no drawdown to divide by - never a made-up value.',
          )
          .nullable(),
        volatility: z
          .coerce.number()
          .describe(
            'REALIZED annualized volatility (not the risk model`s modelVolatility of today`s holdings), percent, or null when unmeasurable (fewer than two returns, or a refused TWR - never a calm 0): population standard deviation of the book`s TWR index returns - one per trading session it holds positions on, whoever`s money bought them - x sqrt(its sessions per year). A session is a completed day on which a held instrument printed a candle: not a weekend or holiday copy, not today, not a day the book held nothing, and a model-valued bond`s daily bars are no prints. A book nothing of which prints counts every day. Sessions per year = 252 + 113.25 x the share of the weekend days the book held positions that were sessions: 252 for listings, 365.25 for a coin; a book that never held over a weekend reads its instruments` calendar.',
          )
          .nullable(),
        maxDrawdown: z
          .coerce.number()
          .describe(
            'Maximum drawdown percentage (largest peak-to-trough decline of the TWR index over the bars the book held positions), or null when unmeasurable (no index - a refused TWR - or a single held bar): never a 0 for a book that was not measured.',
          )
          .nullable(),
        winRate: z
          .coerce.number()
          .describe(
            'Positions in profit, percent (the field keeps the name winRate): positions whose realized + live unrealized result is positive, over every position with a non-zero result. Not a per-trade win rate (owner ruling D30).',
          )
          .nullable(),
        pointInTimeWithheldReason: z
          .literal('PINNED_END_LIVE_ONLY')
          .describe(
            'Why winRate is null, when the cause is known. `PINNED_END_LIVE_ONLY`: the range`s end was pinned (toDate) and the win rate exists only for today`s positions.',
          )
          .nullish(),
        moneyWeightedReturn: z
          .coerce.number()
          .describe(
            'Money-weighted return (IRR), percent: the personal return of the money put in, over the requested window (the value at its start counts as money put in) or since the first money at risk without one. Annualized over a period of a year or more; under a year it is the period`s own return, never a rate per year. A merged book`s is the IRR of its members` combined flows.',
          )
          .nullable(),
        periodReturnPercent: z
          .coerce.number()
          .describe(
            'Simple (non-annualized) time-weighted return over the requested window, percent: the served TWR from the level the window`s first invested bar is measured from (the 0 % inception bar whenever the window holds the book`s first one) to its last bar; without a window, the headline TWR. Null when the window has no usable TWR endpoints, or - with a window - when its TWR was refused.',
          )
          .nullish(),
        annualizedReturn: z
          .coerce.number()
          .describe(
            'Annualized return over the requested window, percent: `periodReturnPercent` compounded per year over the time since the window`s first invested bar - the same definition with or without a window. Null for a period under a year (a return is never annualized below one; `periodReturnPercent` is the answer), without usable TWR endpoints or on a refused TWR, and when withheld (`annualizedReturnWithheldReason`). Never capped. NOT the numerator of Sharpe and Sortino: they rate the mean daily return over the bars with capital at work.',
          )
          .nullish(),
        moneyWeightedReturnWithheldReason: z
          .enum([
            'SYNTHESIZED_OPENING_DATE',
            'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE',
            'MEMBER_UNAVAILABLE',
            'MEMBER_UNPRICED',
            'WINDOW_OPENING_UNKNOWN',
            'SHARED_WALLET',
            'POSITION_FILTER',
          ])
          .describe(
            'Why `moneyWeightedReturn` was withheld, when the cause is a known one. `SYNTHESIZED_OPENING_DATE`: a cash flow the IRR consumes carries a date the pipeline invented (a broker-synced position bought before the connection activity window opens), so the money-at-risk clock is fabricated and no rate per year exists; the user CAN fix it by supplying the real purchase date under Settings -> Broker Connections -> opening lots. `SYNTHESIZED_OPENING_DATE_NO_OVERRIDE`: same fabrication, but a contributing row belongs to a family that flow cannot reach - do NOT offer the opening-lot screen, it would list nothing. Set implies the value IS null; null implies nothing about the value (read the value for null first). A MERGED book`s MWR is the IRR of every member`s flows, so it is withheld whole, never re-published over the other members: for a member`s invented date (the codes above; it wins over the rest, being the one to repair), `MEMBER_UNAVAILABLE` (a member could not be read), `MEMBER_UNPRICED` (a member`s cash flow has no exchange rate into this currency), `WINDOW_OPENING_UNKNOWN` (the value at the window`s opening cannot be stated) or `SHARED_WALLET` (two members hold one wallet and the whole would count part of it twice: its whole cash, with cash as a position, or a row of it that names no book, such as its interest).',
          )
          .nullish(),
        annualizedReturnWithheldReason: z
          .enum(['SYNTHESIZED_OPENING_DATE', 'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE'])
          .describe(
            'Why `annualizedReturn` was withheld, when the cause is a known one. Same codes and same contract as `moneyWeightedReturnWithheldReason`, but a DIFFERENT predicate over different rows, so the two genuinely diverge and each speaks only for its own field. Set only when the requested WINDOW is anchored at the invented date: a window starting after it has an honest duration and keeps publishing.',
          )
          .nullish(),
        positions: z
          .array(
            z
              .object({
                symbol: z
                  .string()
                  .describe('Ticker the point is measured on (the position symbol).'),
                name: z
                  .string()
                  .describe(
                    'Display label for the point. The ticker for equities/ETFs — the same convention the snapshot`s topPerformers/bottomPerformers use, so a symbol reads identically across the analytics tab — and the coin`s name for CRYPTO (a bare coin ticker collides with a real equity ticker, so it is answered from the coin SSOT, never from an equity directory).',
                  ),
                volatility: z
                  .coerce.number()
                  .describe(
                    'Annualized volatility of the SYMBOL`s daily close-to-close returns over the requested window, percent: population σ x sqrt(the sessions a year holds for it, measured on its own candle days - 252 for a listing, 365.25 for a coin). Computed by the same function as the benchmark series` volatility, so the two are directly comparable on one chart.',
                  ),
                returnPercent: z
                  .coerce.number()
                  .describe(
                    'The SYMBOL`s PRICE change over the requested window, percent — first usable close to last usable close. This is NOT the user`s return on the holding (no quantity, cost basis, cash flow or FX): it is the market-side half of the scatter, matched to the benchmark`s own returnPercent.',
                  ),
              })
              .passthrough(),
          )
          .describe(
            "Per-holding scatter points for the 'volatility vs return' chart — one entry per HELD SYMBOL, measured over the SAME fromDate/toDate window as every other field here. See riskPositionPointSchema: these are PRICE statistics of the symbol, not the book`s return on the position. Positions are deduplicated by symbol (two lots of one ticker are one point). A symbol without honest candle coverage for the window is OMITTED rather than zero-filled, so this array is routinely shorter than the holdings list — an empty array means nothing could be measured, never 'everything is flat'. Absent (undefined) when the computation could not run at all; the rest of the payload is unaffected either way.",
          )
          .optional(),
        positionsTruncated: z
          .boolean()
          .describe(
            'True when the holdings were capped to the 50 largest by market value before measuring (RISK_POSITION_POINTS_LIMIT) and the array therefore does NOT describe the whole book. False when every eligible holding was considered — note that points can still be missing from an untruncated array, because coverage-less symbols are dropped independently of this cap. Absent whenever `positions` is absent.',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-model',
    alias: 'portfolioCoreGetRiskModel',
    description: `The covariance model of what the portfolio holds NOW over one window of daily returns: per-position volatility, beta and risk share, the portfolio MODEL volatility (&#x60;modelVolatility&#x60;: today&#x60;s holdings over the window) beside its REALIZED volatility since inception (&#x60;realizedVolatility&#x60;, the sigma of the Sharpe and Sortino), VaR / CVaR, concentration, the correlation matrix (benchmark last), rolling volatility and ten stress scenarios. Returns are measured in the model currency (price history converted at each day&#x60;s FX rate), so currency risk is IN every figure and &#x60;currencyRiskShare&#x60; states its share. The benchmark is &#x60;benchmark&#x60; or, by default, the index of the book&#x60;s home market. Weights and shares are FRACTIONS; volatility, VaR and shocks are PERCENT.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'window',
        type: 'Query',
        schema: z.enum(['1M', '3M', '6M', '1Y', '3Y']).optional(),
      },
      {
        name: 'extraSymbols',
        type: 'Query',
        schema: z.array(z.string().min(1).max(32)).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .string()
          .min(3)
          .max(3)
          .regex(/^[A-Z]{3}$/)
          .optional(),
      },
      {
        name: 'benchmark',
        type: 'Query',
        schema: z.string().min(1).max(32).optional(),
      },
    ],
    response: z
      .object({
        window: z.enum(['1M', '3M', '6M', '1Y', '3Y']),
        sessions: z.coerce.number().describe('Trading sessions behind the window.'),
        asOf: z.string().describe('ISO timestamp of the newest candle the model saw.'),
        currency: z.string(),
        totalValue: z.coerce.number().describe('Positions + cash, in `currency`.'),
        cashWeight: z.coerce.number().describe('FRACTION of total value held as cash.'),
        coverage: z
          .coerce.number()
          .describe(
            'FRACTION of non-cash value that is modelled. 1 = every holding is in the model.',
          ),
        benchmark: z
          .object({
            symbol: z.string(),
            name: z.string(),
            volatility: z.coerce.number().nullable(),
            auto: z
              .boolean()
              .describe(
                'true = no `benchmark` was asked for and the server chose it from the holdings.',
              ),
          })
          .passthrough(),
        positions: z
          .array(
            z
              .object({
                symbol: z.string(),
                name: z.string(),
                assetType: z.string(),
                sector: z
                  .string()
                  .describe(
                    'Canonical sector name - the same word allocation-by-sector uses; null for a fund split over many.',
                  )
                  .nullable(),
                sectorExposure: z
                  .array(z.object({ sector: z.string(), share: z.coerce.number() }).passthrough())
                  .describe(
                    "The holding's value over sectors (shares sum to 1), read off the Allocation tab's own look-through reader (D82): a company is one slice, an ETF its holdings. Absent on a model computed before D82.",
                  )
                  .optional(),
                country: z.string().describe('ISO-3166 alpha-2, null when unknown.').nullable(),
                currency: z.string().describe('Currency the instrument is quoted in.'),
                value: z.coerce.number().describe('Market value in the model currency.'),
                weight: z
                  .coerce.number()
                  .describe('FRACTION (0..1) of total portfolio value, cash included.'),
                modelled: z
                  .boolean()
                  .describe(
                    'false = the holding is OUT of the covariance (riskShare is null, and `coverage` states the value left out); `unmodelledReason` says why.',
                  ),
                unmodelledReason: z
                  .enum(['volatilityUnmeasured', 'correlationUnmeasured'])
                  .describe(
                    'null exactly when modelled. volatilityUnmeasured = too few candles in the window, or no daily prices (a fund valued only on the days it was traded), or past the 50-position cap (volatility/beta null). correlationUnmeasured = its volatility is measured but it cannot be paired with another holding (fewer than 20 aligned returns); an unmeasured pair is never read as uncorrelated (owner ruling D47).',
                  )
                  .nullable(),
                volatility: z
                  .coerce.number()
                  .describe(
                    'Annualised volatility over the window, PERCENT: sigma x sqrt(the sessions a year holds for the instrument, measured on its own candle days - 252 for a listing, 365.25 for a coin).',
                  )
                  .nullable(),
                beta: z
                  .coerce.number()
                  .describe(
                    "Dimson beta against the benchmark over the window: the same-session slope, plus the benchmark's neighbouring session when the two close at different times.",
                  )
                  .nullable(),
                stressBeta: z
                  .coerce.number()
                  .describe(
                    'LONG-RUN beta the stress scenarios scale the market shock by: longest history available up to 3Y of daily returns, whatever the window. Null under one year of aligned returns (the scenarios then assume 1). Not the window beta - one quiet quarter is not a crash sensitivity.',
                  )
                  .nullable(),
                correlationToBenchmark: z
                  .coerce.number()
                  .describe(
                    'Correlation with the benchmark from the same regression as beta, -1..1.',
                  )
                  .nullable(),
                riskShare: z
                  .coerce.number()
                  .describe('FRACTION of portfolio variance; sums to 1 over modelled holdings.')
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('Weight descending.'),
        portfolio: z
          .object({
            modelVolatility: z
              .coerce.number()
              .describe(
                "MODEL volatility, PERCENT: today's holdings and cash applied to the last `sessions` sessions of their prices, annualised from the covariance (ex-ante). It moves with the window and with what is held now, not with past trades. NOT the sigma sharpeRatio and sortinoRatio divide by: that is realizedVolatility.",
              )
              .nullable(),
            realizedVolatility: z
              .coerce.number()
              .describe(
                'REALIZED volatility since the portfolio`s inception (NOT the window), PERCENT: the annualised standard deviation of the book`s own daily TWR returns, from the risk-metrics path. The sigma sharpeRatio and sortinoRatio are built on.',
              )
              .nullable(),
            beta: z
              .coerce.number()
              .describe(
                "Dimson beta of the book's own daily returns (modelled holdings + cash) against the benchmark.",
              )
              .nullable(),
            correlationToBenchmark: z
              .coerce.number()
              .describe(
                'Correlation of the book with the benchmark, from the same regression as its beta, -1..1.',
              )
              .nullable(),
            hhi: z.coerce.number().describe('Herfindahl index of position weights, 0..1.'),
            effectiveBets: z
              .coerce.number()
              .describe('(sum w*sigma / sigma_p)^2 - independent bets after correlations.')
              .nullable(),
            effectiveBetsByWeight: z
              .coerce.number()
              .describe('1 / HHI - independent bets by weights alone.'),
            diversificationRatio: z.coerce.number().describe('sum w*sigma / sigma_p.').nullable(),
            top1Weight: z.coerce.number().describe('Largest position weight, FRACTION.'),
            top3Weight: z.coerce.number().describe('Three largest position weights, FRACTION.'),
            sectorWeights: z.record(z.coerce.number()).describe('Sector -> weight FRACTION.'),
            sectorRiskShares: z
              .record(z.coerce.number())
              .describe('Sector -> FRACTION of portfolio variance.'),
            foreignCurrencyWeight: z
              .coerce.number()
              .describe('FRACTION of value quoted in a currency other than the model currency.'),
            currencyRiskShare: z
              .coerce.number()
              .describe(
                'FRACTION of portfolio VARIANCE that is exchange-rate movement: 1 - variance with every holding in its own quote currency / variance in the model currency, floored at 0. 0 when nothing is quoted in a foreign currency; null when there is no portfolio volatility.',
              )
              .nullable(),
            var95: z
              .coerce.number()
              .describe(
                'One-month VaR 95%, PERCENT, positive = size of the loss, 0 when the worst 5% of months still gained. EMPIRICAL (the absolute 5% quantile of the book`s own overlapping one-month returns - a month is its series` sessions per year / 12, 21 for a listing - never centred on their mean) when `varMethod` is `historical`, the normal approximation z x sigma / sqrt(12) otherwise.',
              )
              .nullable(),
            var99: z
              .coerce.number()
              .describe('One-month VaR 99%, PERCENT - same method as `var95`.')
              .nullable(),
            cvar95: z
              .coerce.number()
              .describe(
                'One-month expected shortfall 95%, PERCENT - same method as `var95`; historical: the mean of the worst ceil(5% x n) one-month windows.',
              )
              .nullable(),
            varMethod: z
              .enum(['historical', 'parametric'])
              .describe(
                '`historical` = empirical quantile of the book`s own return series (needs a year of daily returns); `parametric` = the normal approximation centred on a zero mean: no fat tail and no drift, so it is an estimate, not a measurement.',
              ),
            varObservations: z
              .coerce.number()
              .describe(
                'One-month windows behind the empirical quantiles; 0 when `varMethod` is parametric.',
              ),
            var95Parametric: z
              .coerce.number()
              .describe(
                'The normal-distribution VaR 95%, z x sigma / sqrt(12) centred on a zero mean, always stated. Its gap to a historical `var95` mixes two things: the book`s own DRIFT (the historical quantile is absolute, so a book that rose on average shows a smaller loss) and the SHAPE of its tail (fat tails or skew show a larger one). Do not call the gap the fat tail alone.',
              )
              .nullable(),
            varBacktest: z
              .object({
                observations: z
                  .coerce.number()
                  .describe('Month-ends tested, one month apart (non-overlapping).'),
                exceptions: z
                  .coerce.number()
                  .describe(
                    'Months that lost more than the VaR shown before them (return below -VaR).',
                  ),
                expectedRate: z.coerce.number().describe('0.05 - what a 95% VaR promises.'),
                verdict: z
                  .enum(['ok', 'too_many', 'too_few', 'insufficient'])
                  .describe(
                    'Kupiec proportion-of-failures test at 95%; `insufficient` under 24 month-ends.',
                  ),
              })
              .passthrough()
              .describe(
                'Did the VaR hold? Walks the history month-end by month-end with an expanding window. null when `varMethod` is parametric. The history is TODAY`s weights applied to past returns, so it tests the MODEL, not the investor`s realised experience.',
              )
              .nullable(),
            trackingError: z
              .coerce.number()
              .describe(
                'Annualised sigma of (portfolio - benchmark) daily returns over the window, PERCENT. How far the book can drift from its benchmark in a year - a high correlation does not bound it.',
              )
              .nullable(),
            maxDrawdown: z
              .coerce.number()
              .describe(
                'Max drawdown since the portfolio`s inception (NOT the window), from the risk-metrics path.',
              )
              .nullable(),
            sharpeRatio: z
              .coerce.number()
              .describe(
                'Sharpe ratio since the portfolio`s inception (NOT the window), from the risk-metrics path.',
              )
              .nullable(),
            sharpeWithheldReason: z
              .enum(['NO_RISK_FREE_RATE', 'MODEL_ONLY', 'UNDER_MIN_SPAN', 'NO_VOLATILITY'])
              .describe(
                'Why sharpeRatio and sortinoRatio are null, or null when measured: NO_RISK_FREE_RATE (the measuring currency has no risk-free series, or none over the window - only PLN and USD carry one), MODEL_ONLY (every holding valued by a model), UNDER_MIN_SPAN (under 7 days), NO_VOLATILITY (fewer than two moving daily returns).',
              )
              .nullable(),
            sortinoRatio: z
              .coerce.number()
              .describe(
                'Sortino ratio since the portfolio`s inception, off the SAME series and path as sharpeRatio. null when unmeasurable (no return, or no day below the target) - never 0.',
              )
              .nullable(),
          })
          .passthrough(),
        correlation: z
          .object({
            symbols: z
              .array(z.string())
              .describe(
                'Modelled holdings (weight desc), then extra symbols, then the benchmark LAST.',
              ),
            matrix: z
              .array(z.array(z.coerce.number().nullable()))
              .describe(
                'Row-major, symmetric, diagonal 1, positive semi-definite; null = too few aligned returns.',
              ),
            shrinkage: z
              .coerce.number()
              .describe(
                'FRACTION 0..1 that every off-diagonal cell was pulled towards the book`s average correlation (Ledoit-Wolf analytic intensity). Null = no common sample of trading days, so the cells are pairwise and unshrunk.',
              )
              .nullable(),
            external: z.array(z.string()).describe('Members of `symbols` that are NOT held.'),
          })
          .passthrough(),
        rollingVolatility: z
          .array(
            z
              .object({
                date: z.string(),
                portfolio: z.coerce.number().nullable(),
                benchmark: z.coerce.number().nullable(),
              })
              .passthrough(),
          )
          .describe(
            'Annualised volatility (PERCENT) over a window of `sessions` sessions - the ANALYSIS window, not a constant - oldest first, reaching back as far over the trailing twelve months as the price history above that window allows. The portfolio line is the covariance model at TODAY`s weights and TODAY`s correlations, so its newest point is `portfolio.modelVolatility` itself.',
          ),
        stress: z
          .array(
            z
              .object({
                id: z.enum([
                  'covid2020',
                  'rates2022',
                  'gfc2008',
                  'vix2024',
                  'ai2025',
                  'rates100',
                  'usd10',
                  'oil30',
                  'crypto40',
                  'earnings20',
                ]),
                kind: z.enum(['historical', 'factor']),
                marketShock: z
                  .coerce.number()
                  .describe(
                    'The market leg actually used, PERCENT: the measured benchmark return over the window when the episode was replayable, otherwise the catalogue constant.',
                  ),
                window: z
                  .object({ start: z.string(), end: z.string() })
                  .passthrough()
                  .describe(
                    "The episode's real peak-to-trough window (ISO days), present only when it was measured.",
                  )
                  .nullable(),
                benchmarkReturn: z
                  .coerce.number()
                  .describe(
                    "The benchmark's OWN return over the window, PERCENT; null = the catalogue constant was used.",
                  )
                  .nullable(),
                replayedWeight: z
                  .coerce.number()
                  .describe(
                    'Share of the shocked value (cash excluded) whose row came from history, 0..1.',
                  ),
                total: z
                  .coerce.number()
                  .describe('Portfolio result, PERCENT (negative = loss), cash included.'),
                amount: z.coerce.number().describe('Portfolio result in the model currency.'),
                positions: z
                  .array(
                    z
                      .object({
                        symbol: z.string(),
                        shock: z.coerce.number(),
                        amount: z.coerce.number(),
                        method: z
                          .enum(['replayed', 'estimated'])
                          .describe(
                            "'replayed' = the holding's own measured return over the window.",
                          ),
                        explain: z
                          .object({
                            marketShock: z
                              .coerce.number()
                              .describe(
                                'The market leg this row was built on, PERCENT - the MEASURED benchmark return when replayed.',
                              ),
                            beta: z
                              .coerce.number()
                              .describe(
                                'Blume-adjusted beta the market leg was scaled by (0.67 x stressBeta + 0.33, clamped to 0.3..2.5); null on a replayed row, which used no beta at all.',
                              )
                              .nullable(),
                            adjustment: z
                              .coerce.number()
                              .describe(
                                'Percentage points added on top of market x beta (sector / crypto / FX legs); 0 when replayed.',
                              ),
                            windowStart: z
                              .string()
                              .describe('ISO day the replayed window opened; null = estimate.')
                              .nullable(),
                            windowEnd: z
                              .string()
                              .describe('ISO day the replayed window closed; null = estimate.')
                              .nullable(),
                            coverage: z
                              .coerce.number()
                              .describe(
                                "Share of the window's benchmark sessions this holding's own bars covered, 0..1.",
                              )
                              .nullable(),
                          })
                          .passthrough(),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Per-position shock (PERCENT), amount and where the number came from, worst first.',
                  ),
              })
              .passthrough(),
          )
          .describe('Five historical then five factor.'),
        leveragedSymbols: z
          .array(z.string())
          .describe(
            'Symbols of OPEN leveraged or short positions (CFDs, shorts, written options). Empty = none. They are NOT in `positions`: the volatility, VaR and stress figures exclude them.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/risk-model/pair-correlation',
    alias: 'portfolioCoreGetRiskPairCorrelation',
    description: `ONE symbol pair&#x60;s correlation over each of the five risk windows (1M, 3M, 6M, 1Y, 3Y). Each value is the pair&#x60;s CELL in the correlation matrix the risk model serves for that window - same estimator, same shrinkage, same positive-semi-definite repair - so the number here and the cell a reader clicked are the same number. Pass the &#x60;extraSymbols&#x60;, &#x60;benchmark&#x60; and &#x60;displayCurrency&#x60; the model was read with: they decide which matrix. The symbols need not be held; one that is not joins the matrix as an extra symbol.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'a',
        type: 'Query',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'b',
        type: 'Query',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'extraSymbols',
        type: 'Query',
        schema: z.array(z.string().min(1).max(32)).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .string()
          .min(3)
          .max(3)
          .regex(/^[A-Z]{3}$/)
          .optional(),
      },
      {
        name: 'benchmark',
        type: 'Query',
        schema: z.string().min(1).max(32).optional(),
      },
    ],
    response: z
      .object({
        a: z.string(),
        b: z.string(),
        crossRegion: z
          .boolean()
          .describe(
            'true = the two listings trade in markets that close hours apart, so the values below are lead-lag corrected rather than plain close-to-close.',
          ),
        values: z
          .array(
            z
              .object({
                window: z.enum(['1M', '3M', '6M', '1Y', '3Y']),
                correlation: z.coerce.number().nullable(),
              })
              .passthrough(),
          )
          .describe('The pair`s correlation over each of the five windows, shortest first.'),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/sell',
    alias: 'portfolioCoreSell',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreSell_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/share',
    alias: 'portfolioCoreCreateShare',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreCreateShare_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/snapshot',
    alias: 'portfolioCoreGetSnapshot',
    description: `Returns current state data that does not depend on timeframe: allocations (by asset, sector, geography, industry, position), top/bottom performers, concentration risks, and correlation matrix. This is faster than full analytics and should be fetched separately.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positions',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/summary-metrics',
    alias: 'portfolioCoreGetSummaryMetrics',
    description: `Returns summary metrics extracted from the analytics time series last point. Ensures consistency between chart and summary display.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positions',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'positionIds',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: 'timeframe',
        type: 'Query',
        schema: z
          .enum(['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y', '20Y', 'YTD', 'All'])
          .optional(),
      },
      {
        name: 'fxOverride',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        externalCapitalReturnPercent: z
          .coerce.number()
          .describe(
            "Return on EXTERNAL capital, percent. THE canonical return: one rule, one denominator, identical on every surface. READ IT WITH externalCapitalReturnBasisKind — 'inception' means inception-to-date, 'window' means relative to the requested timeframe's opening bar. Null when it is not defined for this book or this request — read externalCapitalSuppressedReason for which. A null is a gap, NEVER 0%.",
          )
          .nullable(),
        externalCapitalReturnAmount: z
          .coerce.number()
          .describe(
            "The return in currency. Under basisKind 'inception' it is total value less the external capital the book consumed; under 'window' it is the CHANGE in that quantity across the window. Divided by externalCapitalBasis it reproduces externalCapitalReturnPercent exactly in both regimes.",
          )
          .nullable(),
        externalCapitalBasis: z
          .coerce.number()
          .describe(
            "THE denominator. Under basisKind 'inception' the running peak of the smaller of the external and deployed capital legs (price-independent: a live price move changes the numerator only). Under 'window' the EQUITY standing at the window's opening bar, which is a materially different figure — do not label it 'invested capital' without checking the kind.",
          )
          .nullable(),
        externalCapitalBasisSource: z
          .enum(['externalFlows', 'deployedCash', 'tradeLedger', 'none'])
          .describe(
            "Which rung of the data-derived ladder produced the basis. 'none' IS the absence value, which is why this field is not nullable.",
          ),
        externalCapitalSuppressedReason: z
          .enum([
            'positionFilter',
            'noBasisYet',
            'noValueAtDate',
            'zeroBasis',
            'negativeValue',
            'dustBasis',
            'equityWipedOut',
            'microExternalBasis',
            'insufficientExternalHistory',
            'ledgerCannotFundHoldings',
            'neverDeployed',
            'cfdCapitalUnavailable',
            'fxRateUnavailable',
            'costFxRateUnavailable',
            'valueFxRateUnavailable',
            'noWindowOpenBasis',
            'noWindowOpenEquity',
            'capitalAddedInWindow',
            'windowEquityWipedOut',
            'syntheticPoint',
            'unavailable',
            'nonFiniteInput',
            'mergedFundingEntangled',
            'mergedFundingUndeclared',
            'mergedMemberRefused',
          ])
          .describe(
            "Why the return is absent. Null IFF externalCapitalReturnPercent is a number. 'positionFilter' — undefined for a filtered selection. 'noBasisYet'/'zeroBasis' — the book had no external capital at risk yet; render a gap. 'noValueAtDate' — no valuation on that date. 'negativeValue' — the portfolio is worth less than nothing, so a percentage-of-capital is not a measurement. 'dustBasis' — the capital at risk is a rounding residue (at or below 0.05), so a ratio over it is noise. 'equityWipedOut' — the loss exceeds the whole book. 'microExternalBasis' — the basis clears the dust floor but is a negligible fraction (below 5%) of the capital the book deployed, so it was funded by internal churn / already-withdrawn deposits and a return on external capital is not meaningfully computable (evaluated per point, on the externalFlows and deployedCash rungs). 'insufficientExternalHistory' — the book has no external-flow history (deployedCash rung) and its wallet cash balance exceeds the deployed basis, so the value is dominated by non-redeployed sell-proceeds cash and the return is an artefact of the missing deposit history; surface the 'please fill in your history' prompt. (When such a book is still majority-invested an honest cost-basis return is served instead — see externalCapitalReturnBasisKind 'costBasis' — and this reason is null.) 'cfdCapitalUnavailable' — the book has no denominator at all AND carries closed-CFD realized-P&L rows whose capital-at-risk is unknown (source statement not re-imported/backfilled yet), so the closes' unknown margin is the cause of the missing basis; a book with any stateable capital serves instead and reports cfdCapitalMissingCount. The remedy is re-importing the broker statement. 'fxRateUnavailable' — a wallet row has no exchange rate in force on its day (none stored for it or the 14 days before), so it is missing from the cash and the external capital alike; refused from that day on, externalCapitalUnpricedCashRows counts the rows. 'costFxRateUnavailable' — a cost row (a buy, an opened option, a delivery in) has no exchange rate in force on its day, so the cost basis and every basis built on it cannot be stated; refused from that day on, externalCapitalUnpricedCost names the pair and the day. 'valueFxRateUnavailable' — an open holding has no current exchange rate into the portfolio currency, so the book value and the return over it cannot be stated; unconvertedCurrencies names the currency (owner ruling D81). 'noWindowOpenBasis' — the requested window opens on a bar that carries no return. 'noWindowOpenEquity' — no equity was standing when the window opened. 'capitalAddedInWindow' — external capital entered the window AND the flow could not be time-weighted, so no denominator spans it. 'windowEquityWipedOut' — the book was wiped out inside the window. 'syntheticPoint' — a fabricated bar. 'unavailable' — an upstream input was missing. 'nonFiniteInput' — a return-determining input reached the boundary as a PRESENT non-finite value (NaN/Infinity), which cannot be told from an absent optional, so the point is refused rather than let either solver publish NaN or a fabricated number. 'mergedFundingEntangled' — MERGED views only: the members draw on the same external money (a shared funding wallet, or a transfer chain moving one deposit between them), so summing their bases would count that deposit once per member; view the portfolios separately for a return on each. 'ledgerCannotFundHoldings' — the recorded deposits were proven short of what the wallet spent AND the wallet balance is negative, so the holdings are funded by money the ledger never recorded and the ratio would be overstated; complete the deposit history. 'mergedFundingUndeclared' — MERGED views only: the funding partition could not inspect one or more members, so it can neither prove nor rule out that they share external money; the fold refuses rather than risk double-counting a deposit, and the remedy is the same as for mergedFundingEntangled (view the portfolios separately). 'mergedMemberRefused' — MERGED views only: at least one member refused its own return with a verdict about its own capital and the members do not share one reason, so no member diagnosis is true of the whole merge; open each portfolio on its own to see which one and why. 'neverDeployed' — era-17: a certified book whose deposits were NEVER put to work (the deployed peak never cleared the 0.05 dust floor): no capital at work means a return on deployed capital does not exist yet. A named abstention, never a percent and never the microExternalBasis heuristic; the earned amount (externalCapitalReturnAmount) still populates and surfaces show it beside the caption.",
          )
          .nullable(),
        externalCapitalReturnBasisKind: z
          .enum(['inception', 'window', 'costBasis'])
          .describe(
            "WHICH quantity the four fields above carry. 'inception' — inception-to-date, the engine's own quantity, with externalCapitalBasis the all-time peak. 'window' — relative to the requested timeframe's opening bar, with externalCapitalBasis the window's OPENING EQUITY. 'costBasis' — the honest cost-basis fallback served where external-capital history is missing but the book is still majority-invested: externalCapitalReturnPercent is a return on the peak OPEN-POSITIONS cost basis (externalCapitalBasis), and externalCapitalHistoryIncomplete is true. Not nullable: a refusal still answers one of the questions. NEVER re-apply a client-side rebase to fields marked 'window' or 'costBasis'.",
          ),
        externalCapitalPeakDeployed: z
          .coerce.number()
          .describe(
            "The running peak of the GROSS capital the book put to work (rawInvestedCapital before the internal-funding subtraction and the external cap) — the SCALE REFERENCE the 'microExternalBasis' rung divides the basis by, never a denominator of any served return. Present only as a finite number; absent when the point never reached the kernel or the producer predates the field. Consumed by the merged aggregate fold, which re-applies the micro rung to the summed pair before a microExternalBasis member may contribute.",
          )
          .optional(),
        externalCapitalDeployed: z
          .coerce.number()
          .describe(
            "EXTERNAL CAPITAL AT WORK at this point — max(0, min(net external capital, cost basis)): the money the user contributed from outside AND currently has invested. THE CAPITAL LINE a chart draws, and never a denominator of any served return. It falls when positions are sold, does not move when idle cash arrives, and a reinvested winner raises it only by what was actually paid in (the cost basis grows, the external cap does not). Distinct from externalCapitalBasis, which is this quantity's running PEAK over a differently-measured deployment leg — right as a denominator, and a ratchet that can never fall. Present only as a finite number; absent when either leg is unknown, and a reader then falls back rather than drawing a fabricated level.",
          )
          .optional(),
        externalCapitalHistoryIncomplete: z
          .boolean()
          .describe(
            "True when the book's recorded deposit history looks incomplete, so the served return rests on an understated basis. Two disjoint cases: (1) a 'costBasis' fallback point, where the external-capital return was refused for a book with NO deposits and an honest cost-basis return is served instead; (2) a normal return on a book with PARTIAL deposits, where recorded inflows fail to cover recorded outflows (the served number is unchanged, only the hint added). A surface shows a subtle 'history incomplete — complete your history' hint. Margin/CFD books are excluded. Absent/false everywhere else.",
          )
          .optional(),
        externalCapitalNegativeCash: z
          .object({
            from: z.string().describe('First day (YYYY-MM-DD) the wallet fell below zero.'),
            to: z
              .string()
              .describe(
                'First day it was back at or above zero; null while it is still below today.',
              )
              .nullable(),
            lowestBalance: z
              .coerce.number()
              .describe(
                'The lowest balance inside the stretch, in the currency these fields are served in.',
              ),
          })
          .passthrough()
          .describe(
            'The stretch the wallet spent below zero, which the served return counts as EXTERNAL capital (an overdraft is money from outside the recorded ledger: a forgotten deposit or a broker credit line). Only on a book without margin or CFDs whose wallet funds no other book; always beside externalCapitalHistoryIncomplete: true. A surface names the days and the lowest balance and suggests adding the missing deposit or marking the portfolio as margin. Absent everywhere else, and on every bar of a series but the last.',
          )
          .optional(),
        externalCapitalUnpricedCashRows: z
          .coerce.number()
          .int()
          .gt(0)
          .describe(
            "How many wallet rows have no exchange rate in force on their day, so they are missing from the cash and the external capital alike. Present only beside externalCapitalSuppressedReason 'fxRateUnavailable' on the terminal; absent everywhere else.",
          )
          .optional(),
        externalCapitalUnpricedCost: z
          .object({
            pair: z
              .string()
              .describe("The currency pair no rate priced, FROM/TO (e.g. 'USD/PLN')."),
            date: z
              .string()
              .describe('The day of the first cost row it left unpriced (YYYY-MM-DD).'),
          })
          .passthrough()
          .describe(
            "The first cost row no exchange rate in force could price: the pair and its day. The basis is refused from that day on (owner ruling D53); a manual rate on that trade clears it. Present on the terminal of a book with such a row, whichever refusal reason won (a wallet row with no rate refuses 'fxRateUnavailable' first); absent everywhere else.",
          )
          .optional(),
        cfdCapitalMissingCount: z
          .coerce.number()
          .describe(
            "How many of this book's closed-CFD cash rows carry NO capital-at-risk data (a broker statement fragment was never recovered for them — SAXO/LLM-minted closes, XTB exports without a closed-position sheet) AND leave the served return short of their margin. On a book whose funding wallet is its own, their realized P&L is in the return while their margin is NOT in the external-capital denominator, so a served return over a non-zero count rests on an understated basis and should be captioned; a refused return with a non-zero count carries externalCapitalSuppressedReason 'cfdCapitalUnavailable'. SCOPED LIKE THE BASIS: on a funding wallet shared with another portfolio the return takes no close's P&L and owes no margin, so this is 0 even when such closes exist. 0 therefore means the basis is not understated, never that no close lacks capital data; it is also 0 on every book whose closes all carry capital, and on every book without closed CFDs.",
          ),
        totalValue: z
          .coerce.number()
          .describe(
            "What the book is worth right now: the market value of the open positions PLUS the wallet's settled cash, converted to portfolio currency. The cash leg is folded UNCONDITIONALLY — `showCashAsPosition` governs COMPOSITION (the holdings cash row, the allocation rings, the concentration rows, the return basis), never this total. For positions alone read `positionValue`.",
          ),
        totalInvested: z
          .coerce.number()
          .describe(
            "Invested capital: the external money currently at work, i.e. net external deposits (each at its own date exchange rate) plus net in-kind delivered capital, capped at the open positions cost basis and never below 0. It is NOT the denominator of the return: externalCapitalReturnPercent is measured on externalCapitalBasis (the peak of external money at work), which the app labels 'Return basis'. Null when the cost basis cannot be stated: a cost row with no exchange rate in force (externalCapitalSuppressedReason 'costFxRateUnavailable', owner ruling D53) or an open position no spot rate converts (unconvertedCurrencies) — never the figure folded without that row.",
          )
          .nullable(),
        investmentReturnPercent: z
          .coerce.number()
          .describe(
            'The canonical headline (externalCapitalReturnPercent) with the currency move taken out, percent: investmentReturnPercent + currencyImpactPercent = externalCapitalReturnPercent. Null when the split is not measurable (currencyImpactUnavailableReason says why) — never a fabricated value.',
          )
          .nullable(),
        currencyImpactPercent: z
          .coerce.number()
          .describe(
            "The currency's part of externalCapitalReturnPercent, percentage points: what the book's foreign holdings (positions and the cash the return measures) gained or lost from exchange rates since each amount entered its currency, over the same basis. Exactly 0 only when the book never held anything outside its currency; null when not measurable.",
          )
          .nullable(),
        currencyImpactAmount: z
          .coerce.number()
          .describe(
            'The currency impact in money (display currency) — the numerator of currencyImpactPercent. Null when not measurable. A merged view sums its members.',
          )
          .nullable(),
        currencyImpactUnavailableReason: z
          .enum(['returnUnavailable', 'windowed', 'fxUnmeasurable'])
          .describe(
            'Why investmentReturnPercent / currencyImpactPercent are null; null when they are numbers. returnUnavailable: the headline itself is refused. windowed: the headline is a window (a timeframe, a pinned end) — the split is served for the inception headline only. fxUnmeasurable: a rate or a trade the currency measure needs is missing.',
          )
          .nullable(),
        ownContributionReturnPercent: z
          .coerce.number()
          .describe(
            "The SECOND return of a pension plan (PPK / PPE): the return on the participant's OWN contributions, percent, inception to date. The headline (externalCapitalReturnPercent) counts every contribution as external capital and answers what the fund did; this one counts what the employer and the state paid as the participant's income, and what ZUS / PFR took back on an early exit as a cost, so it answers what the plan did for the participant. The gap between the two is the plan's profitability. Null whenever there is none, with ownContributionSuppressedReason saying why: a null is a gap, NEVER 0%.",
          )
          .nullable(),
        ownContributionReturnAmount: z
          .coerce.number()
          .describe(
            "The participant's gain in currency: total value less their own capital, i.e. the headline's amount plus what the employer and the state paid in, net of what was taken back. Divided by ownContributionBasis it reproduces ownContributionReturnPercent. Null when not stated.",
          )
          .nullable(),
        ownContributionBasis: z
          .coerce.number()
          .describe(
            "THE denominator of the second return: the running peak of the participant's own capital at work, the employer's and the state's money excluded. Null when not stated.",
          )
          .nullable(),
        ownContributionSuppressedReason: z
          .enum([
            'positionFilter',
            'noBasisYet',
            'noValueAtDate',
            'zeroBasis',
            'negativeValue',
            'dustBasis',
            'equityWipedOut',
            'microExternalBasis',
            'insufficientExternalHistory',
            'ledgerCannotFundHoldings',
            'neverDeployed',
            'cfdCapitalUnavailable',
            'fxRateUnavailable',
            'costFxRateUnavailable',
            'valueFxRateUnavailable',
            'noWindowOpenBasis',
            'noWindowOpenEquity',
            'capitalAddedInWindow',
            'windowEquityWipedOut',
            'syntheticPoint',
            'unavailable',
            'nonFiniteInput',
            'mergedFundingEntangled',
            'mergedFundingUndeclared',
            'mergedMemberRefused',
            'notApplicable',
            'contributionSourceNotStated',
            'windowed',
            'notComputed',
          ])
          .describe(
            "Why the second return is absent. Null IFF ownContributionReturnPercent is a number. 'notApplicable' - the book has no employer or state contribution (not a pension plan), so there is nothing to separate: show nothing. 'contributionSourceNotStated' - the book has a pension flow whose file does not say who paid (every PPE payment, a PPK transfer-in of unknown composition), so the participant's own return cannot be told from the headline: say so, never show 0%. 'windowed' - the request is a window or a pinned end, and this return is inception to date. 'notComputed' - this producer states no second return (a merged view, a payload older than the field). 'unavailable' - the book has no external capital to take the sponsors out of (no wallet ledger, a funding wallet another book shares). Every other value is the headline's own refusal ladder, applied to the participant's capital (externalCapitalSuppressedReason documents each).",
          )
          .nullable(),
        moneyWeightedReturnWithheldReason: z
          .enum([
            'SYNTHESIZED_OPENING_DATE',
            'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE',
            'MEMBER_UNAVAILABLE',
            'MEMBER_UNPRICED',
            'WINDOW_OPENING_UNKNOWN',
            'SHARED_WALLET',
            'POSITION_FILTER',
          ])
          .describe(
            "Why this book's money-weighted return is withheld (as /risk-metrics states it); null when it is served.",
          )
          .nullish(),
        annualizedReturnWithheldReason: z
          .enum(['SYNTHESIZED_OPENING_DATE', 'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE'])
          .describe(
            "Why this book's annualized return is withheld (as /risk-metrics states it); null when it is served.",
          )
          .nullish(),
        twrRefused: z
          .boolean()
          .describe(
            "True when this book's time-weighted return is REFUSED on some bar of its series (the chart's twrRefused flag): a written-option book with no recorded collateral (D49), or a flow no rate can price. Absent where the producer computes no series.",
          )
          .optional(),
        twrRefusedReason: z
          .enum([
            'unresolvable-fx-rate',
            'no-invested-capital',
            'equity-wiped-out',
            'unpriced-cash',
            'SHARED_WALLET',
            'NO_RECORDED_COLLATERAL',
          ])
          .describe(
            'Why twrRefused is true, from the first refused bar: the kernel refused the series (unresolvable-fx-rate, no-invested-capital), a bar with no growth factor left (equity-wiped-out), foreign cash with no rate in force (unpriced-cash), a shared funding wallet (SHARED_WALLET, D56), only written options with no collateral (NO_RECORDED_COLLATERAL, D49). Null when nothing is refused.',
          )
          .nullish(),
        cumulativeDividends: z
          .coerce.number()
          .describe('Cumulative net dividends (dividends + interest - withholding tax)'),
        totalDividends: z
          .coerce.number()
          .describe('Total dividends (alias for cumulativeDividends for backwards compatibility)'),
        positionValue: z
          .coerce.number()
          .describe(
            'Market value of the open positions alone. EXCLUDES the settled wallet cash that `totalValue` folds in.',
          ),
        positionValueForReturns: z
          .coerce.number()
          .describe(
            '`positionValue` less every position EXCLUDED from performance by the unpriced-open-CFD ruling: an open CFD with no user-supplied current price is not in the return on either side, numerator or denominator. ABSENT when nothing was excluded, which is every book holding no open CFD — read it as `positionValueForReturns ?? positionValue`. Present and SMALLER than `positionValue` means the difference is a broker statement value this book still owns (it rides `totalValue` and the chart) but that no return may be measured from.',
          )
          .optional(),
        realizedGain: z
          .coerce.number()
          .describe(
            'Realized gains from closed positions or partial sales, each realization at the rate of its own day (D34). Null when a realization has no exchange rate in force on its day: the currencies are named in realizedUnconvertedCurrencies. Never 0 for a refusal.',
          )
          .nullable(),
        realizedUnconvertedCurrencies: z
          .array(z.string())
          .describe(
            'Present when realizedGain is null because a realization has no exchange rate on its own day: the currencies that could not be converted. Absent otherwise.',
          )
          .optional(),
        costBasis: z
          .coerce.number()
          .describe(
            'Cost basis used for return calculations. This is the actual investment cost (BUY cost - SELL proceeds + realized gains) used as denominator in return formula. May differ from totalInvested which is adjusted for display purposes. NULL when the value is provably undefined — a NEGATIVE basis means the book divested more than it ever invested, which only an incomplete history can produce, so no number is served. Check `historyCoherence` for the reason and render a placeholder.',
          )
          .nullable(),
        overdraftFunding: z
          .coerce.number()
          .describe(
            "How far the wallet is below zero TODAY, counted by this book as money paid in: a book with no margin and no CFD, whose wallet funds no other book, treats an overdraft as external capital while it lasts (a forgotten deposit or a broker credit line). Absent when the wallet is not below zero or the book is leveraged. The account and merged Invested add it to that book's external money.",
          )
          .optional(),
        walletCashAtFlowFx: z
          .coerce.number()
          .describe(
            "The wallet's cash TODAY at the exchange rate each flow was booked at - the basis `overdraftFunding` and the net deposits are stated in, where `totalValue` revalues foreign cash at today's rate. The merged headline nets one book's idle cash against another book's overdraft with it, as the chart does bar by bar. Negative while overdrawn; absent when the series has no terminal bar.",
          )
          .optional(),
        unrealizedGainLoss: z
          .coerce.number()
          .describe(
            'Unrealized gain/loss on the OPEN POSITIONS: positionValue less their cost basis. NOT `totalValue - totalInvested` — that difference also contains realized gains, dividend income and (since the wealth total folds cash) the settled cash balance, all of which are served as their own fields, so it would double-count them.',
          ),
        currentCapitalGain: z
          .coerce.number()
          .describe(
            'MERGED books only: the degraded pair beside a merge-level canonical refusal — the gain on the external capital currently at work (the per-funding-group aggregate invested, a shared deposit counted once). Measured on the POSITIONS value, NOT totalValue: totalInvested is min-capped to exclude idle cash, so a cash-inclusive numerator would put a deposit in the return. NOT wire-derivable as `positionValue - totalInvested`: this gain is measured on the LIVE MEMBER FAN-OUT positions value (the same producer the merged card prints, so one merge cannot show two gains), while the `positionValue` field above is the series terminal bar (so the header cannot disagree with the chart). On a merged book the two differ by one daily bar. Absent on a single book.',
          )
          .nullish(),
        currentCapitalReturnPercent: z
          .coerce.number()
          .describe(
            'MERGED books only: currentCapitalGain / totalInvested x 100. Null when totalInvested is not positive — a gap, NEVER 0%. Absent on a single book.',
          )
          .nullish(),
        dayChangeAmount: z
          .coerce.number()
          .describe(
            "Aggregate portfolio day-change amount in display currency, derived from the 1D chart window (last bar − first bar) so that `liveValue − dayChangeAmount` reconstructs the 1D chart's anchor bar exactly. Session-aware by construction (a position whose exchange did not trade the portfolio 1D session holds flat and contributes 0). Absent when a 1D window is unavailable (e.g. an anchorless single-point series).",
          )
          .optional(),
        dayChangePercent: z
          .coerce.number()
          .describe(
            'Aggregate portfolio day-change percentage, relative to the 1D anchor position value (positions-only, cash excluded — mirrors the portfolio-metrics dayChange base).',
          )
          .optional(),
        dayChangeDate: z
          .string()
          .describe('Trading date the day change reflects (last 1D chart bar date, ISO 8601).')
          .optional(),
        historyCoherence: z
          .object({
            status: z
              .enum(['COHERENT', 'INCOMPLETE_HISTORY'])
              .describe(
                'COHERENT: every recorded sell is covered by recorded buys. INCOMPLETE_HISTORY: at least one symbol sold more shares than it ever recorded buying.',
              ),
            reason: z
              .literal('SOLD_MORE_THAN_BOUGHT')
              .describe('Machine-readable cause. Null when the history is coherent.')
              .nullable(),
            affectedSymbolCount: z
              .coerce.number()
              .describe(
                'Total number of symbols with a detected gap. May exceed `issues.length`, which is capped so the payload cannot scale with a badly broken book.',
              ),
            issues: z
              .array(
                z
                  .object({
                    symbol: z.string().describe('Instrument symbol the gap was detected on'),
                    firstBreachDate: z
                      .string()
                      .describe(
                        'UTC calendar date of the FIRST moment cumulative sold quantity exceeded cumulative bought quantity. Everything the book records before this date is intact.',
                      ),
                    maxShortfall: z
                      .coerce.number()
                      .describe(
                        'Largest share deficit reached across the history walk. This is how many shares of purchase history are missing at minimum.',
                      ),
                    quantityBought: z.coerce.number().describe('Total quantity recorded bought'),
                    quantitySold: z.coerce.number().describe('Total quantity recorded sold'),
                  })
                  .passthrough(),
              )
              .describe('Bounded sample of the affected symbols, ordered by symbol.'),
          })
          .passthrough()
          .describe(
            'Whether the recorded trade history can support the positions this book claims to have traded. Present whenever a ledger was available to walk. When status is INCOMPLETE_HISTORY the numbers above were computed from an impossible input — surface the warning rather than presenting the return as fact.',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/trade-activity',
    alias: 'portfolioCoreGetOrderActivity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z
      .object({
        from: z
          .string()
          .describe('YYYY-MM-DD, first day of the trailing 12-month window (inclusive).'),
        to: z.string().describe('YYYY-MM-DD, the as-of day (inclusive).'),
        days: z
          .array(
            z
              .object({
                date: z.string().describe('YYYY-MM-DD'),
                count: z.coerce.number().int().describe('Orders placed that day.'),
              })
              .passthrough(),
          )
          .describe('Only days with at least one order (a buy or a sell), ascending.'),
        totalTrades: z.coerce.number().int().describe('Orders inside the window.'),
        activeDays: z.coerce.number().int().describe('Days inside the window with at least one order.'),
        avgTradesPerMonth: z
          .coerce.number()
          .describe(
            'Orders inside the window divided by its 12 months; null for a book with no order.',
          )
          .nullable(),
        avgHoldDays: z
          .coerce.number()
          .describe(
            "Average holding period in DAYS of the units CLOSED inside the window: per position, the held inventory's average age at each close, averaged over positions that closed something in the window. Null with none.",
          )
          .nullable(),
        lastActiveAt: z
          .string()
          .describe('YYYY-MM-DD of the latest order; null with none.')
          .nullable(),
        firstTradeAt: z
          .string()
          .describe('YYYY-MM-DD of the first order; null with none.')
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/transactions',
    alias: 'portfolioCoreGetTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(0).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.enum(['BUY', 'SELL', 'DIVIDEND']).optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'includeDeleted',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z
          .enum([
            'date',
            'amount',
            'type',
            'symbol',
            'quantity',
            'price',
            'fees',
            'pnl',
            'portfolioName',
          ])
          .optional()
          .default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: PaginatedPortfolioTransactionsResponseDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/transactions/export',
    alias: 'portfolioCoreExportTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'positionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(0).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.array(z.enum(['BUY', 'SELL', 'DIVIDEND'])).optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'includeDeleted',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z
          .enum([
            'date',
            'amount',
            'type',
            'symbol',
            'quantity',
            'price',
            'fees',
            'pnl',
            'portfolioName',
          ])
          .optional()
          .default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['csv', 'pdf']).optional().default('csv'),
      },
      {
        name: 'timeZone',
        type: 'Query',
        schema: z
          .enum([
            'Africa/Abidjan',
            'Africa/Accra',
            'Africa/Addis_Ababa',
            'Africa/Algiers',
            'Africa/Asmera',
            'Africa/Bamako',
            'Africa/Bangui',
            'Africa/Banjul',
            'Africa/Bissau',
            'Africa/Blantyre',
            'Africa/Brazzaville',
            'Africa/Bujumbura',
            'Africa/Cairo',
            'Africa/Casablanca',
            'Africa/Ceuta',
            'Africa/Conakry',
            'Africa/Dakar',
            'Africa/Dar_es_Salaam',
            'Africa/Djibouti',
            'Africa/Douala',
            'Africa/El_Aaiun',
            'Africa/Freetown',
            'Africa/Gaborone',
            'Africa/Harare',
            'Africa/Johannesburg',
            'Africa/Juba',
            'Africa/Kampala',
            'Africa/Khartoum',
            'Africa/Kigali',
            'Africa/Kinshasa',
            'Africa/Lagos',
            'Africa/Libreville',
            'Africa/Lome',
            'Africa/Luanda',
            'Africa/Lubumbashi',
            'Africa/Lusaka',
            'Africa/Malabo',
            'Africa/Maputo',
            'Africa/Maseru',
            'Africa/Mbabane',
            'Africa/Mogadishu',
            'Africa/Monrovia',
            'Africa/Nairobi',
            'Africa/Ndjamena',
            'Africa/Niamey',
            'Africa/Nouakchott',
            'Africa/Ouagadougou',
            'Africa/Porto-Novo',
            'Africa/Sao_Tome',
            'Africa/Tripoli',
            'Africa/Tunis',
            'Africa/Windhoek',
            'America/Adak',
            'America/Anchorage',
            'America/Anguilla',
            'America/Antigua',
            'America/Araguaina',
            'America/Argentina/La_Rioja',
            'America/Argentina/Rio_Gallegos',
            'America/Argentina/Salta',
            'America/Argentina/San_Juan',
            'America/Argentina/San_Luis',
            'America/Argentina/Tucuman',
            'America/Argentina/Ushuaia',
            'America/Aruba',
            'America/Asuncion',
            'America/Bahia',
            'America/Bahia_Banderas',
            'America/Barbados',
            'America/Belem',
            'America/Belize',
            'America/Blanc-Sablon',
            'America/Boa_Vista',
            'America/Bogota',
            'America/Boise',
            'America/Buenos_Aires',
            'America/Cambridge_Bay',
            'America/Campo_Grande',
            'America/Cancun',
            'America/Caracas',
            'America/Catamarca',
            'America/Cayenne',
            'America/Cayman',
            'America/Chicago',
            'America/Chihuahua',
            'America/Ciudad_Juarez',
            'America/Coral_Harbour',
            'America/Cordoba',
            'America/Costa_Rica',
            'America/Coyhaique',
            'America/Creston',
            'America/Cuiaba',
            'America/Curacao',
            'America/Danmarkshavn',
            'America/Dawson',
            'America/Dawson_Creek',
            'America/Denver',
            'America/Detroit',
            'America/Dominica',
            'America/Edmonton',
            'America/Eirunepe',
            'America/El_Salvador',
            'America/Fort_Nelson',
            'America/Fortaleza',
            'America/Glace_Bay',
            'America/Godthab',
            'America/Goose_Bay',
            'America/Grand_Turk',
            'America/Grenada',
            'America/Guadeloupe',
            'America/Guatemala',
            'America/Guayaquil',
            'America/Guyana',
            'America/Halifax',
            'America/Havana',
            'America/Hermosillo',
            'America/Indiana/Knox',
            'America/Indiana/Marengo',
            'America/Indiana/Petersburg',
            'America/Indiana/Tell_City',
            'America/Indiana/Vevay',
            'America/Indiana/Vincennes',
            'America/Indiana/Winamac',
            'America/Indianapolis',
            'America/Inuvik',
            'America/Iqaluit',
            'America/Jamaica',
            'America/Jujuy',
            'America/Juneau',
            'America/Kentucky/Monticello',
            'America/Kralendijk',
            'America/La_Paz',
            'America/Lima',
            'America/Los_Angeles',
            'America/Louisville',
            'America/Lower_Princes',
            'America/Maceio',
            'America/Managua',
            'America/Manaus',
            'America/Marigot',
            'America/Martinique',
            'America/Matamoros',
            'America/Mazatlan',
            'America/Mendoza',
            'America/Menominee',
            'America/Merida',
            'America/Metlakatla',
            'America/Mexico_City',
            'America/Miquelon',
            'America/Moncton',
            'America/Monterrey',
            'America/Montevideo',
            'America/Montserrat',
            'America/Nassau',
            'America/New_York',
            'America/Nome',
            'America/Noronha',
            'America/North_Dakota/Beulah',
            'America/North_Dakota/Center',
            'America/North_Dakota/New_Salem',
            'America/Ojinaga',
            'America/Panama',
            'America/Paramaribo',
            'America/Phoenix',
            'America/Port-au-Prince',
            'America/Port_of_Spain',
            'America/Porto_Velho',
            'America/Puerto_Rico',
            'America/Punta_Arenas',
            'America/Rankin_Inlet',
            'America/Recife',
            'America/Regina',
            'America/Resolute',
            'America/Rio_Branco',
            'America/Santarem',
            'America/Santiago',
            'America/Santo_Domingo',
            'America/Sao_Paulo',
            'America/Scoresbysund',
            'America/Sitka',
            'America/St_Barthelemy',
            'America/St_Johns',
            'America/St_Kitts',
            'America/St_Lucia',
            'America/St_Thomas',
            'America/St_Vincent',
            'America/Swift_Current',
            'America/Tegucigalpa',
            'America/Thule',
            'America/Tijuana',
            'America/Toronto',
            'America/Tortola',
            'America/Vancouver',
            'America/Whitehorse',
            'America/Winnipeg',
            'America/Yakutat',
            'Antarctica/Casey',
            'Antarctica/Davis',
            'Antarctica/DumontDUrville',
            'Antarctica/Macquarie',
            'Antarctica/Mawson',
            'Antarctica/McMurdo',
            'Antarctica/Palmer',
            'Antarctica/Rothera',
            'Antarctica/Syowa',
            'Antarctica/Troll',
            'Antarctica/Vostok',
            'Arctic/Longyearbyen',
            'Asia/Aden',
            'Asia/Almaty',
            'Asia/Amman',
            'Asia/Anadyr',
            'Asia/Aqtau',
            'Asia/Aqtobe',
            'Asia/Ashgabat',
            'Asia/Atyrau',
            'Asia/Baghdad',
            'Asia/Bahrain',
            'Asia/Baku',
            'Asia/Bangkok',
            'Asia/Barnaul',
            'Asia/Beirut',
            'Asia/Bishkek',
            'Asia/Brunei',
            'Asia/Calcutta',
            'Asia/Chita',
            'Asia/Colombo',
            'Asia/Damascus',
            'Asia/Dhaka',
            'Asia/Dili',
            'Asia/Dubai',
            'Asia/Dushanbe',
            'Asia/Famagusta',
            'Asia/Gaza',
            'Asia/Hebron',
            'Asia/Hong_Kong',
            'Asia/Hovd',
            'Asia/Irkutsk',
            'Asia/Jakarta',
            'Asia/Jayapura',
            'Asia/Jerusalem',
            'Asia/Kabul',
            'Asia/Kamchatka',
            'Asia/Karachi',
            'Asia/Katmandu',
            'Asia/Khandyga',
            'Asia/Krasnoyarsk',
            'Asia/Kuala_Lumpur',
            'Asia/Kuching',
            'Asia/Kuwait',
            'Asia/Macau',
            'Asia/Magadan',
            'Asia/Makassar',
            'Asia/Manila',
            'Asia/Muscat',
            'Asia/Nicosia',
            'Asia/Novokuznetsk',
            'Asia/Novosibirsk',
            'Asia/Omsk',
            'Asia/Oral',
            'Asia/Phnom_Penh',
            'Asia/Pontianak',
            'Asia/Pyongyang',
            'Asia/Qatar',
            'Asia/Qostanay',
            'Asia/Qyzylorda',
            'Asia/Rangoon',
            'Asia/Riyadh',
            'Asia/Saigon',
            'Asia/Sakhalin',
            'Asia/Samarkand',
            'Asia/Seoul',
            'Asia/Shanghai',
            'Asia/Singapore',
            'Asia/Srednekolymsk',
            'Asia/Taipei',
            'Asia/Tashkent',
            'Asia/Tbilisi',
            'Asia/Tehran',
            'Asia/Thimphu',
            'Asia/Tokyo',
            'Asia/Tomsk',
            'Asia/Ulaanbaatar',
            'Asia/Urumqi',
            'Asia/Ust-Nera',
            'Asia/Vientiane',
            'Asia/Vladivostok',
            'Asia/Yakutsk',
            'Asia/Yekaterinburg',
            'Asia/Yerevan',
            'Atlantic/Azores',
            'Atlantic/Bermuda',
            'Atlantic/Canary',
            'Atlantic/Cape_Verde',
            'Atlantic/Faeroe',
            'Atlantic/Madeira',
            'Atlantic/Reykjavik',
            'Atlantic/South_Georgia',
            'Atlantic/St_Helena',
            'Atlantic/Stanley',
            'Australia/Adelaide',
            'Australia/Brisbane',
            'Australia/Broken_Hill',
            'Australia/Darwin',
            'Australia/Eucla',
            'Australia/Hobart',
            'Australia/Lindeman',
            'Australia/Lord_Howe',
            'Australia/Melbourne',
            'Australia/Perth',
            'Australia/Sydney',
            'Europe/Amsterdam',
            'Europe/Andorra',
            'Europe/Astrakhan',
            'Europe/Athens',
            'Europe/Belgrade',
            'Europe/Berlin',
            'Europe/Bratislava',
            'Europe/Brussels',
            'Europe/Bucharest',
            'Europe/Budapest',
            'Europe/Busingen',
            'Europe/Chisinau',
            'Europe/Copenhagen',
            'Europe/Dublin',
            'Europe/Gibraltar',
            'Europe/Guernsey',
            'Europe/Helsinki',
            'Europe/Isle_of_Man',
            'Europe/Istanbul',
            'Europe/Jersey',
            'Europe/Kaliningrad',
            'Europe/Kiev',
            'Europe/Kirov',
            'Europe/Lisbon',
            'Europe/Ljubljana',
            'Europe/London',
            'Europe/Luxembourg',
            'Europe/Madrid',
            'Europe/Malta',
            'Europe/Mariehamn',
            'Europe/Minsk',
            'Europe/Monaco',
            'Europe/Moscow',
            'Europe/Oslo',
            'Europe/Paris',
            'Europe/Podgorica',
            'Europe/Prague',
            'Europe/Riga',
            'Europe/Rome',
            'Europe/Samara',
            'Europe/San_Marino',
            'Europe/Sarajevo',
            'Europe/Saratov',
            'Europe/Simferopol',
            'Europe/Skopje',
            'Europe/Sofia',
            'Europe/Stockholm',
            'Europe/Tallinn',
            'Europe/Tirane',
            'Europe/Ulyanovsk',
            'Europe/Vaduz',
            'Europe/Vatican',
            'Europe/Vienna',
            'Europe/Vilnius',
            'Europe/Volgograd',
            'Europe/Warsaw',
            'Europe/Zagreb',
            'Europe/Zurich',
            'Indian/Antananarivo',
            'Indian/Chagos',
            'Indian/Christmas',
            'Indian/Cocos',
            'Indian/Comoro',
            'Indian/Kerguelen',
            'Indian/Mahe',
            'Indian/Maldives',
            'Indian/Mauritius',
            'Indian/Mayotte',
            'Indian/Reunion',
            'Pacific/Apia',
            'Pacific/Auckland',
            'Pacific/Bougainville',
            'Pacific/Chatham',
            'Pacific/Easter',
            'Pacific/Efate',
            'Pacific/Enderbury',
            'Pacific/Fakaofo',
            'Pacific/Fiji',
            'Pacific/Funafuti',
            'Pacific/Galapagos',
            'Pacific/Gambier',
            'Pacific/Guadalcanal',
            'Pacific/Guam',
            'Pacific/Honolulu',
            'Pacific/Kiritimati',
            'Pacific/Kosrae',
            'Pacific/Kwajalein',
            'Pacific/Majuro',
            'Pacific/Marquesas',
            'Pacific/Midway',
            'Pacific/Nauru',
            'Pacific/Niue',
            'Pacific/Norfolk',
            'Pacific/Noumea',
            'Pacific/Pago_Pago',
            'Pacific/Palau',
            'Pacific/Pitcairn',
            'Pacific/Ponape',
            'Pacific/Port_Moresby',
            'Pacific/Rarotonga',
            'Pacific/Saipan',
            'Pacific/Tahiti',
            'Pacific/Tarawa',
            'Pacific/Tongatapu',
            'Pacific/Truk',
            'Pacific/Wake',
            'Pacific/Wallis',
            'UTC',
          ])
          .optional()
          .default('UTC'),
      },
      {
        name: 'locale',
        type: 'Query',
        schema: z
          .string()
          .regex(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/)
          .optional()
          .default('en-US'),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/treasury-bonds/:symbol',
    alias: 'treasuryBondsHolding',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: TreasuryBondHoldingDto,
  },
  {
    method: 'post',
    path: '/api/portfolios/:id/treasury-bonds/:symbol/book',
    alias: 'treasuryBondsBook',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: treasuryBondsBook_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: TreasuryBondHoldingDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/treasury-bonds/:symbol/redemption-quote',
    alias: 'treasuryBondsRedemptionQuote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'quantity',
        type: 'Query',
        schema: z.coerce.number().int().gte(1),
      },
    ],
    response: TreasuryRedemptionQuoteDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/valuations',
    alias: 'positionValuationsList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().min(1).max(32).optional(),
      },
    ],
    response: z.array(PositionValuationDto),
  },
  {
    method: 'put',
    path: '/api/portfolios/:id/valuations',
    alias: 'positionValuationsSet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: positionValuationsSet_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.array(PositionValuationDto),
  },
  {
    method: 'get',
    path: '/api/portfolios/:id/valuations/series',
    alias: 'positionValuationsSeries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().min(1).max(32),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: PositionValuationSeriesDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/:portfolioId/import-reconciliation',
    alias: 'portfolioImportReconciliation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'portfolioId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        files: z
          .array(
            z
              .object({
                importedFileId: z
                  .string()
                  .describe('The uploaded file this answer key belongs to.'),
                filename: z
                  .string()
                  .describe('The statement file as the user named it when they uploaded it.'),
                currency: z
                  .string()
                  .describe(
                    'ISO code of the cash wallet the file was folded over. All money fields are in it.',
                  ),
                accountKey: z
                  .string()
                  .describe(
                    "The sub-account the figure belongs to when one file holds several (a myfund account, a SAXO account id); '' for a one-account file.",
                  ),
                basis: z
                  .enum(['window-net', 'closing-at', 'positions'])
                  .describe(
                    "What kind of figure declaredTotal is. `window-net`: the statement's own sum of its cash movements over [windowFrom, asOf) (an XTB `Total`). `closing-at`: the cash the statement states AT asOf (an Ending Cash), compared with everything the import wrote before asOf. `positions`: the statement declares its HOLDINGS and no cash (an eMAKLER `Portfel`); every cash field is null and only the positions answer speaks.",
                  ),
                selfCheck: z
                  .union([
                    z.object({ ok: z.literal(true) }).passthrough(),
                    z
                      .object({
                        ok: z.literal(false),
                        reason: z.enum(['total-neq-rows', 'chain-gap', 'row-identity']),
                        delta: z.coerce.number(),
                      })
                      .passthrough(),
                  ])
                  .describe(
                    "Whether the statement agrees with ITSELF. ok false: `total-neq-rows` (its rows do not sum to its own Total), `chain-gap` (its running balance jumps between rows, as a filtered export's does) or `row-identity` (a row's balance is not the previous one plus its amount), by `delta`. Such a record reads `statement-inconsistent` in BOTH answers: its cash verdict, and its positions verdict when it has a sheet.",
                  ),
                declaredTotal: z
                  .coerce.number()
                  .describe(
                    "The broker's OWN figure, read off the statement (what kind: `basis`). Never derived, never guessed.",
                  )
                  .nullable(),
                importedBalance: z
                  .coerce.number()
                  .describe(
                    'What Stoxly imported, folded the way `basis` says: the cash rows inside [windowFrom, asOf) for `window-net`; for `closing-at`, every import-written cash row of the wallet before asOf, with nothing added to them. Rows the user typed by hand are excluded, and so are analytics-only rows, which carry no cash. null on a `positions` record.',
                  )
                  .nullable(),
                drift: z
                  .coerce.number()
                  .describe(
                    'SIGNED imported minus declared, in `currency`. 0 means the import matches the broker exactly; a drift within 0.02 is rounding, not a discrepancy. Positive = Stoxly holds MORE cash than the statement declared, negative = less.',
                  )
                  .nullable(),
                unclassifiedTotal: z
                  .coerce.number()
                  .describe(
                    'SIGNED sum, in `currency`, of the statement rows whose label Stoxly does not recognise yet: counted, left OUT of the book on purpose, never booked by a default. Because drift is imported minus declared, drift + unclassifiedTotal = 0 means those rows are the whole difference.',
                  ),
                nowBalance: z
                  .coerce.number()
                  .describe(
                    'The SAME fold as importedBalance, evaluated now. It differs from importedBalance only when an import-written row was deleted, corrected or re-imported since — a row the user typed by hand does not count.',
                  )
                  .nullable(),
                positionsAsOf: z
                  .string()
                  .describe(
                    'The instant the open-positions sheet was taken, ISO 8601 UTC — the moment declaredPositions, importedPositions and heldPositions speak for. null = no open sheet in the export.',
                  )
                  .nullable(),
                positionsBasis: z
                  .enum(['snapshot-at', 'window-delta'])
                  .describe(
                    'What the positions half measures. `snapshot-at`: the holdings AT positionsAsOf. `window-delta`: the CHANGE in holdings over [windowFrom, positionsAsOf), so a negative figure is shares sold in the window, never a negative holding. null with no sheet.',
                  )
                  .nullable(),
                declaredPositions: z
                  .record(z.coerce.number())
                  .describe(
                    "The broker's OWN open-positions sheet, quantity per printed ticker, restated into the units the book holds now (every share split the book applied after the sheet). null = the export has no open-positions sheet.",
                  )
                  .nullable(),
                importedPositions: z
                  .record(z.coerce.number())
                  .describe(
                    'What Stoxly holds as of positionsAsOf from import-written, non-CFD rows only, per printed ticker, read LIVE (an imported row deleted or corrected since moves it): every ticker on the sheet (0 when the book has none) plus every ticker the book holds that the sheet does not name. A negative figure is a ledger that sells more than it bought; the app shows it as flat. null with declaredPositions.',
                  )
                  .nullable(),
                heldPositions: z
                  .record(z.coerce.number())
                  .describe(
                    'What the BOOK holds as of positionsAsOf, per printed ticker: importedPositions plus every row the user typed before it, read live (a `window-delta` change is importedPositions alone). The positions verdict compares declaredPositions with THIS. Where it differs from importedPositions, the typed rows are the difference: they either repair the import or contradict the statement (then they are listed in handTypedRows). A negative figure is shown as flat. null with declaredPositions.',
                  )
                  .nullable(),
                handTypedRows: z
                  .array(
                    z
                      .object({
                        transactionId: z.string().describe('The ledger row.'),
                        symbol: z.string().describe("The book's symbol of the row's position."),
                        date: z.string().describe('The trade day, ISO 8601 (YYYY-MM-DD), UTC.'),
                        quantity: z
                          .coerce.number()
                          .describe('Unsigned, in the units the book holds today.'),
                        side: z.enum(['buy', 'sell']),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Rows the user TYPED (not imported), dated before positionsAsOf, that alone make the book disagree with this sheet: on their ticker the imported fold matches the sheet and the book with these rows does not. The statement wins (owner ruling R28): the user deletes them, Stoxly never does. Read live, so a deleted row leaves the list at once. Empty when there are none, and always empty for a `window-delta` sheet, a statement that disagrees with itself (selfCheck.ok false), or no sheet. Quote these, not the import, when the book holds a position the statement does not.',
                  ),
                verdict: z
                  .object({
                    cash: z
                      .enum([
                        'match',
                        'explained-unclassified',
                        'differs',
                        'statement-inconsistent',
                      ])
                      .describe(
                        '`match`: the drift is within 0.02. `explained-unclassified`: the drift is exactly the rows Stoxly does not recognise yet (unclassifiedTotal), left out of the book on purpose — the whole difference is those rows, and they are waiting for a rule. `differs`: neither, and the figures alone do not say why. `statement-inconsistent`: the statement does not agree with ITSELF (selfCheck.ok is false), so its figure says nothing about the book: never blame the import or the user for the drift. Uploading the same file again cannot change it; only for selfCheck.reason `chain-gap` (a filtered export) can a complete, unfiltered export of the account be checked instead. null on a `positions` record (holdings declared, no cash): there is no cash answer, never read it as a match.',
                      )
                      .nullable(),
                    positions: z
                      .union([
                        z
                          .object({ matched: z.coerce.number().int(), total: z.coerce.number().int() })
                          .passthrough(),
                        z.literal('statement-inconsistent'),
                      ])
                      .describe(
                        'Open positions: how many of the tickers on the sheet OR in the book (the union) the BOOK held in the quantity the sheet lists as of the sheet (heldPositions, read live: rows the user typed count, and a lot deleted or corrected since moves this count at once). `statement-inconsistent`: the statement does not agree with ITSELF (selfCheck.ok is false), so its sheet was not compared: no position is a mismatch, whatever the quantities show. null = the export has no open-positions sheet, so nothing is known about positions — never a match, never a fault.',
                      )
                      .nullable(),
                    changedSinceImport: z
                      .coerce.number()
                      .describe(
                        'SIGNED, in `currency`: nowBalance minus importedBalance, to the cent. 0 = nothing the import wrote in this window has moved since. A hand-typed row never moves it. null on a `positions` record.',
                      )
                      .nullable(),
                  })
                  .passthrough()
                  .describe(
                    'The ONE reading of the figures above; the product shows the same one. Quote it, then the figures it is made of.',
                  ),
                windowFrom: z
                  .string()
                  .describe(
                    'Start of the export window the statement speaks for. ISO 8601 UTC, inclusive.',
                  ),
                asOf: z
                  .string()
                  .describe(
                    'End of the export window, ISO 8601 UTC and EXCLUSIVE — the moment the statement was taken. Anything the user did after it is outside what these figures can answer for.',
                  ),
                recordedAt: z
                  .string()
                  .describe(
                    'When this record was written, ISO 8601 UTC. The same export uploaded again gets a NEW record with the same asOf, and only the latest one is listed: what you see IS the current answer for that window.',
                  ),
              })
              .passthrough(),
          )
          .describe(
            'One entry per export window, currency, sub-account and basis (the latest record of each), NEWEST export first. An EMPTY list means no reconciliation is recorded for this book. A record exists ONLY for a statement that prints its own figure (a Total or a closing balance) and was imported with its file since the record shipped; a broker whose exports print none, SnapTrade, a manual book, an older import, or a legacy XTB export imported on its own has none. Empty names NO reason and is NOT evidence of a match or of a problem.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/cash-history',
    alias: 'portfolioCoreGetAccountCashHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        currency: z.string().describe('The currency every totalBalance is in (the requested one).'),
        points: z
          .array(
            z
              .object({
                date: z.string().describe('UTC day, YYYY-MM-DD'),
                totalBalance: z
                  .coerce.number()
                  .describe(
                    'The wallets` cash at the end of that day, each currency at the rate in force on it; the last point is today, at the spot rate /wallets/total-balance converts at.',
                  ),
              })
              .passthrough(),
          )
          .describe(
            'Ascending, a point only where the total changed: a value holds until the next point, and there is no cash before the first. The last point is always today.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/portfolios/compare/projections',
    alias: 'portfolioCoreCompareProjections',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreCompareProjections_Body,
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/held-holdings',
    alias: 'portfolioCoreGetHeldHoldings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        holdings: z
          .array(
            z
              .object({
                symbol: z
                  .string()
                  .describe('Ticker as held (bare or exchange-suffixed); a broker label for a CFD'),
                name: z.string().describe('Display name (the ticker when none resolves)'),
                logo: z.string().describe('Logo URL, empty when none'),
                assetType: z
                  .string()
                  .describe('Asset type of the position (STOCK, ETF, CRYPTO, BOND, OPTION)'),
                isCfd: z
                  .boolean()
                  .describe('True for a CFD contract (a position flavour, not an asset type)'),
                totalQuantity: z
                  .coerce.number()
                  .describe('Quantity across all portfolios (contracts for options / CFDs)'),
                totalValue: z
                  .coerce.number()
                  .describe(
                    'Value in `currency` across all portfolios — the same term this instrument contributes to /portfolios/total-metrics. Negative for a losing CFD.',
                  ),
                priceStaleSince: z
                  .string()
                  .describe(
                    "YYYY-MM-DD of the market close the value is at, when it predates the last completed session (owner ruling D94: a dead line's last close, a held quote, the mirror's last bar). Null on a live price.",
                  )
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('Every valued open holding, largest first'),
        totalCount: z.coerce.number().describe('Number of holdings'),
        currency: z.string().describe('Currency every totalValue is denominated in'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/held-stocks',
    alias: 'portfolioCoreGetHeldStocks',
    requestFormat: 'json',
    response: z
      .object({
        stocks: z
          .array(
            z
              .object({
                symbol: z.string().describe('Stock symbol with exchange suffix (e.g., AAPL.US)'),
                name: z.string().describe('Company name'),
                logo: z.string().describe('Company logo URL'),
                currentPrice: z.coerce.number().describe('Current stock price in USD'),
                changePercent: z.coerce.number().describe('Daily change percentage'),
                priceStaleSince: z
                  .string()
                  .describe(
                    "YYYY-MM-DD of the market CLOSE currentPrice is, when it predates the last completed session (owner ruling D94: a dead line's last close, a held quote, the mirror's last bar). Null on a live price.",
                  )
                  .nullable(),
                assetType: z.string().describe('Asset type (STOCK, ETF, CRYPTO, etc.)'),
                totalQuantity: z.coerce.number().describe('Total quantity held across all portfolios'),
                totalValue: z.coerce.number().describe('Total market value across all portfolios'),
              })
              .passthrough(),
          )
          .describe('List of held stocks'),
        totalCount: z.coerce.number().describe('Total number of unique stocks held'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/monthly-returns',
    alias: 'portfolioCoreGetAccountMonthlyReturns',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        currency: z
          .string()
          .describe('The currency the returns are measured in (the requested one).'),
        monthlyReturns: z
          .array(
            z
              .object({
                month: z.string().describe('YYYY-MM'),
                returnPercent: z
                  .coerce.number()
                  .describe('The month`s time-weighted return as a PERCENTAGE (2.5 = +2.5%).'),
              })
              .passthrough(),
          )
          .describe('Ascending; a month with no capital at work in any book is absent.'),
        unavailablePortfolioIds: z
          .array(z.string())
          .describe(
            'Present only when some book`s analytics FAILED on this read: then monthlyReturns is empty — the account`s return cannot be measured without those books — and these are the books. Say the account`s months are unavailable, never that the account had none.',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/shares',
    alias: 'portfolioCoreGetAllUserShares',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'patch',
    path: '/api/portfolios/shares/:shareId',
    alias: 'portfolioCoreUpdateShareById',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreUpdateShareById_Body,
      },
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'delete',
    path: '/api/portfolios/shares/:shareId',
    alias: 'portfolioCoreDeleteShareById',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/shares/analytics',
    alias: 'portfolioCoreGetSharesAnalytics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'days',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(365).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/summaries',
    alias: 'portfolioCoreGetPortfolioSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'light',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/portfolios/total-metrics',
    alias: 'portfolioCoreGetTotalMetrics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fxOverride',
        type: 'Query',
        schema: z
          .string()
          .describe(
            "Substitute your own CURRENT exchange rate, as a comma-separated CODE:rate list (e.g. 'USD:3.8250,EUR:4.2500'). CODE is the SOURCE currency and the rate is expressed in units of this response's display currency per 1 unit of CODE. It replaces the spot rate: cost basis, invested capital and realized gain keep their trade-day rates, except where no trade-day rate was ever knowable and the current rate was already standing in for it (such a row reports no historicalConversionRate). A refusal is a 400 whose body carries a machine-readable 'code'. FX_OVERRIDE_OUT_OF_BAND: the rate misses the plausible band for CODE against the display currency, and the body states the min and max it missed. FX_OVERRIDE_OFF_REFERENCE: the rate is a fold too far from our own recent rate for the pair; the body states the min and max derived from it. FX_OVERRIDE_INVERTED: the rate is the reciprocal of a real one - CODE per display unit instead of display units per CODE. FX_OVERRIDE_SUB_UNIT_CODE: CODE is a sub-unit (GBX/GBp, ZAC/ZAc, ILA); state the rate against its parent currency, which is what every conversion it could reach is keyed on. FX_OVERRIDE_NO_DISPLAY_CURRENCY: the request named no display currency, so the rate is quoted against nothing. A malformed list (missing separator, non-decimal rate, a currency named twice) is a 400 with a message and no code.",
          )
          .optional(),
      },
    ],
    response: z
      .object({
        externalCapitalReturnPercent: z
          .coerce.number()
          .describe(
            "Return on EXTERNAL capital, percent. THE canonical return: one rule, one denominator, identical on every surface. READ IT WITH externalCapitalReturnBasisKind — 'inception' means inception-to-date, 'window' means relative to the requested timeframe's opening bar. Null when it is not defined for this book or this request — read externalCapitalSuppressedReason for which. A null is a gap, NEVER 0%.",
          )
          .nullable(),
        externalCapitalReturnAmount: z
          .coerce.number()
          .describe(
            "The return in currency. Under basisKind 'inception' it is total value less the external capital the book consumed; under 'window' it is the CHANGE in that quantity across the window. Divided by externalCapitalBasis it reproduces externalCapitalReturnPercent exactly in both regimes.",
          )
          .nullable(),
        externalCapitalBasis: z
          .coerce.number()
          .describe(
            "THE denominator. Under basisKind 'inception' the running peak of the smaller of the external and deployed capital legs (price-independent: a live price move changes the numerator only). Under 'window' the EQUITY standing at the window's opening bar, which is a materially different figure — do not label it 'invested capital' without checking the kind.",
          )
          .nullable(),
        externalCapitalBasisSource: z
          .enum(['externalFlows', 'deployedCash', 'tradeLedger', 'none'])
          .describe(
            "Which rung of the data-derived ladder produced the basis. 'none' IS the absence value, which is why this field is not nullable.",
          ),
        externalCapitalSuppressedReason: z
          .enum([
            'positionFilter',
            'noBasisYet',
            'noValueAtDate',
            'zeroBasis',
            'negativeValue',
            'dustBasis',
            'equityWipedOut',
            'microExternalBasis',
            'insufficientExternalHistory',
            'ledgerCannotFundHoldings',
            'neverDeployed',
            'cfdCapitalUnavailable',
            'fxRateUnavailable',
            'costFxRateUnavailable',
            'valueFxRateUnavailable',
            'noWindowOpenBasis',
            'noWindowOpenEquity',
            'capitalAddedInWindow',
            'windowEquityWipedOut',
            'syntheticPoint',
            'unavailable',
            'nonFiniteInput',
            'mergedFundingEntangled',
            'mergedFundingUndeclared',
            'mergedMemberRefused',
          ])
          .describe(
            "Why the return is absent. Null IFF externalCapitalReturnPercent is a number. 'positionFilter' — undefined for a filtered selection. 'noBasisYet'/'zeroBasis' — the book had no external capital at risk yet; render a gap. 'noValueAtDate' — no valuation on that date. 'negativeValue' — the portfolio is worth less than nothing, so a percentage-of-capital is not a measurement. 'dustBasis' — the capital at risk is a rounding residue (at or below 0.05), so a ratio over it is noise. 'equityWipedOut' — the loss exceeds the whole book. 'microExternalBasis' — the basis clears the dust floor but is a negligible fraction (below 5%) of the capital the book deployed, so it was funded by internal churn / already-withdrawn deposits and a return on external capital is not meaningfully computable (evaluated per point, on the externalFlows and deployedCash rungs). 'insufficientExternalHistory' — the book has no external-flow history (deployedCash rung) and its wallet cash balance exceeds the deployed basis, so the value is dominated by non-redeployed sell-proceeds cash and the return is an artefact of the missing deposit history; surface the 'please fill in your history' prompt. (When such a book is still majority-invested an honest cost-basis return is served instead — see externalCapitalReturnBasisKind 'costBasis' — and this reason is null.) 'cfdCapitalUnavailable' — the book has no denominator at all AND carries closed-CFD realized-P&L rows whose capital-at-risk is unknown (source statement not re-imported/backfilled yet), so the closes' unknown margin is the cause of the missing basis; a book with any stateable capital serves instead and reports cfdCapitalMissingCount. The remedy is re-importing the broker statement. 'fxRateUnavailable' — a wallet row has no exchange rate in force on its day (none stored for it or the 14 days before), so it is missing from the cash and the external capital alike; refused from that day on, externalCapitalUnpricedCashRows counts the rows. 'costFxRateUnavailable' — a cost row (a buy, an opened option, a delivery in) has no exchange rate in force on its day, so the cost basis and every basis built on it cannot be stated; refused from that day on, externalCapitalUnpricedCost names the pair and the day. 'valueFxRateUnavailable' — an open holding has no current exchange rate into the portfolio currency, so the book value and the return over it cannot be stated; unconvertedCurrencies names the currency (owner ruling D81). 'noWindowOpenBasis' — the requested window opens on a bar that carries no return. 'noWindowOpenEquity' — no equity was standing when the window opened. 'capitalAddedInWindow' — external capital entered the window AND the flow could not be time-weighted, so no denominator spans it. 'windowEquityWipedOut' — the book was wiped out inside the window. 'syntheticPoint' — a fabricated bar. 'unavailable' — an upstream input was missing. 'nonFiniteInput' — a return-determining input reached the boundary as a PRESENT non-finite value (NaN/Infinity), which cannot be told from an absent optional, so the point is refused rather than let either solver publish NaN or a fabricated number. 'mergedFundingEntangled' — MERGED views only: the members draw on the same external money (a shared funding wallet, or a transfer chain moving one deposit between them), so summing their bases would count that deposit once per member; view the portfolios separately for a return on each. 'ledgerCannotFundHoldings' — the recorded deposits were proven short of what the wallet spent AND the wallet balance is negative, so the holdings are funded by money the ledger never recorded and the ratio would be overstated; complete the deposit history. 'mergedFundingUndeclared' — MERGED views only: the funding partition could not inspect one or more members, so it can neither prove nor rule out that they share external money; the fold refuses rather than risk double-counting a deposit, and the remedy is the same as for mergedFundingEntangled (view the portfolios separately). 'mergedMemberRefused' — MERGED views only: at least one member refused its own return with a verdict about its own capital and the members do not share one reason, so no member diagnosis is true of the whole merge; open each portfolio on its own to see which one and why. 'neverDeployed' — era-17: a certified book whose deposits were NEVER put to work (the deployed peak never cleared the 0.05 dust floor): no capital at work means a return on deployed capital does not exist yet. A named abstention, never a percent and never the microExternalBasis heuristic; the earned amount (externalCapitalReturnAmount) still populates and surfaces show it beside the caption.",
          )
          .nullable(),
        externalCapitalReturnBasisKind: z
          .enum(['inception', 'window', 'costBasis'])
          .describe(
            "WHICH quantity the four fields above carry. 'inception' — inception-to-date, the engine's own quantity, with externalCapitalBasis the all-time peak. 'window' — relative to the requested timeframe's opening bar, with externalCapitalBasis the window's OPENING EQUITY. 'costBasis' — the honest cost-basis fallback served where external-capital history is missing but the book is still majority-invested: externalCapitalReturnPercent is a return on the peak OPEN-POSITIONS cost basis (externalCapitalBasis), and externalCapitalHistoryIncomplete is true. Not nullable: a refusal still answers one of the questions. NEVER re-apply a client-side rebase to fields marked 'window' or 'costBasis'.",
          ),
        externalCapitalPeakDeployed: z
          .coerce.number()
          .describe(
            "The running peak of the GROSS capital the book put to work (rawInvestedCapital before the internal-funding subtraction and the external cap) — the SCALE REFERENCE the 'microExternalBasis' rung divides the basis by, never a denominator of any served return. Present only as a finite number; absent when the point never reached the kernel or the producer predates the field. Consumed by the merged aggregate fold, which re-applies the micro rung to the summed pair before a microExternalBasis member may contribute.",
          )
          .optional(),
        externalCapitalDeployed: z
          .coerce.number()
          .describe(
            "EXTERNAL CAPITAL AT WORK at this point — max(0, min(net external capital, cost basis)): the money the user contributed from outside AND currently has invested. THE CAPITAL LINE a chart draws, and never a denominator of any served return. It falls when positions are sold, does not move when idle cash arrives, and a reinvested winner raises it only by what was actually paid in (the cost basis grows, the external cap does not). Distinct from externalCapitalBasis, which is this quantity's running PEAK over a differently-measured deployment leg — right as a denominator, and a ratchet that can never fall. Present only as a finite number; absent when either leg is unknown, and a reader then falls back rather than drawing a fabricated level.",
          )
          .optional(),
        externalCapitalHistoryIncomplete: z
          .boolean()
          .describe(
            "True when the book's recorded deposit history looks incomplete, so the served return rests on an understated basis. Two disjoint cases: (1) a 'costBasis' fallback point, where the external-capital return was refused for a book with NO deposits and an honest cost-basis return is served instead; (2) a normal return on a book with PARTIAL deposits, where recorded inflows fail to cover recorded outflows (the served number is unchanged, only the hint added). A surface shows a subtle 'history incomplete — complete your history' hint. Margin/CFD books are excluded. Absent/false everywhere else.",
          )
          .optional(),
        externalCapitalNegativeCash: z
          .object({
            from: z.string().describe('First day (YYYY-MM-DD) the wallet fell below zero.'),
            to: z
              .string()
              .describe(
                'First day it was back at or above zero; null while it is still below today.',
              )
              .nullable(),
            lowestBalance: z
              .coerce.number()
              .describe(
                'The lowest balance inside the stretch, in the currency these fields are served in.',
              ),
          })
          .passthrough()
          .describe(
            'The stretch the wallet spent below zero, which the served return counts as EXTERNAL capital (an overdraft is money from outside the recorded ledger: a forgotten deposit or a broker credit line). Only on a book without margin or CFDs whose wallet funds no other book; always beside externalCapitalHistoryIncomplete: true. A surface names the days and the lowest balance and suggests adding the missing deposit or marking the portfolio as margin. Absent everywhere else, and on every bar of a series but the last.',
          )
          .optional(),
        externalCapitalUnpricedCashRows: z
          .coerce.number()
          .int()
          .gt(0)
          .describe(
            "How many wallet rows have no exchange rate in force on their day, so they are missing from the cash and the external capital alike. Present only beside externalCapitalSuppressedReason 'fxRateUnavailable' on the terminal; absent everywhere else.",
          )
          .optional(),
        externalCapitalUnpricedCost: z
          .object({
            pair: z
              .string()
              .describe("The currency pair no rate priced, FROM/TO (e.g. 'USD/PLN')."),
            date: z
              .string()
              .describe('The day of the first cost row it left unpriced (YYYY-MM-DD).'),
          })
          .passthrough()
          .describe(
            "The first cost row no exchange rate in force could price: the pair and its day. The basis is refused from that day on (owner ruling D53); a manual rate on that trade clears it. Present on the terminal of a book with such a row, whichever refusal reason won (a wallet row with no rate refuses 'fxRateUnavailable' first); absent everywhere else.",
          )
          .optional(),
        cfdCapitalMissingCount: z
          .coerce.number()
          .describe(
            "The account fold's sum of its books' closed-CFD cash rows with NO capital-at-risk data, each book scoped like its basis (0 for a book on a funding wallet shared with another portfolio, whose return takes no close's P&L). Non-zero beside a served externalCapitalReturnPercent means the basis is understated by those closes' margin; 0 means it is not, never that no close lacks capital data. 0 when the fold threw (and the percent is refused).",
          ),
        totalValue: z
          .coerce.number()
          .describe(
            "The account's ASSETS in baseCurrency (owner ruling D46): every open position PLUS the live balance of EVERY wallet the user holds, the wallets no portfolio claims included, each wallet once — the same total /investments prints as Combined value, and the account net worth. A wallet whose currency has no usable rate is left out and named in unconvertedCurrencies. It is NOT the numerator of totalGainLoss below — a return excludes idle cash on both legs — and it is NOT what a grid of INVESTMENTS states: that is totalPositionValue, the cash-free twin below.",
          ),
        totalPositionValue: z
          .coerce.number()
          .describe(
            "The account's OPEN POSITIONS alone in baseCurrency — totalValue less every funding wallet's settled cash. Exactly the sum of the breakdown rows' convertedPositionValue, unconditionally: no wallet de-duplication applies to a positions leg. This is the leg a surface that states INVESTMENTS and CASH as two disjoint columns must read; totalValue folds the two into one number and belongs only under a label that promises cash. One inherited caveat: a book whose analytics-compute entry predates its own `positionValue` contributes its WEALTH total here (`memoizedPositionValue` — the split is unknown, never fabricated as zero cash) until that entry recomputes.",
          ),
        totalInvested: z
          .coerce.number()
          .describe(
            "The account's Invested capital: the external money currently at work, i.e. net external deposits (each at its own date exchange rate) plus net in-kind delivered capital, capped at the open positions cost basis and never below 0. It is NOT the denominator of the return: externalCapitalReturnPercent is measured on externalCapitalBasis (the peak of external money at work), which the app labels 'Return basis'. Null when the cost basis cannot be stated: a cost row with no exchange rate in force (externalCapitalSuppressedReason 'costFxRateUnavailable', owner ruling D53) or an open position no spot rate converts (unconvertedCurrencies) — never the figure folded without that row. In baseCurrency. Null when it cannot be computed (a cross-currency flow with no exchange rate): unknown, never 0. A book whose cost basis is undefined is left out (named in unavailablePortfolioIds with its unavailablePortfolioReasons); null when no book is covered.",
          )
          .nullable(),
        totalGainLoss: z
          .coerce.number()
          .describe(
            'The gain the returnPercentage below measures. When the account fold produced a canonical return this is externalCapitalReturnAmount verbatim, so gain divided by externalCapitalBasis (NOT totalInvested, which is the CURRENT capital at work rather than the peak basis the percent is measured on) reproduces it exactly. On the degraded path it is the CASH-FREE position value less totalInvested — never totalValue less totalInvested, which would publish settled cash as gain against a basis that excludes it — and then gain over totalInvested is the percent. Null when neither is known (no canonical return and totalInvested null).',
          )
          .nullable(),
        returnPercentage: z
          .coerce.number()
          .describe(
            'externalCapitalReturnPercent verbatim when the account fold produced one. Otherwise a DEGRADED figure, NOT the account return: open positions less totalInvested, over totalInvested (realized gains and dividend income left out), and externalCapitalSuppressedReason states why the account return is absent. Null when even that has no denominator, never 0.',
          )
          .nullable(),
        baseCurrency: z
          .string()
          .describe('The currency every converted*/total* figure above is stated in.'),
        portfolioBreakdown: z
          .array(
            z
              .object({
                portfolioId: z.string().describe('Portfolio id.'),
                portfolioName: z.string().describe('Portfolio name.'),
                originalCurrency: z
                  .string()
                  .describe("The book's OWN currency — what the original* fields below are in."),
                originalValue: z
                  .coerce.number()
                  .describe(
                    "The book's WEALTH in its own currency: open positions PLUS settled cash, folded unconditionally. showCashAsPosition governs composition, never a total.",
                  ),
                originalPositionValue: z
                  .coerce.number()
                  .describe(
                    "The book's OPEN POSITIONS alone, in its own currency — originalValue less the settled cash that total folds. A surface that states INVESTMENTS and CASH as two disjoint columns reads this one: folding cash into the investments column publishes the same money twice, once in each column, rather than fixing anything.",
                  ),
                originalInvested: z
                  .coerce.number()
                  .describe(
                    "Invested capital, in the book's own currency. Null when it cannot be stated: a cost row has no exchange rate in force on its day (D53); never a basis folded without it.",
                  )
                  .nullable(),
                originalGainLoss: z
                  .coerce.number()
                  .describe(
                    "The book's OWN return in its own currency: externalCapitalReturnAmount of its /portfolios/:id/summary-metrics, realized gains and income included, the figure its card shows. Null when that return is not defined, with gainLossSuppressedReason saying why. NOT originalValue or originalPositionValue less originalInvested.",
                  )
                  .nullable(),
                convertedValue: z
                  .coerce.number()
                  .describe(
                    "The book's WEALTH (positions plus its funding wallet's settled cash) in the response's baseCurrency, computed as a SINGLE FX leg from each position's purchase currency — not by folding originalValue through exchangeRate. These rows do NOT sum to the response-level totalValue (owner ruling D46): that is the account's assets, every wallet's live balance plus totalPositionValue, so a wallet no book claims is in it and in no row, and a wallet funding several books is stated on each of their rows and counted once there. Sum convertedPositionValue for the positions leg.",
                  ),
                convertedPositionValue: z
                  .coerce.number()
                  .describe(
                    "The baseCurrency twin of originalPositionValue: this book's open positions alone, over the same single FX leg from each position's purchase currency that convertedValue uses. These rows sum to the response-level totalPositionValue with NO exception — the shared-wallet caveat on convertedValue cannot reach a positions leg, because a position belongs to exactly one book while a wallet balance is quoted by every book it funds.",
                  ),
                convertedInvested: z
                  .coerce.number()
                  .describe(
                    "Invested capital in the response's baseCurrency, single-leg. Null exactly when originalInvested is.",
                  )
                  .nullable(),
                convertedGainLoss: z
                  .coerce.number()
                  .describe(
                    "The baseCurrency twin of originalGainLoss: the book's own externalCapitalReturnAmount computed in baseCurrency, or null with gainLossSuppressedReason.",
                  )
                  .nullable(),
                gainLossSuppressedReason: z
                  .enum([
                    'positionFilter',
                    'noBasisYet',
                    'noValueAtDate',
                    'zeroBasis',
                    'negativeValue',
                    'dustBasis',
                    'equityWipedOut',
                    'microExternalBasis',
                    'insufficientExternalHistory',
                    'ledgerCannotFundHoldings',
                    'neverDeployed',
                    'cfdCapitalUnavailable',
                    'fxRateUnavailable',
                    'costFxRateUnavailable',
                    'valueFxRateUnavailable',
                    'noWindowOpenBasis',
                    'noWindowOpenEquity',
                    'capitalAddedInWindow',
                    'windowEquityWipedOut',
                    'syntheticPoint',
                    'unavailable',
                    'nonFiniteInput',
                    'mergedFundingEntangled',
                    'mergedFundingUndeclared',
                    'mergedMemberRefused',
                  ])
                  .describe(
                    "Why originalGainLoss or convertedGainLoss is null — the same vocabulary as externalCapitalSuppressedReason; 'unavailable' when the book could not be computed at all. Null when both are numbers.",
                  )
                  .nullable(),
                exchangeRate: z
                  .coerce.number()
                  .describe(
                    "DISPLAY METADATA ONLY: the originalCurrency -> baseCurrency spot rate, or null when the pair could not be resolved. No money figure on this row passes through it. Null is the refusal the FX rows already render as 'no usable rate' — it used to be a literal 1, which reads as parity between two different currencies.",
                  )
                  .nullable(),
                instrumentFxLegs: z
                  .array(
                    z
                      .object({
                        instrumentCurrency: z
                          .string()
                          .describe(
                            'The instrument QUOTE currency, NORMALIZED to its parent ISO code (GBX -> GBP). The rate beside it is a rate on the PARENT; labelling it with the sub-unit overstates it 100x.',
                          ),
                        instrumentConversionRate: z
                          .coerce.number()
                          .describe(
                            "Units of the row's originalCurrency per 1 unit of instrumentCurrency. Never a fabricated 1: a pair that could not be resolved is omitted from the list instead.",
                          ),
                      })
                      .passthrough(),
                  )
                  .describe(
                    "The quote->account FX legs behind this book, one per distinct instrument quote currency it holds, sorted by currency. These are the conversions exchangeRate cannot see: a book read in its own currency has no book->display leg left to state, yet its foreign-quoted holdings still converted. A leg whose pair could not be resolved is OMITTED rather than served at 1:1, so an empty array reads 'we know of none', never 'there are none'.",
                  ),
                currencyImpactUnavailableReason: z
                  .enum(['returnUnavailable', 'windowed', 'fxUnmeasurable'])
                  .describe(
                    "Why this book's currency effect is not measured; see /summary-metrics.",
                  )
                  .nullish(),
                moneyWeightedReturnWithheldReason: z
                  .enum([
                    'SYNTHESIZED_OPENING_DATE',
                    'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE',
                    'MEMBER_UNAVAILABLE',
                    'MEMBER_UNPRICED',
                    'WINDOW_OPENING_UNKNOWN',
                    'SHARED_WALLET',
                    'POSITION_FILTER',
                  ])
                  .describe(
                    "Why this book's money-weighted return is withheld; see /summary-metrics.",
                  )
                  .nullish(),
                annualizedReturnWithheldReason: z
                  .enum(['SYNTHESIZED_OPENING_DATE', 'SYNTHESIZED_OPENING_DATE_NO_OVERRIDE'])
                  .describe("Why this book's annualized return is withheld; see /summary-metrics.")
                  .nullish(),
                twrRefused: z
                  .boolean()
                  .describe(
                    "Whether this book's TWR series is refused on some bar (see /summary-metrics); null when the book could not be computed.",
                  )
                  .nullish(),
                twrRefusedReason: z
                  .enum([
                    'unresolvable-fx-rate',
                    'no-invested-capital',
                    'equity-wiped-out',
                    'unpriced-cash',
                    'SHARED_WALLET',
                    'NO_RECORDED_COLLATERAL',
                  ])
                  .describe("Why this book's TWR is refused; see /summary-metrics.")
                  .nullish(),
                historyCoherenceReason: z
                  .literal('SOLD_MORE_THAN_BOUGHT')
                  .describe(
                    "Why this book's recorded sells outrun its recorded buys; see /summary-metrics.",
                  )
                  .nullish(),
                ownContributionSuppressedReason: z
                  .enum([
                    'positionFilter',
                    'noBasisYet',
                    'noValueAtDate',
                    'zeroBasis',
                    'negativeValue',
                    'dustBasis',
                    'equityWipedOut',
                    'microExternalBasis',
                    'insufficientExternalHistory',
                    'ledgerCannotFundHoldings',
                    'neverDeployed',
                    'cfdCapitalUnavailable',
                    'fxRateUnavailable',
                    'costFxRateUnavailable',
                    'valueFxRateUnavailable',
                    'noWindowOpenBasis',
                    'noWindowOpenEquity',
                    'capitalAddedInWindow',
                    'windowEquityWipedOut',
                    'syntheticPoint',
                    'unavailable',
                    'nonFiniteInput',
                    'mergedFundingEntangled',
                    'mergedFundingUndeclared',
                    'mergedMemberRefused',
                    'notApplicable',
                    'contributionSourceNotStated',
                    'windowed',
                    'notComputed',
                  ])
                  .describe(
                    "Why this book's second return (the return on the participant's own contributions) is absent, off the SAME compute as /summary-metrics ownContributionSuppressedReason, so Portfolio Health lists it without computing a book again; null when the second return is served, or when the book could not be computed.",
                  )
                  .nullish(),
              })
              .passthrough(),
          )
          .describe(
            'One row per portfolio, in both its own currency and baseCurrency. A book whose metrics could not be computed has no row; it is named in unavailablePortfolioIds.',
          ),
        unavailablePortfolioIds: z
          .array(z.string())
          .describe(
            'Books that could not be computed on this read and are NOT measured in its totals (their money is at most a stale stored figure). Absent when every book answered.',
          )
          .optional(),
        unavailablePortfolioReasons: z
          .record(
            z.enum(['costFxRateUnavailable', 'incoherentHistory', 'realizedFxRateUnavailable']),
          )
          .describe(
            "Why a book in unavailablePortfolioIds is left out, when it answered but its cost basis is undefined: 'costFxRateUnavailable' (a trade has no exchange rate for its day, D53) or 'incoherentHistory' (its history sells more than it bought). Such a book is left out of every figure measured on a cost basis (Invested, the degraded gain and percent, the return); the value totals (totalValue, positionValue) still include it. 'realizedFxRateUnavailable' (a realization has no exchange rate for its own day, D34): the book is left out of the realized total ONLY, which is null when no book is left. A book absent from this map threw on this read and is in no total.",
          )
          .optional(),
        unconvertedCurrencies: z
          .array(z.string())
          .describe(
            'Currencies excluded from the totals on this object because no usable exchange rate into its currency was available: the totals are PARTIAL and must be shown as such. Absent when nothing was excluded.',
          )
          .optional(),
        fundingWalletIds: z
          .array(z.string())
          .describe(
            'Every wallet a portfolio of this account draws on (portfolios.wallet_id, wallet_transactions.portfolio_id, portfolio_transactions.wallet_id). A wallet NOT listed is cash no portfolio claims: /investments folds it into its Combined value and names it (owner ruling D46). totalValue above counts only these wallets.',
          ),
        externalCapitalEntangledPortfolioIds: z
          .array(z.string())
          .describe(
            'Present ONLY beside the mergedFundingEntangled refusal: the portfolio ids the account fold proved to share external money. Absent on every other reason and on a served number.',
          )
          .optional(),
        dayChangeAmount: z
          .coerce.number()
          .describe(
            "The account's day change in baseCurrency: the sum of each book's 1D window computed in baseCurrency (FX move included, each window's ends at their own day's rate). Absent when a book holding positions contributed no 1D window — never a fabricated 0, never the other books served as the whole account.",
          )
          .optional(),
        dayChangePercent: z
          .coerce.number()
          .describe(
            'dayChangeAmount over the summed previous-close anchors of the contributing books, percent. Absent with dayChangeAmount, and also when the summed anchor is not positive.',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/trade-activity',
    alias: 'portfolioCoreGetAllOrderActivity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z
      .object({
        from: z
          .string()
          .describe('YYYY-MM-DD, first day of the trailing 12-month window (inclusive).'),
        to: z.string().describe('YYYY-MM-DD, the as-of day (inclusive).'),
        days: z
          .array(
            z
              .object({
                date: z.string().describe('YYYY-MM-DD'),
                count: z.coerce.number().int().describe('Orders placed that day.'),
              })
              .passthrough(),
          )
          .describe('Only days with at least one order (a buy or a sell), ascending.'),
        totalTrades: z.coerce.number().int().describe('Orders inside the window.'),
        activeDays: z.coerce.number().int().describe('Days inside the window with at least one order.'),
        avgTradesPerMonth: z
          .coerce.number()
          .describe(
            'Orders inside the window divided by its 12 months; null for a book with no order.',
          )
          .nullable(),
        avgHoldDays: z
          .coerce.number()
          .describe(
            "Average holding period in DAYS of the units CLOSED inside the window: per position, the held inventory's average age at each close, averaged over positions that closed something in the window. Null with none.",
          )
          .nullable(),
        lastActiveAt: z
          .string()
          .describe('YYYY-MM-DD of the latest order; null with none.')
          .nullable(),
        firstTradeAt: z
          .string()
          .describe('YYYY-MM-DD of the first order; null with none.')
          .nullable(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/transactions',
    alias: 'portfolioCoreGetAllTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'positionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(0).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.enum(['BUY', 'SELL', 'DIVIDEND']).optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'includeDeleted',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z
          .enum([
            'date',
            'amount',
            'type',
            'symbol',
            'quantity',
            'price',
            'fees',
            'pnl',
            'portfolioName',
          ])
          .optional()
          .default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: 'transactionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedPortfolioTransactionsResponseDto,
  },
  {
    method: 'delete',
    path: '/api/portfolios/transactions/:transactionId',
    alias: 'portfolioCoreDeleteTransaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'transactionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/portfolios/transactions/:transactionId',
    alias: 'portfolioCoreUpdateTransaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: portfolioCoreUpdateTransaction_Body,
      },
      {
        name: 'transactionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: PortfolioTransactionResponseDto,
  },
  {
    method: 'get',
    path: '/api/portfolios/transactions/cash-projection',
    alias: 'portfolioCoreGetCashProjection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'walletId',
        type: 'Query',
        schema: z.string().uuid(),
      },
      {
        name: 'date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'amount',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'transactionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
    ],
    response: z
      .object({
        currency: z.string().describe('The wallet currency every figure is in'),
        balanceAfter: z
          .coerce.number()
          .describe('End-of-day balance on `date` with the change applied (negative = below zero)'),
        lowestBalance: z
          .coerce.number()
          .describe('The lowest end-of-day balance from `date` on, with the change applied'),
        lowestDate: z.string().describe('The day (YYYY-MM-DD) of that lowest balance'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/portfolios/transactions/export',
    alias: 'portfolioCoreExportAllTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'positionId',
        type: 'Query',
        schema: z.string().uuid().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(0).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.array(z.enum(['BUY', 'SELL', 'DIVIDEND'])).optional(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'includeDeleted',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z
          .enum([
            'date',
            'amount',
            'type',
            'symbol',
            'quantity',
            'price',
            'fees',
            'pnl',
            'portfolioName',
          ])
          .optional()
          .default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'portfolioIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['csv', 'pdf']).optional().default('csv'),
      },
      {
        name: 'timeZone',
        type: 'Query',
        schema: z
          .enum([
            'Africa/Abidjan',
            'Africa/Accra',
            'Africa/Addis_Ababa',
            'Africa/Algiers',
            'Africa/Asmera',
            'Africa/Bamako',
            'Africa/Bangui',
            'Africa/Banjul',
            'Africa/Bissau',
            'Africa/Blantyre',
            'Africa/Brazzaville',
            'Africa/Bujumbura',
            'Africa/Cairo',
            'Africa/Casablanca',
            'Africa/Ceuta',
            'Africa/Conakry',
            'Africa/Dakar',
            'Africa/Dar_es_Salaam',
            'Africa/Djibouti',
            'Africa/Douala',
            'Africa/El_Aaiun',
            'Africa/Freetown',
            'Africa/Gaborone',
            'Africa/Harare',
            'Africa/Johannesburg',
            'Africa/Juba',
            'Africa/Kampala',
            'Africa/Khartoum',
            'Africa/Kigali',
            'Africa/Kinshasa',
            'Africa/Lagos',
            'Africa/Libreville',
            'Africa/Lome',
            'Africa/Luanda',
            'Africa/Lubumbashi',
            'Africa/Lusaka',
            'Africa/Malabo',
            'Africa/Maputo',
            'Africa/Maseru',
            'Africa/Mbabane',
            'Africa/Mogadishu',
            'Africa/Monrovia',
            'Africa/Nairobi',
            'Africa/Ndjamena',
            'Africa/Niamey',
            'Africa/Nouakchott',
            'Africa/Ouagadougou',
            'Africa/Porto-Novo',
            'Africa/Sao_Tome',
            'Africa/Tripoli',
            'Africa/Tunis',
            'Africa/Windhoek',
            'America/Adak',
            'America/Anchorage',
            'America/Anguilla',
            'America/Antigua',
            'America/Araguaina',
            'America/Argentina/La_Rioja',
            'America/Argentina/Rio_Gallegos',
            'America/Argentina/Salta',
            'America/Argentina/San_Juan',
            'America/Argentina/San_Luis',
            'America/Argentina/Tucuman',
            'America/Argentina/Ushuaia',
            'America/Aruba',
            'America/Asuncion',
            'America/Bahia',
            'America/Bahia_Banderas',
            'America/Barbados',
            'America/Belem',
            'America/Belize',
            'America/Blanc-Sablon',
            'America/Boa_Vista',
            'America/Bogota',
            'America/Boise',
            'America/Buenos_Aires',
            'America/Cambridge_Bay',
            'America/Campo_Grande',
            'America/Cancun',
            'America/Caracas',
            'America/Catamarca',
            'America/Cayenne',
            'America/Cayman',
            'America/Chicago',
            'America/Chihuahua',
            'America/Ciudad_Juarez',
            'America/Coral_Harbour',
            'America/Cordoba',
            'America/Costa_Rica',
            'America/Coyhaique',
            'America/Creston',
            'America/Cuiaba',
            'America/Curacao',
            'America/Danmarkshavn',
            'America/Dawson',
            'America/Dawson_Creek',
            'America/Denver',
            'America/Detroit',
            'America/Dominica',
            'America/Edmonton',
            'America/Eirunepe',
            'America/El_Salvador',
            'America/Fort_Nelson',
            'America/Fortaleza',
            'America/Glace_Bay',
            'America/Godthab',
            'America/Goose_Bay',
            'America/Grand_Turk',
            'America/Grenada',
            'America/Guadeloupe',
            'America/Guatemala',
            'America/Guayaquil',
            'America/Guyana',
            'America/Halifax',
            'America/Havana',
            'America/Hermosillo',
            'America/Indiana/Knox',
            'America/Indiana/Marengo',
            'America/Indiana/Petersburg',
            'America/Indiana/Tell_City',
            'America/Indiana/Vevay',
            'America/Indiana/Vincennes',
            'America/Indiana/Winamac',
            'America/Indianapolis',
            'America/Inuvik',
            'America/Iqaluit',
            'America/Jamaica',
            'America/Jujuy',
            'America/Juneau',
            'America/Kentucky/Monticello',
            'America/Kralendijk',
            'America/La_Paz',
            'America/Lima',
            'America/Los_Angeles',
            'America/Louisville',
            'America/Lower_Princes',
            'America/Maceio',
            'America/Managua',
            'America/Manaus',
            'America/Marigot',
            'America/Martinique',
            'America/Matamoros',
            'America/Mazatlan',
            'America/Mendoza',
            'America/Menominee',
            'America/Merida',
            'America/Metlakatla',
            'America/Mexico_City',
            'America/Miquelon',
            'America/Moncton',
            'America/Monterrey',
            'America/Montevideo',
            'America/Montserrat',
            'America/Nassau',
            'America/New_York',
            'America/Nome',
            'America/Noronha',
            'America/North_Dakota/Beulah',
            'America/North_Dakota/Center',
            'America/North_Dakota/New_Salem',
            'America/Ojinaga',
            'America/Panama',
            'America/Paramaribo',
            'America/Phoenix',
            'America/Port-au-Prince',
            'America/Port_of_Spain',
            'America/Porto_Velho',
            'America/Puerto_Rico',
            'America/Punta_Arenas',
            'America/Rankin_Inlet',
            'America/Recife',
            'America/Regina',
            'America/Resolute',
            'America/Rio_Branco',
            'America/Santarem',
            'America/Santiago',
            'America/Santo_Domingo',
            'America/Sao_Paulo',
            'America/Scoresbysund',
            'America/Sitka',
            'America/St_Barthelemy',
            'America/St_Johns',
            'America/St_Kitts',
            'America/St_Lucia',
            'America/St_Thomas',
            'America/St_Vincent',
            'America/Swift_Current',
            'America/Tegucigalpa',
            'America/Thule',
            'America/Tijuana',
            'America/Toronto',
            'America/Tortola',
            'America/Vancouver',
            'America/Whitehorse',
            'America/Winnipeg',
            'America/Yakutat',
            'Antarctica/Casey',
            'Antarctica/Davis',
            'Antarctica/DumontDUrville',
            'Antarctica/Macquarie',
            'Antarctica/Mawson',
            'Antarctica/McMurdo',
            'Antarctica/Palmer',
            'Antarctica/Rothera',
            'Antarctica/Syowa',
            'Antarctica/Troll',
            'Antarctica/Vostok',
            'Arctic/Longyearbyen',
            'Asia/Aden',
            'Asia/Almaty',
            'Asia/Amman',
            'Asia/Anadyr',
            'Asia/Aqtau',
            'Asia/Aqtobe',
            'Asia/Ashgabat',
            'Asia/Atyrau',
            'Asia/Baghdad',
            'Asia/Bahrain',
            'Asia/Baku',
            'Asia/Bangkok',
            'Asia/Barnaul',
            'Asia/Beirut',
            'Asia/Bishkek',
            'Asia/Brunei',
            'Asia/Calcutta',
            'Asia/Chita',
            'Asia/Colombo',
            'Asia/Damascus',
            'Asia/Dhaka',
            'Asia/Dili',
            'Asia/Dubai',
            'Asia/Dushanbe',
            'Asia/Famagusta',
            'Asia/Gaza',
            'Asia/Hebron',
            'Asia/Hong_Kong',
            'Asia/Hovd',
            'Asia/Irkutsk',
            'Asia/Jakarta',
            'Asia/Jayapura',
            'Asia/Jerusalem',
            'Asia/Kabul',
            'Asia/Kamchatka',
            'Asia/Karachi',
            'Asia/Katmandu',
            'Asia/Khandyga',
            'Asia/Krasnoyarsk',
            'Asia/Kuala_Lumpur',
            'Asia/Kuching',
            'Asia/Kuwait',
            'Asia/Macau',
            'Asia/Magadan',
            'Asia/Makassar',
            'Asia/Manila',
            'Asia/Muscat',
            'Asia/Nicosia',
            'Asia/Novokuznetsk',
            'Asia/Novosibirsk',
            'Asia/Omsk',
            'Asia/Oral',
            'Asia/Phnom_Penh',
            'Asia/Pontianak',
            'Asia/Pyongyang',
            'Asia/Qatar',
            'Asia/Qostanay',
            'Asia/Qyzylorda',
            'Asia/Rangoon',
            'Asia/Riyadh',
            'Asia/Saigon',
            'Asia/Sakhalin',
            'Asia/Samarkand',
            'Asia/Seoul',
            'Asia/Shanghai',
            'Asia/Singapore',
            'Asia/Srednekolymsk',
            'Asia/Taipei',
            'Asia/Tashkent',
            'Asia/Tbilisi',
            'Asia/Tehran',
            'Asia/Thimphu',
            'Asia/Tokyo',
            'Asia/Tomsk',
            'Asia/Ulaanbaatar',
            'Asia/Urumqi',
            'Asia/Ust-Nera',
            'Asia/Vientiane',
            'Asia/Vladivostok',
            'Asia/Yakutsk',
            'Asia/Yekaterinburg',
            'Asia/Yerevan',
            'Atlantic/Azores',
            'Atlantic/Bermuda',
            'Atlantic/Canary',
            'Atlantic/Cape_Verde',
            'Atlantic/Faeroe',
            'Atlantic/Madeira',
            'Atlantic/Reykjavik',
            'Atlantic/South_Georgia',
            'Atlantic/St_Helena',
            'Atlantic/Stanley',
            'Australia/Adelaide',
            'Australia/Brisbane',
            'Australia/Broken_Hill',
            'Australia/Darwin',
            'Australia/Eucla',
            'Australia/Hobart',
            'Australia/Lindeman',
            'Australia/Lord_Howe',
            'Australia/Melbourne',
            'Australia/Perth',
            'Australia/Sydney',
            'Europe/Amsterdam',
            'Europe/Andorra',
            'Europe/Astrakhan',
            'Europe/Athens',
            'Europe/Belgrade',
            'Europe/Berlin',
            'Europe/Bratislava',
            'Europe/Brussels',
            'Europe/Bucharest',
            'Europe/Budapest',
            'Europe/Busingen',
            'Europe/Chisinau',
            'Europe/Copenhagen',
            'Europe/Dublin',
            'Europe/Gibraltar',
            'Europe/Guernsey',
            'Europe/Helsinki',
            'Europe/Isle_of_Man',
            'Europe/Istanbul',
            'Europe/Jersey',
            'Europe/Kaliningrad',
            'Europe/Kiev',
            'Europe/Kirov',
            'Europe/Lisbon',
            'Europe/Ljubljana',
            'Europe/London',
            'Europe/Luxembourg',
            'Europe/Madrid',
            'Europe/Malta',
            'Europe/Mariehamn',
            'Europe/Minsk',
            'Europe/Monaco',
            'Europe/Moscow',
            'Europe/Oslo',
            'Europe/Paris',
            'Europe/Podgorica',
            'Europe/Prague',
            'Europe/Riga',
            'Europe/Rome',
            'Europe/Samara',
            'Europe/San_Marino',
            'Europe/Sarajevo',
            'Europe/Saratov',
            'Europe/Simferopol',
            'Europe/Skopje',
            'Europe/Sofia',
            'Europe/Stockholm',
            'Europe/Tallinn',
            'Europe/Tirane',
            'Europe/Ulyanovsk',
            'Europe/Vaduz',
            'Europe/Vatican',
            'Europe/Vienna',
            'Europe/Vilnius',
            'Europe/Volgograd',
            'Europe/Warsaw',
            'Europe/Zagreb',
            'Europe/Zurich',
            'Indian/Antananarivo',
            'Indian/Chagos',
            'Indian/Christmas',
            'Indian/Cocos',
            'Indian/Comoro',
            'Indian/Kerguelen',
            'Indian/Mahe',
            'Indian/Maldives',
            'Indian/Mauritius',
            'Indian/Mayotte',
            'Indian/Reunion',
            'Pacific/Apia',
            'Pacific/Auckland',
            'Pacific/Bougainville',
            'Pacific/Chatham',
            'Pacific/Easter',
            'Pacific/Efate',
            'Pacific/Enderbury',
            'Pacific/Fakaofo',
            'Pacific/Fiji',
            'Pacific/Funafuti',
            'Pacific/Galapagos',
            'Pacific/Gambier',
            'Pacific/Guadalcanal',
            'Pacific/Guam',
            'Pacific/Honolulu',
            'Pacific/Kiritimati',
            'Pacific/Kosrae',
            'Pacific/Kwajalein',
            'Pacific/Majuro',
            'Pacific/Marquesas',
            'Pacific/Midway',
            'Pacific/Nauru',
            'Pacific/Niue',
            'Pacific/Norfolk',
            'Pacific/Noumea',
            'Pacific/Pago_Pago',
            'Pacific/Palau',
            'Pacific/Pitcairn',
            'Pacific/Ponape',
            'Pacific/Port_Moresby',
            'Pacific/Rarotonga',
            'Pacific/Saipan',
            'Pacific/Tahiti',
            'Pacific/Tarawa',
            'Pacific/Tongatapu',
            'Pacific/Truk',
            'Pacific/Wake',
            'Pacific/Wallis',
            'UTC',
          ])
          .optional()
          .default('UTC'),
      },
      {
        name: 'locale',
        type: 'Query',
        schema: z
          .string()
          .regex(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/)
          .optional()
          .default('en-US'),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'put',
    path: '/api/positions/:positionId',
    alias: 'positionsUpdatePosition',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: positionsUpdatePosition_Body,
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/positions/:positionId',
    alias: 'positionsDeletePosition',
    description: `Deletes a position and all associated transactions, removes the trades&#x27; linked wallet cash rows and recomputes the affected wallet balances (the cash is returned to the wallet). By default the realized profit and loss is KEPT (#891): the position is archived and its realized figure stays in the portfolio history; pass &#x60;keepRealized&#x3D;false&#x60; to drop it. The response reports how many deleted trades had NO resolvable wallet leg (legacy rows) — their cash was not adjusted. A position written by a statement import is refused 409 &#x60;IMPORTED_POSITION_CASH_EFFECT&#x60; with the cash the delete would move, until &#x60;acknowledgeCashEffect&#x3D;true&#x60; is passed.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'keepRealized',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'acknowledgeCashEffect',
        type: 'Query',
        schema: z.boolean().optional(),
      },
    ],
    response: z
      .object({
        deletedWalletTransactions: z
          .coerce.number()
          .describe('Wallet cash rows deleted with the position (their balances were recomputed).'),
        unresolvedWalletLegs: z
          .coerce.number()
          .describe(
            'Deleted trades whose wallet cash leg could not be resolved — their debits remain in the wallet and the balance was NOT adjusted for them. 0 means a full refund.',
          ),
        archived: z
          .boolean()
          .describe(
            "#891 — true when the position was ARCHIVED rather than removed: the user kept its realized profit and loss, which stays in the portfolio's history.",
          ),
        retainedRealized: z
          .coerce.number()
          .describe(
            "#891 — the realized profit and loss kept in the portfolio's history (position currency). 0 when nothing was kept.",
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `&#x60;IMPORTED_POSITION_CASH_EFFECT&#x60; — the position was written by a statement import and &#x60;acknowledgeCashEffect&#x60; is not set. Nothing was deleted. The body carries &#x60;cash&#x60; (one line per wallet: walletId, walletName, currency, signed amount the balance would change by) and &#x60;unresolvedWalletLegs&#x60; (trades whose cash would NOT move).`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/positions/:positionId/core',
    alias: 'positionsUpdatePositionCore',
    description: `Used during onboarding review to allow users to edit imported positions. Updates core fields like symbol, quantity, average cost basis, and asset type; &#x60;ratio&#x60; carries the lots into a merger successor&#x27;s units (quantity x ratio, price / ratio).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: positionsUpdatePositionCore_Body,
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request body`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/positions/:positionId/name',
    alias: 'positionsRenameFundUnit',
    description: `A fund unit has a name of its own (the key it is stored under is cut from it and never changes with it). Any other class is named by the instrument directory and is refused 400 &#x60;POSITION_NOT_FUND_UNIT&#x60;.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          name: z
            .string()
            .min(1)
            .max(120)
            .describe("The fund as its holder calls it, e.g. 'PPK Mój pracodawca 2045'"),
        }),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: RenamedFundUnitDto,
    errors: [
      {
        status: 400,
        description: `&#x60;POSITION_NOT_FUND_UNIT&#x60; — the holding is not a fund unit`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/positions/:positionId/notes',
    alias: 'positionsUpdatePositionNotes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({ notes: z.string().describe('Free-text notes for the position') })
          .partial(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/positions/:positionId/re-resolve',
    alias: 'positionsReResolvePosition',
    description: `Re-runs the IMPORT&#x27;s own symbol resolver over the identity the statement supplied and the row persists (&#x60;statementName&#x60; / &#x60;statementIsin&#x60; / &#x60;statementSymbol&#x60;, plus the row&#x27;s instrument currency), and applies the answer through the ordinary symbol-edit path — so the cross-company identity guard, the asset-class guard and the transaction &#x60;instrument_symbol&#x60; re-sync all still apply. This is what lets an improvement to resolution reach positions ALREADY imported, with no file to re-upload (#1945). No body.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `&#x60;UNRESOLVED_POSITION&#x60; — the resolver still binds nothing (the body carries the identity it tried); or &#x60;SYMBOL_IDENTITY_MISMATCH&#x60; / &#x60;ASSET_CLASS_MISMATCH&#x60; from the remap guard.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/positions/:positionId/target-price',
    alias: 'positionsUpdatePositionTargetPrice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            targetPrice: z
              .coerce.number()
              .describe('Target price for the position; null clears it')
              .nullable(),
          })
          .partial(),
      },
      {
        name: 'positionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Position not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/projections',
    alias: 'projectionsCreateProjection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: projectionsCreateProjection_Body,
      },
    ],
    response: ProjectionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/projections/:id',
    alias: 'projectionsUpdateProjection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: projectionsUpdateProjection_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ProjectionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden - not your projection`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Projection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/projections/:id',
    alias: 'projectionsDeleteProjection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden - not your projection`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Projection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/projections/:symbol',
    alias: 'projectionsGetProjectionsBySymbol',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.array(ProjectionResponseDto),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/public/portfolio/:shareId',
    alias: 'publicPortfoliosGetPublicPortfolio',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'timeframe',
        type: 'Query',
        schema: z
          .enum(['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y', '20Y', 'YTD', 'All'])
          .optional(),
      },
    ],
    response: PublicPortfolioResponseDto,
    errors: [
      {
        status: 404,
        description: `Share not found or expired`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/public/portfolio/:shareId/metadata',
    alias: 'publicPortfoliosGetPortfolioMetadata',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Share not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/public/portfolio/:shareId/subscribe',
    alias: 'publicPortfoliosSubscribe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: PublicShareViewerDto,
    errors: [
      {
        status: 403,
        description: `The owner cannot subscribe`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Share not found or expired`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/public/portfolio/:shareId/subscribe',
    alias: 'publicPortfoliosUnsubscribe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: PublicShareViewerDto,
    errors: [
      {
        status: 404,
        description: `Share not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/public/portfolio/:shareId/viewer',
    alias: 'publicPortfoliosGetViewer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'shareId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: PublicShareViewerDto,
    errors: [
      {
        status: 404,
        description: `Share not found or expired`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/public/scores',
    alias: 'publicScoresIndex',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        items: z.array(
          z
            .object({
              symbol: z.string(),
              name: z.string().nullable(),
              asOfDate: z.string().describe('yyyy-MM-dd — drives the sitemap lastmod.'),
            })
            .passthrough(),
        ),
        total: z.coerce.number().describe('Companies with a published score across ALL pages.'),
        page: z.coerce.number(),
        pageSize: z.coerce.number(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/public/scores/:symbol',
    alias: 'publicScoresScorePage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        name: z.string().nullable(),
        points: z.coerce.number().describe('Stoxly Points 0-100.').nullable(),
        stars: z.coerce.number().describe('0-5 stars, 0.1 resolution.').nullable(),
        overview: ScreenerTabScoreDto,
        dividends: ScreenerTabScoreDto,
        growth: ScreenerTabScoreDto,
        solidity: ScreenerTabScoreDto,
        valuation: ScreenerTabScoreDto.describe('V2 valuation tab.').optional(),
        momentum: ScreenerTabScoreDto.describe('ruleset_v3 momentum tab (0-10).').optional(),
        percentile: z
          .coerce.number()
          .describe('Overview percentile against the whole universe.')
          .nullable(),
        topPct: z
          .coerce.number()
          .describe("Share of the universe scoring strictly higher ('top X%').")
          .nullable(),
        fvDiscountPct: z.coerce.number().describe('Percent below fair value.').nullable(),
        marketCapUsdMillions: z.coerce.number().nullable(),
        industry: z.string().nullable(),
        country: z.string().nullable(),
        pe: z.coerce.number().nullable(),
        dividendYieldPct: z.coerce.number().nullable(),
        paysDividend: z.boolean(),
        asOfDate: z
          .string()
          .describe('The cross-section date these numbers belong to (yyyy-MM-dd).'),
        rulesetVersion: z.string(),
        disclaimer: z.string().describe('Our scoring, not investment advice.'),
        survivorshipCaveat: z
          .string()
          .describe(
            'The survivorship caveat of the latest completed backtest, VERBATIM from GET /scoring/backtest/summary — contract §F13 requires the SEO page to render it. Null when no completed run has published one.',
          )
          .nullable(),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `The company is outside the scored universe.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/research-reports/:id/export',
    alias: 'researchReportsExport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['pdf', 'html']).optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/research/:symbol/overview/price-stats',
    alias: 'researchOverviewGetPriceStats',
    description: `Beta and the 52-week band from the vendor metric feed; annualised realised volatility and the trailing 1-year change computed from daily closes. Every figure is independently nullable.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        beta: z.coerce.number().describe('Beta vs the local index').nullish(),
        avgVolume10Day: z.coerce.number().describe('10-day average trading volume (shares)').nullish(),
        volatility: z
          .coerce.number()
          .describe('Annualised realised volatility over the trailing year, %')
          .nullish(),
        percentFromHigh: z
          .coerce.number()
          .describe('Distance from the 52-week high, % (negative; 0 at a fresh high)')
          .nullish(),
        changePercent1Y: z.coerce.number().describe('Trailing 1-year change, %').nullish(),
        week52High: z.coerce.number().nullish(),
        week52Low: z.coerce.number().nullish(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/research/:symbol/overview/signals',
    alias: 'researchOverviewGetDailySignals',
    description: `Net OPEN-MARKET insider flow over 30 days (grants and option exercises excluded — compensation is not a market signal) plus a digest of analyst rating changes and the loudest price-target move. Each leg is independently nullable for markets without that coverage.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        insider: z
          .object({
            windowDays: z.coerce.number().describe('Length of the aggregation window in days'),
            netAmount: z
              .coerce.number()
              .describe(
                'Signed money in the trading currency; negative = net selling. Null when unpriced',
              )
              .nullish(),
            buyCount: z.coerce.number().describe('Open-market purchases in the window'),
            sellCount: z.coerce.number().describe('Open-market sales in the window'),
            transactionCount: z.coerce.number().describe('Open-market transactions in the window'),
            currency: z.string().describe('Trading currency').nullish(),
          })
          .passthrough()
          .describe('Null when this market has no insider-transaction coverage at all')
          .nullish(),
        ratings: z
          .object({
            windowDays: z.coerce.number().describe('Length of the digest window in days'),
            upgrades: z.coerce.number(),
            downgrades: z.coerce.number(),
            totalActions: z
              .coerce.number()
              .describe('All rating actions in the window, including maintains'),
            loudestTarget: z
              .object({
                firm: z.string(),
                priceTarget: z.coerce.number().nullish(),
                previousPriceTarget: z.coerce.number().nullish(),
                pricePercentChange: z.coerce.number().describe('Target change, %').nullish(),
              })
              .passthrough()
              .nullish(),
          })
          .passthrough()
          .describe('Null when this market has no analyst-rating coverage at all')
          .nullish(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/research/calendar/export',
    alias: 'researchCalendarExportCalendar',
    description: `The SAME merged stream the grid renders, under the SAME filters, serialized as all-day VEVENTs. The hour rides the summary rather than DTSTART: a vendor time is UTC, and a timed entry would be re-rendered in the consumer own zone.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'countries',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'exchanges',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'kinds',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'impacts',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeMacro',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeHoldings',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/research/calendar/portfolio-earnings',
    alias: 'researchCalendarGetPortfolioEarnings',
    description: `The symbols the reader actually holds, joined against the earnings feed in ONE batch call. Empty for a reader who holds nothing — never an error.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        rows: z
          .array(
            z
              .object({
                symbol: z.string(),
                date: z.string().describe('yyyy-MM-dd.'),
                session: z.string().describe('bmo | amc | dmh.').nullable(),
                epsEstimate: z.coerce.number().describe('Consensus EPS for the quarter.').nullable(),
                fiscalYear: z.coerce.number().nullable(),
                fiscalQuarter: z.coerce.number().nullable(),
              })
              .passthrough(),
          )
          .describe('Earnings dates for the symbols the reader holds, soonest first.'),
        symbolCount: z.coerce.number().describe('Distinct held symbols the window was resolved for.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/research/calendar/stream',
    alias: 'researchCalendarGetStream',
    description: `Macro releases, central-bank events, earnings, earnings calls, dividends, IPOs and market holidays merged into ONE time-ordered list, rated for impact and paged server-side. Filtering happens before slicing, so page numbers stay meaningful with a filter on; the summary counts the unfiltered window, so the filter chips stay usable.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'to',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'symbols',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'countries',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'exchanges',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'kinds',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'impacts',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeMacro',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'includeHoldings',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        rows: z
          .array(
            z
              .object({
                id: z
                  .string()
                  .describe('Stable across re-fetches — the client keys and dedupes on it.'),
                kind: z
                  .enum([
                    'economic',
                    'central-bank',
                    'earnings',
                    'earnings-call',
                    'dividend',
                    'ipo',
                    'holiday',
                  ])
                  .describe('Row discriminator: which extras below are populated.'),
                date: z.string().describe('yyyy-MM-dd.'),
                time: z
                  .string()
                  .describe('HH:mm as the vendor publishes it (UTC), or null for an all-day row.')
                  .nullable(),
                title: z.string(),
                impact: z.enum(['high', 'medium', 'low']),
                symbol: z.string().nullable(),
                country: z.string().describe('ISO country code for macro rows.').nullable(),
                previous: z
                  .coerce.number()
                  .describe("Agenda column 'Previous'. A NUMBER — the client owns formatting.")
                  .nullable(),
                consensus: z.coerce.number().describe("Agenda column 'Consensus'.").nullable(),
                actual: z.coerce.number().describe("Agenda column 'Actual'.").nullable(),
                unit: z
                  .string()
                  .describe(
                    "Unit for the three value columns when they are not money ('%', 'K', 'M').",
                  )
                  .nullable(),
                currency: z
                  .string()
                  .describe('Currency for the three value columns when they ARE money.')
                  .nullable(),
                isPortfolioHolding: z
                  .boolean()
                  .describe("True when the symbol sits in one of the reader's portfolios."),
                session: z.string().describe('Earnings: bmo | amc | dmh.').nullable(),
                fiscalYear: z.coerce.number().nullable(),
                fiscalQuarter: z.coerce.number().nullable(),
                revenueEstimate: z.coerce.number().describe('Earnings: revenue consensus.').nullable(),
                revenueActual: z.coerce.number().describe('Earnings: reported revenue.').nullable(),
                audioUrl: z.string().describe('Earnings call: vendor audio URL.').nullable(),
                payDate: z.string().nullable(),
                recordDate: z.string().nullable(),
                declarationDate: z.string().nullable(),
                exchange: z.string().describe('Holiday / IPO: the exchange.').nullable(),
                priceRange: z.string().describe('IPO: price or price range.').nullable(),
                status: z.string().describe('IPO: vendor status.').nullable(),
                offeringValue: z
                  .coerce.number()
                  .describe('IPO: total value of shares offered.')
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('The requested page, in time order.'),
        page: z.coerce.number().describe('1-based.'),
        pageSize: z.coerce.number(),
        total: z.coerce.number().describe('Rows matching the filter across ALL pages.'),
        summary: z
          .object({
            total: z
              .coerce.number()
              .describe(
                'Rows in the window under every active filter except the kind chips — the base the chips and the two counts below are all taken on.',
              ),
            highImpact: z.coerce.number().describe('High-impact rows on that same base.'),
            byKind: z
              .object({
                economic: z.coerce.number(),
                'central-bank': z.coerce.number(),
                earnings: z.coerce.number(),
                'earnings-call': z.coerce.number(),
                dividend: z.coerce.number(),
                ipo: z.coerce.number(),
                holiday: z.coerce.number(),
              })
              .passthrough()
              .describe(
                'What each kind chip would show if it were the only kind selected. Equal to the number of rows of that kind on the list whenever the kind filter admits it.',
              ),
          })
          .passthrough()
          .describe(
            'Counted over the UNFILTERED window, so a chip whose filter is off still shows how much it would bring back.',
          ),
        window: z.object({ from: z.string(), to: z.string() }).passthrough(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/research/calendar/upcoming/:symbol',
    alias: 'researchCalendarGetUpcoming',
    description: `The company own upcoming rows, soonest first, over the year ahead — the header strip and the &#x27;Events for {symbol}&#x27; card. Today counts as upcoming.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'from',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        rows: z
          .array(
            z
              .object({
                id: z
                  .string()
                  .describe('Stable across re-fetches — the client keys and dedupes on it.'),
                kind: z
                  .enum([
                    'economic',
                    'central-bank',
                    'earnings',
                    'earnings-call',
                    'dividend',
                    'ipo',
                    'holiday',
                  ])
                  .describe('Row discriminator: which extras below are populated.'),
                date: z.string().describe('yyyy-MM-dd.'),
                time: z
                  .string()
                  .describe('HH:mm as the vendor publishes it (UTC), or null for an all-day row.')
                  .nullable(),
                title: z.string(),
                impact: z.enum(['high', 'medium', 'low']),
                symbol: z.string().nullable(),
                country: z.string().describe('ISO country code for macro rows.').nullable(),
                previous: z
                  .coerce.number()
                  .describe("Agenda column 'Previous'. A NUMBER — the client owns formatting.")
                  .nullable(),
                consensus: z.coerce.number().describe("Agenda column 'Consensus'.").nullable(),
                actual: z.coerce.number().describe("Agenda column 'Actual'.").nullable(),
                unit: z
                  .string()
                  .describe(
                    "Unit for the three value columns when they are not money ('%', 'K', 'M').",
                  )
                  .nullable(),
                currency: z
                  .string()
                  .describe('Currency for the three value columns when they ARE money.')
                  .nullable(),
                isPortfolioHolding: z
                  .boolean()
                  .describe("True when the symbol sits in one of the reader's portfolios."),
                session: z.string().describe('Earnings: bmo | amc | dmh.').nullable(),
                fiscalYear: z.coerce.number().nullable(),
                fiscalQuarter: z.coerce.number().nullable(),
                revenueEstimate: z.coerce.number().describe('Earnings: revenue consensus.').nullable(),
                revenueActual: z.coerce.number().describe('Earnings: reported revenue.').nullable(),
                audioUrl: z.string().describe('Earnings call: vendor audio URL.').nullable(),
                payDate: z.string().nullable(),
                recordDate: z.string().nullable(),
                declarationDate: z.string().nullable(),
                exchange: z.string().describe('Holiday / IPO: the exchange.').nullable(),
                priceRange: z.string().describe('IPO: price or price range.').nullable(),
                status: z.string().describe('IPO: vendor status.').nullable(),
                offeringValue: z
                  .coerce.number()
                  .describe('IPO: total value of shares offered.')
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('Soonest first, today included.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/fair-value',
    alias: 'scoringGetFairValue',
    description: `Three bases (earnings/sales/dividend-Gordon) with deviations, the composite deviation (median), P/E-P/S premiums vs the 5y averages, and the snapshot-backed FV history the ±25/50/75% bands are drawn from.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        available: z.boolean(),
        asOfDate: z.string().nullable(),
        reportingCurrency: z.string().nullable(),
        priceCurrency: z.string().nullable(),
        priceComparable: z
          .boolean()
          .describe(
            'True when the price and the fair values share a currency basis — deviations and the price overlay are meaningful. False (GBX/ADR mismatch) = show FVs without the price.',
          ),
        current: z
          .object({
            earnings: FairValueBasisDto.nullable(),
            sales: FairValueBasisDto.nullable(),
            dividend: FairValueBasisDto.nullable(),
            dividendBasisUnavailable: z
              .enum(['no-dividend', 'yield-below-floor'])
              .describe(
                "Set exactly when `dividend` is null, so the UI can say WHY instead of just hiding the basis. `yield-below-floor` = a real payer whose current yield is too small for a perpetuity model to say anything (Gordon on a 0.34% yield answers 'overvalued by 1033%').",
              )
              .nullable(),
            stage: z
              .enum(['mature', 'profitable', 'growth'])
              .describe(
                'V2 valuation axis: the deterministic development stage that picks which basis appraises the company (mature → DCF, profitable → earnings, growth → sales). Absent on bundles persisted before V2.',
              )
              .nullish(),
            dcf: FairValueBasisDto.describe(
              "V2: the mature stage's conservative two-stage DCF per share (reporting currency). NOT part of compositeDeviationPct — the composite stays on the three classic bases.",
            ).nullish(),
            compositeDeviationPct: z
              .coerce.number()
              .describe(
                'MEDIAN of the available CLASSIC basis deviations (the screener discount is −this).',
              )
              .nullable(),
            currentPe: z.coerce.number().nullable(),
            currentPs: z.coerce.number().nullable(),
            evToEbitda: z.coerce.number().nullable(),
            pePremiumPct: z.coerce.number().describe('P/E premium vs its 5y average, %.').nullable(),
            psPremiumPct: z.coerce.number().describe('P/S premium vs its 5y average, %.').nullable(),
          })
          .passthrough()
          .nullable(),
        series: z.array(
          z
            .object({
              asOfDate: z.string(),
              earnings: z.coerce.number().nullable(),
              sales: z.coerce.number().nullable(),
              dividend: z.coerce.number().nullable(),
            })
            .passthrough(),
        ),
        disclaimer: z.string().describe('Our scoring, not investment advice.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/peer-group',
    alias: 'scoringGetPeerGroup',
    description: `The QC comparison table + the factor medians of the SAME group (the radar overlay). Mode list reads the caller’s watchlist (auth required).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'mode',
        type: 'Query',
        schema: z.enum(['industry', 'country', 'cap', 'list']).optional(),
      },
      {
        name: 'watchlistId',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        available: z.boolean(),
        mode: z.enum(['industry', 'country', 'cap', 'list']),
        groupKey: z.string().describe('Group key (label source).').nullable(),
        memberCount: z.coerce.number().describe('Full group size (not the table).').nullable(),
        peers: z.array(
          z
            .object({
              symbol: z.string(),
              name: z.string().nullable(),
              isSelf: z.boolean(),
              marketCapUsdMillions: z.coerce.number().describe('USD millions.').nullable(),
              revenueCagr5yPct: z.coerce.number().describe('5y revenue CAGR, %.').nullable(),
              operatingMarginPct: z.coerce.number().describe('Operating margin, %.').nullable(),
              pe: z.coerce.number().nullable(),
              dividendYieldPct: z.coerce.number().nullable(),
              momentum6mPct: z.coerce.number().describe('6-month price return, %.').nullable(),
            })
            .passthrough(),
        ),
        selfFactors: FactorProfileDto.nullable(),
        factorMedians: FactorProfileDto.nullable(),
        groupSource: z
          .enum(['profile-peers', 'industry', 'country', 'cap', 'list'])
          .describe(
            'Where the members came from. `profile-peers` = the competitors the research named; the rest = the dimension or watchlist selection. Drives the honest group label.',
          ),
        disclaimer: z.string().describe('Our scoring, not investment advice.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/quality',
    alias: 'scoringGetQualityScoring',
    description: `The full Quality Check page payload: 49 evaluated criteria across 4 tabs, block/tab sums, Stoxly Points, rule-based narrative facts, the 12-year chart pairs and the as-of Piotroski/Altman card data. Our scoring, not investment advice.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'asOf',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Normalized symbol the score belongs to.'),
        modelVersion: z.string().describe('Ruleset version on the wire (ruleset_v1).'),
        asOfDate: z.string().describe('The as-of date the engine saw the world at.'),
        computedAt: z.string().describe('Compute timestamp (ISO).'),
        reportingCurrency: z.string().nullable(),
        priceCurrency: z.string().nullable(),
        anchorFiscalYear: z.coerce.number().describe('Latest visible fiscal year.').nullable(),
        anchorPeriodEnd: z.string().nullable(),
        anchorPublishedAt: z.string().describe('Its publication date.').nullable(),
        tabs: z
          .array(
            z
              .object({
                tab: z.enum([
                  'overview',
                  'dividends',
                  'growth',
                  'solidity',
                  'valuation',
                  'momentum',
                ]),
                score: z.coerce.number(),
                max: z.coerce.number().describe('Contract scale (15/10/12/12/9/10).'),
                normalized: z
                  .coerce.number()
                  .describe(
                    'Pass share of the EVALUABLE criteria in 0-100 (no_data leaves the denominator).',
                  )
                  .nullable(),
                band: z.enum(['high', 'mid', 'low']),
                isEmpty: z.boolean().describe('True when no criterion of the tab was evaluable.'),
                blocks: z.array(
                  z
                    .object({
                      block: z.string(),
                      score: z.coerce.number(),
                      max: z.coerce.number(),
                      band: z.enum(['high', 'mid', 'low']),
                    })
                    .passthrough(),
                ),
                criteria: z
                  .array(
                    z
                      .object({
                        id: z.string().describe('Stable criterion id (FE i18n key segment).'),
                        block: z.string().describe('Block key inside the tab.'),
                        status: z.enum(['pass', 'fail', 'no_data', 'not_verified']),
                        value: z.coerce.number().describe('Display value in `unit`.').nullable(),
                        unit: z.enum(['percent', 'pp', 'ratio', 'x', 'count', 'trend', 'none']),
                        threshold: z.coerce.number().describe('Pass threshold in `unit`.').nullable(),
                        lowerIsBetter: z
                          .boolean()
                          .describe('True when the healthy direction is DOWN.'),
                        scored: z
                          .boolean()
                          .describe('False = informational row outside every sum.'),
                        score: z
                          .coerce.number()
                          .describe(
                            'Continuous 0-100 reading of a valuation or momentum feature; null when it abstained.',
                          )
                          .nullish(),
                        weight: z
                          .coerce.number()
                          .describe(
                            'Weight AFTER redistribution over the features that actually scored.',
                          )
                          .optional(),
                        groupKey: z
                          .string()
                          .describe('Reference group used: the GICS-like sector, or `universe`.')
                          .nullish(),
                        groupCount: z.coerce.number().nullish(),
                        groupMedian: z
                          .coerce.number()
                          .describe("Group centre in the FEATURE'S own unit — never a z-score.")
                          .nullish(),
                        contribution: z
                          .coerce.number()
                          .describe(
                            'weight x (score - 50) — signed points this feature moved the axis.',
                          )
                          .nullish(),
                        referenceAsOf: z
                          .string()
                          .describe(
                            'As-of date of the cross-section the score was measured against.',
                          )
                          .nullish(),
                        abstainReason: z
                          .enum([
                            'no_data',
                            'gated_share_basis',
                            'ev_non_positive',
                            'group_too_small',
                            'negative_flow',
                            'no_reference',
                          ])
                          .describe('Why the feature produced no score.')
                          .nullish(),
                        warranted: z
                          .object({
                            currentPe: z.coerce.number().describe('Current trailing P/E.'),
                            warrantedPe: z
                              .coerce.number()
                              .describe(
                                'The P/E the growth and ROIC below warrant under the kernel discount policy.',
                              ),
                            growthPct: z
                              .coerce.number()
                              .describe(
                                'The pace of the business the model priced (median of the 5y EPS / revenue / FCF CAGRs), percent, before the cap.',
                              ),
                            roicPct: z
                              .coerce.number()
                              .describe('Return on invested capital, percent, before the clamps.'),
                          })
                          .passthrough()
                          .describe(
                            'The growth-adjusted valuation feature only: the multiples and rates it compared. Absent on every other criterion.',
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe('Page-order criteria.'),
                limiting: z
                  .array(
                    z
                      .object({
                        criterionId: z.string().describe('Criterion the row mirrors.'),
                        value: z.coerce.number().nullable(),
                        threshold: z.coerce.number().nullable(),
                        scaleMax: z.coerce.number().describe('Visualization scale for the fill bar.'),
                        lowerIsBetter: z.boolean(),
                        unit: z.enum(['percent', 'pp', 'ratio', 'x', 'count', 'trend', 'none']),
                        status: z.enum(['pass', 'fail', 'no_data', 'not_verified']),
                        groupMedian: z
                          .coerce.number()
                          .describe(
                            "Valuation tab only: the reference group's median in the feature's own unit — the tick this bar is read against, in place of an absolute threshold.",
                          )
                          .nullish(),
                      })
                      .passthrough(),
                  )
                  .describe("'Co ogranicza wynik'."),
                verdict: z
                  .object({
                    band: z.enum(['high', 'mid', 'low']),
                    failedCriteria: z.array(z.string()).describe('Up to two failed criterion ids.'),
                    noDataCount: z.coerce.number().describe('Scored criteria whose inputs were missing.'),
                  })
                  .passthrough(),
                insight: z
                  .object({
                    criterionId: z.string().describe('The binding limiter.').nullable(),
                    allClear: z.boolean(),
                    isDataGap: z.boolean().describe('True when the limiter is a data gap.'),
                    value: z.coerce.number().nullable(),
                    threshold: z.coerce.number().nullable(),
                    unit: z
                      .enum(['percent', 'pp', 'ratio', 'x', 'count', 'trend', 'none'])
                      .nullable(),
                  })
                  .passthrough(),
                axis: z
                  .object({
                    featuresScored: z.coerce.number().describe('Features that produced a score.'),
                    featuresTotal: z.coerce.number().describe('Features the axis is made of.'),
                    groupKey: z
                      .string()
                      .describe('Reference group: the GICS-like sector, or `universe`.')
                      .nullable(),
                    groupCount: z.coerce.number().describe('Members of that group.').nullable(),
                    referenceAsOf: z
                      .string()
                      .describe('As-of date of the cross-section used.')
                      .nullable(),
                  })
                  .passthrough()
                  .describe(
                    'Continuous tabs only (valuation, momentum): what the score is relative to.',
                  )
                  .nullish(),
              })
              .passthrough(),
          )
          .describe('overview/dividends/growth/solidity/valuation/momentum.'),
        stoxlyPoints: z
          .coerce.number()
          .describe('0-100 weighted composite (weights from the active config; launch = equal).')
          .nullable(),
        weightsVersion: z
          .string()
          .describe('The weight-config version the composite was computed with (F13).'),
        summary: z
          .object({
            band: z.enum(['high', 'mid', 'low']).nullable(),
            strongestTab: z
              .enum(['overview', 'dividends', 'growth', 'solidity', 'valuation', 'momentum'])
              .nullable(),
            weakestTab: z
              .enum(['overview', 'dividends', 'growth', 'solidity', 'valuation', 'momentum'])
              .nullable(),
            stars: z.coerce.number().describe('0-5 stars, tenths.').nullable(),
          })
          .passthrough(),
        series: z
          .object({
            dividendsVsFcf: QualityPairSeriesDto.describe(
              'Dividends paid vs FCF; line = payout of FCF (%).',
            ),
            revenueVsGrossProfit: QualityPairSeriesDto.describe(
              'Revenue vs gross profit; line = net margin (%).',
            ),
            debtVsEbitda: QualityPairSeriesDto.describe(
              'Total debt vs EBITDA; line = interest coverage (x).',
            ),
            ebit: z
              .object({ years: z.array(z.string()), values: z.array(z.coerce.number().nullable()) })
              .passthrough(),
          })
          .passthrough(),
        piotroski: z
          .object({
            score: z.coerce.number().describe('0-9 as-of score.').nullable(),
            criteria: z.array(
              z
                .object({
                  key: z.string().describe('Fundamentals-map key (paper order).'),
                  passed: z.boolean().nullable(),
                  value: z.coerce.number().nullable(),
                  unit: z.enum(['percent', 'pp', 'x', 'ratio', 'currency']),
                })
                .passthrough(),
            ),
          })
          .passthrough(),
        metrics: z
          .object({
            revenueCagr5yPct: z.coerce.number().nullable(),
            revenueCagr10yPct: z.coerce.number().nullable(),
            revenueUpYears10y: z.coerce.number().nullable(),
            revenueTrendR2: z.coerce.number().nullable(),
            epsCagr5yPct: z.coerce.number().nullable(),
            epsToRevenueCagrRatio: z.coerce.number().nullable(),
            grossMarginChange5yPp: z.coerce.number().nullable(),
            netMarginChange5yPp: z.coerce.number().nullable(),
            fcfCagr5yPct: z.coerce.number().nullable(),
            fcfPositiveYears10y: z.coerce.number().nullable(),
            fcfConversionPct: z.coerce.number().nullable(),
            freeCashFlow: z.coerce.number().nullable(),
            sharesChange1yPct: z.coerce.number().nullable(),
            sharesChange5yPct: z.coerce.number().nullable(),
            roicPct: z.coerce.number().nullable(),
            roePct: z.coerce.number().nullable(),
            roaPct: z.coerce.number().nullable(),
            roaChangeYoYPp: z.coerce.number().nullable(),
            grossMarginPct: z.coerce.number().nullable(),
            operatingMarginPct: z.coerce.number().nullable(),
            netMarginPct: z.coerce.number().nullable(),
            grossMarginChangeYoYPp: z.coerce.number().nullable(),
            netDebtToEbitda: z.coerce.number().nullable(),
            netDebt: z.coerce.number().nullable(),
            ebitda: z.coerce.number().nullable(),
            interestCoverage: z.coerce.number().nullable(),
            hasEarningsWithoutInterestBurden: z.boolean(),
            debtChange5yPct: z.coerce.number().nullable(),
            totalDebtLatest: z.coerce.number().nullable(),
            equityRatioPct: z.coerce.number().nullable(),
            currentRatio: z.coerce.number().nullable(),
            quickRatio: z.coerce.number().nullable(),
            cashToShortTermDebt: z.coerce.number().nullable(),
            shortTermDebt: z.coerce.number().nullable(),
            cashLatest: z.coerce.number().nullable(),
            leverageChangeYoY: z.coerce.number().nullable(),
            cfoToNetIncome: z.coerce.number().nullable(),
            cfoExceedsNetIncome: z.boolean().nullable(),
            altmanZ: z.coerce.number().describe('Classic Altman Z (1968).').nullable(),
            paysDividend: z.boolean(),
            legalForm: z
              .enum(['corporation', 'partnership', 'trust', 'fund', 'unknown'])
              .describe(
                "The issuer's LEGAL FORM from its registered name. A tax-transparent partnership distributes cash flow rather than earnings, so the payout-of-earnings criterion abstains for it (see `payoutOfEarningsStatus`).",
              ),
            hasAnnualData: z.boolean(),
            dividendYieldPct: z.coerce.number().nullable(),
            dpsCagr5yPct: z.coerce.number().nullable(),
            dpsCagr10yPct: z.coerce.number().nullable(),
            payoutOfEarningsPct: z.coerce.number().nullable(),
            payoutOfFcfPct: z.coerce.number().nullable(),
            fcfCoverageOfDividends: z.coerce.number().nullable(),
            dpsCutCount10y: z.coerce.number().nullable(),
            dpsPaidYears10y: z.coerce.number().nullable(),
            dpsIncreaseYears10y: z.coerce.number().nullable(),
            dpsNoCutStreakYears: z.coerce.number().nullable(),
            lastClose: z.coerce.number().nullable(),
            volatility250dPct: z.coerce.number().nullable(),
            momentum12mPct: z.coerce.number().nullable(),
            momentum6mPct: z.coerce.number().nullable(),
            momentum12m1Pct: z
              .coerce.number()
              .describe(
                'Price one month ago over price twelve months ago, percent (momentum axis).',
              )
              .nullish(),
            momentum6m1Pct: z
              .coerce.number()
              .describe('Price one month ago over price six months ago, percent (momentum axis).')
              .nullish(),
            sma200Slope: z.coerce.number().nullable(),
            sma200TrendUp: z.boolean().nullable(),
            marketCapAsOf: z
              .coerce.number()
              .describe(
                'As-of market cap (lastClose × diluted shares), trading currency; currency-gated.',
              )
              .nullable(),
            earningsYieldPct: z
              .coerce.number()
              .describe(
                'Latest EPS / lastClose × 100; positive-EPS only, currency- and basis-gated.',
              )
              .nullish(),
            fcfYieldPct: z
              .coerce.number()
              .describe(
                'Latest FCF / as-of market cap × 100; positive-FCF only, gated via market cap.',
              )
              .nullish(),
            earningsYieldSignedPct: z
              .coerce.number()
              .describe(
                'Latest EPS / lastClose × 100 WITHOUT the positive-EPS gate (V3 valuation axis).',
              )
              .nullish(),
            fcfYieldSignedPct: z
              .coerce.number()
              .describe(
                'Latest FCF / as-of market cap × 100 WITHOUT the positive-flow gate (V3 valuation axis).',
              )
              .nullish(),
            perShareBasisOk: z
              .boolean()
              .describe(
                'False when the statements per-share unit is proven not to be the traded share (dual-class/DR basis, e.g. BRK.B A-equivalents vs the B tape) — every price-vs-per-share metric then abstains (null).',
              )
              .optional(),
          })
          .passthrough(),
        moonshot: z
          .object({
            isMoonshot: z.boolean().describe('Both gates plus at least the minimum marker count.'),
            classifiable: z.boolean().describe('False when no annual data exists at all.'),
            hypergrowth: z
              .boolean()
              .describe('Gate 1: 5y revenue CAGR at or above the hypergrowth bar.'),
            reinvesting: z
              .boolean()
              .describe('Gate 2: pays no dividend (growth-stage capital posture).'),
            markers: z
              .object({
                elevatedVolatility: z
                  .boolean()
                  .describe('250-session volatility at or above the moonshot bar.'),
                shortHistory: z
                  .boolean()
                  .describe('The 10y revenue window is unobservable (young record).'),
                marginInflection: z
                  .boolean()
                  .describe('5y net-margin swing at or above the inflection bar.'),
                equityFunded: z
                  .boolean()
                  .describe('Share count grown past the dilution bar (5y, or 1y fallback).'),
              })
              .passthrough(),
            markersMet: z.coerce.number().describe('How many of the four markers fired (0-4).'),
            version: z.string().describe('Classifier version (moonshot_v1).'),
          })
          .passthrough()
          .describe(
            'Moonshot profile classification; absent on payloads cached before the field landed (the FE derives the identical result from `metrics` via the shared classifier).',
          )
          .optional(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/seasonal-cycle',
    alias: 'scoringGetSeasonalCycle',
    description: `Pre-aggregated day-of-year window scan over up to ~21 years of daily closes: the optimal window with hit rate/median/average, the best and worst windows, and the compounded cycle-profit series.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        available: z
          .boolean()
          .describe('False when the tape covers fewer than 8 observable years.'),
        optimal: z
          .object({
            startDoy: z.coerce.number().describe('Window start, day-of-year (7-day grid).'),
            endDoy: z
              .coerce.number()
              .describe('Window end, day-of-year; < startDoy when it wraps the year end.'),
            avgReturnPct: z.coerce.number(),
            medianReturnPct: z.coerce.number(),
            hitRatePct: z
              .coerce.number()
              .describe('Share of observed years with a positive return, 0–100.'),
            years: z.coerce.number().describe('Years the window was observable in.'),
          })
          .passthrough()
          .nullable(),
        best: z.array(
          z
            .object({
              startDoy: z.coerce.number().describe('Window start, day-of-year (7-day grid).'),
              endDoy: z
                .coerce.number()
                .describe('Window end, day-of-year; < startDoy when it wraps the year end.'),
              avgReturnPct: z.coerce.number(),
              medianReturnPct: z.coerce.number(),
              hitRatePct: z
                .coerce.number()
                .describe('Share of observed years with a positive return, 0–100.'),
              years: z.coerce.number().describe('Years the window was observable in.'),
            })
            .passthrough(),
        ),
        worst: z.array(
          z
            .object({
              startDoy: z.coerce.number().describe('Window start, day-of-year (7-day grid).'),
              endDoy: z
                .coerce.number()
                .describe('Window end, day-of-year; < startDoy when it wraps the year end.'),
              avgReturnPct: z.coerce.number(),
              medianReturnPct: z.coerce.number(),
              hitRatePct: z
                .coerce.number()
                .describe('Share of observed years with a positive return, 0–100.'),
              years: z.coerce.number().describe('Years the window was observable in.'),
            })
            .passthrough(),
        ),
        cumulative: z.array(
          z
            .object({
              year: z.coerce.number(),
              valuePct: z
                .coerce.number()
                .describe('Compounded optimal-window profit through this year, %.'),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/total-returns',
    alias: 'scoringGetTotalReturns',
    description: `Calendar-year price return vs the prior year-end close plus the dividend contribution off the same base; the dividend leg fails closed on a currency mismatch.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        currency: z.string().describe('Trading currency of the legs.').nullable(),
        rows: z.array(
          z
            .object({
              year: z.coerce.number(),
              priceReturnPct: z.coerce.number(),
              dividendReturnPct: z
                .coerce.number()
                .describe(
                  'Ex-date dividends over the prior year-end close, %; null = currency-gated.',
                )
                .nullable(),
              totalReturnPct: z.coerce.number().nullable(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/:symbol/universe-context',
    alias: 'scoringGetUniverseContext',
    description: `The daily universe sweep result: per-tab percentiles vs the ruleset reference groups (universe / dividend payers / industry), the &#x27;top X%&#x27; quality rank, the 0-100 factor profile with combined pairs, and industry/universe group medians. available&#x3D;false until the sweep has ranked the symbol.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        available: z.boolean(),
        asOfDate: z.string().describe('Cross-section as-of date.').nullable(),
        rulesetVersion: z.string(),
        industry: z.string().nullable(),
        country: z.string().nullable(),
        capBucket: z.string().nullable(),
        tabPercentiles: TabPercentilesDto.nullable(),
        topPct: z
          .coerce.number()
          .describe("Share of the universe with a STRICTLY higher overview score ('top X%').")
          .nullable(),
        factors: FactorProfileDto.nullable(),
        combined: z
          .object({
            qualityValue: z.coerce.number().nullable(),
            valueMomentum: z.coerce.number().nullable(),
            growthQuality: z.coerce.number().nullable(),
          })
          .passthrough()
          .nullable(),
        factorTilePercentiles: z
          .object({ basic: z.coerce.number().nullable(), combined: z.coerce.number().nullable() })
          .passthrough()
          .nullable(),
        groupSizes: z
          .object({
            universe: z.coerce.number(),
            dividendPayers: z.coerce.number(),
            industry: z.coerce.number().nullable(),
          })
          .passthrough()
          .nullable(),
        industryStats: z
          .object({
            kind: z
              .string()
              .describe('Group kind (industry/country/cap/universe/dividend_payers).'),
            key: z
              .string()
              .describe("Group key (industry name, country, bucket, 'all', 'payers')."),
            memberCount: z.coerce.number(),
            factorMedians: FactorProfileDto,
            metricMedians: z
              .object({
                roePct: z.coerce.number().nullable(),
                revenueCagr5yPct: z.coerce.number().nullable(),
                epsCagr5yPct: z.coerce.number().nullable(),
                fcfCagr5yPct: z.coerce.number().nullable(),
                grossMarginPct: z.coerce.number().nullable(),
                operatingMarginPct: z.coerce.number().nullable(),
                netMarginPct: z.coerce.number().nullable(),
                equityRatioPct: z.coerce.number().nullable(),
                dividendYieldPct: z.coerce.number().nullable(),
                volatility250dPct: z.coerce.number().nullable(),
                momentum6mPct: z.coerce.number().nullable(),
                momentum12mPct: z.coerce.number().nullable(),
                pe: z.coerce.number().nullable(),
                ps: z.coerce.number().nullable(),
                evToEbitda: z.coerce.number().nullable(),
              })
              .passthrough(),
          })
          .passthrough()
          .nullable(),
        universeStats: z
          .object({
            kind: z
              .string()
              .describe('Group kind (industry/country/cap/universe/dividend_payers).'),
            key: z
              .string()
              .describe("Group key (industry name, country, bucket, 'all', 'payers')."),
            memberCount: z.coerce.number(),
            factorMedians: FactorProfileDto,
            metricMedians: z
              .object({
                roePct: z.coerce.number().nullable(),
                revenueCagr5yPct: z.coerce.number().nullable(),
                epsCagr5yPct: z.coerce.number().nullable(),
                fcfCagr5yPct: z.coerce.number().nullable(),
                grossMarginPct: z.coerce.number().nullable(),
                operatingMarginPct: z.coerce.number().nullable(),
                netMarginPct: z.coerce.number().nullable(),
                equityRatioPct: z.coerce.number().nullable(),
                dividendYieldPct: z.coerce.number().nullable(),
                volatility250dPct: z.coerce.number().nullable(),
                momentum6mPct: z.coerce.number().nullable(),
                momentum12mPct: z.coerce.number().nullable(),
                pe: z.coerce.number().nullable(),
                ps: z.coerce.number().nullable(),
                evToEbitda: z.coerce.number().nullable(),
              })
              .passthrough(),
          })
          .passthrough()
          .nullable(),
        disclaimer: z.string().describe('Our scoring, not investment advice.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/scoring/backtest/replay/:symbol',
    alias: 'scoringBacktestGetReplay',
    description: `One row per 1/3/5/10-year look-back: the persisted as-of snapshot re-scored with the ACTIVE weights (zero look-ahead), the split-adjusted PRICE-ONLY return from that quarter to today, and the same measurement on the company’s venue-mapped benchmark (null when its market has no agreed proxy). Facts about one company — no aggregate, no &#x27;beats the market&#x27; claim; the survivorship caveat and the disclaimer are part of the payload and must be rendered with it. Public, like the score pages.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'symbol',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string().describe('Normalized symbol.'),
        name: z.string().nullable(),
        rulesetVersion: z.string(),
        weightsVersion: z
          .string()
          .describe('The ACTIVE weight config used for the read-time composite.'),
        benchmarkSymbol: z
          .string()
          .describe(
            'The venue-mapped benchmark of THIS company (BACKTEST_BENCHMARK_MAP). Null = no agreed proxy for its market — benchmark columns stay explicitly empty.',
          )
          .nullable(),
        benchmarkIsTotalReturn: z
          .boolean()
          .describe(
            'True when the benchmark proxy is a TOTAL-RETURN instrument (its price already accumulates dividends) while the stock leg is price-only — the excess is then CONSERVATIVELY understated by roughly the dividend yield; surfaces render a methodology note off this flag.',
          ),
        asOfToday: z.string().describe("The 'today' of the measurement (yyyy-MM-dd)."),
        rows: z.array(
          z
            .object({
              horizonYears: z.coerce.number().describe('Look-back horizon in years: 1 | 3 | 5 | 10.'),
              asOfDate: z
                .string()
                .describe(
                  "The snapshot-grid quarter end standing in for 'today − horizon' — an EXISTING snapshot date, never an arbitrary one.",
                ),
              points: z
                .coerce.number()
                .describe(
                  'Stoxly Points of the as-of snapshot under the ACTIVE weights (read-time renormalization, the backtest path). Null = not scorable then.',
                )
                .nullable(),
              stars: z.coerce.number().describe('0-5 stars from the points.').nullable(),
              tabBands: z
                .object({
                  overview: z.enum(['high', 'mid', 'low']).nullable(),
                  dividends: z.enum(['high', 'mid', 'low']).nullable(),
                  growth: z.enum(['high', 'mid', 'low']).nullable(),
                  solidity: z.enum(['high', 'mid', 'low']).nullable(),
                  valuation: z.enum(['high', 'mid', 'low']).nullish(),
                  momentum: z.enum(['high', 'mid', 'low']).nullish(),
                })
                .passthrough(),
              stockReturnPct: z
                .coerce.number()
                .describe(
                  'Split-adjusted PRICE-ONLY return, close ≤ asOfDate (14-day staleness) → last close ≤ today, moat-only. Null = no anchor (correctness-or-null).',
                )
                .nullable(),
              benchmarkReturnPct: z
                .coerce.number()
                .describe(
                  'Same arithmetic on the company benchmark over the same requested dates. Null when benchmarkSymbol is null or the moat cannot anchor the proxy.',
                )
                .nullable(),
              excessPct: z
                .coerce.number()
                .describe('stockReturnPct − benchmarkReturnPct; null when either leg is null.')
                .nullable(),
              truncatedByDelisting: z
                .boolean()
                .describe(
                  'True = the stock return runs to the LAST traded close of a delisted listing (exitDate ≠ today) — the UI must say so.',
                ),
              entryDate: z
                .string()
                .describe('The actual entry bar date (close ≤ asOfDate).')
                .nullable(),
              exitDate: z
                .string()
                .describe('The actual exit bar date (last close ≤ today, or the delisting exit).')
                .nullable(),
            })
            .passthrough(),
        ),
        returnBasis: z
          .literal('price-only-adjusted')
          .describe('Split-adjusted closes, no dividend leg — on BOTH sides.'),
        survivorshipCaveat: z
          .string()
          .describe(
            'VERBATIM survivorship caveat of the latest completed backtest run — MUST be rendered wherever the rows are. Null (no completed run) ⇒ the panel does not render at all.',
          )
          .nullable(),
        disclaimer: z.string().describe('MUST accompany the rows on every surface.'),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Malformed symbol.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The instrument registry does not know the symbol.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/scoring/backtest/summary',
    alias: 'scoringBacktestGetSummary',
    description: `Quarterly-rebalanced quintile forward returns of Stoxly Points over the 10-year snapshot grid, 1/2/5/10-year horizons vs the SPY proxy, the weight-search verdict (train vs out-of-sample IC) and the equity curve. Survivorship status is DEGRADED — the caveat field must be rendered wherever these numbers appear. Our scoring, not investment advice.`,
    requestFormat: 'json',
    response: z
      .object({
        available: z.boolean().describe('False until a completed run exists.'),
        runId: z.string().nullable(),
        finishedAt: z.string().nullable(),
        rulesetVersion: z.string(),
        methodology: z
          .object({
            sections: z.coerce.number(),
            firstSection: z.string().nullable(),
            lastSection: z.string().nullable(),
            quantileCount: z.coerce.number(),
            minRowsPerSection: z.coerce.number(),
            benchmarkSymbol: z.string(),
            returnBasis: z
              .literal('price-only-adjusted')
              .describe('Split-adjusted closes, no dividend leg (both sides).'),
            rebalance: z.literal('quarterly'),
            horizons: z.array(z.string()),
            benchmarkMap: z
              .record(z.string().nullable())
              .describe(
                'Per-company benchmark map (venue class → proxy; null = no agreed proxy). Absent on runs persisted before the mapping landed.',
              )
              .optional(),
            benchmarkCoverage: z
              .object({
                symbolsWithBenchmark: z
                  .coerce.number()
                  .describe('Contributing symbols whose proxy anchored.'),
                symbolsBenchmarkNull: z.coerce.number().describe('Contributing symbols mapped to null.'),
                benchmarksUnanchored: z
                  .array(z.string())
                  .describe('Mapped proxies the moat could not anchor.'),
              })
              .passthrough()
              .describe('How far the per-company mapping reached; absent on legacy runs.')
              .optional(),
          })
          .passthrough()
          .nullable(),
        survivorship: z
          .object({
            status: z.literal('degraded').describe('The declared status.'),
            liveSymbols: z.coerce.number(),
            delistedIncluded: z.coerce.number().describe('Dated delisted members that participate.'),
            delistedWithSnapshots: z.coerce.number(),
            delistedExcludedUndated: z
              .coerce.number()
              .describe('Delisted rows without a date — excluded.'),
            renamedExcluded: z.coerce.number().describe('Renamed listings — continuity, excluded.'),
            symbolsWithSnapshots: z.coerce.number(),
            caveat: z.string().describe('The caveat SEO pages must render verbatim.'),
          })
          .passthrough()
          .nullable(),
        horizons: z
          .array(
            z
              .object({
                horizon: z.string().describe('y1 / y2 / y5 / y10.'),
                sectionsUsed: z.coerce.number(),
                scoredRows: z.coerce.number(),
                excludedNoReturn: z.coerce.number(),
                quantiles: z.array(
                  z
                    .object({
                      quantile: z.coerce.number().describe('1 = top scores.'),
                      meanReturnPct: z.coerce.number().nullable(),
                      medianReturnPct: z.coerce.number().nullable(),
                      count: z.coerce.number(),
                      truncatedByDelistingCount: z
                        .coerce.number()
                        .describe('Rows whose horizon ended at a delisting.'),
                    })
                    .passthrough(),
                ),
                spreadTopBottomPct: z.coerce.number().describe('Mean of per-section Q1−Q5.').nullable(),
                hitRate: z.coerce.number().describe('Sections where Q1 beat Q5.').nullable(),
                meanIc: z.coerce.number().describe('Mean per-section Spearman IC.').nullable(),
                icTStat: z.coerce.number().nullable(),
                icSections: z.coerce.number(),
                benchmarkMeanReturnPct: z
                  .coerce.number()
                  .describe('Benchmark mean, same sections.')
                  .nullable(),
                topQuantileMeanReturnPct: z.coerce.number().nullable(),
                excess: z
                  .object({
                    rows: z.coerce.number(),
                    meanExcessPct: z.coerce.number().nullable(),
                    medianExcessPct: z.coerce.number().nullable(),
                    excludedNoBenchmark: z.coerce.number().describe('Rows with no agreed/anchored proxy.'),
                    excludedTruncated: z
                      .coerce.number()
                      .describe('Rows whose horizon ended at a delisting.'),
                    quantiles: z.array(
                      z
                        .object({
                          quantile: z.coerce.number().describe('1 = top scores.'),
                          meanExcessPct: z.coerce.number().nullable(),
                          count: z.coerce.number(),
                        })
                        .passthrough(),
                    ),
                  })
                  .passthrough()
                  .describe(
                    'Per-company excess block; absent on runs persisted before the mapping landed.',
                  )
                  .optional(),
              })
              .passthrough(),
          )
          .describe('Equal launch weights.'),
        horizonsTuned: z
          .array(
            z
              .object({
                horizon: z.string().describe('y1 / y2 / y5 / y10.'),
                sectionsUsed: z.coerce.number(),
                scoredRows: z.coerce.number(),
                excludedNoReturn: z.coerce.number(),
                quantiles: z.array(
                  z
                    .object({
                      quantile: z.coerce.number().describe('1 = top scores.'),
                      meanReturnPct: z.coerce.number().nullable(),
                      medianReturnPct: z.coerce.number().nullable(),
                      count: z.coerce.number(),
                      truncatedByDelistingCount: z
                        .coerce.number()
                        .describe('Rows whose horizon ended at a delisting.'),
                    })
                    .passthrough(),
                ),
                spreadTopBottomPct: z.coerce.number().describe('Mean of per-section Q1−Q5.').nullable(),
                hitRate: z.coerce.number().describe('Sections where Q1 beat Q5.').nullable(),
                meanIc: z.coerce.number().describe('Mean per-section Spearman IC.').nullable(),
                icTStat: z.coerce.number().nullable(),
                icSections: z.coerce.number(),
                benchmarkMeanReturnPct: z
                  .coerce.number()
                  .describe('Benchmark mean, same sections.')
                  .nullable(),
                topQuantileMeanReturnPct: z.coerce.number().nullable(),
                excess: z
                  .object({
                    rows: z.coerce.number(),
                    meanExcessPct: z.coerce.number().nullable(),
                    medianExcessPct: z.coerce.number().nullable(),
                    excludedNoBenchmark: z.coerce.number().describe('Rows with no agreed/anchored proxy.'),
                    excludedTruncated: z
                      .coerce.number()
                      .describe('Rows whose horizon ended at a delisting.'),
                    quantiles: z.array(
                      z
                        .object({
                          quantile: z.coerce.number().describe('1 = top scores.'),
                          meanExcessPct: z.coerce.number().nullable(),
                          count: z.coerce.number(),
                        })
                        .passthrough(),
                    ),
                  })
                  .passthrough()
                  .describe(
                    'Per-company excess block; absent on runs persisted before the mapping landed.',
                  )
                  .optional(),
              })
              .passthrough(),
          )
          .describe('The best searched weights (whole period), when a search converged.')
          .nullable(),
        equityCurve: z.array(
          z
            .object({
              date: z.string(),
              topQuantile: z.coerce.number().describe('Top-quintile portfolio, base 100.'),
              benchmark: z.coerce.number().describe('Benchmark (SPY proxy), base 100.'),
            })
            .passthrough(),
        ),
        weightSearch: z
          .object({
            objectiveHorizon: z.string(),
            trainSections: z.coerce.number(),
            testSections: z.coerce.number(),
            purgedSections: z.coerce.number().describe('Train sections dropped by the purge window.'),
            gridSize: z.coerce.number(),
            equal: z
              .object({
                weights: WeightVectorDto,
                trainMeanIc: z.coerce.number().nullable(),
                oosMeanIc: z.coerce.number().nullable(),
              })
              .passthrough(),
            best: z
              .object({
                weights: WeightVectorDto,
                trainMeanIc: z.coerce.number().nullable(),
                oosMeanIc: z.coerce.number().nullable(),
              })
              .passthrough()
              .nullable(),
            oosImproved: z.boolean().describe('True only when best beat equal OOS.'),
            proposedConfigVersion: z.string().nullable(),
          })
          .passthrough()
          .nullable(),
        horizonsActive: z
          .array(
            z
              .object({
                horizon: z.string().describe('y1 / y2 / y5 / y10.'),
                sectionsUsed: z.coerce.number(),
                scoredRows: z.coerce.number(),
                excludedNoReturn: z.coerce.number(),
                quantiles: z.array(
                  z
                    .object({
                      quantile: z.coerce.number().describe('1 = top scores.'),
                      meanReturnPct: z.coerce.number().nullable(),
                      medianReturnPct: z.coerce.number().nullable(),
                      count: z.coerce.number(),
                      truncatedByDelistingCount: z
                        .coerce.number()
                        .describe('Rows whose horizon ended at a delisting.'),
                    })
                    .passthrough(),
                ),
                spreadTopBottomPct: z.coerce.number().describe('Mean of per-section Q1−Q5.').nullable(),
                hitRate: z.coerce.number().describe('Sections where Q1 beat Q5.').nullable(),
                meanIc: z.coerce.number().describe('Mean per-section Spearman IC.').nullable(),
                icTStat: z.coerce.number().nullable(),
                icSections: z.coerce.number(),
                benchmarkMeanReturnPct: z
                  .coerce.number()
                  .describe('Benchmark mean, same sections.')
                  .nullable(),
                topQuantileMeanReturnPct: z.coerce.number().nullable(),
                excess: z
                  .object({
                    rows: z.coerce.number(),
                    meanExcessPct: z.coerce.number().nullable(),
                    medianExcessPct: z.coerce.number().nullable(),
                    excludedNoBenchmark: z.coerce.number().describe('Rows with no agreed/anchored proxy.'),
                    excludedTruncated: z
                      .coerce.number()
                      .describe('Rows whose horizon ended at a delisting.'),
                    quantiles: z.array(
                      z
                        .object({
                          quantile: z.coerce.number().describe('1 = top scores.'),
                          meanExcessPct: z.coerce.number().nullable(),
                          count: z.coerce.number(),
                        })
                        .passthrough(),
                    ),
                  })
                  .passthrough()
                  .describe(
                    'Per-company excess block; absent on runs persisted before the mapping landed.',
                  )
                  .optional(),
              })
              .passthrough(),
          )
          .describe(
            'Per-horizon results under the weight vector the run found ACTIVE (what production composites with); null on runs persisted before 2026-08-22.',
          )
          .nullish(),
        equityCurveActive: z
          .array(
            z
              .object({
                date: z.string(),
                topQuantile: z.coerce.number().describe('Top-quintile portfolio, base 100.'),
                benchmark: z.coerce.number().describe('Benchmark (SPY proxy), base 100.'),
              })
              .passthrough(),
          )
          .describe(
            'Quarterly top-quantile basket vs benchmark under the ACTIVE weight vector; null on runs persisted before 2026-08-22.',
          )
          .nullish(),
        runWeights: z
          .object({ version: z.string(), weights: WeightVectorDto })
          .passthrough()
          .describe('The vector `equityCurveActive` / `horizonsActive` were scored with.')
          .nullish(),
        activeWeights: z
          .object({ version: z.string(), weights: WeightVectorDto })
          .passthrough()
          .describe('What production composites use NOW.'),
        disclaimer: z.string(),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/screener',
    alias: 'screenerScreen',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional(),
      },
      {
        name: 'pageSize',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional(),
      },
      {
        name: 'sortKey',
        type: 'Query',
        schema: z
          .enum([
            'symbol',
            'points',
            'overview',
            'dividends',
            'growth',
            'solidity',
            'valuation',
            'momentum',
            'percentile',
            'fvDiscount',
            'marketCap',
            'pe',
            'dividendYield',
          ])
          .optional(),
      },
      {
        name: 'sortDir',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'minOverview',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minDividends',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minGrowth',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minSolidity',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minValuation',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minMomentum',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minPoints',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minPercentile',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'minFvDiscountPct',
        type: 'Query',
        schema: z.coerce.number().optional(),
      },
      {
        name: 'industries',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'countries',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'capBuckets',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'payingOnly',
        type: 'Query',
        schema: z.boolean().optional(),
      },
    ],
    response: z
      .object({
        rows: z.array(
          z
            .object({
              symbol: z
                .string()
                .describe('Normalized (Finnhub) symbol — the key for every scoring surface.'),
              name: z
                .string()
                .describe('Registry display name; null when the instrument row carries none.')
                .nullable(),
              points: z
                .coerce.number()
                .describe('Stoxly Points 0-100 — the equal-weight mean of the tabs that have data.')
                .nullable(),
              overview: ScreenerTabScoreDto,
              dividends: ScreenerTabScoreDto,
              growth: ScreenerTabScoreDto,
              solidity: ScreenerTabScoreDto,
              valuation: ScreenerTabScoreDto.describe('V2 valuation tab.').optional(),
              momentum: ScreenerTabScoreDto.describe(
                'ruleset_v3 momentum tab (0-10): the universe percentile of the 6-1 / 12-1 windows.',
              ).optional(),
              percentile: z
                .coerce.number()
                .describe(
                  "Overview percentile against the whole universe ('better than N% of companies'). Null under the 5-member group floor.",
                )
                .nullable(),
              fvDiscountPct: z
                .coerce.number()
                .describe(
                  'Percent BELOW fair value (the negated composite deviation): positive = undervalued.',
                )
                .nullable(),
              marketCapUsdMillions: z
                .coerce.number()
                .describe('Market capitalisation in USD millions.')
                .nullable(),
              capBucket: z.string().describe('mega | large | mid | small | micro.').nullable(),
              industry: z.string().nullable(),
              country: z.string().describe('ISO country of the company profile.').nullable(),
              pe: z
                .coerce.number()
                .describe(
                  "P/E on the LAST CLOSED FISCAL YEAR — as-of close divided by the latest ANNUAL diluted EPS (`fairValue.currentPe`). It is NOT the trailing-twelve-months P/E the company page shows under 'P/E (TTM)', and the two diverge by 30-50% for any issuer whose fiscal year does not close on its latest reported quarter (ALL 6.74 vs 5.14, LRCX 74.7 vs 54.6, FIX 57.9 vs 38.4). The basis is deliberate: the screener ranks beside `fvDiscountPct`, whose earnings anchor is the mean of the company's own YEAR-END multiples, so a TTM numerator here would be inconsistent with the discount printed next to it. Consumers MUST name the basis in the column header — see `research.screener.columns.pe`.",
                )
                .nullable(),
              dividendYieldPct: z
                .coerce.number()
                .describe(
                  "Dividend yield in percent. NULL is an ABSTENTION, never a zero: a non-payer gets 0, while a payer whose per-share basis is not the traded one (a depositary line — TSM 1 ADR = 5 ordinaries) gets null, because DPS-per-ordinary over a price-per-ADS is a x5 error dressed as a yield. So `paysDividend: true` beside a null yield is the engine saying 'pays, basis not expressible', not 'no data'.",
                )
                .nullable(),
              paysDividend: z.boolean().describe('Whether the company pays a dividend at all.'),
            })
            .passthrough(),
        ),
        page: z.coerce.number().describe('1-based page number.'),
        pageSize: z.coerce.number(),
        total: z.coerce.number().describe('Rows matching the filter across ALL pages, not just this one.'),
        sortKey: z
          .enum([
            'symbol',
            'points',
            'overview',
            'dividends',
            'growth',
            'solidity',
            'valuation',
            'momentum',
            'percentile',
            'fvDiscount',
            'marketCap',
            'pe',
            'dividendYield',
          ])
          .describe('The sort actually applied.'),
        sortDir: z.enum(['asc', 'desc']),
        rulesetVersion: z
          .string()
          .describe('Ruleset the scores were produced by — the version the numbers belong to.'),
        asOfDate: z
          .string()
          .describe(
            "The cross-section these rows come from (R8-A3-05). Every screener number is the universe sweep's FROZEN projection of that day, not a live read: a company page opened now can already show a different tab sum, and this is the date that says so. Null when no cross-section has been published yet.",
          )
          .nullable(),
        disclaimer: z.string().describe('Our scoring, not investment advice.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/screener-presets',
    alias: 'screenerPresetsList',
    requestFormat: 'json',
    response: z.array(ScreenerPresetDto),
  },
  {
    method: 'post',
    path: '/api/screener-presets',
    alias: 'screenerPresetsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: screenerPresetsCreate_Body,
      },
    ],
    response: ScreenerPresetDto,
    errors: [
      {
        status: 400,
        description: `Invalid payload, or the per-user preset cap is reached`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The user already has a preset with this name`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/screener-presets/:id',
    alias: 'screenerPresetsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        message: z
          .string()
          .describe(
            "Always 'Preset deleted' — a delete that removed nothing is a 404, never a 200",
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `No such preset, or it belongs to another user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/screener/facets',
    alias: 'screenerFacets',
    requestFormat: 'json',
    response: z
      .object({
        industries: z.array(
          z
            .object({
              value: z.string(),
              count: z
                .coerce.number()
                .describe('Universe members carrying this value, before any filter.'),
            })
            .passthrough(),
        ),
        countries: z.array(
          z
            .object({
              value: z.string(),
              count: z
                .coerce.number()
                .describe('Universe members carrying this value, before any filter.'),
            })
            .passthrough(),
        ),
        capBuckets: z
          .array(
            z
              .object({
                value: z.string(),
                count: z
                  .coerce.number()
                  .describe('Universe members carrying this value, before any filter.'),
              })
              .passthrough(),
          )
          .describe('mega / large / mid / small / micro.'),
        universeSize: z.coerce.number().describe('Total scoreable companies in the universe.'),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/seo/export',
    alias: 'seoExportExport',
    requestFormat: 'json',
    response: z
      .object({
        generatedAt: z.string(),
        asOfDate: z
          .string()
          .describe(
            'The newest `scoring_ranks.as_of_date` in the corpus — what every page prints.',
          ),
        skipped: z
          .object({
            invalidSymbol: z
              .coerce.number()
              .describe('Symbols whose spelling cannot be a static asset path'),
            noProfile: z.coerce.number().describe('Symbols with no stored `stock_profile` row'),
          })
          .passthrough(),
        pages: z.array(
          z
            .object({
              kind: z.literal('overview'),
              path: z
                .string()
                .describe(
                  'Taken VERBATIM by the build script. No trailing slash, because that is the path the SPA router matches (file = `dist{path}.html`).',
                ),
              lastmod: z.string().describe('`scoring_ranks.as_of_date` for this page'),
              locale: z.string(),
              symbol: z.string().describe('Normalized symbol'),
              alternates: z.array(
                z
                  .object({
                    hreflang: z.string().describe('`hreflang` value; `x-default` for the fallback'),
                    path: z.string(),
                  })
                  .passthrough(),
              ),
              head: z.string().describe('Injected before `</head>` of the SPA shell.'),
              body: z.string().describe("Injected inside `<div id='app'>` of the SPA shell."),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/snaptrade/callback',
    alias: 'snaptradeHandleCallback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: snaptradeHandleCallback_Body,
      },
    ],
    response: z
      .object({
        success: z.boolean(),
        connectionId: z.string(),
        brokerageName: z.string(),
        accountsConnected: z.coerce.number(),
        message: z.string().optional(),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/snaptrade/connect',
    alias: 'snaptradeGetConnectionPortal',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: snaptradeGetConnectionPortal_Body,
      },
    ],
    response: z
      .object({
        redirectUrl: z.string().describe('URL to redirect user to SnapTrade connection portal'),
        sessionId: z.string().describe('Session ID for tracking the connection').optional(),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `User not registered with SnapTrade`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/snaptrade/connections',
    alias: 'snaptradeListConnections',
    requestFormat: 'json',
    response: z.array(
      z
        .object({
          id: z.string(),
          authorizationId: z.string(),
          brokerageName: z.string(),
          brokerageSlug: z.string(),
          brokerageLogoUrl: z.string().optional(),
          status: z.enum(['ACTIVE', 'DISCONNECTED', 'ERROR', 'PENDING']),
          lastSyncAt: z
            .string()
            .datetime({ offset: true })
            .describe('ISO instant of the last sync.')
            .optional(),
          syncError: z.string().optional(),
          accounts: z.array(
            z
              .object({
                id: z.string(),
                name: z.string(),
                number: z.string(),
                type: z.string(),
                currency: z.string(),
                balance: z.coerce.number().optional(),
                institutionName: z.string().optional(),
              })
              .passthrough(),
          ),
          portfolioId: z.string().optional(),
          walletId: z.string().optional(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          awaitingInitialTransactions: z
            .boolean()
            .describe(
              'Step 4 (RC-D): true while any of the connection accounts still awaits its initial transactions sync at the broker (IBKR: minutes to 72h). Holdings/balances are live; transaction history and the synthesized opening lots are not, by design.',
            ),
        })
        .passthrough(),
    ),
  },
  {
    method: 'get',
    path: '/api/snaptrade/connections/:connectionId/opening-lots',
    alias: 'snaptradeGetOpeningLots',
    requestFormat: 'json',
    parameters: [
      {
        name: 'connectionId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        awaitingInitialTransactions: z
          .boolean()
          .describe(
            'True while any of the connection accounts still awaits its initial transactions sync at the broker — positions listed then carry source=awaiting-history.',
          ),
        positions: z.array(
          z
            .object({
              symbol: z.string().describe('Instrument symbol of the position'),
              brokerQuantity: z.coerce.number().describe('Current broker-reported quantity (signed)'),
              gapQuantity: z
                .coerce.number()
                .describe(
                  'Quantity the transaction history cannot explain — the synthesized lot size. While awaiting history this is the full broker quantity.',
                ),
              price: z
                .coerce.number()
                .describe(
                  'The lot price in effect: the user override when present, else the broker average price (the default the user edits).',
                ),
              currency: z
                .string()
                .describe('Currency the price is denominated in (instrument currency)'),
              executedAt: z
                .string()
                .datetime({ offset: true })
                .describe(
                  'The synthesized lot date in effect (override date or the ladder anchor), ISO instant. Absent while the connection awaits its initial transaction history (nothing synthesized yet).',
                )
                .optional(),
              source: z
                .enum(['broker-estimate', 'user-override', 'awaiting-history'])
                .describe(
                  'Provenance of the lot: pipeline guess (broker-estimate), user-provided (user-override), or not yet synthesized (awaiting-history).',
                ),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Connection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/snaptrade/connections/:connectionId/opening-lots',
    alias: 'snaptradeUpsertOpeningLot',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: snaptradeUpsertOpeningLot_Body,
      },
      {
        name: 'connectionId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        symbol: z.string(),
        price: z.coerce.number(),
        currency: z.string(),
        executedAt: z.string().datetime({ offset: true }),
        quantity: z.coerce.number().nullish(),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough(),
    errors: [
      {
        status: 404,
        description: `Connection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/snaptrade/connections/:connectionId/opening-lots',
    alias: 'snaptradeDeleteOpeningLot',
    requestFormat: 'json',
    parameters: [
      {
        name: 'connectionId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'symbol',
        type: 'Query',
        schema: z.string().max(100),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Connection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/snaptrade/connections/:id',
    alias: 'snaptradeDisconnectBroker',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `Connection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/snaptrade/refresh-connections',
    alias: 'snaptradeRefreshConnections',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/snaptrade/status',
    alias: 'snaptradeGetRegistrationStatus',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/snaptrade/sync',
    alias: 'snaptradeSyncAllConnections',
    requestFormat: 'json',
    response: z
      .object({
        success: z.boolean(),
        totalWalletsCreated: z.coerce.number(),
        totalPortfoliosCreated: z.coerce.number(),
        totalTransactionsSynced: z.coerce.number(),
        results: z.array(SyncResultDto),
      })
      .passthrough(),
  },
  {
    method: 'post',
    path: '/api/snaptrade/sync/:connectionId',
    alias: 'snaptradeSyncConnection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'connectionId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: SyncResultDto,
    errors: [
      {
        status: 404,
        description: `Connection not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/treasury-bonds/series/:code',
    alias: 'treasurySeriesDescribe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'code',
        type: 'Path',
        schema: z.string().min(1).max(32),
      },
    ],
    response: TreasurySeriesDto,
  },
  {
    method: 'post',
    path: '/api/users/beta-discount/claim',
    alias: 'usersClaimBetaDiscount',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/clear-account',
    alias: 'usersClearAccount',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Demo users must use Exit Demo Mode`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/delete-account',
    alias: 'usersDeleteAccount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersDeleteAccount_Body,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Demo or admin accounts cannot be deleted`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `ACTIVE_SUBSCRIPTION: cancel the subscription before deleting the account`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/demo/activate',
    alias: 'usersActivateDemoMode',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `User is already in demo mode, or the account already holds something leaving demo mode would delete: books, watchlists, alert rules, assistant chats, saved research, presets, imported files (code DEMO_REQUIRES_EMPTY_ACCOUNT, with the rows held per group)`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/demo/exit',
    alias: 'usersExitDemoMode',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `User is not in demo mode`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/import/unknown-labels/:id/confirm',
    alias: 'importUnknownLabelsConfirm',
    description: `Records the caller&#x27;s answer on their own hits of an unknown-label record, for the team&#x27;s queue. It books nothing; the latest answer replaces an earlier one.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: importUnknownLabelsConfirm_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Not a canonical event`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No hit of the caller on that label`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/users/onboarding-progress',
    alias: 'usersUpdateOnboardingProgress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersUpdateOnboardingProgress_Body,
      },
    ],
    response: OnboardingStatusResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/onboarding-status',
    alias: 'usersGetOnboardingStatus',
    requestFormat: 'json',
    response: OnboardingStatusResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/onboarding/complete',
    alias: 'usersCompleteOnboarding',
    requestFormat: 'json',
    response: OnboardingStatusResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/onboarding/draft',
    alias: 'usersGetOnboardingDraft',
    requestFormat: 'json',
    response: z
      .object({
        draft: z
          .object({})
          .partial()
          .passthrough()
          .describe('The saved onboarding draft, or null if no draft exists')
          .nullable(),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/users/onboarding/draft',
    alias: 'usersSaveOnboardingDraft',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersSaveOnboardingDraft_Body,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Stale baseSavedAt: another session saved the draft first (code ONBOARDING_DRAFT_CONFLICT; reload the draft and retry)`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/users/onboarding/draft',
    alias: 'usersClearOnboardingDraft',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/onboarding/import-data',
    alias: 'usersImportData',
    requestFormat: 'json',
    response: z
      .object({
        success: z.boolean().describe('Whether import was fully successful'),
        walletsCreated: z.coerce.number().describe('Number of wallets created'),
        walletsMerged: z.coerce.number().describe('Number of wallets merged into'),
        portfoliosCreated: z.coerce.number().describe('Number of portfolios created'),
        portfoliosMerged: z.coerce.number().describe('Number of portfolios merged into'),
        wallets: z
          .array(
            z
              .object({
                id: z.string().describe('Wallet ID'),
                name: z.string().describe('Wallet name'),
                currency: z.string().describe('Wallet currency'),
                balance: z.coerce.number().describe('Wallet balance'),
                transactionCount: z.coerce.number().describe('Number of transactions imported'),
                skippedCount: z.coerce.number().describe('Number of duplicate transactions skipped'),
                isExisting: z
                  .boolean()
                  .describe('Whether this is an existing wallet that was merged into'),
                firstTransaction: z
                  .object({
                    type: z.string().describe('First transaction type'),
                    date: z.string().describe('First transaction date in ISO format'),
                  })
                  .passthrough()
                  .describe('First transaction info (earliest by date)')
                  .optional(),
                walletOnly: z
                  .boolean()
                  .describe(
                    'True when the import DELIBERATELY created no portfolio for this wallet: its currency group carries no position-building operations (a dividend/fee-only sibling of a multi-currency account, a pure-cash or CFD-cash statement). Consumers must not read the wallet-without-portfolio shape as missing broker statements.',
                  )
                  .optional(),
                delta: z
                  .object({
                    added: z
                      .coerce.number()
                      .int()
                      .describe('Rows this upload wrote that the previous import had not'),
                    unchanged: z
                      .coerce.number()
                      .int()
                      .describe(
                        'Rows the previous import had and this upload restated (a row a person corrected is kept, and counted here)',
                      ),
                    removed: z
                      .coerce.number()
                      .int()
                      .describe(
                        'Rows the previous import had that this upload no longer carries; re-uploading the full export restores them',
                      ),
                  })
                  .passthrough()
                  .describe(
                    'What this re-import did to the book, read back after the last file wrote. Null for a book this upload CREATED, or a half of it the upload did not rewrite (a split-file export whose other half is still owed).',
                  )
                  .nullable(),
                sourceFiles: z
                  .array(z.string())
                  .describe(
                    'The original filename of every file of THIS upload written into the book. The results summary lists each file with the book it landed in. Empty when the record read failed.',
                  ),
              })
              .passthrough(),
          )
          .describe('List of created/merged wallets'),
        portfolios: z
          .array(
            z
              .object({
                id: z.string().describe('Portfolio ID'),
                name: z.string().describe('Portfolio name'),
                positionsCount: z.coerce.number().describe('Number of open positions'),
                soldPositionsCount: z.coerce.number().describe('Number of closed/sold positions'),
                totalValue: z.coerce.number().describe('Total portfolio value'),
                skippedTransactionCount: z
                  .coerce.number()
                  .describe('Number of duplicate transactions skipped'),
                isExisting: z
                  .boolean()
                  .describe('Whether this is an existing portfolio that was merged into'),
                symbolReplacements: z
                  .array(
                    z
                      .object({
                        originalSymbol: z
                          .string()
                          .describe('Original symbol from broker statement'),
                        resolvedSymbol: z.string().describe('Resolved symbol used in the system'),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        lifecycleStatus: z
                          .string()
                          .describe(
                            "Why the symbol moved, when a retired registry code was routed to its live successor: 'renamed' = the ORIGINAL ticker is that code (CCC.WA → MDV.WA); 'renamed-choice' = the symbol a person chose for this holding in an earlier import is that code (their pin now trades as the resolved symbol). Absent for plain fuzzy/ISIN replacements. Other values may follow: a consumer shows its generic copy for one it does not know.",
                          )
                          .optional(),
                        statementIsin: z
                          .string()
                          .describe(
                            'The ISIN the broker statement printed for this holding. Provenance for the review screen only, so the reader can judge the proposed symbol against it; it never becomes the symbol. Absent when the statement printed no ISIN.',
                          )
                          .optional(),
                        statementName: z
                          .string()
                          .describe(
                            'The security name the broker statement printed for this holding (a name that only repeats the ticker is not carried). Provenance for the review screen only; it never becomes the symbol. Absent when the statement printed no name.',
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe('Symbols that were fuzzy-matched and replaced during import')
                  .optional(),
                tickerNormalizations: z
                  .array(
                    z
                      .object({
                        original: z
                          .string()
                          .describe('Original ticker with suffix (e.g., NFLX.US)'),
                        normalized: z
                          .string()
                          .describe('Normalized ticker without suffix (e.g., NFLX)'),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        matchedBy: z
                          .enum([
                            'isin-key',
                            'curated-alias',
                            'venue-pick',
                            'exact-row',
                            'venue-strip',
                            'renamed-successor',
                            'notation-variant',
                            'venue-coded-etf',
                            'statement-isin',
                            'statement-name',
                            'isin-bridge',
                            'exact-base',
                            'country-sibling',
                            'label-name',
                            'venue-abbreviation',
                            'cached-fuzzy',
                            'dead-ticker-probe',
                            'fuzzy',
                            'unresolved',
                            'book-choice',
                            'user-choice',
                          ])
                          .describe(
                            "HOW the import matched the position's symbol, when that PROVED it: the resolver step that bound it (an ISIN, the exchange's notation of the printed listing, a curated alias, an inference the traded price corroborated, a person's earlier choice …), so the review can say how instead of asking the reader to trust a mapping. ABSENT when nothing proved the bind — an inference no price corroborated, a market the broker never named — and the review then asks the reader to verify the instrument; absent too on a review restored from a draft saved before the field existed.",
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe('Tickers that were normalized (suffix stripped) during import')
                  .optional(),
                sellsWithoutBuys: z
                  .array(z.string())
                  .describe(
                    'Symbols with SELL transactions but no matching BUY (unknown cost basis)',
                  )
                  .optional(),
                unpricedReceipts: z
                  .array(z.string())
                  .describe(
                    'Symbols of OPEN positions holding shares delivered in kind that no market close could price (cost stored as 0): the open review asks the user for their price',
                  )
                  .optional(),
                unverifiedSymbols: z
                  .array(
                    z
                      .object({
                        symbol: z.string().describe('Symbol that could not be verified'),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        brokerLabel: z
                          .string()
                          .describe(
                            "The broker's own name for this security, when it differs from the unresolved symbol. An ISIN-keyed group that never resolved shows the raw ISIN — 'PL0GF0033084' names nothing a human can act on, while the statement that carried it also said 'FW20M2620'. Provenance for the review screen only; it never becomes the symbol.",
                          )
                          .optional(),
                        statementIsin: z
                          .string()
                          .describe(
                            'The ISIN the broker statement printed for this holding. Provenance for the review screen only, so the reader can judge the proposed symbol against it; it never becomes the symbol. Absent when the statement printed no ISIN.',
                          )
                          .optional(),
                        statementName: z
                          .string()
                          .describe(
                            'The security name the broker statement printed for this holding (a name that only repeats the ticker is not carried). Provenance for the review screen only; it never becomes the symbol. Absent when the statement printed no name.',
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Symbols that could not be verified against market data providers (Finnhub/Yahoo) - may have incorrect prices',
                  )
                  .optional(),
                delistedSymbols: z
                  .array(
                    z
                      .object({
                        symbol: z
                          .string()
                          .describe('The resolved instrument symbol (a delisted registry row)'),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        delistedAt: z
                          .string()
                          .describe('When the instrument was delisted (ISO date), when known')
                          .nullish(),
                        successorAsk: z
                          .object({
                            exitedAt: z
                              .string()
                              .describe('The day the statement printed the exit at no value (ISO)'),
                            quantity: z.coerce.number().describe('The units that left, in the old line'),
                            suggestedSymbol: z
                              .string()
                              .describe(
                                'The successor the registry already knows (a rename tombstone or the chain answering with the successor), to pre-fill; null when nothing knows it. The person confirms.',
                              )
                              .nullable(),
                            suggestedRatio: z
                              .coerce.number()
                              .describe(
                                'New units per old unit of the curated merger that named `suggestedSymbol` (SHO.WA -> CBF.WA at 0.2281), to pre-fill with it; null when no curated merger named the successor. The person confirms.',
                              )
                              .nullable(),
                            handSale: z
                              .object({
                                day: z.string().describe('The latest of those sales (YYYY-MM-DD)'),
                                quantity: z
                                  .coerce.number()
                                  .describe('The units those sales closed, in the old line'),
                              })
                              .passthrough()
                              .describe(
                                "The person's own sales of the old line, typed in by hand, when they are why a curated merger asks instead of converting (R67). Answered while they stand, the carried lots and those sales count the holding twice, so review asks the person to delete them first. Null for every other question.",
                              )
                              .nullable(),
                          })
                          .passthrough()
                          .describe(
                            'Set when the statement printed the holding LEAVING at no value and never named what replaced it (a Trading 212 merger: RTL -> GNL at 0.67). Nothing was sold: the lots stay open, and review asks for the successor instrument and the ratio, applied through PATCH /positions/:id/core (`symbol` + `ratio`), cost and dates kept.',
                          )
                          .optional(),
                        statementIsin: z
                          .string()
                          .describe(
                            'The ISIN the broker statement printed for this holding. Provenance for the review screen only, so the reader can judge the proposed symbol against it; it never becomes the symbol. Absent when the statement printed no ISIN.',
                          )
                          .optional(),
                        statementName: z
                          .string()
                          .describe(
                            'The security name the broker statement printed for this holding (a name that only repeats the ticker is not carried). Provenance for the review screen only; it never becomes the symbol. Absent when the statement printed no name.',
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Positions resolved onto DELISTED instruments (identity confirmed, no live pricing) — amber, confirmable: the user acknowledges the security is no longer listed instead of being forced to remap it onto a lookalike',
                  )
                  .optional(),
                splitNotices: z
                  .array(
                    z
                      .object({
                        symbol: z.string().describe('The resolved position symbol'),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        adjustmentApplied: z
                          .boolean()
                          .describe(
                            'True when the import rescaled quantities/prices for these events (verify holdings against the broker — an export that was ALREADY split-adjusted would be double-adjusted); false when quantities stayed broker-stated (a pre-split export may show outdated share counts)',
                          ),
                        firstTradeDate: z
                          .string()
                          .describe("The position's earliest trade date (YYYY-MM-DD)"),
                        events: z
                          .array(
                            z
                              .object({
                                date: z.string().describe('Split effective date (YYYY-MM-DD)'),
                                fromFactor: z
                                  .coerce.number()
                                  .describe(
                                    'Pre-split units of the ratio (9 in a 9-for-8 consolidation)',
                                  ),
                                toFactor: z
                                  .coerce.number()
                                  .describe(
                                    'Post-split units of the ratio (8 in a 9-for-8 consolidation)',
                                  ),
                              })
                              .passthrough(),
                          )
                          .describe('Splits strictly after the first trade, oldest first'),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Advisory split-since-purchase notices: a split/consolidation dated after a position first trade. Informative only — NEVER gates review (requiresReview ignores it).',
                  )
                  .optional(),
                ambiguousAssetTypes: z
                  .array(
                    z
                      .object({
                        symbol: z
                          .string()
                          .describe(
                            'The broker-stated ticker that matched more than one asset class',
                          ),
                        positionId: z.string().describe('Position ID that was affected'),
                        isClosed: z.boolean().describe('Whether the position is closed (sold)'),
                        guessedAssetType: z
                          .string()
                          .describe(
                            'The asset type the import GUESSED and stored. Not a confirmation - onboarding review blocks until the user picks a type explicitly.',
                          ),
                        candidateAssetTypes: z
                          .array(z.string())
                          .describe(
                            "The asset types this ticker plausibly resolves to (always 2 or more), e.g. ['CRYPTO','ETF'] for BTC (the coin and the Grayscale Bitcoin Trust).",
                          ),
                      })
                      .passthrough(),
                  )
                  .describe(
                    'Tickers that matched more than one asset class (BTC = the coin AND the Grayscale trust). The import had to store a guess; review BLOCKS until the user picks the type explicitly, because the wrong pick stamps the wrong instrument currency onto every lot.',
                  )
                  .optional(),
                requiresReview: z
                  .boolean()
                  .describe(
                    'Whether this portfolio requires review (has symbol replacements, ticker normalizations, sells without buys, unverified or delisted symbols)',
                  )
                  .default(false),
                cfdReview: z
                  .object({
                    walletId: z
                      .string()
                      .describe(
                        "The portfolio's wallet — the funding wallet a 'not a CFD' re-booking needs",
                      )
                      .nullable(),
                    open: z.array(
                      z
                        .object({
                          positionId: z.string(),
                          kind: z
                            .enum(['CFD', 'FUTURE'])
                            .describe(
                              'CFD, or an exchange-traded FUTURE (the same row flavour, §4.18.1): a future is never re-booked as shares, and its underlying is the one its contract code states.',
                            ),
                          symbol: z
                            .string()
                            .describe('CFD symbol as the broker names it (GOLD, US100, ORCL.US)'),
                          direction: z
                            .enum(['BUY', 'SELL'])
                            .describe('BUY = long, SELL = short')
                            .nullable(),
                          quantity: z
                            .coerce.number()
                            .describe('Volume in lots (positive; direction carries the sign)'),
                          openPrice: z
                            .coerce.number()
                            .describe(
                              'Open price in the underlying quote units. NULL when the statement reported no usable entry price (`entryPriceUnknown`) — the stored placeholder is never served as a price.',
                            )
                            .nullable(),
                          margin: z
                            .coerce.number()
                            .describe('Broker-blocked margin, account currency')
                            .nullable(),
                          currency: z.string().describe('Account currency of margin / P&L'),
                          instrumentCurrency: z
                            .string()
                            .describe(
                              "The INSTRUMENT's quote currency (the open price's unit) — what a 'not a CFD' re-booking must submit the shares in. NULL when the row carries none (pre-2026-09-07 imports).",
                            )
                            .nullable(),
                          unrealizedAtStatement: z
                            .coerce.number()
                            .describe(
                              "The broker's own unrealized P&L (plus accrued swap) as of the statement — the only value an open CFD carries until it is priced. NULL for a manual row.",
                            )
                            .nullable(),
                          statementDate: z
                            .string()
                            .describe('ISO date the statement values are as-of')
                            .nullable(),
                          openedAt: z
                            .string()
                            .describe('ISO instant the contract was opened')
                            .nullable(),
                          entryPriceUnknown: z
                            .boolean()
                            .describe('The statement reported no usable entry price for this row'),
                          bonusShareSuspect: z
                            .boolean()
                            .describe(
                              "Looks like a bonus / spin-off SHARE allocation the parser tagged as a CFD (open price at or below 0.001, or unknown entry with no margin blocked): CRQ.PL / S2B.PL class. The review offers 'not a CFD' on these.",
                            ),
                          underlying: z
                            .object({
                              quoteSymbol: z
                                .string()
                                .describe(
                                  "Where the CFD's price would come from: the curated map's quote symbol for a bare symbol ('BTC', 'EUR/USD'), the ticker itself for a stock-CFD (ORCL.US).",
                                )
                                .nullable(),
                              source: z
                                .enum(['STOCK', 'CRYPTO', 'FX', 'YAHOO', 'UNPRICED', 'UNKNOWN'])
                                .describe(
                                  'STOCK = a dotted stock-CFD priced off its own ticker; CRYPTO/FX = a curated map source; UNPRICED = curated but no source (GOLD, US100…); UNKNOWN = a bare symbol the map does not know.',
                                ),
                              note: z
                                .string()
                                .describe("The curated map's human note, when any")
                                .nullable(),
                            })
                            .passthrough(),
                        })
                        .passthrough(),
                    ),
                    closed: z
                      .object({
                        count: z
                          .coerce.number()
                          .describe('Closed-CFD realized P&L rows (`Realized P&L #` wallet rows)'),
                        realizedPnl: z
                          .coerce.number()
                          .describe('Σ realized P&L of those rows, wallet currency'),
                        currency: z.string().describe('Wallet currency of `realizedPnl`'),
                        missingCapitalCount: z
                          .coerce.number()
                          .describe(
                            "Rows whose `cfd_capital` is NULL (unknown) — one such row refuses the whole book's return. `[]` (deliberately empty) rows are NOT counted.",
                          ),
                      })
                      .passthrough(),
                    hints: z
                      .array(CfdOpenPositionHintDto)
                      .describe(
                        'Likely-open CFDs the statement cannot show (dividend-equivalent evidence)',
                      ),
                  })
                  .passthrough()
                  .describe(
                    'CFD facts for the onboarding review: open contracts found in the file, the closed-CFD P&L summary (with the count of rows missing capital), likely-open hints.',
                  ),
                delta: z
                  .object({
                    added: z
                      .coerce.number()
                      .int()
                      .describe('Rows this upload wrote that the previous import had not'),
                    unchanged: z
                      .coerce.number()
                      .int()
                      .describe(
                        'Rows the previous import had and this upload restated (a row a person corrected is kept, and counted here)',
                      ),
                    removed: z
                      .coerce.number()
                      .int()
                      .describe(
                        'Rows the previous import had that this upload no longer carries; re-uploading the full export restores them',
                      ),
                  })
                  .passthrough()
                  .describe(
                    'What this re-import did to the book, read back after the last file wrote. Null for a book this upload CREATED, or a half of it the upload did not rewrite (a split-file export whose other half is still owed).',
                  )
                  .nullable(),
                sourceFiles: z
                  .array(z.string())
                  .describe(
                    'The original filename of every file of THIS upload written into the book. The results summary lists each file with the book it landed in. Empty when the record read failed.',
                  ),
                importedFileIds: z
                  .array(z.string())
                  .describe(
                    "The `importedFileId` of every reconciliation record THIS upload wrote for the book (an XTB statement with a declared Total, each file its own record) — a book lists every record it ever had, so these ids pick out this upload's. Empty when no file of the upload states a Total.",
                  ),
              })
              .passthrough(),
          )
          .describe('List of created/merged portfolios'),
        errors: z
          .array(
            z
              .object({
                filename: z.string().describe('Filename that failed to process'),
                error: z.string().describe('Error message'),
                details: z.string().describe('Additional error details').optional(),
                code: z
                  .enum([
                    'NOT_A_STATEMENT',
                    'POSITIONS_ONLY',
                    'MISSING_DATES',
                    'MISSING_SYMBOLS',
                    'MISSING_AMOUNTS',
                    'MISSING_TRANSACTION_TYPES',
                    'NO_TRANSACTIONS',
                    'DUPLICATE_STATEMENT',
                    'UNSUPPORTED_FORMAT',
                    'AI_UNAVAILABLE',
                    'MISSING_CASH_FILE',
                    'MISSING_TRADES_FILE',
                    'MISSING_VALUATIONS_FILE',
                    'INCOMPLETE_HISTORY_WINDOW',
                    'MARGIN_CASH_INCOMPLETE',
                    'CFD_CAPITAL_INCOMPLETE',
                    'CFD_CURRENCY_UNKNOWN',
                    'FUTURES_UNPAIRED_CLOSE',
                    'FUTURES_EXPIRED_OPEN',
                    'CFD_ACCOUNT_NOT_IMPORTED',
                    'TRANSFER_COUNTERPART_MISSING',
                    'FUND_UNIT_ROWS_NOT_BOOKED',
                    'ROWS_NOT_RECOGNISED',
                    'UNSUPPORTED_REPORT_TYPE',
                    'PARTIAL_HISTORY_REPLACE',
                    'EXTRACTION_UNVERIFIED',
                    'TARGET_PORTFOLIO_MISMATCH',
                    'EMPTY_EXPORT',
                    'STATEMENT_WINDOW_GAP',
                    'AMBIGUOUS_TARGET',
                    'EXPORT_KINDS_UNMATCHED',
                    'TRADES_FILE_REQUIRED',
                    'ACCOUNT_NOT_NAMED',
                    'IMPORT_IN_PROGRESS',
                    'EMPTY_EXPORT_FUNDED_ACCOUNT',
                    'IMPORT_INTERNAL_ERROR',
                    'EMPTY_FILE',
                  ])
                  .describe(
                    'Machine-readable import error code (the fixed taxonomy the UI localizes)',
                  )
                  .optional(),
                fileKind: z
                  .string()
                  .describe(
                    "Statement file kind. Keys the localized 'which export to fetch' copy in the UI, so it is stamped wherever that steering exists. It means TWO things, told apart by `code`: under MISSING_TRADES_FILE / MISSING_CASH_FILE / MISSING_VALUATIONS_FILE it is the half of a split-file broker export the user still OWES; under UNSUPPORTED_REPORT_TYPE, POSITIONS_ONLY and NO_TRANSACTIONS it is what the uploaded file IS, and the steering names the export to fetch instead (see import-report-kinds.ts, which decides which kinds carry it). A kind that is already the right export never carries it.",
                  )
                  .optional(),
                namedBroker: z
                  .enum([
                    'XTB',
                    'FREEDOM24',
                    'INTERACTIVE_BROKERS',
                    'DEGIRO',
                    'TRADING212',
                    'SAXO',
                    'MBANK',
                    'BOSSA',
                    'REVOLUT',
                    'REVOLUT_X',
                    'BINANCE',
                    'KRAKEN',
                    'ING',
                    'TRADE_REPUBLIC',
                    'SCALABLE_CAPITAL',
                    'MYFUND',
                    'PKO_BP',
                    'ETORO',
                    'NORDNET',
                    'PORTFOLIO_PERFORMANCE',
                    'TREASURY_REGISTER',
                    'FUND_UNITS',
                    'UNKNOWN',
                  ])
                  .describe(
                    "UNSUPPORTED_FORMAT / NOT_A_STATEMENT / POSITIONS_ONLY only: the supported broker the refused file's content names (a converted PDF's letterhead, a hand-made analysis). The UI renders that broker's working export instead of the generic copy (R74).",
                  )
                  .optional(),
                period: z
                  .object({
                    from: z.string().describe('First day the file was exported for (YYYY-MM-DD)'),
                    to: z.string().describe('Last day the file was exported for (YYYY-MM-DD)'),
                  })
                  .passthrough()
                  .describe(
                    'A refusal that turns on the period the file covers (EMPTY_EXPORT of a Freedom24 broker report whose period holds no transaction): the days its export dialog took. Structured so the UI names them localized instead of lifting dates out of the English details.',
                  )
                  .optional(),
                gap: z
                  .object({
                    from: z
                      .string()
                      .describe('Last day the earlier statement reaches (YYYY-MM-DD)'),
                    to: z.string().describe('First day the later statement starts (YYYY-MM-DD)'),
                  })
                  .passthrough()
                  .describe(
                    'STATEMENT_WINDOW_GAP only: the period no uploaded or stored statement covers, as the two days to type into the broker export dialog. Structured so the UI renders it localized instead of lifting dates out of the English details.',
                  )
                  .optional(),
                handTyped: z
                  .object({
                    count: z
                      .coerce.number()
                      .int()
                      .describe(
                        'Rows the user entered by hand after the last imported row of the account',
                      ),
                    since: z
                      .string()
                      .describe('Day of the first of them (YYYY-MM-DD); null when there are none')
                      .nullable(),
                  })
                  .passthrough()
                  .describe(
                    'PARTIAL_HISTORY_REPLACE and STATEMENT_WINDOW_GAP only: the hand-typed rows the full-history export they ask for also contains. Non-zero means the user must delete them before importing that export, or they are counted twice. Always present on those two codes.',
                  )
                  .optional(),
                rowsNotBooked: z
                  .array(
                    z
                      .object({
                        account: z
                          .string()
                          .describe(
                            "The account the rows belong to, named the way its book is: the product, the provider and the last characters of its number ('PPE ING ···4321'), never the whole number. An account with a row that cannot be booked is not imported at all.",
                          ),
                        reason: z
                          .enum([
                            'file-unreadable',
                            'missing-amount',
                            'contradictory',
                            'unreadable',
                            'swap-not-closed',
                            'unsupported-product',
                            'unsupported-operation',
                            'account-held-back',
                          ])
                          .describe(
                            'Why the rows were not booked. `unsupported-operation` = an operation of the file the import does not support yet (`label`); `account-held-back` = rows that are fine in an account holding a row that cannot be booked (an account is imported whole or not at all); `unsupported-product` = an account type not supported yet; `missing-amount` = the file prints no amount for the rows; `contradictory` / `unreadable` = the rows do not add up or could not be read; `swap-not-closed` = a switch between funds whose legs do not add up; `file-unreadable` = the file is cut off or damaged (one line for the file, `rows` is 1).',
                          ),
                        rows: z.coerce.number().int().describe('How many rows (1 for `file-unreadable`)'),
                        label: z
                          .string()
                          .describe(
                            '`unsupported-operation` only: the operation as the file spells it. Absent on the one line that counts the rows of the operations beyond the few that are named.',
                          )
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .describe(
                    "FUND_UNIT_ROWS_NOT_BOOKED and ROWS_NOT_RECOGNISED only (the latter: `unsupported-operation` lines, the rest of the file imported): what was left out of a fund-unit history, by reason, with the number of rows. Structured so the UI words it in the user's language.",
                  )
                  .optional(),
                ask: z
                  .object({
                    bookKey: z
                      .string()
                      .describe(
                        'The book the question is about (`broker|currency[|account]`). Echo it back as the key of `bookTargets` on the resubmit; one entry per bookKey.',
                      ),
                    reason: z
                      .enum([
                        'disjoint-window',
                        'no-dated-rows',
                        'other-currency',
                        'fewer-rows',
                        'several-accounts',
                        'empty-book',
                        'account-not-printed',
                      ])
                      .describe(
                        "Why the routing could not decide: `no-dated-rows` = the upload has no dated row to compare with the candidates; `disjoint-window` = every row falls outside a candidate's own window; `several-accounts` = the statement names two or more of the user's own accounts at the broker (`accounts`), so nothing proves it is ONE account's export; `empty-book` = no book of this account exists yet, but the user has empty portfolios they made by hand in its currency (the candidates), so the statement may be meant for one of them; `account-not-printed` = the statement prints no account number (Kraken) and the user already has a book of the broker whose rows it does not restate: that account, or another one (R178); `other-currency` / `fewer-rows` are reserved (not emitted yet).",
                      ),
                    accounts: z
                      .array(z.string())
                      .describe(
                        'several-accounts only: the account numbers the statement names, as printed, sorted.',
                      )
                      .optional(),
                    candidates: z
                      .array(
                        z
                          .object({
                            portfolioId: z
                              .string()
                              .describe('A portfolio the statement may continue'),
                            name: z.string().describe('Portfolio name'),
                            currency: z.string().describe('Portfolio currency'),
                            window: z
                              .object({
                                from: z
                                  .string()
                                  .describe('First day of its imported rows (YYYY-MM-DD)'),
                                to: z
                                  .string()
                                  .describe('Last day of its imported rows (YYYY-MM-DD)'),
                              })
                              .passthrough()
                              .describe(
                                'The span its earlier imports wrote; null when it holds no imported row',
                              )
                              .nullable(),
                          })
                          .passthrough(),
                      )
                      .describe(
                        "The existing portfolios to choose from, oldest first; 'a new portfolio' is always the other answer. Empty only for `several-accounts` on an account with no book yet.",
                      ),
                  })
                  .passthrough()
                  .describe(
                    "AMBIGUOUS_TARGET only: the question the user must answer before this book is written. Nothing was written for it; resubmit the same files with `bookTargets = { [bookKey]: portfolioId | 'new' }`.",
                  )
                  .optional(),
                files: z
                  .array(z.string())
                  .describe(
                    'The uploaded files this entry is about, as the user named them, when it is about more than the one file `filename` names: a refused book read from several files, or one currency group of a file. The results list each of these files with this entry.',
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Set when the entry refuses ONE currency group of its files (the by-currency import writes a book per currency): that currency. The groups of the same files in other currencies may have been imported, so the file is partly imported, not refused.',
                  )
                  .optional(),
              })
              .passthrough(),
          )
          .describe('List of errors if any'),
        skippedTransactions: z
          .array(
            z
              .object({
                date: z.string().describe('Transaction date'),
                amount: z.coerce.number().describe('Transaction amount'),
                type: z.string().describe('Transaction type (BUY, SELL, DEPOSIT, etc.)'),
                symbol: z.string().describe('Symbol for investment transactions').optional(),
                reason: z
                  .enum(['duplicate', 'invalid_date', 'future_date'])
                  .describe(
                    'Reason for skipping: a duplicate row, an unreadable date, or a date after today',
                  ),
              })
              .passthrough(),
          )
          .describe('List of skipped duplicate transactions'),
        unpricedIncome: z
          .array(
            z
              .object({
                symbol: z.string().describe('The statement ticker of the receipt'),
                date: z.string().describe('The day it was received (YYYY-MM-DD)'),
                unpricedReason: z
                  .literal('NO_CLOSE_ON_RECEIPT_DAY')
                  .describe('NO_CLOSE_ON_RECEIPT_DAY: no market close exists for that day'),
              })
              .passthrough(),
          )
          .describe(
            'Promotional shares and staking rewards (owner ruling D42: income at fair value on the day received) that no close could price: booked at zero cost, no income',
          )
          .optional(),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `No files provided or invalid file format`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 413,
        description: `File size exceeds limit`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/onboarding/reset-session',
    alias: 'usersResetOnboardingSession',
    description: `Deletes only the wallets and portfolios created during the current onboarding session, without affecting pre-existing data.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersResetOnboardingSession_Body,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Some items do not belong to this user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/preferences',
    alias: 'usersGetPreferences',
    requestFormat: 'json',
    response: PreferencesResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/users/preferences',
    alias: 'usersUpdatePreferences',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersUpdatePreferences_Body,
      },
    ],
    response: PreferencesResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/preferences/custom-benchmarks',
    alias: 'usersGetCustomBenchmarks',
    requestFormat: 'json',
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/preferences/custom-benchmarks',
    alias: 'usersCreateCustomBenchmark',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersCreateCustomBenchmark_Body,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Invalid input data or benchmark already exists`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/users/preferences/custom-benchmarks/:id',
    alias: 'usersDeleteCustomBenchmark',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Custom benchmark not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/preferences/custom-timeframes',
    alias: 'usersGetCustomTimeframes',
    requestFormat: 'json',
    response: z
      .object({ items: z.array(CustomTimeframeResponseDto).describe('List of custom timeframes') })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/preferences/custom-timeframes',
    alias: 'usersCreateCustomTimeframe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersCreateCustomTimeframe_Body,
      },
    ],
    response: CustomTimeframeResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/users/preferences/custom-timeframes/:id',
    alias: 'usersDeleteCustomTimeframe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Custom timeframe not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/profile',
    alias: 'usersGetProfile',
    requestFormat: 'json',
    response: ProfileResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/users/profile',
    alias: 'usersUpdateProfile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: usersUpdateProfile_Body,
      },
    ],
    response: ProfileResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallet-transactions/summary',
    alias: 'walletTransactionsGetSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'type',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'DEPOSIT',
              'WITHDRAW',
              'TRANSFER_IN',
              'TRANSFER_OUT',
              'INVEST',
              'DIVEST',
              'REMOVAL',
              'INTEREST',
              'INTEREST_CHARGE',
              'FEE',
              'FEE_REFUND',
              'TAX_REFUND',
              'DIVIDEND',
              'OPTION_PREMIUM_PAID',
              'OPTION_PREMIUM_RECEIVED',
              'OPTION_EXERCISE',
              'OPTION_ASSIGNMENT',
              'PROFIT',
              'FX_CONVERSION',
            ]),
          )
          .optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'searchSentences',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'deposit',
              'withdrawal',
              'transferTo',
              'transferFrom',
              'realizedProfit',
              'closedCfdResult',
              'portfolioInvestment',
              'portfolioDivestment',
              'topUpForPurchase',
              'dividendReceived',
              'dividendWithholdingTax',
              'optionPremiumPaid',
              'optionPremiumPaidToClose',
              'optionPremiumReceived',
              'optionExerciseBuy',
              'optionExerciseSell',
              'optionAssignmentBuy',
              'optionAssignmentSell',
              'treasuryBondCoupon',
              'treasuryBondMaturity',
              'treasuryBondEarly',
              'bondCoupon',
              'bondMaturity',
              'bondEarly',
              'interestTax',
              'earlyRedemptionFee',
              'eventCommission',
              'eventCustodyFee',
              'eventAdrFee',
              'eventRegulatoryFee',
              'eventFxFee',
              'eventAdvisoryFee',
              'eventFee',
              'eventFeeRefund',
              'eventTransactionTax',
              'eventInterestTax',
              'eventDividendTax',
              'eventInterest',
              'eventBondCoupon',
              'eventSecuritiesLending',
              'typedTransaction',
              'incomeInKind',
              'labelledIncomeInKind',
              'importBaselineFunding',
              'snaptradeOpeningBalance',
              'snaptradeBaselineFunding',
              'snaptradeFxConversion',
              'snaptradeFxResidualOnDay',
              'snaptradeFxResidual',
              'snaptradeOpeningLot',
              'snaptradeBrokerHolding',
              'snaptradeOpeningBasis',
              'snaptradeCommission',
              'snaptradeOptionLeg',
              'allocationFromClosedSheet',
              'allocationFromOpenSheet',
              'allocationFromBothSheets',
              'allocationOpenSheet',
              'allocationOpenSheetHealH3',
            ]),
          )
          .optional(),
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'walletId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'walletIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z
      .object({
        allAccounts: z
          .boolean()
          .describe(
            'TRUE for the all-wallets view, where transfers between the user wallets are left out of deposits and withdrawals (they cancel in the net only within one currency). FALSE for one wallet or a part of them, where every transfer leg is money in or out of the view.',
          ),
        currencies: z
          .array(
            z
              .object({
                currency: z.string().describe('Row currency code; sums never cross currencies'),
                deposits: z
                  .coerce.number()
                  .describe(
                    'Money in: deposits plus, for one wallet, transfers in in the row currency',
                  ),
                depositCount: z.coerce.number().int().describe('Number of entries behind `deposits`'),
                withdrawals: z
                  .coerce.number()
                  .describe(
                    'Money out as a positive figure: withdrawals, removals, transfers out in the row currency',
                  ),
                withdrawalCount: z
                  .coerce.number()
                  .int()
                  .describe('Number of entries behind `withdrawals`'),
                fees: z
                  .coerce.number()
                  .describe(
                    'Fees minus fee refunds plus interest charges, positive when it cost money in the row currency',
                  ),
                feeCount: z
                  .coerce.number()
                  .int()
                  .describe('Number of fee, fee refund and interest charge entries'),
                net: z
                  .coerce.number()
                  .describe(
                    'Signed sum of every entry in view, analytics-only rows excluded in the row currency',
                  ),
                netCount: z.coerce.number().int().describe('Number of entries behind `net`'),
                internalTransferIn: z
                  .coerce.number()
                  .describe('Transfers in from the user other wallets in the row currency'),
                internalTransferOut: z
                  .coerce.number()
                  .describe(
                    'Transfers out to the user other wallets, positive in the row currency',
                  ),
              })
              .passthrough(),
          )
          .describe('One row per currency that has entries in view, ordered by currency'),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/wallets',
    alias: 'walletsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsCreate_Body,
      },
    ],
    response: WalletResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Wallet with this name already exists`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets',
    alias: 'walletsFindAll',
    requestFormat: 'json',
    response: z.array(WalletResponseDto),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id',
    alias: 'walletsFindOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: WalletResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/wallets/:id',
    alias: 'walletsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: WalletResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/wallets/:id',
    alias: 'walletsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        message: z.string(),
        affectedPortfolioIds: z
          .array(z.string())
          .describe('IDs of portfolios that had positions deleted due to this wallet deletion'),
      })
      .partial()
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The wallet is the ONLY wallet of a regular portfolio (mandatory-cash-wallet invariant, #862) and cannot be deleted on its own. Body carries code WALLET_LINKED_TO_PORTFOLIO plus the blocking portfolios; delete the portfolio (its wallet cascades, #957) or add another wallet to it first. A wallet a portfolio holds beside another is deleted: its main role passes to the next wallet (owner R163).`,
        schema: z
          .object({
            code: z.string(),
            message: z.string(),
            portfolios: z.array(z.object({ id: z.string(), name: z.string() }).passthrough()),
          })
          .partial()
          .passthrough(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id/balance-history',
    alias: 'walletsGetBalanceHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'period',
        type: 'Query',
        schema: z
          .enum(['7d', '30d', '90d', '1y', '2y', '3y', '5y', '10y', 'all'])
          .optional()
          .default('30d'),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'timeZone',
        type: 'Query',
        schema: z
          .enum([
            'Africa/Abidjan',
            'Africa/Accra',
            'Africa/Addis_Ababa',
            'Africa/Algiers',
            'Africa/Asmera',
            'Africa/Bamako',
            'Africa/Bangui',
            'Africa/Banjul',
            'Africa/Bissau',
            'Africa/Blantyre',
            'Africa/Brazzaville',
            'Africa/Bujumbura',
            'Africa/Cairo',
            'Africa/Casablanca',
            'Africa/Ceuta',
            'Africa/Conakry',
            'Africa/Dakar',
            'Africa/Dar_es_Salaam',
            'Africa/Djibouti',
            'Africa/Douala',
            'Africa/El_Aaiun',
            'Africa/Freetown',
            'Africa/Gaborone',
            'Africa/Harare',
            'Africa/Johannesburg',
            'Africa/Juba',
            'Africa/Kampala',
            'Africa/Khartoum',
            'Africa/Kigali',
            'Africa/Kinshasa',
            'Africa/Lagos',
            'Africa/Libreville',
            'Africa/Lome',
            'Africa/Luanda',
            'Africa/Lubumbashi',
            'Africa/Lusaka',
            'Africa/Malabo',
            'Africa/Maputo',
            'Africa/Maseru',
            'Africa/Mbabane',
            'Africa/Mogadishu',
            'Africa/Monrovia',
            'Africa/Nairobi',
            'Africa/Ndjamena',
            'Africa/Niamey',
            'Africa/Nouakchott',
            'Africa/Ouagadougou',
            'Africa/Porto-Novo',
            'Africa/Sao_Tome',
            'Africa/Tripoli',
            'Africa/Tunis',
            'Africa/Windhoek',
            'America/Adak',
            'America/Anchorage',
            'America/Anguilla',
            'America/Antigua',
            'America/Araguaina',
            'America/Argentina/La_Rioja',
            'America/Argentina/Rio_Gallegos',
            'America/Argentina/Salta',
            'America/Argentina/San_Juan',
            'America/Argentina/San_Luis',
            'America/Argentina/Tucuman',
            'America/Argentina/Ushuaia',
            'America/Aruba',
            'America/Asuncion',
            'America/Bahia',
            'America/Bahia_Banderas',
            'America/Barbados',
            'America/Belem',
            'America/Belize',
            'America/Blanc-Sablon',
            'America/Boa_Vista',
            'America/Bogota',
            'America/Boise',
            'America/Buenos_Aires',
            'America/Cambridge_Bay',
            'America/Campo_Grande',
            'America/Cancun',
            'America/Caracas',
            'America/Catamarca',
            'America/Cayenne',
            'America/Cayman',
            'America/Chicago',
            'America/Chihuahua',
            'America/Ciudad_Juarez',
            'America/Coral_Harbour',
            'America/Cordoba',
            'America/Costa_Rica',
            'America/Coyhaique',
            'America/Creston',
            'America/Cuiaba',
            'America/Curacao',
            'America/Danmarkshavn',
            'America/Dawson',
            'America/Dawson_Creek',
            'America/Denver',
            'America/Detroit',
            'America/Dominica',
            'America/Edmonton',
            'America/Eirunepe',
            'America/El_Salvador',
            'America/Fort_Nelson',
            'America/Fortaleza',
            'America/Glace_Bay',
            'America/Godthab',
            'America/Goose_Bay',
            'America/Grand_Turk',
            'America/Grenada',
            'America/Guadeloupe',
            'America/Guatemala',
            'America/Guayaquil',
            'America/Guyana',
            'America/Halifax',
            'America/Havana',
            'America/Hermosillo',
            'America/Indiana/Knox',
            'America/Indiana/Marengo',
            'America/Indiana/Petersburg',
            'America/Indiana/Tell_City',
            'America/Indiana/Vevay',
            'America/Indiana/Vincennes',
            'America/Indiana/Winamac',
            'America/Indianapolis',
            'America/Inuvik',
            'America/Iqaluit',
            'America/Jamaica',
            'America/Jujuy',
            'America/Juneau',
            'America/Kentucky/Monticello',
            'America/Kralendijk',
            'America/La_Paz',
            'America/Lima',
            'America/Los_Angeles',
            'America/Louisville',
            'America/Lower_Princes',
            'America/Maceio',
            'America/Managua',
            'America/Manaus',
            'America/Marigot',
            'America/Martinique',
            'America/Matamoros',
            'America/Mazatlan',
            'America/Mendoza',
            'America/Menominee',
            'America/Merida',
            'America/Metlakatla',
            'America/Mexico_City',
            'America/Miquelon',
            'America/Moncton',
            'America/Monterrey',
            'America/Montevideo',
            'America/Montserrat',
            'America/Nassau',
            'America/New_York',
            'America/Nome',
            'America/Noronha',
            'America/North_Dakota/Beulah',
            'America/North_Dakota/Center',
            'America/North_Dakota/New_Salem',
            'America/Ojinaga',
            'America/Panama',
            'America/Paramaribo',
            'America/Phoenix',
            'America/Port-au-Prince',
            'America/Port_of_Spain',
            'America/Porto_Velho',
            'America/Puerto_Rico',
            'America/Punta_Arenas',
            'America/Rankin_Inlet',
            'America/Recife',
            'America/Regina',
            'America/Resolute',
            'America/Rio_Branco',
            'America/Santarem',
            'America/Santiago',
            'America/Santo_Domingo',
            'America/Sao_Paulo',
            'America/Scoresbysund',
            'America/Sitka',
            'America/St_Barthelemy',
            'America/St_Johns',
            'America/St_Kitts',
            'America/St_Lucia',
            'America/St_Thomas',
            'America/St_Vincent',
            'America/Swift_Current',
            'America/Tegucigalpa',
            'America/Thule',
            'America/Tijuana',
            'America/Toronto',
            'America/Tortola',
            'America/Vancouver',
            'America/Whitehorse',
            'America/Winnipeg',
            'America/Yakutat',
            'Antarctica/Casey',
            'Antarctica/Davis',
            'Antarctica/DumontDUrville',
            'Antarctica/Macquarie',
            'Antarctica/Mawson',
            'Antarctica/McMurdo',
            'Antarctica/Palmer',
            'Antarctica/Rothera',
            'Antarctica/Syowa',
            'Antarctica/Troll',
            'Antarctica/Vostok',
            'Arctic/Longyearbyen',
            'Asia/Aden',
            'Asia/Almaty',
            'Asia/Amman',
            'Asia/Anadyr',
            'Asia/Aqtau',
            'Asia/Aqtobe',
            'Asia/Ashgabat',
            'Asia/Atyrau',
            'Asia/Baghdad',
            'Asia/Bahrain',
            'Asia/Baku',
            'Asia/Bangkok',
            'Asia/Barnaul',
            'Asia/Beirut',
            'Asia/Bishkek',
            'Asia/Brunei',
            'Asia/Calcutta',
            'Asia/Chita',
            'Asia/Colombo',
            'Asia/Damascus',
            'Asia/Dhaka',
            'Asia/Dili',
            'Asia/Dubai',
            'Asia/Dushanbe',
            'Asia/Famagusta',
            'Asia/Gaza',
            'Asia/Hebron',
            'Asia/Hong_Kong',
            'Asia/Hovd',
            'Asia/Irkutsk',
            'Asia/Jakarta',
            'Asia/Jayapura',
            'Asia/Jerusalem',
            'Asia/Kabul',
            'Asia/Kamchatka',
            'Asia/Karachi',
            'Asia/Katmandu',
            'Asia/Khandyga',
            'Asia/Krasnoyarsk',
            'Asia/Kuala_Lumpur',
            'Asia/Kuching',
            'Asia/Kuwait',
            'Asia/Macau',
            'Asia/Magadan',
            'Asia/Makassar',
            'Asia/Manila',
            'Asia/Muscat',
            'Asia/Nicosia',
            'Asia/Novokuznetsk',
            'Asia/Novosibirsk',
            'Asia/Omsk',
            'Asia/Oral',
            'Asia/Phnom_Penh',
            'Asia/Pontianak',
            'Asia/Pyongyang',
            'Asia/Qatar',
            'Asia/Qostanay',
            'Asia/Qyzylorda',
            'Asia/Rangoon',
            'Asia/Riyadh',
            'Asia/Saigon',
            'Asia/Sakhalin',
            'Asia/Samarkand',
            'Asia/Seoul',
            'Asia/Shanghai',
            'Asia/Singapore',
            'Asia/Srednekolymsk',
            'Asia/Taipei',
            'Asia/Tashkent',
            'Asia/Tbilisi',
            'Asia/Tehran',
            'Asia/Thimphu',
            'Asia/Tokyo',
            'Asia/Tomsk',
            'Asia/Ulaanbaatar',
            'Asia/Urumqi',
            'Asia/Ust-Nera',
            'Asia/Vientiane',
            'Asia/Vladivostok',
            'Asia/Yakutsk',
            'Asia/Yekaterinburg',
            'Asia/Yerevan',
            'Atlantic/Azores',
            'Atlantic/Bermuda',
            'Atlantic/Canary',
            'Atlantic/Cape_Verde',
            'Atlantic/Faeroe',
            'Atlantic/Madeira',
            'Atlantic/Reykjavik',
            'Atlantic/South_Georgia',
            'Atlantic/St_Helena',
            'Atlantic/Stanley',
            'Australia/Adelaide',
            'Australia/Brisbane',
            'Australia/Broken_Hill',
            'Australia/Darwin',
            'Australia/Eucla',
            'Australia/Hobart',
            'Australia/Lindeman',
            'Australia/Lord_Howe',
            'Australia/Melbourne',
            'Australia/Perth',
            'Australia/Sydney',
            'Europe/Amsterdam',
            'Europe/Andorra',
            'Europe/Astrakhan',
            'Europe/Athens',
            'Europe/Belgrade',
            'Europe/Berlin',
            'Europe/Bratislava',
            'Europe/Brussels',
            'Europe/Bucharest',
            'Europe/Budapest',
            'Europe/Busingen',
            'Europe/Chisinau',
            'Europe/Copenhagen',
            'Europe/Dublin',
            'Europe/Gibraltar',
            'Europe/Guernsey',
            'Europe/Helsinki',
            'Europe/Isle_of_Man',
            'Europe/Istanbul',
            'Europe/Jersey',
            'Europe/Kaliningrad',
            'Europe/Kiev',
            'Europe/Kirov',
            'Europe/Lisbon',
            'Europe/Ljubljana',
            'Europe/London',
            'Europe/Luxembourg',
            'Europe/Madrid',
            'Europe/Malta',
            'Europe/Mariehamn',
            'Europe/Minsk',
            'Europe/Monaco',
            'Europe/Moscow',
            'Europe/Oslo',
            'Europe/Paris',
            'Europe/Podgorica',
            'Europe/Prague',
            'Europe/Riga',
            'Europe/Rome',
            'Europe/Samara',
            'Europe/San_Marino',
            'Europe/Sarajevo',
            'Europe/Saratov',
            'Europe/Simferopol',
            'Europe/Skopje',
            'Europe/Sofia',
            'Europe/Stockholm',
            'Europe/Tallinn',
            'Europe/Tirane',
            'Europe/Ulyanovsk',
            'Europe/Vaduz',
            'Europe/Vatican',
            'Europe/Vienna',
            'Europe/Vilnius',
            'Europe/Volgograd',
            'Europe/Warsaw',
            'Europe/Zagreb',
            'Europe/Zurich',
            'Indian/Antananarivo',
            'Indian/Chagos',
            'Indian/Christmas',
            'Indian/Cocos',
            'Indian/Comoro',
            'Indian/Kerguelen',
            'Indian/Mahe',
            'Indian/Maldives',
            'Indian/Mauritius',
            'Indian/Mayotte',
            'Indian/Reunion',
            'Pacific/Apia',
            'Pacific/Auckland',
            'Pacific/Bougainville',
            'Pacific/Chatham',
            'Pacific/Easter',
            'Pacific/Efate',
            'Pacific/Enderbury',
            'Pacific/Fakaofo',
            'Pacific/Fiji',
            'Pacific/Funafuti',
            'Pacific/Galapagos',
            'Pacific/Gambier',
            'Pacific/Guadalcanal',
            'Pacific/Guam',
            'Pacific/Honolulu',
            'Pacific/Kiritimati',
            'Pacific/Kosrae',
            'Pacific/Kwajalein',
            'Pacific/Majuro',
            'Pacific/Marquesas',
            'Pacific/Midway',
            'Pacific/Nauru',
            'Pacific/Niue',
            'Pacific/Norfolk',
            'Pacific/Noumea',
            'Pacific/Pago_Pago',
            'Pacific/Palau',
            'Pacific/Pitcairn',
            'Pacific/Ponape',
            'Pacific/Port_Moresby',
            'Pacific/Rarotonga',
            'Pacific/Saipan',
            'Pacific/Tahiti',
            'Pacific/Tarawa',
            'Pacific/Tongatapu',
            'Pacific/Truk',
            'Pacific/Wake',
            'Pacific/Wallis',
            'UTC',
          ])
          .optional()
          .default('UTC'),
      },
      {
        name: 'resolution',
        type: 'Query',
        schema: z.enum(['hour', 'day', 'week', 'month']).optional(),
      },
      {
        name: 'walletIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z
      .object({
        data: z
          .array(
            z
              .object({
                date: z.string().describe('Date of the balance snapshot'),
                balance: z.coerce.number().describe('Balance at this point in time'),
                change: z.coerce.number().describe('Change from previous period'),
                percentChange: z
                  .coerce.number()
                  .describe(
                    'Percentage change from previous period; null when that balance was zero or overdrawn (a percent of a negative base has no meaningful sign)',
                  )
                  .nullable(),
                high: z.coerce.number().describe('Highest balance during this period (intraday high)'),
                low: z.coerce.number().describe('Lowest balance during this period (intraday low)'),
              })
              .passthrough(),
          )
          .describe('Array of balance history data points'),
        meta: z
          .object({
            period: z.string().describe('Period of the data'),
            resolution: z.string().describe('Resolution of the data'),
            high: z.coerce.number().describe('Highest balance in the period'),
            low: z.coerce.number().describe('Lowest balance in the period'),
            average: z.coerce.number().describe('Average of the daily closing balances in the period'),
            startDate: z.string().describe('Start date of the period'),
            endDate: z.string().describe('End date of the period'),
            change: z
              .coerce.number()
              .describe(
                'Last closing balance minus the balance before the period (opening balance)',
              ),
            percentChange: z
              .coerce.number()
              .describe(
                'Percentage change over the period, from the balance before its first day; null when that opening balance was zero or overdrawn and the balance moved',
              )
              .nullable(),
          })
          .passthrough()
          .describe('Metadata about the balance history'),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `walletIds span more than one currency`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id/deletion-impact',
    alias: 'walletsGetDeletionImpact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z
      .object({
        walletId: z.string(),
        balance: z.coerce.number().describe('Current wallet balance in `currency`'),
        currency: z.string(),
        isOrphan: z
          .boolean()
          .describe('True when no portfolio holds the wallet as home or cash side'),
        walletTransactions: z
          .object({
            total: z.coerce.number(),
            byKind: z.record(z.coerce.number()).describe('Live row count per transaction type'),
            latest: z
              .array(
                z
                  .object({
                    id: z.string(),
                    date: z.string().datetime({ offset: true }),
                    kind: z
                      .string()
                      .describe('Wallet transaction type, e.g. DEPOSIT, WITHDRAW, FEE'),
                    amount: z.coerce.number().describe('Signed amount in `currency`'),
                    currency: z.string(),
                  })
                  .passthrough(),
              )
              .describe('The 10 most recent rows'),
          })
          .passthrough(),
        portfolioTransactions: z
          .object({
            total: z.coerce.number().describe('Live portfolio transactions settled in this wallet'),
            latest: z
              .array(
                z
                  .object({
                    id: z.string(),
                    date: z.string().datetime({ offset: true }),
                    side: z
                      .string()
                      .describe('Portfolio transaction type, e.g. BUY, SELL, DIVIDEND'),
                    symbol: z.string(),
                    name: z
                      .string()
                      .describe(
                        'The holding as the user reads it: a fund unit by its statement name, else the symbol',
                      ),
                    assetType: z.string(),
                    quantity: z.coerce.number(),
                    amount: z.coerce.number().describe('Total amount in `currency`'),
                    currency: z.string(),
                  })
                  .passthrough(),
              )
              .describe('The 10 most recent rows'),
          })
          .passthrough(),
        books: z
          .array(z.object({ id: z.string(), name: z.string() }).passthrough())
          .describe('Distinct portfolios whose live rows touch this wallet'),
        symbols: z
          .object({
            total: z.coerce.number().describe('Distinct instrument symbols those trades touch'),
            sample: z.array(z.string()).describe('Up to 10 of them, alphabetical'),
          })
          .passthrough(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/wallets/:id/deposit',
    alias: 'walletsDeposit',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsDeposit_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: WalletResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id/portfolio-transactions-count',
    alias: 'walletsGetPortfolioTransactionsCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ count: z.coerce.number() }).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id/transactions',
    alias: 'walletsGetTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'DEPOSIT',
              'WITHDRAW',
              'TRANSFER_IN',
              'TRANSFER_OUT',
              'INVEST',
              'DIVEST',
              'REMOVAL',
              'INTEREST',
              'INTEREST_CHARGE',
              'FEE',
              'FEE_REFUND',
              'TAX_REFUND',
              'DIVIDEND',
              'OPTION_PREMIUM_PAID',
              'OPTION_PREMIUM_RECEIVED',
              'OPTION_EXERCISE',
              'OPTION_ASSIGNMENT',
              'PROFIT',
              'FX_CONVERSION',
            ]),
          )
          .optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'searchSentences',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'deposit',
              'withdrawal',
              'transferTo',
              'transferFrom',
              'realizedProfit',
              'closedCfdResult',
              'portfolioInvestment',
              'portfolioDivestment',
              'topUpForPurchase',
              'dividendReceived',
              'dividendWithholdingTax',
              'optionPremiumPaid',
              'optionPremiumPaidToClose',
              'optionPremiumReceived',
              'optionExerciseBuy',
              'optionExerciseSell',
              'optionAssignmentBuy',
              'optionAssignmentSell',
              'treasuryBondCoupon',
              'treasuryBondMaturity',
              'treasuryBondEarly',
              'bondCoupon',
              'bondMaturity',
              'bondEarly',
              'interestTax',
              'earlyRedemptionFee',
              'eventCommission',
              'eventCustodyFee',
              'eventAdrFee',
              'eventRegulatoryFee',
              'eventFxFee',
              'eventAdvisoryFee',
              'eventFee',
              'eventFeeRefund',
              'eventTransactionTax',
              'eventInterestTax',
              'eventDividendTax',
              'eventInterest',
              'eventBondCoupon',
              'eventSecuritiesLending',
              'typedTransaction',
              'incomeInKind',
              'labelledIncomeInKind',
              'importBaselineFunding',
              'snaptradeOpeningBalance',
              'snaptradeBaselineFunding',
              'snaptradeFxConversion',
              'snaptradeFxResidualOnDay',
              'snaptradeFxResidual',
              'snaptradeOpeningLot',
              'snaptradeBrokerHolding',
              'snaptradeOpeningBasis',
              'snaptradeCommission',
              'snaptradeOptionLeg',
              'allocationFromClosedSheet',
              'allocationFromOpenSheet',
              'allocationFromBothSheets',
              'allocationOpenSheet',
              'allocationOpenSheetHealH3',
            ]),
          )
          .optional(),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z.enum(['date', 'amount', 'type']).optional().default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
    ],
    response: z
      .object({
        data: z
          .array(
            z
              .object({
                id: z.string().describe('Transaction ID'),
                walletId: z.string().describe('Wallet ID'),
                walletName: z
                  .string()
                  .describe(
                    "The owning wallet's name. Served on the CROSS-wallet list, where a row cannot be read without it. The client used to join this itself against its own wallets query, which printed a hardcoded 'Unknown Wallet' whenever the join missed — including for every row while that second query was still in flight. Absent on the single-wallet history, whose rows all belong to the wallet already named on the page.",
                  )
                  .optional(),
                type: z
                  .enum([
                    'DEPOSIT',
                    'WITHDRAW',
                    'TRANSFER_IN',
                    'TRANSFER_OUT',
                    'INVEST',
                    'DIVEST',
                    'REMOVAL',
                    'INTEREST',
                    'INTEREST_CHARGE',
                    'FEE',
                    'FEE_REFUND',
                    'TAX_REFUND',
                    'DIVIDEND',
                    'OPTION_PREMIUM_PAID',
                    'OPTION_PREMIUM_RECEIVED',
                    'OPTION_EXERCISE',
                    'OPTION_ASSIGNMENT',
                    'PROFIT',
                    'FX_CONVERSION',
                  ])
                  .describe('Transaction type'),
                displayLabel: z
                  .string()
                  .describe(
                    "Human-readable transaction type label. Names the row as it is SHOWN, which is not always its booking: an own-account move (`isInternalTransfer`) booked as a deposit or a withdrawal reads 'Transfer In' / 'Transfer Out' by the sign of its amount.",
                  ),
                amount: z.coerce.number().describe('Transaction amount'),
                currency: z.string().describe('Currency code'),
                exchangeRate: z
                  .coerce.number()
                  .describe('Exchange rate (for currency conversion)')
                  .nullish(),
                reference: z.string().describe('Transaction reference').nullish(),
                analyticsOnly: z
                  .boolean()
                  .describe(
                    'TRUE when this row never moved cash: realized profit/loss the ledger records for analytics only. Computed ONCE here by the kernel predicate `isAnalyticsOnlyTxn` (type PROFIT, or a PORTFOLIO_PROFIT_ reference on any type). REQUIRED, never optional: a client that re-derived the rule would drift from the SQL that already excludes these rows from the running balance, which is exactly the B12 defect. A user summed the amount column of their own ledger and got 1 009,45 more than their 9,25 balance, because nothing on the wire told the two kinds of row apart.',
                  ),
                isInternalTransfer: z
                  .boolean()
                  .describe(
                    "TRUE when this row is money moving between two of the user's OWN accounts (an XTB ISA sub-account, an IBKR internal transfer), not money from or to a bank. It may still be BOOKED as DEPOSIT / WITHDRAW / REMOVAL, since per book it is that book's capital (owner ruling E#1), so `type` keeps the booking and `displayLabel` names it a transfer. REQUIRED: absent would read as 'not a transfer', the defect it ends (an XTB wallet listed its ISA moves as deposits).",
                  ),
                contributionSource: z
                  .enum(['EMPLOYEE', 'EMPLOYER', 'STATE', 'UNSTATED'])
                  .describe(
                    "WHO paid in this pension-plan flow (PPK / PPE, owner ruling R59, plan decision O8): EMPLOYEE, EMPLOYER or STATE (the state's welcome and annual payments) when the file names the payer, UNSTATED when it is a pension-plan deposit or payout and the file does not say who paid. Null on every row that is no pension-plan contribution (a portfolio deposit, an IKE, a fee): the user's own money. REQUIRED, never optional: absent would read as 'the user's own money', the one thing this field exists to tell apart from what the employer and the state add.",
                  )
                  .nullable(),
                portfolioTransactionId: z
                  .string()
                  .describe(
                    'The portfolio transaction this cash leg belongs to (a dividend, a trade), or null for a row booked on the wallet alone. REQUIRED, never optional: a client edits a linked dividend through its portfolio transaction, and absent would send it to the bare cash edit, which leaves the holding behind.',
                  )
                  .nullable(),
                convertedAmount: z
                  .coerce.number()
                  .describe(
                    'APPROXIMATE `amount` in `displayCurrency` at the rate stored for the row trade day, read at request time and never stored. Set only when the row currency differs from the requested display currency and a rate exists.',
                  )
                  .optional(),
                displayCurrency: z
                  .string()
                  .describe('The currency of `convertedAmount`; set with it')
                  .optional(),
                date: z.string().describe('Transaction date'),
                description: z.string().describe('Transaction description').nullish(),
                createdAt: z.string().describe('Creation timestamp'),
                balance: z.coerce.number().describe('Running balance after this transaction'),
              })
              .passthrough(),
          )
          .describe('List of transactions with running balance'),
        meta: PaginationMetaDto.describe('Pagination metadata'),
        omittedNoRate: z
          .coerce.number()
          .int()
          .describe(
            'Rows an active amount window left out only because no stored rate carries them into the display currency (they are never compared in their own currency)',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests - rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/:id/transactions/export',
    alias: 'walletsExportTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'DEPOSIT',
              'WITHDRAW',
              'TRANSFER_IN',
              'TRANSFER_OUT',
              'INVEST',
              'DIVEST',
              'REMOVAL',
              'INTEREST',
              'INTEREST_CHARGE',
              'FEE',
              'FEE_REFUND',
              'TAX_REFUND',
              'DIVIDEND',
              'OPTION_PREMIUM_PAID',
              'OPTION_PREMIUM_RECEIVED',
              'OPTION_EXERCISE',
              'OPTION_ASSIGNMENT',
              'PROFIT',
              'FX_CONVERSION',
            ]),
          )
          .optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'searchSentences',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'deposit',
              'withdrawal',
              'transferTo',
              'transferFrom',
              'realizedProfit',
              'closedCfdResult',
              'portfolioInvestment',
              'portfolioDivestment',
              'topUpForPurchase',
              'dividendReceived',
              'dividendWithholdingTax',
              'optionPremiumPaid',
              'optionPremiumPaidToClose',
              'optionPremiumReceived',
              'optionExerciseBuy',
              'optionExerciseSell',
              'optionAssignmentBuy',
              'optionAssignmentSell',
              'treasuryBondCoupon',
              'treasuryBondMaturity',
              'treasuryBondEarly',
              'bondCoupon',
              'bondMaturity',
              'bondEarly',
              'interestTax',
              'earlyRedemptionFee',
              'eventCommission',
              'eventCustodyFee',
              'eventAdrFee',
              'eventRegulatoryFee',
              'eventFxFee',
              'eventAdvisoryFee',
              'eventFee',
              'eventFeeRefund',
              'eventTransactionTax',
              'eventInterestTax',
              'eventDividendTax',
              'eventInterest',
              'eventBondCoupon',
              'eventSecuritiesLending',
              'typedTransaction',
              'incomeInKind',
              'labelledIncomeInKind',
              'importBaselineFunding',
              'snaptradeOpeningBalance',
              'snaptradeBaselineFunding',
              'snaptradeFxConversion',
              'snaptradeFxResidualOnDay',
              'snaptradeFxResidual',
              'snaptradeOpeningLot',
              'snaptradeBrokerHolding',
              'snaptradeOpeningBasis',
              'snaptradeCommission',
              'snaptradeOptionLeg',
              'allocationFromClosedSheet',
              'allocationFromOpenSheet',
              'allocationFromBothSheets',
              'allocationOpenSheet',
              'allocationOpenSheetHealH3',
            ]),
          )
          .optional(),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z.enum(['date', 'amount', 'type']).optional().default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['csv', 'pdf']).optional(),
      },
      {
        name: 'types',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'DEPOSIT',
              'WITHDRAW',
              'TRANSFER_IN',
              'TRANSFER_OUT',
              'INVEST',
              'DIVEST',
              'REMOVAL',
              'INTEREST',
              'INTEREST_CHARGE',
              'FEE',
              'FEE_REFUND',
              'TAX_REFUND',
              'DIVIDEND',
              'OPTION_PREMIUM_PAID',
              'OPTION_PREMIUM_RECEIVED',
              'OPTION_EXERCISE',
              'OPTION_ASSIGNMENT',
              'PROFIT',
              'FX_CONVERSION',
            ]),
          )
          .optional(),
      },
      {
        name: 'walletIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: 'timeZone',
        type: 'Query',
        schema: z
          .enum([
            'Africa/Abidjan',
            'Africa/Accra',
            'Africa/Addis_Ababa',
            'Africa/Algiers',
            'Africa/Asmera',
            'Africa/Bamako',
            'Africa/Bangui',
            'Africa/Banjul',
            'Africa/Bissau',
            'Africa/Blantyre',
            'Africa/Brazzaville',
            'Africa/Bujumbura',
            'Africa/Cairo',
            'Africa/Casablanca',
            'Africa/Ceuta',
            'Africa/Conakry',
            'Africa/Dakar',
            'Africa/Dar_es_Salaam',
            'Africa/Djibouti',
            'Africa/Douala',
            'Africa/El_Aaiun',
            'Africa/Freetown',
            'Africa/Gaborone',
            'Africa/Harare',
            'Africa/Johannesburg',
            'Africa/Juba',
            'Africa/Kampala',
            'Africa/Khartoum',
            'Africa/Kigali',
            'Africa/Kinshasa',
            'Africa/Lagos',
            'Africa/Libreville',
            'Africa/Lome',
            'Africa/Luanda',
            'Africa/Lubumbashi',
            'Africa/Lusaka',
            'Africa/Malabo',
            'Africa/Maputo',
            'Africa/Maseru',
            'Africa/Mbabane',
            'Africa/Mogadishu',
            'Africa/Monrovia',
            'Africa/Nairobi',
            'Africa/Ndjamena',
            'Africa/Niamey',
            'Africa/Nouakchott',
            'Africa/Ouagadougou',
            'Africa/Porto-Novo',
            'Africa/Sao_Tome',
            'Africa/Tripoli',
            'Africa/Tunis',
            'Africa/Windhoek',
            'America/Adak',
            'America/Anchorage',
            'America/Anguilla',
            'America/Antigua',
            'America/Araguaina',
            'America/Argentina/La_Rioja',
            'America/Argentina/Rio_Gallegos',
            'America/Argentina/Salta',
            'America/Argentina/San_Juan',
            'America/Argentina/San_Luis',
            'America/Argentina/Tucuman',
            'America/Argentina/Ushuaia',
            'America/Aruba',
            'America/Asuncion',
            'America/Bahia',
            'America/Bahia_Banderas',
            'America/Barbados',
            'America/Belem',
            'America/Belize',
            'America/Blanc-Sablon',
            'America/Boa_Vista',
            'America/Bogota',
            'America/Boise',
            'America/Buenos_Aires',
            'America/Cambridge_Bay',
            'America/Campo_Grande',
            'America/Cancun',
            'America/Caracas',
            'America/Catamarca',
            'America/Cayenne',
            'America/Cayman',
            'America/Chicago',
            'America/Chihuahua',
            'America/Ciudad_Juarez',
            'America/Coral_Harbour',
            'America/Cordoba',
            'America/Costa_Rica',
            'America/Coyhaique',
            'America/Creston',
            'America/Cuiaba',
            'America/Curacao',
            'America/Danmarkshavn',
            'America/Dawson',
            'America/Dawson_Creek',
            'America/Denver',
            'America/Detroit',
            'America/Dominica',
            'America/Edmonton',
            'America/Eirunepe',
            'America/El_Salvador',
            'America/Fort_Nelson',
            'America/Fortaleza',
            'America/Glace_Bay',
            'America/Godthab',
            'America/Goose_Bay',
            'America/Grand_Turk',
            'America/Grenada',
            'America/Guadeloupe',
            'America/Guatemala',
            'America/Guayaquil',
            'America/Guyana',
            'America/Halifax',
            'America/Havana',
            'America/Hermosillo',
            'America/Indiana/Knox',
            'America/Indiana/Marengo',
            'America/Indiana/Petersburg',
            'America/Indiana/Tell_City',
            'America/Indiana/Vevay',
            'America/Indiana/Vincennes',
            'America/Indiana/Winamac',
            'America/Indianapolis',
            'America/Inuvik',
            'America/Iqaluit',
            'America/Jamaica',
            'America/Jujuy',
            'America/Juneau',
            'America/Kentucky/Monticello',
            'America/Kralendijk',
            'America/La_Paz',
            'America/Lima',
            'America/Los_Angeles',
            'America/Louisville',
            'America/Lower_Princes',
            'America/Maceio',
            'America/Managua',
            'America/Manaus',
            'America/Marigot',
            'America/Martinique',
            'America/Matamoros',
            'America/Mazatlan',
            'America/Mendoza',
            'America/Menominee',
            'America/Merida',
            'America/Metlakatla',
            'America/Mexico_City',
            'America/Miquelon',
            'America/Moncton',
            'America/Monterrey',
            'America/Montevideo',
            'America/Montserrat',
            'America/Nassau',
            'America/New_York',
            'America/Nome',
            'America/Noronha',
            'America/North_Dakota/Beulah',
            'America/North_Dakota/Center',
            'America/North_Dakota/New_Salem',
            'America/Ojinaga',
            'America/Panama',
            'America/Paramaribo',
            'America/Phoenix',
            'America/Port-au-Prince',
            'America/Port_of_Spain',
            'America/Porto_Velho',
            'America/Puerto_Rico',
            'America/Punta_Arenas',
            'America/Rankin_Inlet',
            'America/Recife',
            'America/Regina',
            'America/Resolute',
            'America/Rio_Branco',
            'America/Santarem',
            'America/Santiago',
            'America/Santo_Domingo',
            'America/Sao_Paulo',
            'America/Scoresbysund',
            'America/Sitka',
            'America/St_Barthelemy',
            'America/St_Johns',
            'America/St_Kitts',
            'America/St_Lucia',
            'America/St_Thomas',
            'America/St_Vincent',
            'America/Swift_Current',
            'America/Tegucigalpa',
            'America/Thule',
            'America/Tijuana',
            'America/Toronto',
            'America/Tortola',
            'America/Vancouver',
            'America/Whitehorse',
            'America/Winnipeg',
            'America/Yakutat',
            'Antarctica/Casey',
            'Antarctica/Davis',
            'Antarctica/DumontDUrville',
            'Antarctica/Macquarie',
            'Antarctica/Mawson',
            'Antarctica/McMurdo',
            'Antarctica/Palmer',
            'Antarctica/Rothera',
            'Antarctica/Syowa',
            'Antarctica/Troll',
            'Antarctica/Vostok',
            'Arctic/Longyearbyen',
            'Asia/Aden',
            'Asia/Almaty',
            'Asia/Amman',
            'Asia/Anadyr',
            'Asia/Aqtau',
            'Asia/Aqtobe',
            'Asia/Ashgabat',
            'Asia/Atyrau',
            'Asia/Baghdad',
            'Asia/Bahrain',
            'Asia/Baku',
            'Asia/Bangkok',
            'Asia/Barnaul',
            'Asia/Beirut',
            'Asia/Bishkek',
            'Asia/Brunei',
            'Asia/Calcutta',
            'Asia/Chita',
            'Asia/Colombo',
            'Asia/Damascus',
            'Asia/Dhaka',
            'Asia/Dili',
            'Asia/Dubai',
            'Asia/Dushanbe',
            'Asia/Famagusta',
            'Asia/Gaza',
            'Asia/Hebron',
            'Asia/Hong_Kong',
            'Asia/Hovd',
            'Asia/Irkutsk',
            'Asia/Jakarta',
            'Asia/Jayapura',
            'Asia/Jerusalem',
            'Asia/Kabul',
            'Asia/Kamchatka',
            'Asia/Karachi',
            'Asia/Katmandu',
            'Asia/Khandyga',
            'Asia/Krasnoyarsk',
            'Asia/Kuala_Lumpur',
            'Asia/Kuching',
            'Asia/Kuwait',
            'Asia/Macau',
            'Asia/Magadan',
            'Asia/Makassar',
            'Asia/Manila',
            'Asia/Muscat',
            'Asia/Nicosia',
            'Asia/Novokuznetsk',
            'Asia/Novosibirsk',
            'Asia/Omsk',
            'Asia/Oral',
            'Asia/Phnom_Penh',
            'Asia/Pontianak',
            'Asia/Pyongyang',
            'Asia/Qatar',
            'Asia/Qostanay',
            'Asia/Qyzylorda',
            'Asia/Rangoon',
            'Asia/Riyadh',
            'Asia/Saigon',
            'Asia/Sakhalin',
            'Asia/Samarkand',
            'Asia/Seoul',
            'Asia/Shanghai',
            'Asia/Singapore',
            'Asia/Srednekolymsk',
            'Asia/Taipei',
            'Asia/Tashkent',
            'Asia/Tbilisi',
            'Asia/Tehran',
            'Asia/Thimphu',
            'Asia/Tokyo',
            'Asia/Tomsk',
            'Asia/Ulaanbaatar',
            'Asia/Urumqi',
            'Asia/Ust-Nera',
            'Asia/Vientiane',
            'Asia/Vladivostok',
            'Asia/Yakutsk',
            'Asia/Yekaterinburg',
            'Asia/Yerevan',
            'Atlantic/Azores',
            'Atlantic/Bermuda',
            'Atlantic/Canary',
            'Atlantic/Cape_Verde',
            'Atlantic/Faeroe',
            'Atlantic/Madeira',
            'Atlantic/Reykjavik',
            'Atlantic/South_Georgia',
            'Atlantic/St_Helena',
            'Atlantic/Stanley',
            'Australia/Adelaide',
            'Australia/Brisbane',
            'Australia/Broken_Hill',
            'Australia/Darwin',
            'Australia/Eucla',
            'Australia/Hobart',
            'Australia/Lindeman',
            'Australia/Lord_Howe',
            'Australia/Melbourne',
            'Australia/Perth',
            'Australia/Sydney',
            'Europe/Amsterdam',
            'Europe/Andorra',
            'Europe/Astrakhan',
            'Europe/Athens',
            'Europe/Belgrade',
            'Europe/Berlin',
            'Europe/Bratislava',
            'Europe/Brussels',
            'Europe/Bucharest',
            'Europe/Budapest',
            'Europe/Busingen',
            'Europe/Chisinau',
            'Europe/Copenhagen',
            'Europe/Dublin',
            'Europe/Gibraltar',
            'Europe/Guernsey',
            'Europe/Helsinki',
            'Europe/Isle_of_Man',
            'Europe/Istanbul',
            'Europe/Jersey',
            'Europe/Kaliningrad',
            'Europe/Kiev',
            'Europe/Kirov',
            'Europe/Lisbon',
            'Europe/Ljubljana',
            'Europe/London',
            'Europe/Luxembourg',
            'Europe/Madrid',
            'Europe/Malta',
            'Europe/Mariehamn',
            'Europe/Minsk',
            'Europe/Monaco',
            'Europe/Moscow',
            'Europe/Oslo',
            'Europe/Paris',
            'Europe/Podgorica',
            'Europe/Prague',
            'Europe/Riga',
            'Europe/Rome',
            'Europe/Samara',
            'Europe/San_Marino',
            'Europe/Sarajevo',
            'Europe/Saratov',
            'Europe/Simferopol',
            'Europe/Skopje',
            'Europe/Sofia',
            'Europe/Stockholm',
            'Europe/Tallinn',
            'Europe/Tirane',
            'Europe/Ulyanovsk',
            'Europe/Vaduz',
            'Europe/Vatican',
            'Europe/Vienna',
            'Europe/Vilnius',
            'Europe/Volgograd',
            'Europe/Warsaw',
            'Europe/Zagreb',
            'Europe/Zurich',
            'Indian/Antananarivo',
            'Indian/Chagos',
            'Indian/Christmas',
            'Indian/Cocos',
            'Indian/Comoro',
            'Indian/Kerguelen',
            'Indian/Mahe',
            'Indian/Maldives',
            'Indian/Mauritius',
            'Indian/Mayotte',
            'Indian/Reunion',
            'Pacific/Apia',
            'Pacific/Auckland',
            'Pacific/Bougainville',
            'Pacific/Chatham',
            'Pacific/Easter',
            'Pacific/Efate',
            'Pacific/Enderbury',
            'Pacific/Fakaofo',
            'Pacific/Fiji',
            'Pacific/Funafuti',
            'Pacific/Galapagos',
            'Pacific/Gambier',
            'Pacific/Guadalcanal',
            'Pacific/Guam',
            'Pacific/Honolulu',
            'Pacific/Kiritimati',
            'Pacific/Kosrae',
            'Pacific/Kwajalein',
            'Pacific/Majuro',
            'Pacific/Marquesas',
            'Pacific/Midway',
            'Pacific/Nauru',
            'Pacific/Niue',
            'Pacific/Norfolk',
            'Pacific/Noumea',
            'Pacific/Pago_Pago',
            'Pacific/Palau',
            'Pacific/Pitcairn',
            'Pacific/Ponape',
            'Pacific/Port_Moresby',
            'Pacific/Rarotonga',
            'Pacific/Saipan',
            'Pacific/Tahiti',
            'Pacific/Tarawa',
            'Pacific/Tongatapu',
            'Pacific/Truk',
            'Pacific/Wake',
            'Pacific/Wallis',
            'UTC',
          ])
          .optional()
          .default('UTC'),
      },
      {
        name: 'locale',
        type: 'Query',
        schema: z
          .string()
          .regex(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/)
          .optional()
          .default('en-US'),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests - rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/wallets/:id/withdraw',
    alias: 'walletsWithdraw',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsWithdraw_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: WalletResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/api/wallets/:walletId/transactions/:transactionId',
    alias: 'walletsUpdateTransaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsUpdateTransaction_Body,
      },
      {
        name: 'walletId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'transactionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: WalletTransactionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input data, or an edit the ledger cannot take (an amount/type edit on one transfer leg, a re-type of a CFD close)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet or transaction not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/wallets/:walletId/transactions/:transactionId',
    alias: 'walletsDeleteTransaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'walletId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'transactionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `The row names a booked treasury-bond lot (delete those bookings first)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet or transaction not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/recent-transactions',
    alias: 'walletsGetRecentTransactions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.coerce.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'DEPOSIT',
              'WITHDRAW',
              'TRANSFER_IN',
              'TRANSFER_OUT',
              'INVEST',
              'DIVEST',
              'REMOVAL',
              'INTEREST',
              'INTEREST_CHARGE',
              'FEE',
              'FEE_REFUND',
              'TAX_REFUND',
              'DIVIDEND',
              'OPTION_PREMIUM_PAID',
              'OPTION_PREMIUM_RECEIVED',
              'OPTION_EXERCISE',
              'OPTION_ASSIGNMENT',
              'PROFIT',
              'FX_CONVERSION',
            ]),
          )
          .optional(),
      },
      {
        name: 'startDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'endDate',
        type: 'Query',
        schema: startDate,
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'searchSentences',
        type: 'Query',
        schema: z
          .array(
            z.enum([
              'deposit',
              'withdrawal',
              'transferTo',
              'transferFrom',
              'realizedProfit',
              'closedCfdResult',
              'portfolioInvestment',
              'portfolioDivestment',
              'topUpForPurchase',
              'dividendReceived',
              'dividendWithholdingTax',
              'optionPremiumPaid',
              'optionPremiumPaidToClose',
              'optionPremiumReceived',
              'optionExerciseBuy',
              'optionExerciseSell',
              'optionAssignmentBuy',
              'optionAssignmentSell',
              'treasuryBondCoupon',
              'treasuryBondMaturity',
              'treasuryBondEarly',
              'bondCoupon',
              'bondMaturity',
              'bondEarly',
              'interestTax',
              'earlyRedemptionFee',
              'eventCommission',
              'eventCustodyFee',
              'eventAdrFee',
              'eventRegulatoryFee',
              'eventFxFee',
              'eventAdvisoryFee',
              'eventFee',
              'eventFeeRefund',
              'eventTransactionTax',
              'eventInterestTax',
              'eventDividendTax',
              'eventInterest',
              'eventBondCoupon',
              'eventSecuritiesLending',
              'typedTransaction',
              'incomeInKind',
              'labelledIncomeInKind',
              'importBaselineFunding',
              'snaptradeOpeningBalance',
              'snaptradeBaselineFunding',
              'snaptradeFxConversion',
              'snaptradeFxResidualOnDay',
              'snaptradeFxResidual',
              'snaptradeOpeningLot',
              'snaptradeBrokerHolding',
              'snaptradeOpeningBasis',
              'snaptradeCommission',
              'snaptradeOptionLeg',
              'allocationFromClosedSheet',
              'allocationFromOpenSheet',
              'allocationFromBothSheets',
              'allocationOpenSheet',
              'allocationOpenSheetHealH3',
            ]),
          )
          .optional(),
      },
      {
        name: 'sortBy',
        type: 'Query',
        schema: z.enum(['date', 'amount', 'type', 'walletName']).optional().default('date'),
      },
      {
        name: 'sortOrder',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('desc'),
      },
      {
        name: 'minAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'maxAmount',
        type: 'Query',
        schema: z.coerce.number().gte(0).optional(),
      },
      {
        name: 'displayCurrency',
        type: 'Query',
        schema: z
          .enum([
            'USD',
            'EUR',
            'GBP',
            'JPY',
            'CHF',
            'CAD',
            'AUD',
            'NZD',
            'CNY',
            'INR',
            'SEK',
            'NOK',
            'DKK',
            'PLN',
            'CZK',
            'HUF',
            'RON',
            'RUB',
            'TRY',
            'ILS',
            'AED',
            'SAR',
            'BRL',
            'MXN',
            'ARS',
            'CLP',
            'COP',
            'PEN',
            'UYU',
            'ZAR',
            'KRW',
            'SGD',
            'HKD',
            'TWD',
            'THB',
            'IDR',
            'MYR',
            'PHP',
            'VND',
            'HRK',
            'BGN',
          ])
          .optional(),
      },
      {
        name: 'walletIds',
        type: 'Query',
        schema: z.array(z.string().uuid()).optional(),
      },
    ],
    response: z
      .object({
        data: z
          .array(WalletTransactionResponseDto)
          .describe('List of recent transactions across all wallets'),
        meta: PaginationMetaDto.describe('Pagination metadata'),
        omittedNoRate: z
          .coerce.number()
          .int()
          .describe(
            'Rows an active amount window left out only because no stored rate carries them into the display currency (they are never compared in their own currency)',
          ),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/wallets/total-balance',
    alias: 'walletsGetTotalBalance',
    requestFormat: 'json',
    parameters: [
      {
        name: 'baseCurrency',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fxOverride',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        totalBalance: z.coerce.number().describe('Total balance converted to base currency'),
        totalAvailableBalance: z
          .coerce.number()
          .describe('Total available balance converted to base currency'),
        baseCurrency: z.string().describe('Base currency used for conversion'),
        walletBreakdown: z
          .array(
            z
              .object({
                walletId: z.string(),
                walletName: z.string(),
                originalCurrency: z.string(),
                originalBalance: z.coerce.number(),
                originalAvailableBalance: z.coerce.number(),
                convertedBalance: z.coerce.number(),
                convertedAvailableBalance: z.coerce.number(),
                exchangeRate: z.coerce.number(),
              })
              .passthrough(),
          )
          .describe('Breakdown of balances by wallet with original and converted amounts'),
        unconverted: z
          .array(
            z
              .object({
                walletId: z.string(),
                walletName: z.string(),
                currency: z.string(),
                originalBalance: z.coerce.number(),
                originalAvailableBalance: z.coerce.number(),
              })
              .passthrough(),
          )
          .describe(
            'Wallets EXCLUDED from the totals because no usable exchange rate to baseCurrency exists (correctness-or-null: a partial total beats a fabricated 1:1 conversion). Absent when every wallet converted.',
          )
          .optional(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/wallets/transfer',
    alias: 'walletsTransfer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: walletsTransfer_Body,
      },
    ],
    response: z
      .object({
        fromWalletId: z.string().describe('Source wallet ID'),
        toWalletId: z.string().describe('Destination wallet ID'),
        amountSent: z.coerce.number().describe('Amount transferred from source wallet'),
        fromCurrency: z.string().describe('Currency of the source wallet'),
        amountReceived: z.coerce.number().describe('Amount received in destination wallet'),
        toCurrency: z.string().describe('Currency of the destination wallet'),
        exchangeRate: z
          .coerce.number()
          .describe('Exchange rate used for the transfer (null for same currency)')
          .nullable(),
        fee: z.coerce.number().describe('Transfer fee (currently 0)'),
        fromWalletBalance: z.coerce.number().describe('Updated balance of source wallet'),
        toWalletBalance: z.coerce.number().describe('Updated balance of destination wallet'),
        transactionDate: z.string().describe('Transfer transaction date'),
        description: z.string().describe('Transfer description').nullable(),
      })
      .passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid input data`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wallet not found`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Too many requests - rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/watchlists',
    alias: 'watchlistsFindAll',
    requestFormat: 'json',
    response: z.array(WatchlistResponseDto),
  },
  {
    method: 'post',
    path: '/api/watchlists',
    alias: 'watchlistsCreate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: watchlistsCreate_Body,
      },
    ],
    response: WatchlistResponseDto,
  },
  {
    method: 'get',
    path: '/api/watchlists/:id',
    alias: 'watchlistsFindOne',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: WatchlistResponseDto,
  },
  {
    method: 'patch',
    path: '/api/watchlists/:id',
    alias: 'watchlistsUpdate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: watchlistsUpdate_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: WatchlistResponseDto,
  },
  {
    method: 'delete',
    path: '/api/watchlists/:id',
    alias: 'watchlistsDelete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/watchlists/:id/items',
    alias: 'watchlistsAddItem',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: watchlistsAddItem_Body,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: WatchlistItemResponseDto,
  },
  {
    method: 'patch',
    path: '/api/watchlists/:id/items/:itemId',
    alias: 'watchlistsUpdateItem',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          pinned: z
            .boolean()
            .describe('Pin (true) or unpin (false) the item in the research sidebar.'),
        }),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'itemId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: WatchlistItemResponseDto,
  },
  {
    method: 'delete',
    path: '/api/watchlists/:id/items/:itemId',
    alias: 'watchlistsRemoveItem',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'itemId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/watchlists/:id/rows',
    alias: 'watchlistRowsGetRows',
    description: `ONE batch for the whole list instead of three vendor round-trips per symbol. Correctness-or-null per field: a symbol without quotes, without a metric snapshot or outside the scored universe returns nulls in those cells and keeps the rest.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z
      .object({
        watchlistId: z.string().describe('The watchlist the rows belong to.'),
        rows: z
          .array(
            z
              .object({
                symbol: z
                  .string()
                  .describe(
                    'The symbol AS STORED on the watchlist item (broker notation is preserved).',
                  ),
                name: z
                  .string()
                  .describe(
                    'Instrument display name from the local registry; null when the symbol is unknown there.',
                  )
                  .nullable(),
                currency: z
                  .string()
                  .describe(
                    'Trading currency, normalized to the MAJOR unit (a GBX-quoted listing reports GBP, and `price` is divided to match).',
                  )
                  .nullable(),
                price: z
                  .coerce.number()
                  .describe(
                    'Last regular-session price in the major unit of `currency`. Null when every price-engine leg refused.',
                  )
                  .nullable(),
                changePercent: z
                  .coerce.number()
                  .describe(
                    'Regular-session day change in PERCENT (-1.23 = -1.23%), derived from the engine price and its session baseline. Null without a usable baseline.',
                  )
                  .nullable(),
                marketCap: z
                  .coerce.number()
                  .describe(
                    "Market capitalization in RAW currency units (not millions), from the metric kernel. The kernel resolves it to the issuer's reporting basis (a foreign ADR reports USD), so it is not guaranteed to be denominated in `currency`.",
                  )
                  .nullable(),
                peRatio: z
                  .coerce.number()
                  .describe(
                    'TTM price/earnings from the metric kernel. Null for a loss-maker or a symbol without a usable earnings base.',
                  )
                  .nullable(),
                stoxlyPoints: z
                  .coerce.number()
                  .describe(
                    'Stoxly Points 0-100 (composite quality score) read verbatim from the latest scoring cross-section. Null for a company that has no snapshot yet, or a secondary listing.',
                  )
                  .nullable(),
              })
              .passthrough(),
          )
          .describe('One row per watchlist item, in the item order (`added_at DESC`).'),
        asOf: z
          .string()
          .describe(
            'ISO-8601 instant the batch was composed — the freshness stamp for the whole table.',
          ),
      })
      .passthrough(),
  },
  {
    method: 'get',
    path: '/api/watchlists/pinned-items',
    alias: 'watchlistsGetPinnedItems',
    requestFormat: 'json',
    response: z.array(WatchlistItemResponseDto),
  },
  {
    method: 'post',
    path: '/api/webhooks/news',
    alias: 'newsWebhooksHandle',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/webhooks/snaptrade/webhook',
    alias: 'snaptradeWebhookHandleWebhook',
    requestFormat: 'json',
    response: z.unknown(),
  },
]);
